import {
  createNavigationRequestId,
  emitNavigationRequestProgress,
} from "@/lib/navigation-progress";
import {
  clearOfflineApiResponses,
  readOfflineApiResponse,
  writeOfflineApiResponse,
} from "@/lib/offline/offline-api-cache";
import { writeCurrencyPreference } from "@/lib/currency";
import { normalizeProductImageUrls, selectPrimaryProductImage } from "@/lib/product-images";

type ViteImportMeta = ImportMeta & {
  env?: Record<string, string | undefined>;
};

export type MarketplaceRole = "buyer" | "seller" | "courier" | "quality_assessor" | "admin";
export type DeliveryAddress = {
  id: string;
  label: string;
  addressLine1: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  locationLabel?: string;
  additionalLocationInfo?: string;
  isPrimary: boolean;
};
export type CourierVerificationStatus =
  | "NOT_SUBMITTED"
  | "PENDING"
  | "PARTIALLY_VERIFIED"
  | "FULLY_VERIFIED"
  | "VERIFIED"
  | "REJECTED";
export type SellerVerificationStatus = CourierVerificationStatus;

export function isFullyVerifiedCredentialStatus(
  status: CourierVerificationStatus | undefined,
): boolean {
  return status === "FULLY_VERIFIED" || status === "VERIFIED";
}

export function isPartiallyVerifiedCredentialStatus(
  status: CourierVerificationStatus | undefined,
): boolean {
  return status === "PARTIALLY_VERIFIED";
}
export type SortDirection = "asc" | "desc";

// Default admin avatar source: https://pngtree.com/so/admin-avatar
export const DEFAULT_ADMIN_AVATAR_URL =
  "https://png.pngtree.com/png-vector/20240720/ourmid/pngtree-man-with-glasses-and-a-suit-in-a-circular-profile-icon-vector-png-image_7239211.png";

export const AUTH_SESSION_UPDATED_EVENT = "sparelynk-auth-session-updated";
export const NAVIGATION_PENDING_ACTIONS_CHANGED_EVENT =
  "sparelynk:navigation-pending-actions-changed";

export function resolveUserAvatarUrl(
  user?: {
    role?: MarketplaceRole | string | null;
    avatarUrl?: string | null;
    avatar?: string | null;
  } | null,
): string | undefined {
  const avatarUrl = [user?.avatarUrl, user?.avatar].find(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );

  if (avatarUrl) {
    return avatarUrl.trim();
  }

  return String(user?.role ?? "")
    .trim()
    .toLowerCase() === "admin"
    ? DEFAULT_ADMIN_AVATAR_URL
    : undefined;
}

export type PageQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
  [key: string]: string | number | boolean | undefined;
};

export type PageResult<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type PartRequestStatus = "PENDING_ADMIN" | "PUBLISHED" | "REJECTED";

export type PartRequestSellerResponseView = {
  id: string;
  sellerId: string;
  sellerUserId: string;
  sellerName: string;
  note?: string;
  respondedAt: string;
};

export type PartRequestView = {
  id: string;
  buyerId: string;
  buyerName: string;
  partName: string;
  partNumber?: string;
  brand?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: number;
  quantity: number;
  details?: string;
  imageUrl?: string;
  status: PartRequestStatus;
  adminReviewNote?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  publishedAt?: string;
  sellerResponses: PartRequestSellerResponseView[];
  createdAt: string;
  updatedAt: string;
};

export type CreatePartRequestPayload = {
  partName: string;
  partNumber: string;
  brand: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  quantity: number;
  details: string;
  imageUrl?: string;
};

export type PartRequestCheckResult = {
  exists: boolean;
  existingProductId?: string;
  existingProductTitle?: string;
};

export type PartRequestExistingProductView = {
  existingProductId: string;
  existingProductTitle?: string;
};

export type PartRequestOptions = {
  partNames: string[];
  partNumbers: string[];
  brands: string[];
  vehicleMakes: string[];
  vehicleModels: string[];
  vehicleYears: number[];
};

export type PartRequestImageUploadView = {
  url: string;
  imageUrl: string;
  publicUrl?: string;
  contentType?: string;
};

export type PartRequestPendingCountView = {
  count: number;
};

export type PartRequestNotificationView = {
  id: string;
  requestId: string;
  type: "PART_REQUEST_PUBLISHED" | string;
  title: string;
  message: string;
  href: string;
  read: boolean;
  readAt?: string;
  createdAt: string;
};

export type PartRequestNotificationsView = {
  items: PartRequestNotificationView[];
  unreadCount: number;
};

export type NavigationPendingActionsResponse = {
  role: MarketplaceRole | string;
  total: number;
  counts: Record<string, number>;
};


export type AuthenticatedUser = {
  id: string;
  name: string;
  email: string;
  role: MarketplaceRole;
  isActive?: boolean;
  emailVerified?: boolean;
  mustChangePassword?: boolean;
  twoFactorEmailEnabled?: boolean;
  twoFactorAuthenticatorEnabled?: boolean;
  displayName?: string;
  avatarUrl?: string | null;
  phone?: string;
  address?: string;
  deliveryAddresses?: DeliveryAddress[];
  profileComplete?: boolean;
  canBuyProducts?: boolean;
  nationality?: string;
  preferredCurrency?: string;
  loyaltyBadges?: string[];
  courierIdentificationType?: string;
  courierIdentificationNumber?: string;
  courierIdentificationImageUrls?: string[];
  courierSelfieImageUrls?: string[];
  courierDeliveryMethod?: string;
  courierDriverLicenseNumber?: string;
  courierDriverLicenseExpiryDate?: string;
  courierDriverLicenseImageUrls?: string[];
  courierVehiclePlateNumber?: string;
  courierVehicleRegistrationExpiryDate?: string;
  courierVehicleRegistrationImageUrls?: string[];
  courierInsuranceProvider?: string;
  courierInsurancePolicyNumber?: string;
  courierInsuranceExpiryDate?: string;
  courierInsuranceImageUrls?: string[];
  courierEmergencyContactName?: string;
  courierEmergencyContactPhone?: string;
  courierExperience?: string;
  courierOtherWorkExperience?: string;
  courierVerificationStatus?: CourierVerificationStatus;
  courierVerificationNotes?: string;
  courierCredentialsSubmittedAt?: string;
  courierCredentialsVerifiedAt?: string;
  courierCredentialsVerifiedBy?: string;
  sellerIdentificationType?: string;
  sellerIdentificationNumber?: string;
  sellerIdentificationImageUrls?: string[];
  sellerSelfieImageUrls?: string[];
  sellerClassification?: string;
  sellerBusinessName?: string;
  sellerBusinessRegistrationNumber?: string;
  sellerBusinessRegistrationImageUrls?: string[];
  sellerTaxIdentificationNumber?: string;
  sellerBirCertificateImageUrls?: string[];
  sellerProofOfAddressImageUrls?: string[];
  sellerExperience?: string;
  sellerOtherWorkExperience?: string;
  sellerVerificationStatus?: SellerVerificationStatus;
  sellerVerificationNotes?: string;
  sellerCredentialsSubmittedAt?: string;
  sellerCredentialsVerifiedAt?: string;
  sellerCredentialsVerifiedBy?: string;
  qualityAssessorProfessionalTitle?: string;
  qualityAssessorLicenseNumber?: string;
  qualityAssessorAccreditingBody?: string;
  qualityAssessorCredentialImageUrls?: string[];
  qualityAssessorExperience?: string;
  qualityAssessorESignatureUrl?: string;
  qualityAssessorVerificationStatus?: SellerVerificationStatus;
  qualityAssessorVerificationNotes?: string;
  qualityAssessorCredentialsSubmittedAt?: string;
  qualityAssessorCredentialsVerifiedAt?: string;
  qualityAssessorCredentialsVerifiedBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type AuthSession = {
  user: AuthenticatedUser;
  token?: string;
  accessToken?: string;
  tokenType?: string;
  expiresAt?: string;
  expiresIn?: number;
  requiresEmailVerification?: boolean;
  emailVerificationSent?: boolean;
  verificationToken?: string;
  verificationUrl?: string;
  requiresTwoFactor?: boolean;
  twoFactorChallengeToken?: string;
  twoFactorMethods?: Array<"email" | "authenticator">;
  twoFactorEmailSent?: boolean;
  twoFactorCompletedMethods?: Array<"email" | "authenticator">;
  twoFactorRemainingMethods?: Array<"email" | "authenticator">;
  twoFactorAttemptsRemaining?: number;
  twoFactorLocked?: boolean;
  twoFactorRecoveryToken?: string;
  twoFactorBackupCodeAvailable?: boolean;
  twoFactorBackupEmails?: string[];
  remember?: boolean;
  createdAt?: string;
  persistedAt?: string;
};


export type TwoFactorSettings = {
  emailEnabled: boolean;
  authenticatorEnabled: boolean;
  primaryEmail?: string;
  backupEmail?: string;
  backupEmails?: string[];
  maskedEmail?: string;
  maskedBackupEmail?: string;
  maskedBackupEmails?: string[];
  backupCodesRemaining?: number;
  backupCodesViewable?: boolean;
  twoFactorAttemptsRemaining?: number;
  twoFactorLocked?: boolean;
};

export type BackupCodes = {
  codes: string[];
  generatedAt: string;
};

export type AccountRecoveryMessage = {
  id: string;
  senderRole: "user" | "admin";
  message: string;
  createdAt: string;
};

export type AccountRecoveryAttachment = {
  id: string;
  kind: string;
  fileName: string;
  contentType: string;
  size: number;
  url: string;
  createdAt: string;
};

export type AccountRecoveryCase = {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  requestType: "unlock" | "reset_2fa" | string;
  status: "OPEN" | "WAITING_USER" | "UNDER_REVIEW" | "APPROVED" | "REJECTED" | "UNLOCKED" | string;
  requiredAttachmentKinds?: string[];
  messages?: AccountRecoveryMessage[];
  attachments?: AccountRecoveryAttachment[];
  adminComment?: string;
  resetTwoFactor?: boolean;
  otpExpiresAt?: string;
  createdAt: string;
  updatedAt: string;
  accessToken?: string;
  oneTimeCode?: string;
};

export type AccountRecoveryUploadStatus = {
  uploadId: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: number[];
  uploadedBytes: number;
  percent: number;
  state: string;
};

export type AuthenticatorSetup = {
  secret: string;
  otpauthUrl: string;
  setupToken: string;
};

export type AssistantChatMessage = {
  role: "user" | "assistant";
  content: string;
};

export type AssistantReplyOption = {
  id: string;
  label: string;
  message: string;
};

export type AssistantOptionSelection = {
  replyToken: string;
  optionId: string;
};

export type AssistantFeedbackRating = "correct" | "incorrect";

export type AssistantFeedbackPayload = {
  feedback_token: string;
  rating: AssistantFeedbackRating;
  comment?: string;
};

export type AssistantFeedbackResponse = {
  accepted: boolean;
  learned: boolean;
  message: string;
};

export type AssistantChatPayload = {
  message: string;
  history?: AssistantChatMessage[];
  context?: {
    pathname?: string;
    role?: string;
    name?: string;
  };
  selection?: {
    reply_token: string;
    option_id: string;
  };
};

export type AssistantChatResponse = {
  reply: string;
  provider: "python" | "hybrid" | "local" | "ai" | "built-in" | string;
  model?: string;
  confidence?: number;
  intent?: string;
  entities?: Record<string, string[]>;
  sentiment?: "positive" | "negative" | "neutral" | string;
  options?: AssistantReplyOption[];
  reply_token?: string;
  feedback_token?: string;
};

export type AssistantCapabilitiesResponse = {
  ready: boolean;
  provider: "python" | "local" | string;
  model: string;
  fallback: string;
  features: string[];
};

export function notifyNavigationPendingActionsChanged() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(NAVIGATION_PENDING_ACTIONS_CHANGED_EVENT));
}

function notifyAuthSessionUpdated(session: AuthSession | null) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AuthSession | null>(AUTH_SESSION_UPDATED_EVENT, {
      detail: session,
    }),
  );
}

export type UserView = AuthenticatedUser & {
  status?: string;
  [key: string]: unknown;
};

export type VendorProfileView = {
  id: string;
  userId?: string;
  name?: string;
  storeName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  address?: string;
  description?: string;
  status?: string;
  rating?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type ProductView = {
  id: string;
  title: string;
  name: string;
  description?: string;
  sku?: string;
  productNumber?: string;
  partNumber?: string;
  category?: string;
  brand?: string;
  vehicleType?: string;
  vehicleModel?: string;
  modelYears?: number[];
  variant?: string;
  engine?: string;
  market?: string;
  location?: string;
  condition?: string;
  conditionDescription?: string;
  knownDefects?: string;
  testingStatus?: string;
  repairHistory?: string;
  qualityAssuranceStatus?: "PENDING" | "PASSED" | "REJECTED" | string;
  qualityAssuranceProfessionalName?: string;
  qualityAssuranceProfessionalTitle?: string;
  qualityAssuranceCredentialNumber?: string;
  qualityAssuranceSignatureUrl?: string;
  qualityAssuranceEvidenceUrls?: string[];
  qualityAssuranceSellerNotes?: string;
  qualityAssuranceAssessorUserId?: string;
  qualityAssuranceAssessmentSource?: string;
  qualityAssuranceWrittenAssessment?: string;
  qualityAssuranceAdminComment?: string;
  qualityAssuranceAdminEvidenceUrls?: string[];
  qualityAssuranceReviewedAt?: string;
  qualityAssurancePassedAt?: string;
  qualityAssuranceSeal?: boolean;
  price: number;
  salePrice?: number;
  effectivePrice?: number;
  currency?: string;
  commissionPercent?: number;
  commissionAmount?: number;
  sellerNetPrice?: number;
  fulfillmentBySpareLynk?: boolean;
  fulfillmentFeePerUnit?: number;
  advertisingActive?: boolean;
  advertisingCostPerClick?: number;
  advertisingBudgetRemaining?: number;
  advertisingCampaignId?: string;
  stock?: number;
  stockQty?: number;
  quantity?: number;
  status?: string;
  listingState?: string;
  vendorId?: string;
  vendorName?: string;
  adminReviewComment?: string;
  reviewComments?: ProductReviewComment[];
  buyerReviews?: BuyerReviewView[];
  extraProtectionFee?: number;
  baseShippingFee?: number;
  baseShippingFeeCurrency?: string;
  buyerShippingFeePercent?: number;
  freeShippingQuantityThreshold?: number;
  shopRating?: number;
  shopReviews?: number;
  imageUrl?: string;
  primaryImageUrl?: string;
  images?: string[];
  imageUrls?: string[];
  spinImageUrls?: string[];
  rating?: number;
  reviews?: number;
  specs?: Record<string, unknown>;
  fitment?: unknown[];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type FitmentView = {
  id: string;
  productId: string;
  productDisplayName?: string;
  make: string;
  model: string;
  year: number;
  trim?: string;
  engine?: string;
  confidence?: string;
  compatibilityLabel?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type WishlistItemView = {
  id: string;
  productId: string;
  userId?: string;
  product?: ProductView | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type CartItemView = {
  id: string;
  productId: string;
  userId?: string;
  quantity: number;
  product?: ProductView | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type CustomerEventType =
  | "PAGE_VIEW"
  | "ENGAGEMENT"
  | "SEARCH"
  | "SEARCH_RESULTS_VIEW"
  | "PRODUCT_IMPRESSION"
  | "PRODUCT_CLICK"
  | "PRODUCT_VIEW"
  | "CART_ADD"
  | "CART_REMOVE"
  | "WISHLIST_ADD"
  | "WISHLIST_REMOVE"
  | "CHECKOUT_START"
  | "PURCHASE"
  | "RETURN"
  | "REVIEW"
  | "FILTER_APPLY"
  | "EXPERIMENT_VIEW";

export type CustomerEventContext = {
  pathname?: string;
  referrer?: string;
  deviceType?: string;
  browser?: string;
  operatingSystem?: string;
  locale?: string;
  timeZone?: string;
  connectionType?: string;
  connectionMbps?: number;
  screenWidth?: number;
  screenHeight?: number;
  country?: string;
  region?: string;
  city?: string;
  membershipTier?: string;
};

export type TrackCustomerEventPayload = {
  anonymousId?: string;
  sessionId: string;
  eventType: CustomerEventType;
  productId?: string;
  orderId?: string;
  query?: string;
  category?: string;
  brand?: string;
  vehicleType?: string;
  vehicleModel?: string;
  value?: number;
  quantity?: number;
  context?: CustomerEventContext;
  metadata?: Record<string, string | number | boolean | string[]>;
  occurredAt?: string;
};

export type CustomerProfileView = {
  subjectKey: string;
  userId?: string;
  anonymousId?: string;
  categoryScores?: Record<string, number>;
  brandScores?: Record<string, number>;
  vehicleScores?: Record<string, number>;
  productScores?: Record<string, number>;
  searchTermScores?: Record<string, number>;
  deliveryWindowScores?: Record<string, number>;
  observedPriceAverage?: number;
  purchaseCount?: number;
  returnCount?: number;
  priceSensitivity: number;
  returnLikelihood: number;
  preferredDeliveryWindow?: string;
  lastEventAt?: string;
};

export type ProductRecommendationView = {
  product: ProductView;
  score: number;
  reasons: string[];
};

export type RecommendationResultView = {
  subjectKey?: string;
  personalized: boolean;
  profile?: CustomerProfileView;
  items: ProductRecommendationView[];
};

export type ExperimentAssignmentView = {
  subjectKey?: string;
  experimentKey: string;
  variant: string;
  createdAt?: string;
};

export type CustomerMetricView = {
  key: string;
  count: number;
};

export type CustomerDataSummaryView = {
  windowDays: number;
  totalEvents: number;
  uniqueUsers: number;
  uniqueSessions: number;
  productViews: number;
  cartAdds: number;
  checkoutStarts: number;
  purchases: number;
  returns: number;
  conversionRate: number;
  cartAbandonmentRate: number;
  topSearches: CustomerMetricView[];
  topCategories: CustomerMetricView[];
  topProducts: CustomerMetricView[];
  eventsByType: Record<string, number>;
};

export type OrderAddressPayload = {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  lat?: number;
  lng?: number;
  lon?: number;
  locationLabel?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    lat?: number;
    lng?: number;
    lon?: number;
    label?: string;
    [key: string]: unknown;
  };
  additionalLocationInfo?: string;
  [key: string]: unknown;
};

export type CreateCheckoutOrderPayload = {
  customerId?: string;
  items: Array<{
    productId: string;
    title?: string;
    productName?: string;
    productNumber?: string;
    partNumber?: string;
    sku?: string;
    vendorId?: string;
    quantity: number;
    price?: number;
    unitPrice?: number;
    imageUrl?: string;
    productImage?: string;
    extraProtectionSelected?: boolean;
    extraProtectionFee?: number;
    [key: string]: unknown;
  }>;
  shippingAddress?: OrderAddressPayload;
  billingAddress?: OrderAddressPayload;
  paymentMethod?: string;
  paymentStatus?: string;
  deliveryMethod?: string;
  notes?: string;
  subtotal?: number;
  tax?: number;
  shipping?: number;
  shippingFee?: number;
  buyerShippingFee?: number;
  shippingPayer?: "none" | "buyer" | "seller" | "shared" | string;
  freeShippingApplied?: boolean;
  freeShippingReason?: string;
  freeShippingQuantityThreshold?: number;
  discount?: number;
  total?: number;
  currency?: string;
  [key: string]: unknown;
};

export type CreateStripeCheckoutPayload = CreateCheckoutOrderPayload & {
  successUrl?: string;
  cancelUrl?: string;
};

export type RetryStripeCheckoutPayload = {
  successUrl?: string;
  cancelUrl?: string;
  [key: string]: unknown;
};

export type ConfirmStripeOrderPaymentPayload = {
  sessionId: string;
};

export type StripeCheckoutSessionView = {
  id: string;
  url: string;
  paymentStatus?: string;
  amountTotal?: number;
  currency?: string;
  publishableKey?: string;
  [key: string]: unknown;
};

export type StripePaymentConfigView = {
  enabled: boolean;
  publishableKey?: string;
  currency: string;
  [key: string]: unknown;
};

export type StripeCheckoutOrderView = {
  order: OrderView;
  checkoutSession: StripeCheckoutSessionView;
};

export type CheckoutMethod =
  "online_payment" | "cash_on_delivery" | "pickup" | "wallet";

export type DeliveryMethod = "DELIVERY" | "PICKUP";

export type OrderDeliveryStatus =
  | "order_placed"
  | "seller_processing"
  | "ready_for_pickup"
  | "picked_up_by_courier"
  | "in_transit"
  | "out_for_delivery"
  | "delivery_attempted"
  | "delivered"
  | "received"
  | "return_requested"
  | "return_in_transit"
  | "returned"
  | "refund_requested"
  | "refunded"
  | "cancelled";

export type OrderCourierView = {
  courierId?: string;
  name?: string;
  email?: string;
  trackingNumber?: string;
  contactPhone?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  estimatedDeliveryAt?: string;
  assignedAt?: string;
  [key: string]: unknown;
};

export type OrderCourierReassignmentView = {
  id: string;
  status: "open" | "completed" | "cancelled" | string;
  requestedByCourierId?: string;
  requestedByCourierName?: string;
  previousCourier?: OrderCourierView | null;
  reason: string;
  handoffLocation?: string;
  notes?: string;
  requestedAt: string;
  acceptedByCourierId?: string;
  acceptedByCourierName?: string;
  acceptedAt?: string;
  cancelledAt?: string;
  [key: string]: unknown;
};

export type OrderDeliveryEventView = {
  id: string;
  status: OrderDeliveryStatus;
  label: string;
  locationLabel?: string;
  notes?: string;
  actorRole?: string;
  courierName?: string;
  proofUrls?: string[];
  paymentProofUrls?: string[];
  createdAt: string;
  [key: string]: unknown;
};

export type ConversationProductAttachmentView = {
  id: string;
  name: string;
  imageUrl?: string;
  price?: number;
  vendorId?: string;
};

export type ConversationMessageReplyView = {
  messageId: string;
  senderName?: string;
  message: string;
};

export type ConversationMessageContextView = {
  type: "product" | "products" | "shop" | "order" | string;
  id: string;
  label: string;
  images: string[];
  productId?: string;
  orderId?: string;
  orderIds?: string[];
  products?: ConversationProductAttachmentView[];
};

export type ConversationMediaAttachmentView = {
  id: string;
  key?: string;
  url: string;
  fileName: string;
  contentType: string;
  mediaType: "image" | "video" | "audio" | "document" | string;
  size: number;
};

export type OrderMessageView = {
  vendorId?: string;
  channel?: "courier_seller" | "courier_buyer";
  id: string;
  senderId?: string;
  senderRole: "buyer" | "seller" | "courier" | "admin" | string;
  senderName: string;
  senderAvatarUrl?: string;
  message: string;
  createdAt: string;
  context?: ConversationMessageContextView;
  replyTo?: ConversationMessageReplyView;
  attachments?: ConversationMediaAttachmentView[];
};

export type ShopConversationView = {
  id?: string;
  buyerId: string;
  vendorId: string;
  buyerName: string;
  sellerName: string;
  buyerAvatarUrl?: string;
  sellerAvatarUrl?: string;
  messages: OrderMessageView[];
  unseenCount: number;
  buyerLastReadAt?: string;
  sellerLastReadAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type SendShopConversationMessagePayload = {
  buyerId: string;
  vendorId: string;
  buyerName: string;
  sellerName: string;
  senderId: string;
  senderRole: "buyer" | "seller" | "quality_assessor";
  senderName: string;
  senderAvatarUrl?: string;
  message: string;
  context?: ConversationMessageContextView;
  replyTo?: ConversationMessageReplyView;
  attachments?: ConversationMediaAttachmentView[];
};

export type ShopConversationMessageResult = {
  id?: string;
  buyerId: string;
  vendorId: string;
  buyerName: string;
  sellerName: string;
  buyerAvatarUrl?: string;
  sellerAvatarUrl?: string;
  message: OrderMessageView;
  unseenCount: number;
  createdAt?: string;
  updatedAt?: string;
};

export type MarkShopConversationReadPayload = {
  buyerId: string;
  vendorId: string;
  readerId: string;
  readerRole: "buyer" | "seller" | "quality_assessor";
};

export type ShopConversationRealtimeEvent = {
  type: string;
  buyerId: string;
  vendorId: string;
  data?: unknown;
  publishedAt?: string;
};

export type SubscribeShopConversationEventsInput = {
  participantId: string;
  role: "buyer" | "seller" | "quality_assessor";
  vendorId?: string;
  onEvent: (event: ShopConversationRealtimeEvent) => void;
  onError?: (error: unknown) => void;
};

export type CreateOrderMessagePayload = {
  vendorId?: string;
  senderId?: string;
  senderRole: "seller" | "courier" | "buyer";
  channel?: "courier_seller" | "courier_buyer";
  attachments?: ConversationMediaAttachmentView[];
  senderName: string;
  message: string;
  context?: ConversationMessageContextView;
  replyTo?: ConversationMessageReplyView;
};

export type MarkOrderMessagesReadPayload = {
  vendorId?: string;
  channel?: "courier_seller" | "courier_buyer";
  readerId: string;
  readerRole: "seller" | "courier" | "buyer";
};

export type OrderMessagesReadResult = {
  orderId: string;
  readerRole: "seller" | "courier" | "buyer";
  readAt: string;
};

export type OrderDeliveryMode = "courier_marketplace" | "seller_self_delivery";

export type UpdateOrderPaymentStatusPayload = {
  paymentStatus: string;
  actorId?: string;
  actorRole?: "seller" | "courier" | "admin" | string;
};

export type UpdateOrderDeliveryPayload = {
  status: OrderDeliveryStatus | string;
  actorId?: string;
  deliveryMode?: OrderDeliveryMode;
  courierId?: string;
  courierName?: string;
  courierEmail?: string;
  trackingNumber?: string;
  contactPhone?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  estimatedDeliveryAt?: string;
  locationLabel?: string;
  latitude?: number;
  longitude?: number;
  locationSource?: string;
  notes?: string;
  actorRole?: string;
  proofUrls?: string[];
  paymentProofUrls?: string[];
  shippingFee?: number;
  courierFeePercent?: number;
  platformFeePercent?: number;
  rendezvousLocation?: string;
  rendezvousAdditionalLocationInfo?: string;
  rendezvousLatitude?: number;
  rendezvousLongitude?: number;
  rendezvousLocationSource?: string;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  dropoffLocationSource?: string;
  shippingDistanceMeters?: number;
  shippingDurationSeconds?: number;
  settlementStatus?: string;
  codRemittanceStatus?: string;
  courierPaymentStatus?: string;
  [key: string]: unknown;
};

export type ClaimOrderDeliveryPayload = {
  courierId: string;
  courierName: string;
  courierEmail: string;
  contactPhone: string;
  trackingNumber?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  estimatedDeliveryAt?: string;
};

export type ConfirmCourierPickupPayload = {
  sellerId: string;
  locationLabel?: string;
  latitude?: number;
  longitude?: number;
  locationSource?: string;
  notes?: string;
};

export type UpdateOrderDeliveryEventPayload = {
  courierId: string;
  notes?: string;
  locationLabel?: string;
};

export type RequestCourierReassignmentPayload = {
  courierId: string;
  reason: string;
  handoffLocation: string;
  notes?: string;
};

export type CancelCourierReassignmentPayload = {
  courierId: string;
};

export type CancelOrderPayload = {
  buyerId: string;
  reason: string;
  notes?: string;
};

export type DeliveryProofUploadView = {
  url: string;
  key: string;
  contentType: string;
  size: number;
};

export type DeliveryProofResumableUploadSessionView = {
  uploadId: string;
  key: string;
  partSize: number;
  proofKind: "delivery" | "payment";
  contentType: string;
  fileName: string;
  fileSize: number;
  uploadedParts: ResumableUploadPartView[];
};

export type ReturnRefundPlaybackSourceView = {
  url: string;
  type?: string;
  label?: string;
  width?: number;
  bitrate?: number;
};

export type ReturnRefundEvidenceView = {
  url: string;
  key: string;
  mediaType: "image" | "video";
  contentType: string;
  fileName: string;
  size: number;
  sources?: ReturnRefundPlaybackSourceView[];
};

export type ReturnRefundRequestView = {
  id: string;
  action: "return" | "refund";
  status: "pending_admin_review" | "approved" | "rejected" | string;
  buyerNotes?: string;
  evidence: ReturnRefundEvidenceView[];
  refundAmount: number;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  adminNotes?: string;
};

export type ResumableUploadPartView = {
  partNumber: number;
  etag: string;
  size: number;
};

export type ResumableUploadSessionView = {
  uploadId: string;
  key: string;
  partSize: number;
  mediaType: "image" | "video";
  contentType: string;
  fileName: string;
  fileSize: number;
  uploadedParts: ResumableUploadPartView[];
};

export type BuyerOrderDeliveryActionPayload = {
  action: "received" | "return" | "refund";
  notes?: string;
  proofUrls?: string[];
  evidence?: ReturnRefundEvidenceView[];
  [key: string]: unknown;
};

export type OrderTrackingPointView = {
  latitude: number;
  longitude: number;
  label?: string;
  status?: string;
  source?: string;
  accuracyMeters?: number;
  heading?: number;
  speedMps?: number;
  updatedAt?: string;
  [key: string]: unknown;
};

export type OrderTrackingLocationView = OrderTrackingPointView & {
  orderId?: string;
  expiresAt?: string;
  isLive: boolean;
  track?: OrderTrackingPointView[];
};

export type UpdateOrderShippingAddressPayload = OrderAddressPayload;

export type TrackingConfigView = {
  googleMapsPlatformApiKey: string;
  googleMapsMapId: string;
  trackingLocationTtlSeconds: number;
  trackingBroadcastIntervalMs: number;
  [key: string]: unknown;
};

export type ReverseGeocodeLocationView = {
  latitude: number;
  longitude: number;
  label: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  [key: string]: unknown;
};

export type RouteDistanceView = {
  distanceMeters: number;
  durationSeconds: number;
  distanceText: string;
  durationText: string;
  [key: string]: unknown;
};

export type ReverseGeocodeLocationContext = {
  label?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type UpdateOrderTrackingPayload = {
  actorId: string;
  actorRole: "courier" | "seller";
  latitude: number;
  longitude: number;
  label?: string;
  status?: string;
  source?: string;
  accuracyMeters?: number;
  heading?: number;
  speedMps?: number;
  [key: string]: unknown;
};

export type OrderItemView = {
  productId: string;
  productName: string;
  productImage?: string;
  vendorId?: string;
  productNumber?: string;
  partNumber?: string;
  sku?: string;
  category?: string;
  referralFeePercent?: number;
  referralFeeAmount?: number;
  fulfillmentBySpareLynk?: boolean;
  fulfillmentFeePerUnit?: number;
  fulfillmentFeeAmount?: number;
  quantity: number;
  unitPrice: number;
  extraProtectionSelected?: boolean;
  extraProtectionFee?: number;
  total: number;
  [key: string]: unknown;
};

export type CourierRatingView = {
  rating: number;
  review?: string;
  buyerId: string;
  buyerName: string;
  createdAt: string;
  updatedAt: string;
};

export type RateCourierPayload = {
  rating: number;
  review?: string;
};

export type OrderView = {
  id: string;
  orderNumber?: string;
  displayName?: string;
  customerId?: string;
  customerDisplayName?: string;
  customerEmail?: string;
  vendorId?: string;
  vendorName?: string;
  productId?: string;
  productName?: string;
  productImage?: string;
  quantity?: number;
  unitPrice?: number;
  status?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  subtotal?: number;
  tax?: number;
  shipping?: number;
  shippingFee?: number;
  buyerShippingFee?: number;
  sellerShippingContribution?: number;
  shippingPayer?: string;
  freeShippingApplied?: boolean;
  freeShippingReason?: string;
  freeShippingQuantityThreshold?: number;
  discount?: number;
  total: number;
  extraProtectionFee?: number;
  extraProtectionSelected?: boolean;
  currency?: string;
  courierFeePercent?: number;
  courierFeeAmount?: number;
  courierPayoutAmount?: number;
  courierWalletCreditedAt?: string;
  sellerWalletCreditedAt?: string;
  platformWalletCreditedAt?: string;
  platformFeePercent?: number;
  referralFeeAmount?: number;
  perItemSellingFeeAmount?: number;
  fulfillmentFeeAmount?: number;
  platformFeeAmount?: number;
  sellerNetAmount?: number;
  rendezvousLocation?: string;
  rendezvousAdditionalLocationInfo?: string;
  rendezvousLatitude?: number;
  rendezvousLongitude?: number;
  rendezvousLocationSource?: string;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  dropoffLocationSource?: string;
  shippingDistanceMeters?: number;
  shippingDurationSeconds?: number;
  settlementStatus?: string;
  codRemittanceStatus?: string;
  courierPaymentStatus?: string;
  paymentMethod?: string;
  deliveryMethod?: string;
  deliveryMode?: OrderDeliveryMode;
  courierOfferPublished?: boolean;
  courierOfferPublishedAt?: string;
  deliveryStatus?: OrderDeliveryStatus | string;
  courier?: OrderCourierView | null;
  courierRating?: CourierRatingView | null;
  courierReassignment?: OrderCourierReassignmentView | null;
  courierReassignments?: OrderCourierReassignmentView[];
  courierId?: string;
  courierName?: string;
  courierEmail?: string;
  trackingNumber?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  estimatedDeliveryAt?: string;
  deliveryEvents?: OrderDeliveryEventView[];
  messages?: OrderMessageView[];
  sellerMessagesReadAt?: string;
  courierMessagesReadAt?: string;
  courierBuyerMessagesReadAt?: string;
  sellerMessagesReadAtByVendor?: Record<string, string>;
  courierMessagesReadAtByVendor?: Record<string, string>;
  cancellationReason?: string;
  proofUrls?: string[];
  paymentProofUrls?: string[];
  returnRefundRequest?: ReturnRefundRequestView | null;
  trackingLocation?: OrderTrackingLocationView | null;
  itemCount?: number;
  items?: OrderItemView[];
  fulfillmentBySpareLynk?: boolean;
  fulfillmentUnits?: number;
  shippingAddress?: OrderAddressPayload | null;
  billingAddress?: OrderAddressPayload | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type WalletTransactionView = {
  id: string;
  type: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  reference: string;
  orderId?: string;
  createdAt: string;
};

export type WalletSummaryView = {
  role: MarketplaceRole;
  userId?: string;
  currency: string;
  grossSales: number;
  availableBalance: number;
  pendingBalance: number;
  courierEarnings: number;
  platformEarnings: number;
  sellerEarnings: number;
  qualityAssessorEarnings: number;
  qualityAssessorAssessments: number;
  cashInTotal?: number;
  refundTotal?: number;
  purchaseTotal?: number;
  cashoutTotal?: number;
  transferSentTotal?: number;
  transferReceivedTotal?: number;
  orders: number;
  transactions?: WalletTransactionView[];
};

export type StripeBalanceView = {
  currency: string;
  availableBalance: number;
  pendingBalance: number;
  totalBalance: number;
  livemode: boolean;
};

export type MarketplaceFeeSettingsView = {
  minimumReferralFee: number;
  perItemSellingFee: number;
  professionalMonthlyPrice: number;
  fulfillmentFeePerUnit: number;
  minimumAdvertisingBudget: number;
  qualityAssessorAssessmentFee?: number;
  categoryReferralFeePercents: Record<string, number>;
  pricingModel?: string;
  cashoutEnabled?: boolean;
  cashoutSandbox?: boolean;
  cashoutOTPRequired?: boolean;
  connectWebhookConfigured?: boolean;
};

export type MarketplaceFeeSettingsUpdate = Partial<
  Pick<
    MarketplaceFeeSettingsView,
    | "minimumReferralFee"
    | "perItemSellingFee"
    | "categoryReferralFeePercents"
  >
>;


export type ReportEmailPreferences = {
  weekly: boolean;
  monthly: boolean;
  yearly: boolean;
  reports?: ReportEmailSelection[] | null;
};

export type ReportEmailSelection = {
  key: string;
  label: string;
};

export type ReportEmailPeriod = "weekly" | "monthly" | "yearly" | "custom";
export type ReportPerspective =
  | "marketing"
  | "sales"
  | "business"
  | "owner"
  | "seller"
  | "buyer"
  | "admin"
  | "manager"
  | "courier"
  | "quality_assessor";

export type SendReportEmailPayload = {
  period: ReportEmailPeriod;
  perspective?: ReportPerspective;
  from?: string;
  to?: string;
  reports?: ReportEmailSelection[];
};

export type PushNotificationConfig = {
  supported: boolean;
  vapidPublicKey: string;
};

export type PushSubscriptionPayload = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

export type ReportPushSubscriptionPayload = PushSubscriptionPayload;

export type ReportMetricComparisonView = {
  key: string;
  label: string;
  current: number;
  previous: number;
  change: number;
  changePercent: number | null;
  unit: "currency" | "percent" | "count" | string;
  direction: "up" | "down" | "flat" | string;
  status: "positive" | "negative" | "neutral" | "unavailable" | string;
  explanation: string;
  available: boolean;
};

export type ReportInsightItemView = {
  key: string;
  title: string;
  detail: string;
  priority: "High" | "Medium" | "Low" | string;
  metric?: string;
  entity?: string;
};

export type ReportInterpretationView = {
  previousPeriod: { from: string; to: string };
  executiveSummary: string[];
  comparisons: ReportMetricComparisonView[];
  highlights: ReportInsightItemView[];
  alerts: ReportInsightItemView[];
  actions: ReportInsightItemView[];
};

export type RoleReportSummaryView = {
  orders: number;
  completedOrders: number;
  cancelledOrders: number;
  refundedOrders: number;
  units: number;
  revenue: number;
  averageOrderValue: number;
  ratedOrders: number;
  averageRating: number;
  personalOrders: number;
  personalCompleted: number;
  personalCancelled: number;
  personalRefunded: number;
  personalSpend: number;
  personalAverageOrderValue: number;
  personalPayouts: number;
  personalRatedOrders: number;
  personalAverageRating: number;
};

export type RoleReportTrendView = {
  period: string;
  orders: number;
  completed: number;
  units: number;
  revenue: number;
};

export type RoleReportProductRankView = {
  rank: number;
  id: string;
  name: string;
  orders: number;
  units: number;
  revenue: number;
  rating: number;
  reviews: number;
  demandScore: number;
};

export type RoleReportItemRankView = {
  rank: number;
  id: string;
  name: string;
  orders: number;
  units: number;
  value: number;
};

export type RoleReportSellerRankView = {
  rank: number;
  id: string;
  name: string;
  orders: number;
  units: number;
  revenue: number;
  rating: number;
  reviews: number;
};

export type RoleReportBuyerRankView = {
  rank: number;
  id: string;
  name: string;
  email?: string;
  orders: number;
  units: number;
  spend: number;
};

export type RoleReportCourierRankView = {
  rank: number;
  id: string;
  name: string;
  parcels: number;
  successfulDeliveries: number;
  successRate: number;
  payouts: number;
  rating: number;
  reviews: number;
  performanceScore: number;
};

export type RoleReportRouteRankView = {
  rank: number;
  origin: string;
  destination: string;
  deliveries: number;
};

export type RoleReportView = {
  role: "buyer" | "seller" | "courier" | "admin" | string;
  generatedAt: string;
  dateFrom: string;
  dateTo: string;
  previousDateFrom: string;
  previousDateTo: string;
  currency: string;
  summary: RoleReportSummaryView;
  previousSummary: RoleReportSummaryView;
  interpretation: ReportInterpretationView;
  monthly: RoleReportTrendView[];
  yearly: RoleReportTrendView[];
  personalMonthly: RoleReportTrendView[];
  personalYearly: RoleReportTrendView[];
  products: RoleReportProductRankView[];
  fitments: RoleReportItemRankView[];
  sellers: RoleReportSellerRankView[];
  buyers: RoleReportBuyerRankView[];
  couriers: RoleReportCourierRankView[];
  routes: RoleReportRouteRankView[];
  places: RoleReportItemRankView[];
  recommendations: string[];
};

export type AdminReportSummaryView = {
  grossMerchandiseValue: number;
  platformRevenue: number;
  referralFeeRevenue: number;
  perItemFeeRevenue: number;
  fulfillmentRevenue: number;
  subscriptionRevenue: number;
  advertisingRevenue: number;
  sellerNetRevenue: number;
  courierPayouts: number;
  taxes: number;
  discounts: number;
  orders: number;
  completedOrders: number;
  cancelledOrders: number;
  refundedOrders: number;
  averageOrderValue: number;
  platformTakeRate: number;
  totalUsers: number;
  activeUsers: number;
  totalBuyers: number;
  totalSellers: number;
  totalCouriers: number;
  newUsers: number;
  newBuyers: number;
  newSellers: number;
  newCouriers: number;
  totalProducts: number;
  liveProducts: number;
  fulfillmentOrders: number;
  fulfillmentUnits: number;
};

export type AdminMonthlyReportView = {
  month: string;
  orders: number;
  completedOrders: number;
  grossSales: number;
  platformRevenue: number;
  referralFeeRevenue: number;
  perItemFeeRevenue: number;
  fulfillmentRevenue: number;
  subscriptionRevenue: number;
  advertisingRevenue: number;
  sellerNetRevenue: number;
  courierPayouts: number;
  refundedOrders: number;
  newBuyers: number;
  newSellers: number;
  newCouriers: number;
};

export type AdminYearlyReportView = {
  year: string;
  orders: number;
  completedOrders: number;
  refundedOrders: number;
  grossSales: number;
  platformRevenue: number;
  referralFeeRevenue: number;
  perItemFeeRevenue: number;
  fulfillmentRevenue: number;
  subscriptionRevenue: number;
  advertisingRevenue: number;
  sellerNetRevenue: number;
  courierPayouts: number;
  newBuyers: number;
  newSellers: number;
  newCouriers: number;
};

export type AdminCountAmountReportView = {
  name: string;
  count: number;
  grossSales: number;
  platformRevenue: number;
};

export type AdminCategoryReportView = {
  category: string;
  orders: number;
  units: number;
  grossSales: number;
  referralFeeRevenue: number;
  averageSellingPrice: number;
};

export type AdminSellerReportView = {
  sellerId: string;
  sellerName: string;
  orders: number;
  units: number;
  grossSales: number;
  platformRevenue: number;
  fulfillmentRevenue: number;
  sellerNetRevenue: number;
};

export type AdminUserReportView = {
  role: string;
  total: number;
  active: number;
  inactive: number;
  verified: number;
};

export type AdminProductReportView = {
  name: string;
  count: number;
  stockQty: number;
};

export type AdminServiceRevenueView = {
  serviceType: string;
  transactions: number;
  revenue: number;
};

export type AdminActionItemsView = {
  pendingProductApprovals: number;
  pendingSellerVerification: number;
  pendingCourierVerification: number;
  activeOrders: number;
  pendingReturnReviews: number;
  unsettledOrders: number;
  lowStockProducts: number;
  outOfStockProducts: number;
};

export type AdminRecentOrderReportView = {
  orderId: string;
  orderNumber: string;
  createdAt: string;
  status: string;
  paymentMethod: string;
  sellerName: string;
  items: number;
  total: number;
  platformRevenue: number;
  fulfillmentRevenue: number;
  sellerNet: number;
  settlementStatus: string;
};

export type AdminFeeScheduleView = {
  minimumReferralFee: number;
  perItemSellingFee: number;
  professionalMonthlyPrice: number;
  fulfillmentFeePerUnit: number;
  minimumAdvertisingBudget: number;
  qualityAssessorAssessmentFee?: number;
  categoryReferralFeePercents: Record<string, number>;
  pricingModel: string;
};


export type AdminPlatformProfitReportView = {
  platformProfit: number;
  marketplaceFees: number;
  serviceRevenue: number;
  grossSales: number;
  sellerNetRevenue: number;
  courierPayouts: number;
  profitMargin: number;
  basis: string;
};

export type AdminDemandPeriodComparisonView = {
  profitChangePercent: number;
  grossSalesChangePercent: number;
  ordersChangePercent: number;
  completedOrdersChangePercent: number;
  unitsChangePercent: number;
};

export type AdminDemandSellerRankView = {
  rank: number;
  sellerId: string;
  userId: string;
  walletOwnerId: string;
  sellerName: string;
  orders: number;
  units: number;
  grossSales: number;
  platformRevenue: number;
  sellerNetRevenue: number;
  demandScore: number;
  recommendedBadge: string;
};

export type AdminDemandBuyerRankView = {
  rank: number;
  buyerId: string;
  userId: string;
  walletOwnerId: string;
  buyerName: string;
  orders: number;
  units: number;
  grossSpend: number;
  loyaltyScore: number;
  recommendedBadge: string;
};

export type AdminDemandCourierRankView = {
  rank: number;
  courierId: string;
  userId: string;
  walletOwnerId: string;
  courierName: string;
  parcelsReceived: number;
  successfulDeliveries: number;
  successRate: number;
  courierPayouts: number;
  rating: number;
  reviews: number;
  performanceScore: number;
  recommendedBadge: string;
};

export type AdminDemandItemRankView = {
  rank: number;
  id: string;
  name: string;
  orders: number;
  units: number;
  grossSales: number;
  demandScore: number;
};

export type AdminDemandPeriodView = {
  key: string;
  label: string;
  dateFrom: string;
  dateTo: string;
  profit: AdminPlatformProfitReportView;
  orders: number;
  revenueOrders: number;
  completedOrders: number;
  cancelledOrders: number;
  refundedOrders: number;
  units: number;
  successfulDeliveryRate: number;
  comparison: AdminDemandPeriodComparisonView;
  topSellers: AdminDemandSellerRankView[];
  topBuyers: AdminDemandBuyerRankView[];
  topCouriers: AdminDemandCourierRankView[];
  topProducts: AdminDemandItemRankView[];
  topCategories: AdminDemandItemRankView[];
  topFitments: AdminDemandItemRankView[];
  topModels: AdminDemandItemRankView[];
};

export type AdminDemandDashboardView = {
  thisMonth: AdminDemandPeriodView;
  previousMonth: AdminDemandPeriodView;
  thisYear: AdminDemandPeriodView;
  previousYear: AdminDemandPeriodView;
};

export type AdminBusinessRecommendationView = {
  priority: string;
  title: string;
  action: string;
  evidence: string;
  metric: string;
  changePercent: number;
};

export type AdminBusinessRecommendationsView = {
  nextMonth: AdminBusinessRecommendationView[];
  nextYear: AdminBusinessRecommendationView[];
};

export type AdminReportRewardPayload = {
  userId: string;
  walletOwnerId: string;
  role: "seller" | "buyer" | "courier";
  amount: number;
  currency: string;
  subject?: string;
  message?: string;
  badgeCode?: string;
  periodKey: string;
};

export type AdminReportRewardResponse = {
  rewarded: boolean;
  alreadyRewarded: boolean;
  messageSent: boolean;
  warning?: string;
  userId: string;
  walletOwnerId: string;
  role: "seller" | "buyer" | "courier";
  amount: number;
  currency: string;
  walletBalance: number;
  badgeCode: string;
  badges: string[];
  reference: string;
};

export type AssessmentReportSummaryView = {
  total: number;
  passed: number;
  rejected: number;
  approvalRate: number;
  uniqueAssessors: number;
  uniqueSellers: number;
  uniqueCategories: number;
  evidenceItems: number;
  averageEvidencePerAssessment: number;
};

export type AssessmentReportTrendView = {
  period: string;
  total: number;
  passed: number;
  rejected: number;
  evidenceItems: number;
};

export type AssessmentReportCategoryView = {
  category: string;
  total: number;
  passed: number;
  rejected: number;
  approvalRate: number;
};

export type AssessmentReportSellerView = {
  sellerId: string;
  sellerName: string;
  total: number;
  passed: number;
  rejected: number;
  approvalRate: number;
};

export type AssessmentReportAssessorView = {
  assessorId: string;
  assessorName: string;
  total: number;
  passed: number;
  rejected: number;
  evidenceItems: number;
  approvalRate: number;
};

export type AssessmentReportSourceView = {
  name: string;
  count: number;
};

export type AssessmentReportFindingView = {
  name: string;
  count: number;
};

export type AssessmentReportFindingsView = {
  authenticity: AssessmentReportFindingView[];
  condition: AssessmentReportFindingView[];
  testing: AssessmentReportFindingView[];
  defects: AssessmentReportFindingView[];
};

export type AssessmentReportRecentView = {
  productId: string;
  productName: string;
  productNumber: string;
  sellerId: string;
  sellerName: string;
  category: string;
  status: string;
  source: string;
  assessorId: string;
  assessorName: string;
  professionalTitle: string;
  reviewedAt: string;
  evidenceCount: number;
  writtenAssessment: string;
};

export type AssessmentReportView = {
  generatedAt: string;
  dateFrom: string;
  dateTo: string;
  previousDateFrom: string;
  previousDateTo: string;
  scope: "admin" | "quality_assessor" | string;
  summary: AssessmentReportSummaryView;
  previousSummary: AssessmentReportSummaryView;
  interpretation: ReportInterpretationView;
  monthly: AssessmentReportTrendView[];
  categories: AssessmentReportCategoryView[];
  sellers: AssessmentReportSellerView[];
  assessors: AssessmentReportAssessorView[];
  sources: AssessmentReportSourceView[];
  findings: AssessmentReportFindingsView;
  recent: AssessmentReportRecentView[];
};

export type AdminReportResponse = {
  generatedAt: string;
  dateFrom: string;
  dateTo: string;
  previousDateFrom: string;
  previousDateTo: string;
  currency: string;
  summary: AdminReportSummaryView;
  previousSummary: AdminReportSummaryView;
  interpretation: ReportInterpretationView;
  monthly: AdminMonthlyReportView[];
  yearly: AdminYearlyReportView[];
  orderStatuses: AdminCountAmountReportView[];
  paymentMethods: AdminCountAmountReportView[];
  categories: AdminCategoryReportView[];
  sellers: AdminSellerReportView[];
  users: AdminUserReportView[];
  products: AdminProductReportView[];
  serviceRevenue: AdminServiceRevenueView[];
  actionItems: AdminActionItemsView;
  recentOrders: AdminRecentOrderReportView[];
  feeSchedule: AdminFeeScheduleView;
  demand: AdminDemandDashboardView;
  recommendations: AdminBusinessRecommendationsView;
  assessments: AssessmentReportView;
};

export type SellerSubscriptionView = {
  plan: "INDIVIDUAL" | "PROFESSIONAL" | string;
  status: string;
  price: number;
  currency: string;
  startedAt?: string;
  expiresAt?: string;
  active: boolean;
  autoRenew: boolean;
  cancelAtPeriodEnd: boolean;
};

export type SellerSubscriptionCheckoutView = {
  id: string;
  url: string;
  paymentStatus?: string;
  amountTotal?: number;
  currency?: string;
  publishableKey?: string;
  clientReferenceId?: string;
  customerId?: string;
  subscriptionId?: string;
  status?: string;
};

export type SellerServiceProductView = {
  id: string;
  title: string;
  productNumber?: string;
  currency: string;
  stockQty: number;
  fulfillmentBySpareLynk: boolean;
  fulfillmentFeePerUnit: number;
  advertisingActive: boolean;
  advertisingCostPerClick: number;
  advertisingBudgetRemaining: number;
  advertisingCampaignId?: string;
};

export type AdvertisingCampaignView = {
  id: string;
  vendorId: string;
  productId: string;
  productName: string;
  status: string;
  budget: number;
  budgetRemaining: number;
  costPerClick: number;
  clicks: number;
  spend: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  cancelledAt?: string;
};

export type SellerServicesResponse = {
  subscription: SellerSubscriptionView;
  products: SellerServiceProductView[];
  campaigns: AdvertisingCampaignView[];
  fulfillment: {
    totalProducts: number;
    enabledProducts: number;
    enabledStockUnits: number;
  };
  verificationStatus: SellerVerificationStatus;
  subscriptionEligible: boolean;
  pricing: {
    professionalMonthlyPrice: number;
    fulfillmentFeePerUnit: number;
    minimumAdvertisingBudget: number;
    qualityAssessorAssessmentFee?: number;
  };
};

export type SellerAdvertisingCreatePayload = {
  productId: string;
  budget: number;
  costPerClick: number;
  currency?: string;
};

export type WarehouseBinView = {
  id: string;
  code: string;
  zone: string;
  aisle?: string;
  rack?: string;
  shelf?: string;
  capacity: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type WarehouseInventoryView = {
  id: string;
  vendorId: string;
  productId: string;
  productName: string;
  sku?: string;
  productNumber?: string;
  binId: string;
  binCode: string;
  onHandQty: number;
  reservedQty: number;
  updatedAt: string;
};

export type WarehouseTransferView = {
  id: string;
  transferNumber: string;
  sellerUserId: string;
  vendorId: string;
  productId: string;
  productName: string;
  sku?: string;
  productNumber?: string;
  requestedQty: number;
  receivedQty: number;
  status: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
};

export type WarehouseAllocationView = {
  binId: string;
  binCode: string;
  quantity: number;
};

export type WarehouseTaskLineView = {
  productId: string;
  productName: string;
  sku?: string;
  productNumber?: string;
  quantity: number;
  pickedQty: number;
  packedQty: number;
  allocations: WarehouseAllocationView[];
};

export type WarehouseTaskView = {
  id: string;
  taskNumber: string;
  orderId?: string;
  orderNumber: string;
  vendorId?: string;
  status: string;
  lines: WarehouseTaskLineView[];
  createdAt: string;
  updatedAt: string;
  pickedAt?: string;
  packedAt?: string;
  dispatchedAt?: string;
  cancelledAt?: string;
};

export type WarehouseMovementView = {
  id: string;
  type: string;
  vendorId: string;
  productId: string;
  productName: string;
  quantity: number;
  fromBinId?: string;
  fromBinCode?: string;
  toBinId?: string;
  toBinCode?: string;
  reference: string;
  actorUserId?: string;
  createdAt: string;
};

export type WarehouseOverviewResponse = {
  bins: WarehouseBinView[];
  inventory: WarehouseInventoryView[];
  transfers: WarehouseTransferView[];
  tasks: WarehouseTaskView[];
  movements: WarehouseMovementView[];
};

export type SellerWarehouseResponse = {
  inventory: WarehouseInventoryView[];
  transfers: WarehouseTransferView[];
};

export type WalletCashInPayload = {
  userId: string;
  role: MarketplaceRole;
  amount: number;
  currency?: string;
  reference?: string;
};

export type WalletStripeCashInPayload = WalletCashInPayload & {
  successUrl?: string;
  cancelUrl?: string;
  email?: string;
  customerEmail?: string;
};

export type WalletStripeCashInCheckoutView = {
  id?: string;
  sessionId?: string;
  url: string;
  paymentStatus?: string;
  amount?: number;
  currency?: string;
  reference?: string;
  publishableKey?: string;
  [key: string]: unknown;
};

export type WalletStripeCashInConfirmationPayload = {
  userId: string;
  role: MarketplaceRole;
  sessionId: string;
  reference?: string;
};

export type WalletStripeCashInConfirmationView = {
  status?: string;
  paymentStatus?: string;
  reference?: string;
  sessionId?: string;
  userId?: string;
  role?: MarketplaceRole | string;
  amount?: number;
  currency?: string;
  wallet?: WalletSummaryView;
  summary?: WalletSummaryView;
  transaction?: WalletTransactionView;
  [key: string]: unknown;
};

export type WalletTransferPayload = {
  senderUserId: string;
  senderRole: MarketplaceRole;
  recipientUserId: string;
  recipientRole: MarketplaceRole;
  amount: number;
  currency?: string;
  note?: string;
  reference?: string;
};

export type WalletTransferReceiptView = {
  receiptId: string;
  reference: string;
  status: string;
  amount: number;
  currency: string;
  senderRole: MarketplaceRole;
  senderUserId: string;
  recipientRole: MarketplaceRole;
  recipientUserId: string;
  note?: string;
  createdAt: string;
  senderWallet: WalletSummaryView;
  recipientWallet: WalletSummaryView;
};

export type WalletCashoutBasePayload = {
  userId: string;
  role: Extract<MarketplaceRole, "admin" | "seller" | "courier" | "quality_assessor" | "buyer">;
  email: string;
  amount: number;
  currency?: string;
  stripeAccountId?: string;
  bankAccountId: string;
  reference?: string;
};

export type WalletCashoutOTPPayload = WalletCashoutBasePayload;

export type WalletCashoutPayload = WalletCashoutBasePayload & {
  otpChallengeId: string;
  otpCode: string;
};

export type WalletCashoutOTPChallengeView = {
  challengeId: string;
  emailMasked: string;
  expiresAt: string;
  resendAfterSeconds: number;
  sandbox: boolean;
};

export type WalletCashoutView = {
  id: string;
  amount: number;
  currency: string;
  status: "pending" | "paid" | "failed" | "canceled" | "sandbox_completed";
  bankAccountId: string;
  stripeAccountId?: string;
  transferId?: string;
  reference?: string;
  sandbox: boolean;
  arrivalDate?: number;
  created?: number;
};

export type WalletRecipientSearchResultView = {
  id: string;
  walletOwnerId: string;
  role: MarketplaceRole;
  name: string;
  email: string;
  profileName?: string;
};

export class AutopartsApiError extends Error {
  status: number;
  field?: string;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown, field?: string) {
    super(message);
    this.name = "AutopartsApiError";
    this.status = status;
    this.data = data;
    this.field = field;
  }
}

export const SERVER_MAINTENANCE_MESSAGE =
  "The server is currently under maintenance. Please try again later.";

function isServerUnavailableStatus(status: number): boolean {
  return status >= 500 && status <= 599;
}

function getServerAvailabilityErrorMessage(
  status: number,
  fallback: string,
): string {
  return isServerUnavailableStatus(status)
    ? SERVER_MAINTENANCE_MESSAGE
    : fallback;
}

function getNetworkRequestErrorMessage(error: unknown): string {
  if (
    error instanceof Error &&
    (error.name === "AbortError" ||
      error.message.toLowerCase().includes("request was aborted"))
  ) {
    return error.message;
  }

  return SERVER_MAINTENANCE_MESSAGE;
}

export function isServerMaintenanceError(error: unknown): boolean {
  return (
    error instanceof AutopartsApiError &&
    error.message === SERVER_MAINTENANCE_MESSAGE
  );
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  allowReadCacheWithSignal?: boolean;
  forceRefresh?: boolean;
  skipAuth?: boolean;
  preserveEnvelope?: boolean;
};

export type ApiUploadProgressPhase =
  "preparing" | "uploading" | "processing" | "complete";

export type ApiUploadProgress = {
  loaded: number;
  total: number | null;
  percent: number | null;
  lengthComputable: boolean;
  phase?: ApiUploadProgressPhase;
  remainingPercent?: number;
  uploadedChunks?: number;
  totalChunks?: number;
};

export type CredentialUploadTaskState =
  | "preparing"
  | "uploading"
  | "paused"
  | "processing"
  | "complete"
  | "cancelled";

export type CredentialUploadTask = {
  promise: Promise<CredentialUploadView>;
  pause: () => void;
  resume: () => void;
  cancel: () => void;
  isPaused: () => boolean;
};

export type CredentialUploadTaskOptions = {
  onUploadProgress?: (progress: ApiUploadProgress) => void;
  onStateChange?: (state: CredentialUploadTaskState, error?: unknown) => void;
  pauseOnRecoverableError?: boolean;
};

export type UploadRequestOptions = RequestOptions & {
  onUploadProgress?: (progress: ApiUploadProgress) => void;
  uploadTotalBytes?: number;
};

type ApiResponseEnvelope<T = unknown> = {
  message?: string;
  data?: T;
  error?: {
    message?: string;
    field?: string;
  };
  errors?: unknown;
  success?: boolean;
};

const LOCAL_API_BASE_URL = "http://localhost:8080";
const AUTH_SESSION_STORAGE_KEY = "sparelynk.auth.session";
const AUTH_TOKEN_STORAGE_KEY = "sparelynk.auth.token";
const AUTH_STORAGE_MODE_KEY = "sparelynk.auth.storage";
const AUTH_EXPIRY_SKEW_SECONDS = 30;
const AUTH_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const REVIEW_MEDIA_UPLOAD_PART_BYTES = 5 * 1024 * 1024;
const DELIVERY_PROOF_MULTIPART_MIN_BYTES = 5 * 1024 * 1024;

function getDefaultApiBaseUrl(): string {
  if (typeof window === "undefined") return LOCAL_API_BASE_URL;

  const hostname = window.location.hostname.trim().toLowerCase();
  const isLocalHost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost");

  if (isLocalHost) return LOCAL_API_BASE_URL;

  const origin = window.location.origin.trim();
  return origin && origin !== "null" ? `${origin}/api` : LOCAL_API_BASE_URL;
}

function getConfiguredBaseUrl(): string {
  const env = (import.meta as ViteImportMeta).env;

  return sanitizeBaseUrl(
    env?.VITE_PUBLIC_API_URL ||
      env?.VITE_API_URL ||
      env?.VITE_AUTOPARTS_API_URL ||
      getDefaultApiBaseUrl(),
  );
}

function sanitizeBaseUrl(value: string): string {
  const fallback = getDefaultApiBaseUrl();
  const normalized = String(value || fallback).trim();
  if (!normalized) return fallback;
  return normalized.replace(/\/+$/, "");
}

function trimApiSuffix(value: string): string {
  return sanitizeBaseUrl(value).replace(/\/api$/i, "");
}

export function resolveApiAssetUrl(value: string): string {
  const source = String(value ?? "").trim();
  if (!source) return "";
  if (/^https?:\/\//i.test(source) || source.startsWith("data:") || source.startsWith("blob:")) return source;
  const root = trimApiSuffix(getConfiguredBaseUrl());
  return `${root}${source.startsWith("/") ? source : `/${source}`}`;
}

function getCredentialMediaPath(value: string): string | null {
  const source = String(value ?? "").trim();
  if (!source) return null;

  let pathname = source;
  let search = "";
  try {
    const base =
      typeof window === "undefined"
        ? "http://localhost"
        : window.location.origin;
    const parsed = new URL(source, base);
    pathname = parsed.pathname;
    search = parsed.search;
  } catch {
    const queryIndex = source.indexOf("?");
    pathname = queryIndex >= 0 ? source.slice(0, queryIndex) : source;
    search = queryIndex >= 0 ? source.slice(queryIndex) : "";
  }

  const normalizedPath = pathname.replace(/\\/g, "/");
  const lowerPath = normalizedPath.toLowerCase();
  const markers = [
    {
      marker: "/uploads/courier-credentials/",
      folder: "courier-credentials",
    },
    {
      marker: "/courier-credentials/uploads/",
      folder: "courier-credentials",
    },
    {
      marker: "/uploads/seller-credentials/",
      folder: "seller-credentials",
    },
    {
      marker: "/seller-credentials/uploads/",
      folder: "seller-credentials",
    },
    {
      marker: "/uploads/quality-assessor-credentials/",
      folder: "quality-assessor-credentials",
    },
    {
      marker: "/quality-assessor-credentials/uploads/",
      folder: "quality-assessor-credentials",
    },
  ] as const;

  for (const { marker, folder } of markers) {
    const markerIndex = lowerPath.indexOf(marker);
    if (markerIndex < 0) continue;

    const key = normalizedPath
      .slice(markerIndex + marker.length)
      .replace(/^\/+/, "");
    if (!key || key.includes("..")) return null;

    return `/uploads/${folder}/${key}${search}`;
  }

  return null;
}

export function getCredentialImageUrlCandidates(value: string): string[] {
  const source = String(value ?? "").trim();
  if (!source) return [];

  const mediaPath = getCredentialMediaPath(source);
  if (!mediaPath) return [source];

  return Array.from(
    new Set([
      source,
      ...getBaseUrlCandidates().map((baseUrl) => `${baseUrl}${mediaPath}`),
    ]),
  );
}

function getLastItem<T>(items: readonly T[] | null | undefined): T | undefined {
  return items?.[items.length - 1];
}

let preferredApiBaseUrl: string | null = null;

const API_READ_CACHE_TTL_MS = 30_000;
const API_DYNAMIC_READ_CACHE_TTL_MS = 2_000;
const API_MESSAGE_READ_CACHE_TTL_MS = 5_000;
const API_STATS_READ_CACHE_TTL_MS = 15_000;
const API_CACHE_REFRESH_QUERY_PARAM = "__sparelynk_refresh";

type ApiReadCacheEntry = {
  expiresAt: number;
  value: unknown;
};

const apiReadCache = new Map<string, ApiReadCacheEntry>();
const apiReadRequests = new Map<string, Promise<unknown>>();
let apiReadCacheVersion = 0;

function getApiReadCacheScope(): string {
  const session = readStoredAuthSession();
  return `${session?.user?.id ?? "anonymous"}:${session?.user?.role ?? "guest"}`;
}

function isShopConversationReadPath(path: string): boolean {
  const pathname = path.split("?", 1)[0].toLowerCase();
  return (
    pathname === "/messages/conversations" ||
    pathname === "/shop-conversations"
  );
}

function isShopConversationMutationPath(path: string): boolean {
  const pathname = path.split("?", 1)[0].toLowerCase();
  return [
    "/messages/conversations/messages",
    "/messages/conversations/read",
    "/shop-conversations/messages",
    "/shop-conversations/read",
  ].includes(pathname);
}

const OFFLINE_CATALOG_CACHE_SCOPE = "public-catalog";

function isOfflinePersistableReadPath(path: string): boolean {
  const pathname = path.split("?", 1)[0].toLowerCase();
  return (
    pathname === "/products" ||
    /^\/products\/[^/]+$/.test(pathname) ||
    pathname === "/vendors" ||
    pathname === "/vendor-profiles" ||
    /^\/vendors\/[^/]+$/.test(pathname)
  );
}

function isBrowserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function shouldInvalidateOfflineCatalogCache(path: string): boolean {
  const pathname = path.split("?", 1)[0].toLowerCase();
  return (
    pathname.startsWith("/products") ||
    pathname.startsWith("/vendors") ||
    pathname.startsWith("/vendor-profiles") ||
    pathname.startsWith("/buyer-reviews") ||
    pathname.includes("/reviews")
  );
}

function invalidateOfflineCatalogCache(): void {
  void clearOfflineApiResponses(OFFLINE_CATALOG_CACHE_SCOPE);
}

function isCacheableReadPath(path: string): boolean {
  const pathname = path.split("?", 1)[0].toLowerCase();

  if (isShopConversationReadPath(pathname)) {
    return true;
  }

  if (
    pathname === "/auth/me" ||
    pathname === "/assistant/capabilities" ||
    /^\/users\/[^/]+\/vendor-profile$/.test(pathname)
  ) {
    return true;
  }

  if (pathname === "/products" || /^\/products\/[^/]+$/.test(pathname)) {
    return true;
  }

  if (pathname === "/orders" || /^\/orders\/[^/]+$/.test(pathname)) {
    return true;
  }

  if (pathname === "/users" || /^\/users\/[^/]+$/.test(pathname)) {
    return true;
  }

  return (
    pathname === "/vendors" ||
    /^\/vendors\/[^/]+$/.test(pathname) ||
    pathname === "/platform/stats"
  );
}

function getApiReadCacheTtl(path: string): number {
  const pathname = path.split("?", 1)[0].toLowerCase();

  if (isShopConversationReadPath(pathname)) {
    return API_MESSAGE_READ_CACHE_TTL_MS;
  }

  if (pathname === "/auth/me") {
    return API_MESSAGE_READ_CACHE_TTL_MS;
  }

  if (pathname === "/platform/stats") {
    return API_STATS_READ_CACHE_TTL_MS;
  }

  if (pathname === "/orders" || /^\/orders\/[^/]+$/.test(pathname)) {
    return API_DYNAMIC_READ_CACHE_TTL_MS;
  }

  return API_READ_CACHE_TTL_MS;
}

function buildApiReadCacheKey(path: string, options: RequestOptions): string {
  return [
    options.skipAuth ? "public" : getApiReadCacheScope(),
    options.skipAuth ? "public" : "authenticated",
    options.preserveEnvelope ? "envelope" : "data",
    path,
  ].join(":");
}

function shouldInvalidateApiReadCache(path: string): boolean {
  const pathname = path.split("?", 1)[0].toLowerCase();

  return ![
    "/messages/conversations/messages",
    "/messages/conversations/read",
    "/shop-conversations/messages",
    "/shop-conversations/read",
  ].includes(pathname);
}

function invalidateApiReadCache(): void {
  apiReadCacheVersion += 1;
  apiReadCache.clear();
  apiReadRequests.clear();
}

function invalidateShopConversationApiReadCache(): void {
  apiReadCacheVersion += 1;

  for (const key of apiReadCache.keys()) {
    if (
      key.includes("/messages/conversations") ||
      key.includes("/shop-conversations")
    ) {
      apiReadCache.delete(key);
    }
  }

  for (const key of apiReadRequests.keys()) {
    if (
      key.includes("/messages/conversations") ||
      key.includes("/shop-conversations")
    ) {
      apiReadRequests.delete(key);
    }
  }
}

function getBaseUrlCandidates(): string[] {
  const configured = getConfiguredBaseUrl();
  const withoutApi = trimApiSuffix(configured);
  const withApi = `${withoutApi}/api`;
  const candidates = Array.from(
    new Set([configured, withoutApi, withApi].map(sanitizeBaseUrl)),
  );

  if (!preferredApiBaseUrl || !candidates.includes(preferredApiBaseUrl)) {
    return candidates;
  }

  return [
    preferredApiBaseUrl,
    ...candidates.filter((candidate) => candidate !== preferredApiBaseUrl),
  ];
}

function getStorage(
  kind: "local" | "session" = readPreferredStorageKind(),
): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readPreferredStorageKind(): "local" | "session" {
  if (typeof window === "undefined") return "local";

  try {
    const value = window.localStorage.getItem(AUTH_STORAGE_MODE_KEY);
    return value === "session" ? "session" : "local";
  } catch {
    return "local";
  }
}

function setPreferredStorageKind(kind: "local" | "session") {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(AUTH_STORAGE_MODE_KEY, kind);
  } catch {
    // Ignore storage failures.
  }
}

function clearStorageValue(key: string) {
  for (const kind of ["local", "session"] as const) {
    const storage = getStorage(kind);
    try {
      storage?.removeItem(key);
    } catch {
      // Ignore storage failures.
    }
  }
}

function writeStorageValue(
  key: string,
  value: string,
  remember = false,
): boolean {
  const attempts = remember
    ? (["local", "session"] as const)
    : (["session"] as const);

  for (const kind of attempts) {
    const storage = getStorage(kind);
    if (!storage) continue;

    try {
      if (storage.getItem(key) !== value) {
        storage.setItem(key, value);
      }

      if (storage.getItem(key) === value) {
        for (const otherKind of ["local", "session"] as const) {
          if (otherKind === kind) continue;
          try {
            getStorage(otherKind)?.removeItem(key);
          } catch {
            // Ignore cleanup failures after the target write succeeded.
          }
        }
        setPreferredStorageKind(kind);
        return true;
      }
    } catch {
      // Try the fallback storage, if one is available.
    }
  }

  return false;
}

function readStorageValue(key: string): string | null {
  const preferred = getStorage(readPreferredStorageKind());

  try {
    const preferredValue = preferred?.getItem(key);
    if (preferredValue) return preferredValue;
  } catch {
    // Try other storage below.
  }

  for (const kind of ["local", "session"] as const) {
    const storage = getStorage(kind);
    try {
      const value = storage?.getItem(key);
      if (value) return value;
    } catch {
      // Ignore storage failures.
    }
  }

  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

function normalizeRecordKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getRecordProperty(
  record: Record<string, unknown>,
  keys: string[],
): unknown {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) {
      return record[key];
    }
  }

  const normalizedKeys = new Set(keys.map(normalizeRecordKey));

  for (const [recordKey, value] of Object.entries(record)) {
    if (
      value !== undefined &&
      value !== null &&
      normalizedKeys.has(normalizeRecordKey(recordKey))
    ) {
      return value;
    }
  }

  return undefined;
}

function unwrapEnvelope(value: unknown): unknown {
  let current = value;

  for (let index = 0; index < 4; index += 1) {
    if (!isRecord(current)) return current;

    const nestedPayload = getRecordProperty(current, [
      "data",
      "result",
      "payload",
    ]);

    if (nestedPayload !== undefined && nestedPayload !== null) {
      current = nestedPayload;
      continue;
    }

    return current;
  }

  return current;
}

function getString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === "boolean") {
    return String(value);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  const record = readRecord(value);
  if (record) {
    const directValue = getRecordProperty(record, [
      "$oid",
      "oid",
      "hex",
      "id",
      "_id",
      "$id",
      "value",
      "string",
      "date",
      "$date",
    ]);

    if (
      directValue !== undefined &&
      directValue !== null &&
      directValue !== value
    ) {
      const normalized = getString(directValue);
      if (normalized) return normalized;
    }
  }

  return undefined;
}

function getFirstString(
  record: Record<string, unknown>,
  keys: string[],
): string | undefined {
  return getString(getRecordProperty(record, keys));
}

function getNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  const record = readRecord(value);
  if (record) {
    const directValue = getRecordProperty(record, [
      "$numberInt",
      "$numberLong",
      "$numberDouble",
      "$numberDecimal",
      "numberInt",
      "numberLong",
      "numberDouble",
      "numberDecimal",
      "value",
    ]);

    if (
      directValue !== undefined &&
      directValue !== null &&
      directValue !== value
    ) {
      const normalized = getNumber(directValue);
      if (normalized !== undefined) return normalized;
    }
  }

  return undefined;
}

function getFirstNumber(
  record: Record<string, unknown>,
  keys: string[],
): number | undefined {
  return getNumber(getRecordProperty(record, keys));
}

function getNumberArray(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const normalized = Array.from(
    new Set(
      value
        .map((entry) => getNumber(entry))
        .filter((entry): entry is number => Number.isFinite(entry)),
    ),
  ).sort((left, right) => right - left);

  return normalized.length > 0 ? normalized : undefined;
}

function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

function normalizeCommissionPercent(value: number | undefined): number {
  if (!Number.isFinite(value ?? NaN)) return 0;
  return Math.min(100, Math.max(0, Number(value)));
}

function calculateCommissionAmount(price: number, percent: number): number {
  return roundMoney(
    ((Number.isFinite(price) ? price : 0) *
      normalizeCommissionPercent(percent)) /
      100,
  );
}

function getProductCommissionPercent(record: Record<string, unknown>): number {
  return normalizeCommissionPercent(
    getFirstNumber(record, [
      "commissionPercent",
      "commission_percent",
      "platformCommissionPercent",
      "platform_commission_percent",
      "adminCommissionPercent",
      "admin_commission_percent",
    ]),
  );
}

function getProductCommissionAmount(
  record: Record<string, unknown>,
  price: number,
  percent: number,
): number {
  return roundMoney(
    getFirstNumber(record, [
      "commissionAmount",
      "commission_amount",
      "platformCommissionAmount",
      "platform_commission_amount",
      "adminCommissionAmount",
      "admin_commission_amount",
    ]) ?? calculateCommissionAmount(price, percent),
  );
}

function getProductSellerNetPrice(
  record: Record<string, unknown>,
  price: number,
  commissionAmount: number,
): number {
  return roundMoney(
    getFirstNumber(record, [
      "sellerNetPrice",
      "seller_net_price",
      "sellerNetAmount",
      "seller_net_amount",
    ]) ?? Math.max(0, price - commissionAmount),
  );
}

function getBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === "string") {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    if (
      [
        "true",
        "1",
        "yes",
        "active",
        "verified",
        "required",
        "sent",
        "pending",
        "verify",
        "verificationrequired",
      ].includes(normalized)
    ) {
      return true;
    }
    if (
      [
        "false",
        "0",
        "no",
        "inactive",
        "disabled",
        "notrequired",
        "none",
      ].includes(normalized)
    ) {
      return false;
    }
  }

  return undefined;
}

function getFirstBoolean(
  record: Record<string, unknown>,
  keys: string[],
): boolean | undefined {
  return getBoolean(getRecordProperty(record, keys));
}

function readNestedRecord(
  record: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> | null {
  const value = getRecordProperty(record, keys);
  return readRecord(value);
}

const AUTH_TOKEN_KEYS = [
  "token",
  "accessToken",
  "access_token",
  "jwt",
  "sessionToken",
  "session_token",
  "authToken",
  "auth_token",
  "bearerToken",
  "bearer_token",
];

const AUTH_NESTED_KEYS = [
  "data",
  "result",
  "payload",
  "session",
  "auth",
  "authentication",
  "credentials",
  "currentSession",
  "current_session",
  "authSession",
  "auth_session",
  "login",
];

function isLikelyJwtToken(value: string): boolean {
  return value.split(".").length === 3;
}

function decodeBase64UrlSegment(value: string): string | null {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );

  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    if (typeof TextDecoder !== "undefined") {
      return new TextDecoder().decode(bytes);
    }

    return binary;
  } catch {
    return null;
  }
}

function readJwtClaims(
  token: string | undefined,
): Record<string, unknown> | null {
  if (!token || !isLikelyJwtToken(token)) return null;

  const payload = token.split(".")[1];
  const decoded = decodeBase64UrlSegment(payload);
  if (!decoded) return null;

  try {
    return readRecord(JSON.parse(decoded));
  } catch {
    return null;
  }
}

function isAuthTokenExpired(token: string | null | undefined): boolean {
  const claims = readJwtClaims(token ?? undefined);
  const exp = claims
    ? getNumber(getRecordProperty(claims, ["exp"]))
    : undefined;

  if (!exp) return false;

  return exp <= Math.floor(Date.now() / 1000) + AUTH_EXPIRY_SKEW_SECONDS;
}

function parseAuthTimestamp(value: string | undefined): number | undefined {
  const raw = getString(value);
  if (!raw) return undefined;

  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isAuthSessionExpired(session: AuthSession | null): boolean {
  if (!session) return false;

  const expiresAt = parseAuthTimestamp(session.expiresAt);
  if (expiresAt !== undefined) {
    return expiresAt <= Date.now() + AUTH_EXPIRY_SKEW_SECONDS * 1000;
  }

  return isAuthTokenExpired(session.token ?? session.accessToken);
}

function withAuthSessionPersistenceWindow(session: AuthSession): AuthSession {
  const token = getString(session.token) || getString(session.accessToken);
  const claims = readJwtClaims(token);
  const tokenIssuedAt = claims
    ? getNumber(getRecordProperty(claims, ["iat"]))
    : undefined;
  const issuedAt =
    parseAuthTimestamp(session.persistedAt) ??
    (tokenIssuedAt ? tokenIssuedAt * 1000 : Date.now());
  const expiresAt =
    parseAuthTimestamp(session.expiresAt) !== undefined
      ? session.expiresAt
      : getJwtExpiresAt(claims) ||
        new Date(issuedAt + AUTH_SESSION_MAX_AGE_MS).toISOString();
  const parsedExpiresAt = parseAuthTimestamp(expiresAt);
  const expiresIn =
    session.expiresIn ??
    (parsedExpiresAt !== undefined
      ? Math.max(0, Math.floor((parsedExpiresAt - Date.now()) / 1000))
      : undefined);

  return {
    ...session,
    token,
    accessToken: getString(session.accessToken) || token,
    tokenType: session.tokenType ?? (token ? "Bearer" : undefined),
    expiresAt,
    expiresIn,
    persistedAt: session.persistedAt ?? new Date(issuedAt).toISOString(),
  };
}

function getSessionAuthToken(
  session: AuthSession | null | undefined,
): string | undefined {
  return getString(session?.token) || getString(session?.accessToken);
}

function canUseTokenlessAuthSession(session: AuthSession | null): boolean {
  const user = session?.user;
  const requiresFirstPasswordChange = Boolean(
    user &&
    user.role === "admin" &&
    (user.mustChangePassword || user.emailVerified === false),
  );

  return Boolean(
    user && requiresFirstPasswordChange && !isAuthSessionExpired(session),
  );
}

function hasUsableAuthSession(session: AuthSession | null): boolean {
  const token = getSessionAuthToken(session);
  const canContinuePasswordChange = canUseTokenlessAuthSession(session);

  return Boolean(
    session?.user &&
    (!session.requiresEmailVerification || canContinuePasswordChange) &&
    !isAuthSessionExpired(session) &&
    (token || canContinuePasswordChange),
  );
}

function getAuthTokenFromValue(value: unknown, depth = 0): string | undefined {
  if (depth > 5) return undefined;

  const direct = getString(value);
  if (direct && isLikelyJwtToken(direct)) return direct;

  const record = readRecord(value);
  if (!record) return undefined;

  const directToken = getFirstString(record, AUTH_TOKEN_KEYS);
  if (directToken) return directToken;

  for (const key of AUTH_NESTED_KEYS) {
    const nestedToken = getAuthTokenFromValue(
      getRecordProperty(record, [key]),
      depth + 1,
    );
    if (nestedToken) return nestedToken;
  }

  return undefined;
}

function normalizeAuthHeaderToken(value: string | null): string | undefined {
  const header = getString(value);
  if (!header) return undefined;

  if (header.toLowerCase().startsWith("bearer ")) {
    return getString(header.slice(7));
  }

  return header;
}

function getAuthTokenFromResponseHeaders(headers: Headers): string | undefined {
  return (
    normalizeAuthHeaderToken(headers.get("X-Auth-Token")) ||
    normalizeAuthHeaderToken(headers.get("X-Access-Token")) ||
    normalizeAuthHeaderToken(headers.get("Authorization"))
  );
}

function getAuthTokenFromXhrResponseHeaders(
  xhr: XMLHttpRequest,
): string | undefined {
  return (
    normalizeAuthHeaderToken(xhr.getResponseHeader("X-Auth-Token")) ||
    normalizeAuthHeaderToken(xhr.getResponseHeader("X-Access-Token")) ||
    normalizeAuthHeaderToken(xhr.getResponseHeader("Authorization"))
  );
}

function attachAuthTokenToPayload(
  value: unknown,
  token: string | undefined,
): unknown {
  if (!token || getAuthTokenFromValue(value)) return value;

  const record = readRecord(value);
  if (!record) return value;

  return {
    ...record,
    token,
    accessToken: token,
    access_token: token,
    tokenType: getFirstString(record, ["tokenType", "token_type"]) || "Bearer",
    token_type: getFirstString(record, ["tokenType", "token_type"]) || "Bearer",
  };
}

function normalizeAuthenticatedUserFromToken(
  token: string | undefined,
): AuthenticatedUser | null {
  const claims = readJwtClaims(token);
  if (!claims) return null;

  const id = getFirstString(claims, [
    "sub",
    "subject",
    "id",
    "_id",
    "$id",
    "userId",
    "user_id",
  ]);
  const email = getFirstString(claims, [
    "email",
    "emailAddress",
    "email_address",
    "login",
    "username",
  ]);
  const role = normalizeMarketplaceRole(
    getRecordProperty(claims, [
      "role",
      "accountType",
      "account_type",
      "userRole",
      "user_role",
      "type",
    ]),
  );

  if (!id || !email || !role) return null;

  const displayName = getFirstString(claims, ["displayName", "display_name"]);
  const name =
    getFirstString(claims, [
      "name",
      "fullName",
      "full_name",
      "displayName",
      "display_name",
    ]) ||
    email.split("@")[0] ||
    email;

  return {
    id,
    name,
    email,
    role,
    displayName,
    avatarUrl: null,
  };
}

function getJwtExpiresAt(
  claims: Record<string, unknown> | null,
): string | undefined {
  const exp = claims
    ? getNumber(getRecordProperty(claims, ["exp", "expiresAt", "expires_at"]))
    : undefined;
  if (!exp) return undefined;

  const date = new Date(exp * 1000);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function getJwtExpiresIn(
  claims: Record<string, unknown> | null,
): number | undefined {
  const exp = claims
    ? getNumber(getRecordProperty(claims, ["exp"]))
    : undefined;
  if (!exp) return undefined;

  return Math.max(0, Math.floor(exp - Date.now() / 1000));
}

function getImageStrings(value: unknown): string[] {
  if (typeof value === "string") return [value].filter(Boolean);
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (isRecord(entry)) {
        return getFirstString(entry, [
          "url",
          "imageUrl",
          "image_url",
          "src",
          "path",
        ]);
      }
      return undefined;
    })
    .filter((entry): entry is string => Boolean(entry));
}

function getStringValues(value: unknown): string[] {
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized ? [normalized] : [];
  }
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map((entry) => getString(entry)?.trim())
        .filter((entry): entry is string => Boolean(entry)),
    ),
  );
}

function normalizeCourierVerificationStatus(
  value: unknown,
): CourierVerificationStatus | undefined {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (
    normalized === "NOT_SUBMITTED" ||
    normalized === "PENDING" ||
    normalized === "PARTIALLY_VERIFIED" ||
    normalized === "FULLY_VERIFIED" ||
    normalized === "VERIFIED" ||
    normalized === "REJECTED"
  ) {
    return normalized;
  }

  return undefined;
}

function normalizeSellerVerificationStatus(
  value: unknown,
): SellerVerificationStatus | undefined {
  return normalizeCourierVerificationStatus(value);
}

export function normalizeMarketplaceRole(
  value: unknown,
): MarketplaceRole | undefined {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (!normalized) return undefined;
  if (["buyer", "customer", "customeruser", "client"].includes(normalized))
    return "buyer";
  if (
    [
      "seller",
      "vendor",
      "vendoruser",
      "merchant",
      "merchantuser",
      "shop",
      "store",
    ].includes(normalized)
  ) {
    return "seller";
  }
  if (["courier", "driver", "delivery", "deliverydriver"].includes(normalized))
    return "courier";
  if (["qualityassessor", "assessor", "qualityprofessional", "quality"].includes(normalized))
    return "quality_assessor";
  if (
    ["admin", "administrator", "superadmin", "adminuser"].includes(normalized)
  )
    return "admin";

  return undefined;
}

function normalizeDeliveryAddressesFromUserRecord(
  record: Record<string, unknown>,
): DeliveryAddress[] {
  const rawAddresses = getRecordProperty(record, [
    "deliveryAddresses",
    "delivery_addresses",
  ]);
  const values = Array.isArray(rawAddresses) ? rawAddresses : [];
  const normalized = values
    .map((value, index): DeliveryAddress | null => {
      const address = readRecord(value);
      if (!address) return null;
      const addressLine1 = getFirstString(address, [
        "addressLine1",
        "address_line_1",
        "address",
      ]);
      if (!addressLine1) return null;
      return {
        id:
          getFirstString(address, ["id", "addressId", "address_id"]) ||
          `delivery-address-${index + 1}`,
        label:
          getFirstString(address, ["label", "name"]) ||
          (index === 0
            ? "Primary delivery address"
            : index === 1
              ? "Secondary delivery address"
              : `Delivery address ${index + 1}`),
        addressLine1,
        addressLine2: getFirstString(address, [
          "addressLine2",
          "address_line_2",
        ]),
        city: getFirstString(address, ["city"]),
        state: getFirstString(address, ["state", "province", "region"]),
        postalCode: getFirstString(address, [
          "postalCode",
          "postal_code",
          "zipCode",
          "zip_code",
        ]),
        country: getFirstString(address, ["country"]),
        latitude: getNumber(
          getRecordProperty(address, ["latitude", "lat"]),
        ),
        longitude: getNumber(
          getRecordProperty(address, ["longitude", "lng", "lon"]),
        ),
        locationLabel: getFirstString(address, [
          "locationLabel",
          "location_label",
        ]),
        additionalLocationInfo: getFirstString(address, [
          "additionalLocationInfo",
          "additional_location_info",
        ]),
        isPrimary:
          getBoolean(
            getRecordProperty(address, ["isPrimary", "is_primary"]),
          ) ?? false,
      };
    })
    .filter((value): value is DeliveryAddress => Boolean(value));

  const legacyAddress = getFirstString(record, [
    "address",
    "addressLine1",
    "address_line_1",
    "streetAddress",
    "street_address",
  ]);
  if (normalized.length === 0 && legacyAddress) {
    normalized.push({
      id: "delivery-address-1",
      label: "Primary delivery address",
      addressLine1: legacyAddress,
      isPrimary: true,
    });
  }

  if (normalized.length > 0) {
    const primaryIndex = normalized.findIndex((address) => address.isPrimary);
    const selectedPrimaryIndex = primaryIndex >= 0 ? primaryIndex : 0;
    normalized.forEach((address, index) => {
      address.isPrimary = index === selectedPrimaryIndex;
    });
  }
  return normalized;
}

export function normalizeAuthenticatedUser(
  value: unknown,
): AuthenticatedUser | null {
  const unwrapped = unwrapEnvelope(value);
  const record = readRecord(unwrapped);

  if (!record) {
    return normalizeAuthenticatedUserFromToken(getAuthTokenFromValue(value));
  }

  const directUser = readNestedRecord(record, [
    "user",
    "account",
    "profile",
    "currentUser",
    "current_user",
    "customer",
    "vendor",
    "seller",
    "courier",
    "driver",
    "deliveryUser",
    "delivery_user",
    "admin",
  ]);

  if (directUser) {
    const nested = normalizeAuthenticatedUser(directUser);
    if (nested) return nested;
  }

  const id = getFirstString(record, [
    "id",
    "_id",
    "$id",
    "userId",
    "user_id",
    "accountId",
    "account_id",
  ]);
  const email = getFirstString(record, [
    "email",
    "emailAddress",
    "email_address",
    "login",
    "username",
  ]);
  const role = normalizeMarketplaceRole(
    getRecordProperty(record, [
      "role",
      "accountType",
      "account_type",
      "userRole",
      "user_role",
      "type",
    ]),
  );

  if (!id || !email || !role) {
    return normalizeAuthenticatedUserFromToken(
      getAuthTokenFromValue(record) ?? getAuthTokenFromValue(value),
    );
  }

  const displayName = getFirstString(record, ["displayName", "display_name"]);
  const name =
    getFirstString(record, [
      "name",
      "fullName",
      "full_name",
      "displayName",
      "display_name",
    ]) || email;

  return {
    id,
    name,
    email,
    role,
    isActive: getBoolean(
      getRecordProperty(record, ["isActive", "is_active", "active", "enabled"]),
    ),
    emailVerified: getBoolean(
      getRecordProperty(record, [
        "emailVerified",
        "email_verified",
        "isEmailVerified",
        "is_email_verified",
        "verified",
      ]),
    ),
    mustChangePassword: getBoolean(
      getRecordProperty(record, [
        "mustChangePassword",
        "must_change_password",
        "forcePasswordChange",
        "force_password_change",
      ]),
    ),
    twoFactorEmailEnabled: getBoolean(
      getRecordProperty(record, [
        "twoFactorEmailEnabled",
        "two_factor_email_enabled",
      ]),
    ),
    twoFactorAuthenticatorEnabled: getBoolean(
      getRecordProperty(record, [
        "twoFactorAuthenticatorEnabled",
        "two_factor_authenticator_enabled",
      ]),
    ),
    displayName,
    avatarUrl:
      getFirstString(record, [
        "avatarUrl",
        "avatar_url",
        "avatar",
        "imageUrl",
        "image_url",
      ]) ?? null,
    phone: getFirstString(record, [
      "phone",
      "phoneNumber",
      "phone_number",
      "contactPhone",
      "contact_phone",
    ]),
    address: getFirstString(record, [
      "address",
      "addressLine1",
      "address_line_1",
      "streetAddress",
      "street_address",
    ]),
    deliveryAddresses: normalizeDeliveryAddressesFromUserRecord(record),
    profileComplete: getBoolean(
      getRecordProperty(record, ["profileComplete", "profile_complete"]),
    ),
    canBuyProducts: getBoolean(
      getRecordProperty(record, ["canBuyProducts", "can_buy_products"]),
    ),
    nationality: getFirstString(record, [
      "nationality",
      "country",
      "countryName",
      "country_name",
    ]),
    preferredCurrency: getFirstString(record, [
      "preferredCurrency",
      "preferred_currency",
      "currencyPreference",
      "currency_preference",
    ])?.toUpperCase(),
    loyaltyBadges: getStringValues(
      getRecordProperty(record, ["loyaltyBadges", "loyalty_badges", "badges"]),
    ),
    courierIdentificationType: getFirstString(record, [
      "courierIdentificationType",
      "courier_identification_type",
    ]),
    courierIdentificationNumber: getFirstString(record, [
      "courierIdentificationNumber",
      "courier_identification_number",
    ]),
    courierIdentificationImageUrls: getImageStrings(
      getRecordProperty(record, [
        "courierIdentificationImageUrls",
        "courierIdentificationImageURLs",
        "courier_identification_image_urls",
      ]),
    ),
    courierSelfieImageUrls: getImageStrings(
      getRecordProperty(record, [
        "courierSelfieImageUrls",
        "courierSelfieImageURLs",
        "courier_selfie_image_urls",
      ]),
    ),
    courierDeliveryMethod: getFirstString(record, [
      "courierDeliveryMethod",
      "courier_delivery_method",
    ]),
    courierDriverLicenseNumber: getFirstString(record, [
      "courierDriverLicenseNumber",
      "courier_driver_license_number",
    ]),
    courierDriverLicenseExpiryDate: getFirstString(record, [
      "courierDriverLicenseExpiryDate",
      "courier_driver_license_expiry_date",
    ]),
    courierDriverLicenseImageUrls: getImageStrings(
      getRecordProperty(record, [
        "courierDriverLicenseImageUrls",
        "courierDriverLicenseImageURLs",
        "courier_driver_license_image_urls",
      ]),
    ),
    courierVehiclePlateNumber: getFirstString(record, [
      "courierVehiclePlateNumber",
      "courier_vehicle_plate_number",
    ]),
    courierVehicleRegistrationExpiryDate: getFirstString(record, [
      "courierVehicleRegistrationExpiryDate",
      "courier_vehicle_registration_expiry_date",
    ]),
    courierVehicleRegistrationImageUrls: getImageStrings(
      getRecordProperty(record, [
        "courierVehicleRegistrationImageUrls",
        "courierVehicleRegistrationImageURLs",
        "courier_vehicle_registration_image_urls",
      ]),
    ),
    courierInsuranceProvider: getFirstString(record, [
      "courierInsuranceProvider",
      "courier_insurance_provider",
    ]),
    courierInsurancePolicyNumber: getFirstString(record, [
      "courierInsurancePolicyNumber",
      "courier_insurance_policy_number",
    ]),
    courierInsuranceExpiryDate: getFirstString(record, [
      "courierInsuranceExpiryDate",
      "courier_insurance_expiry_date",
    ]),
    courierInsuranceImageUrls: getImageStrings(
      getRecordProperty(record, [
        "courierInsuranceImageUrls",
        "courierInsuranceImageURLs",
        "courier_insurance_image_urls",
      ]),
    ),
    courierEmergencyContactName: getFirstString(record, [
      "courierEmergencyContactName",
      "courier_emergency_contact_name",
    ]),
    courierEmergencyContactPhone: getFirstString(record, [
      "courierEmergencyContactPhone",
      "courier_emergency_contact_phone",
    ]),
    courierExperience: getFirstString(record, [
      "courierExperience",
      "courier_experience",
    ]),
    courierOtherWorkExperience: getFirstString(record, [
      "courierOtherWorkExperience",
      "courier_other_work_experience",
    ]),
    courierVerificationStatus: normalizeCourierVerificationStatus(
      getRecordProperty(record, [
        "courierVerificationStatus",
        "courier_verification_status",
      ]),
    ),
    courierVerificationNotes: getFirstString(record, [
      "courierVerificationNotes",
      "courier_verification_notes",
    ]),
    courierCredentialsSubmittedAt: getFirstString(record, [
      "courierCredentialsSubmittedAt",
      "courier_credentials_submitted_at",
    ]),
    courierCredentialsVerifiedAt: getFirstString(record, [
      "courierCredentialsVerifiedAt",
      "courier_credentials_verified_at",
    ]),
    courierCredentialsVerifiedBy: getFirstString(record, [
      "courierCredentialsVerifiedBy",
      "courier_credentials_verified_by",
    ]),
    sellerIdentificationType: getFirstString(record, [
      "sellerIdentificationType",
      "seller_identification_type",
    ]),
    sellerIdentificationNumber: getFirstString(record, [
      "sellerIdentificationNumber",
      "seller_identification_number",
    ]),
    sellerIdentificationImageUrls: getImageStrings(
      getRecordProperty(record, [
        "sellerIdentificationImageUrls",
        "sellerIdentificationImageURLs",
        "seller_identification_image_urls",
      ]),
    ),
    sellerSelfieImageUrls: getImageStrings(
      getRecordProperty(record, [
        "sellerSelfieImageUrls",
        "sellerSelfieImageURLs",
        "seller_selfie_image_urls",
      ]),
    ),
    sellerClassification: getFirstString(record, [
      "sellerClassification",
      "seller_classification",
    ]),
    sellerBusinessName: getFirstString(record, [
      "sellerBusinessName",
      "seller_business_name",
    ]),
    sellerBusinessRegistrationNumber: getFirstString(record, [
      "sellerBusinessRegistrationNumber",
      "seller_business_registration_number",
    ]),
    sellerBusinessRegistrationImageUrls: getImageStrings(
      getRecordProperty(record, [
        "sellerBusinessRegistrationImageUrls",
        "sellerBusinessRegistrationImageURLs",
        "seller_business_registration_image_urls",
      ]),
    ),
    sellerTaxIdentificationNumber: getFirstString(record, [
      "sellerTaxIdentificationNumber",
      "seller_tax_identification_number",
    ]),
    sellerBirCertificateImageUrls: getImageStrings(
      getRecordProperty(record, [
        "sellerBirCertificateImageUrls",
        "sellerBIRCertificateImageURLs",
        "seller_bir_certificate_image_urls",
      ]),
    ),
    sellerProofOfAddressImageUrls: getImageStrings(
      getRecordProperty(record, [
        "sellerProofOfAddressImageUrls",
        "sellerProofOfAddressImageURLs",
        "seller_proof_of_address_image_urls",
      ]),
    ),
    sellerExperience: getFirstString(record, [
      "sellerExperience",
      "seller_experience",
    ]),
    sellerOtherWorkExperience: getFirstString(record, [
      "sellerOtherWorkExperience",
      "seller_other_work_experience",
    ]),
    sellerVerificationStatus: normalizeSellerVerificationStatus(
      getRecordProperty(record, [
        "sellerVerificationStatus",
        "seller_verification_status",
      ]),
    ),
    sellerVerificationNotes: getFirstString(record, [
      "sellerVerificationNotes",
      "seller_verification_notes",
    ]),
    sellerCredentialsSubmittedAt: getFirstString(record, [
      "sellerCredentialsSubmittedAt",
      "seller_credentials_submitted_at",
    ]),
    sellerCredentialsVerifiedAt: getFirstString(record, [
      "sellerCredentialsVerifiedAt",
      "seller_credentials_verified_at",
    ]),
    sellerCredentialsVerifiedBy: getFirstString(record, [
      "sellerCredentialsVerifiedBy",
      "seller_credentials_verified_by",
    ]),
    qualityAssessorProfessionalTitle: getFirstString(record, ["qualityAssessorProfessionalTitle", "quality_assessor_professional_title"]),
    qualityAssessorLicenseNumber: getFirstString(record, ["qualityAssessorLicenseNumber", "quality_assessor_license_number"]),
    qualityAssessorAccreditingBody: getFirstString(record, ["qualityAssessorAccreditingBody", "quality_assessor_accrediting_body"]),
    qualityAssessorCredentialImageUrls: getImageStrings(getRecordProperty(record, ["qualityAssessorCredentialImageUrls", "qualityAssessorCredentialImageURLs", "quality_assessor_credential_image_urls"])),
    qualityAssessorExperience: getFirstString(record, ["qualityAssessorExperience", "quality_assessor_experience"]),
    qualityAssessorESignatureUrl: getFirstString(record, ["qualityAssessorESignatureUrl", "quality_assessor_e_signature_url"]),
    qualityAssessorVerificationStatus: normalizeSellerVerificationStatus(getRecordProperty(record, ["qualityAssessorVerificationStatus", "quality_assessor_verification_status"])),
    qualityAssessorVerificationNotes: getFirstString(record, ["qualityAssessorVerificationNotes", "quality_assessor_verification_notes"]),
    qualityAssessorCredentialsSubmittedAt: getFirstString(record, ["qualityAssessorCredentialsSubmittedAt", "quality_assessor_credentials_submitted_at"]),
    qualityAssessorCredentialsVerifiedAt: getFirstString(record, ["qualityAssessorCredentialsVerifiedAt", "quality_assessor_credentials_verified_at"]),
    qualityAssessorCredentialsVerifiedBy: getFirstString(record, ["qualityAssessorCredentialsVerifiedBy", "quality_assessor_credentials_verified_by"]),
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

function collectRecords(value: unknown, depth = 0): Record<string, unknown>[] {
  if (depth > 6) return [];

  const record = readRecord(value);
  if (!record) return [];

  const records = [record];
  for (const key of [
    "data",
    "result",
    "payload",
    "session",
    "auth",
    "authentication",
    "credentials",
    "currentSession",
    "current_session",
    "authSession",
    "auth_session",
    "login",
    "user",
    "account",
    "profile",
    "courier",
    "driver",
    "deliveryUser",
    "delivery_user",
  ]) {
    const nested = getRecordProperty(record, [key]);
    records.push(...collectRecords(nested, depth + 1));
  }

  return records;
}

function firstStringAcrossRecords(
  records: Record<string, unknown>[],
  keys: string[],
): string | undefined {
  for (const record of records) {
    const value = getFirstString(record, keys);
    if (value) return value;
  }
  return undefined;
}

function firstNumberAcrossRecords(
  records: Record<string, unknown>[],
  keys: string[],
): number | undefined {
  for (const record of records) {
    const value = getFirstNumber(record, keys);
    if (value !== undefined) return value;
  }
  return undefined;
}

function firstBooleanAcrossRecords(
  records: Record<string, unknown>[],
  keys: string[],
): boolean | undefined {
  for (const record of records) {
    const value = getFirstBoolean(record, keys);
    if (value !== undefined) return value;
  }
  return undefined;
}

function firstMarketplaceRoleAcrossRecords(
  records: Record<string, unknown>[],
): MarketplaceRole | undefined {
  for (const record of records) {
    const role = normalizeMarketplaceRole(
      getRecordProperty(record, [
        "role",
        "accountType",
        "account_type",
        "userRole",
        "user_role",
        "type",
      ]),
    );

    if (role) return role;
  }

  return undefined;
}

function isTrustedTokenlessAdminEmail(email: string | undefined): boolean {
  const normalized = getString(email)?.toLowerCase();
  if (!normalized) return false;

  return (
    normalized === "admin@sparelynk.local" ||
    normalized.endsWith("@sparelynk.local")
  );
}

function buildTokenlessAdminSessionFromSuccessfulLogin(
  response: unknown,
  email: string | undefined,
  remember = false,
): AuthSession | null {
  if (!isSuccessfulAuthResponse(response)) return null;

  const records = uniqueRecords([
    ...collectRecords(response),
    ...collectRecords(unwrapEnvelope(response)),
  ]);
  const responseUser = findFirstAuthenticatedUser(records);
  const responseRole =
    responseUser?.role ?? firstMarketplaceRoleAcrossRecords(records);
  const responseEmail =
    responseUser?.email ??
    firstStringAcrossRecords(records, [
      "email",
      "emailAddress",
      "email_address",
      "login",
      "username",
    ]) ??
    getString(email);

  if (!responseEmail) return null;

  const isAdminLogin =
    responseRole === "admin" || isTrustedTokenlessAdminEmail(responseEmail);
  if (!isAdminLogin) return null;

  const normalizedEmail = responseEmail.toLowerCase();
  const resolvedUser: AuthenticatedUser = {
    id:
      responseUser?.id ??
      firstStringAcrossRecords(records, [
        "id",
        "_id",
        "$id",
        "userId",
        "user_id",
        "accountId",
        "account_id",
      ]) ??
      normalizedEmail,
    name:
      responseUser?.name ??
      firstStringAcrossRecords(records, [
        "name",
        "fullName",
        "full_name",
        "displayName",
        "display_name",
      ]) ??
      normalizedEmail.split("@")[0] ??
      "Admin",
    email: normalizedEmail,
    role: "admin",
    isActive: responseUser?.isActive ?? true,
    emailVerified: responseUser?.emailVerified ?? true,
    mustChangePassword: responseUser?.mustChangePassword ?? false,
    displayName: responseUser?.displayName,
    avatarUrl: responseUser?.avatarUrl ?? null,
    createdAt: responseUser?.createdAt,
    updatedAt: responseUser?.updatedAt,
  };

  const session: AuthSession = {
    user: resolvedUser,
    token: undefined,
    accessToken: undefined,
    tokenType: undefined,
    expiresAt: undefined,
    expiresIn: undefined,
    requiresEmailVerification: false,
    emailVerificationSent: false,
  };

  rememberAuthSession(session, remember);
  return session;
}

function uniqueRecords(
  records: Record<string, unknown>[],
): Record<string, unknown>[] {
  const seen = new Set<Record<string, unknown>>();
  const unique: Record<string, unknown>[] = [];

  for (const record of records) {
    if (seen.has(record)) continue;
    seen.add(record);
    unique.push(record);
  }

  return unique;
}

function findFirstAuthenticatedUser(
  records: Record<string, unknown>[],
  token?: string,
): AuthenticatedUser | null {
  for (const record of records) {
    const user = normalizeAuthenticatedUser(record);
    if (user) return user;
  }

  return normalizeAuthenticatedUserFromToken(token);
}

export function normalizeAuthSession(value: unknown): AuthSession | null {
  const unwrapped = unwrapEnvelope(value);
  const records = uniqueRecords([
    ...collectRecords(value),
    ...collectRecords(unwrapped),
  ]);
  const token =
    getAuthTokenFromValue(value) ||
    getAuthTokenFromValue(unwrapped) ||
    firstStringAcrossRecords(records, AUTH_TOKEN_KEYS);
  const claims = readJwtClaims(token);
  const user = findFirstAuthenticatedUser(records, token);

  if (!user) return null;

  const accessToken =
    firstStringAcrossRecords(records, ["accessToken", "access_token"]) || token;
  const requiresEmailVerification = firstBooleanAcrossRecords(records, [
    "requiresEmailVerification",
    "requires_email_verification",
    "emailVerificationRequired",
    "email_verification_required",
    "verificationRequired",
    "verification_required",
  ]);
  const emailVerificationSent = firstBooleanAcrossRecords(records, [
    "emailVerificationSent",
    "email_verification_sent",
    "verificationEmailSent",
    "verification_email_sent",
  ]);
  const requiresTwoFactor = firstBooleanAcrossRecords(records, [
    "requiresTwoFactor",
    "requires_two_factor",
    "twoFactorRequired",
    "two_factor_required",
  ]);
  const methodsValue = records
    .map((record) =>
      getRecordProperty(record, ["twoFactorMethods", "two_factor_methods"]),
    )
    .find(Array.isArray);
  const twoFactorMethods = Array.isArray(methodsValue)
    ? methodsValue
        .map((method) => String(method).trim().toLowerCase())
        .filter(
          (method): method is "email" | "authenticator" =>
            method === "email" || method === "authenticator",
        )
    : undefined;
  const normalizeRequiredMethods = (keys: string[]) => {
    const value = records.map((record) => getRecordProperty(record, keys)).find(Array.isArray);
    return Array.isArray(value)
      ? value.map((method) => String(method).trim().toLowerCase()).filter(
          (method): method is "email" | "authenticator" => method === "email" || method === "authenticator",
        )
      : undefined;
  };

  return {
    user,
    token,
    accessToken,
    tokenType:
      firstStringAcrossRecords(records, ["tokenType", "token_type"]) ||
      (accessToken || token ? "Bearer" : undefined),
    expiresAt:
      firstStringAcrossRecords(records, ["expiresAt", "expires_at"]) ||
      getJwtExpiresAt(claims),
    expiresIn:
      firstNumberAcrossRecords(records, ["expiresIn", "expires_in"]) ||
      getJwtExpiresIn(claims),
    requiresEmailVerification,
    emailVerificationSent,
    verificationToken: firstStringAcrossRecords(records, [
      "verificationToken",
      "verification_token",
      "emailVerificationToken",
      "email_verification_token",
    ]),
    verificationUrl: firstStringAcrossRecords(records, [
      "verificationUrl",
      "verificationURL",
      "verification_url",
      "emailVerificationUrl",
      "emailVerificationURL",
      "email_verification_url",
      "verifyUrl",
      "verifyURL",
      "verify_url",
    ]),
    requiresTwoFactor,
    twoFactorChallengeToken: firstStringAcrossRecords(records, [
      "twoFactorChallengeToken",
      "two_factor_challenge_token",
      "challengeToken",
      "challenge_token",
    ]),
    twoFactorMethods,
    twoFactorEmailSent: firstBooleanAcrossRecords(records, [
      "twoFactorEmailSent",
      "two_factor_email_sent",
    ]),
    twoFactorCompletedMethods: normalizeRequiredMethods(["twoFactorCompletedMethods", "two_factor_completed_methods"]),
    twoFactorRemainingMethods: normalizeRequiredMethods(["twoFactorRemainingMethods", "two_factor_remaining_methods"]),
    twoFactorAttemptsRemaining: firstNumberAcrossRecords(records, ["twoFactorAttemptsRemaining", "two_factor_attempts_remaining"]),
    twoFactorLocked: firstBooleanAcrossRecords(records, ["twoFactorLocked", "two_factor_locked"]),
    twoFactorRecoveryToken: firstStringAcrossRecords(records, ["twoFactorRecoveryToken", "two_factor_recovery_token"]),
    twoFactorBackupCodeAvailable: firstBooleanAcrossRecords(records, ["twoFactorBackupCodeAvailable", "two_factor_backup_code_available"]),
    twoFactorBackupEmails: (() => {
      const value = records.map((record) => getRecordProperty(record, ["twoFactorBackupEmails", "two_factor_backup_emails"])).find(Array.isArray);
      return Array.isArray(value) ? value.map(String).filter(Boolean) : undefined;
    })(),
    remember: firstBooleanAcrossRecords(records, ["remember", "rememberMe"]),
    createdAt: firstStringAcrossRecords(records, ["createdAt", "created_at"]),
    persistedAt: firstStringAcrossRecords(records, [
      "persistedAt",
      "persisted_at",
    ]),
  };
}

export function readStoredAuthSession(): AuthSession | null {
  const serialized = readStorageValue(AUTH_SESSION_STORAGE_KEY);
  if (!serialized) return null;

  try {
    const normalizedSession = normalizeAuthSession(JSON.parse(serialized));
    const session = normalizedSession
      ? withAuthSessionPersistenceWindow(normalizedSession)
      : null;

    if (!hasUsableAuthSession(session)) {
      clearStoredAuthSession();
      return null;
    }

    if (serialized !== JSON.stringify(session)) {
      void writeStorageValue(
        AUTH_SESSION_STORAGE_KEY,
        JSON.stringify(session),
        readPreferredStorageKind() === "local",
      );
    }

    return session;
  } catch {
    clearStoredAuthSession();
    return null;
  }
}

export function readStoredAuthToken(): string | null {
  const token = readStorageValue(AUTH_TOKEN_STORAGE_KEY);

  if (token) {
    if (isAuthTokenExpired(token)) {
      clearStoredAuthSession();
      return null;
    }

    return token;
  }

  const session = readStoredAuthSession();
  return session?.token ?? session?.accessToken ?? null;
}

export function readFrontendAuthState(): boolean {
  return Boolean(readStoredAuthToken() || readStoredAuthSession()?.user);
}

export function clearStoredAuthSession() {
  const hadStoredAuth = Boolean(
    readStorageValue(AUTH_SESSION_STORAGE_KEY) ||
      readStorageValue(AUTH_TOKEN_STORAGE_KEY),
  );
  clearStorageValue(AUTH_SESSION_STORAGE_KEY);
  clearStorageValue(AUTH_TOKEN_STORAGE_KEY);
  if (hadStoredAuth) {
    notifyAuthSessionUpdated(null);
  }
}

export function rememberAuthSession(
  value: unknown,
  remember = false,
): AuthSession | null {
  const normalizedSession = normalizeAuthSession(value);
  const existingSession = readStoredAuthSession();
  const normalizedToken = getSessionAuthToken(normalizedSession);
  const existingToken = getSessionAuthToken(existingSession);
  const canReusePersistenceWindow = Boolean(
    normalizedToken && existingToken && normalizedToken === existingToken,
  );
  const session = normalizedSession
    ? withAuthSessionPersistenceWindow({
        ...normalizedSession,
        ...(canReusePersistenceWindow && existingSession
          ? {
              persistedAt:
                normalizedSession.persistedAt ?? existingSession.persistedAt,
              expiresAt: normalizedSession.expiresAt ?? existingSession.expiresAt,
              expiresIn: existingSession.expiresIn ?? normalizedSession.expiresIn,
            }
          : {}),
      })
    : null;

  if (!hasUsableAuthSession(session)) {
    clearStoredAuthSession();
    return null;
  }

  const token = getSessionAuthToken(session);
  const previousSerializedSession = readStorageValue(AUTH_SESSION_STORAGE_KEY);
  const serializedSession = JSON.stringify({
    ...session,
    token,
    accessToken: session.accessToken ?? token,
    tokenType: session.tokenType ?? (token ? "Bearer" : undefined),
  });

  const sessionWasSaved = writeStorageValue(
    AUTH_SESSION_STORAGE_KEY,
    serializedSession,
    remember,
  );

  if (!sessionWasSaved) {
    clearStoredAuthSession();
    return null;
  }

  if (token) {
    const tokenWasSaved = writeStorageValue(
      AUTH_TOKEN_STORAGE_KEY,
      token,
      remember,
    );

    if (!tokenWasSaved) {
      clearStorageValue(AUTH_TOKEN_STORAGE_KEY);
    }
  } else {
    clearStorageValue(AUTH_TOKEN_STORAGE_KEY);
  }

  if (session.user?.preferredCurrency || session.user?.nationality) {
    writeCurrencyPreference({
      nationality: session.user.nationality,
      preferredCurrency: session.user.preferredCurrency,
    });
  }

  if (previousSerializedSession !== serializedSession) {
    notifyAuthSessionUpdated(session);
  }
  return session;
}

export function updateStoredAuthSessionUser(
  user: AuthenticatedUser,
  sessionSource?: unknown,
): AuthSession | null {
  const sourceSession =
    normalizeAuthSession(sessionSource) ?? readStoredAuthSession();
  const token =
    getSessionAuthToken(sourceSession) ?? readStoredAuthToken() ?? undefined;
  const remember = readPreferredStorageKind() === "local";

  const session: AuthSession = withAuthSessionPersistenceWindow({
    ...(sourceSession ?? {}),
    user,
    token,
    accessToken: sourceSession?.accessToken ?? token,
    tokenType: sourceSession?.tokenType ?? (token ? "Bearer" : undefined),
  });

  if (!hasUsableAuthSession(session)) {
    clearStoredAuthSession();
    return null;
  }

  const previousSerializedSession = readStorageValue(AUTH_SESSION_STORAGE_KEY);
  const serializedSession = JSON.stringify(session);
  const sessionWasSaved = writeStorageValue(
    AUTH_SESSION_STORAGE_KEY,
    serializedSession,
    remember,
  );

  if (!sessionWasSaved) {
    clearStoredAuthSession();
    return null;
  }

  if (token) {
    const tokenWasSaved = writeStorageValue(
      AUTH_TOKEN_STORAGE_KEY,
      token,
      remember,
    );

    if (!tokenWasSaved) {
      clearStorageValue(AUTH_TOKEN_STORAGE_KEY);
    }
  } else {
    clearStorageValue(AUTH_TOKEN_STORAGE_KEY);
  }

  if (user.preferredCurrency || user.nationality) {
    writeCurrencyPreference({
      nationality: user.nationality,
      preferredCurrency: user.preferredCurrency,
    });
  }

  if (previousSerializedSession !== serializedSession) {
    notifyAuthSessionUpdated(session);
  }
  return session;
}

function mergeAuthSessionToken(
  session: AuthSession,
  token: string | undefined,
): AuthSession {
  const resolvedToken = getSessionAuthToken(session) ?? token;

  return {
    ...session,
    token: resolvedToken,
    accessToken: getString(session.accessToken) || resolvedToken,
    tokenType: session.tokenType ?? (resolvedToken ? "Bearer" : undefined),
  };
}

function rememberAuthTokenOnly(
  token: string | undefined,
  remember = false,
): string | undefined {
  const resolvedToken = getString(token);
  if (!resolvedToken || isAuthTokenExpired(resolvedToken)) return undefined;

  return writeStorageValue(AUTH_TOKEN_STORAGE_KEY, resolvedToken, remember)
    ? resolvedToken
    : undefined;
}

function shouldPersistSession(session: AuthSession): boolean {
  return hasUsableAuthSession(session);
}

function buildQueryString(query?: PageQuery | Record<string, unknown>): string {
  if (!query) return "";

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

function resolveErrorMessage(
  payload: unknown,
  fallback: string,
): { message: string; field?: string } {
  const record = readRecord(payload);
  const error = record ? readRecord(record.error) : null;

  if (error) {
    return {
      message:
        getFirstString(error, ["message", "error", "detail"]) || fallback,
      field: getFirstString(error, ["field", "path"]),
    };
  }

  if (record) {
    return {
      message:
        getFirstString(record, ["message", "error", "detail", "title"]) ||
        fallback,
      field: getFirstString(record, ["field", "path"]),
    };
  }

  return { message: getString(payload) || fallback };
}

const LOGIN_PATHS = [
  "/auth/login",
  "/auth/signin",
  "/login",
  "/sessions",
] as const;

function isLoginRequestPath(path: string): boolean {
  return LOGIN_PATHS.some((loginPath) => loginPath === path);
}

function getLoginErrorMessage(message: string, status: number): string {
  if (![400, 401, 403, 404, 405].includes(status)) return message;

  const normalized = message.toLowerCase();

  if (
    !message ||
    (normalized.includes("invalid") &&
      normalized.includes("login") &&
      normalized.includes("response")) ||
    normalized.includes("request failed with status") ||
    normalized.includes("resource not found") ||
    normalized.includes("not found") ||
    normalized.includes("method not allowed") ||
    normalized.includes("unauthorized") ||
    normalized.includes("forbidden") ||
    normalized.includes("invalid credentials") ||
    normalized.includes("invalid email") ||
    normalized.includes("invalid password")
  ) {
    return "Unable to sign in. Please check your email and password.";
  }

  return message;
}

function parseResponseText(text: string): ApiResponseEnvelope | string | null {
  if (!text) return null;

  try {
    return JSON.parse(text) as ApiResponseEnvelope;
  } catch {
    return text;
  }
}

function isRequestFormDataBody(value: unknown): value is FormData {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

function serializeJsonRequestBody(value: unknown): string {
  return JSON.stringify(value) ?? "null";
}

function createFetchRequestBody(value: unknown): BodyInit | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (isRequestFormDataBody(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value;
  }

  return serializeJsonRequestBody(value);
}

function createUploadRequestBody(
  value: unknown,
): XMLHttpRequestBodyInit | null {
  if (value === undefined) {
    return null;
  }

  if (isRequestFormDataBody(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value;
  }

  return serializeJsonRequestBody(value);
}

type ResponseDownloadProgress = {
  loaded: number;
  total: number | null;
  percent: number | null;
};

function readResponseContentLength(response: Response): number | null {
  const value = Number(response.headers.get("Content-Length"));
  return Number.isFinite(value) && value > 0 ? value : null;
}

async function readResponseText(
  response: Response,
  onProgress?: (progress: ResponseDownloadProgress) => void,
): Promise<string> {
  const total = readResponseContentLength(response);

  if (!response.body || typeof response.body.getReader !== "function") {
    const text = await response.text();
    const loaded = new TextEncoder().encode(text).byteLength;
    onProgress?.({
      loaded,
      total,
      percent: total ? Math.min(100, (loaded / total) * 100) : null,
    });
    return text;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let loaded = 0;
  let text = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    loaded += value.byteLength;
    text += decoder.decode(value, { stream: true });
    onProgress?.({
      loaded,
      total,
      percent: total ? Math.min(100, (loaded / total) * 100) : null,
    });
  }

  text += decoder.decode();
  onProgress?.({
    loaded,
    total,
    percent: total ? 100 : null,
  });
  return text;
}

async function parseResponse(
  response: Response,
  onProgress?: (progress: ResponseDownloadProgress) => void,
): Promise<ApiResponseEnvelope | string | null> {
  const text = await readResponseText(response, onProgress);
  return parseResponseText(text);
}

function isRetriableStatus(status: number, baseUrl?: string): boolean {
  if (status !== 404 && status !== 405) return false;

  return !preferredApiBaseUrl || preferredApiBaseUrl !== baseUrl;
}

async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method =
    options.method ?? (options.body === undefined ? "GET" : "POST");
  const canShareReadRequest =
    method.toUpperCase() === "GET" &&
    (!options.signal || options.allowReadCacheWithSignal) &&
    isCacheableReadPath(path);
  const shouldUseCachedValue = canShareReadRequest && !options.forceRefresh;

  if (!canShareReadRequest) {
    return executeRequest<T>(path, options);
  }

  const cacheKey = buildApiReadCacheKey(path, options);
  const cached = shouldUseCachedValue ? apiReadCache.get(cacheKey) : undefined;

  if (cached && cached.expiresAt > Date.now()) {
    return cached.value as T;
  }

  const pending = apiReadRequests.get(cacheKey);
  if (pending) {
    return (await pending) as T;
  }

  const shouldPersistOffline = isOfflinePersistableReadPath(path);
  const offlineCacheKey = shouldPersistOffline
    ? `${OFFLINE_CATALOG_CACHE_SCOPE}:${options.preserveEnvelope ? "envelope" : "data"}:${path}`
    : "";

  if (shouldPersistOffline && isBrowserOffline()) {
    const offlineHit = await readOfflineApiResponse<T>(offlineCacheKey);
    if (offlineHit) {
      apiReadCache.set(cacheKey, {
        expiresAt: Date.now() + getApiReadCacheTtl(path),
        value: offlineHit.value,
      });
      return offlineHit.value;
    }
  }

  const cacheVersion = apiReadCacheVersion;
  const requestPromise = (async () => {
    try {
      const value = await executeRequest<T>(path, options);

      if (cacheVersion === apiReadCacheVersion) {
        apiReadCache.set(cacheKey, {
          expiresAt: Date.now() + getApiReadCacheTtl(path),
          value,
        });
      }

      if (shouldPersistOffline) {
        void writeOfflineApiResponse({
          cacheKey: offlineCacheKey,
          scope: OFFLINE_CATALOG_CACHE_SCOPE,
          path,
          value,
        });
      }

      return value;
    } catch (error) {
      if (shouldPersistOffline) {
        const offlineHit = await readOfflineApiResponse<T>(offlineCacheKey);
        if (offlineHit) {
          apiReadCache.set(cacheKey, {
            expiresAt: Date.now() + getApiReadCacheTtl(path),
            value: offlineHit.value,
          });
          return offlineHit.value;
        }
      }

      throw error;
    }
  })();

  apiReadRequests.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    if (apiReadRequests.get(cacheKey) === requestPromise) {
      apiReadRequests.delete(cacheKey);
    }
  }
}

function withApiCacheRefresh(path: string, shouldRefresh: boolean): string {
  if (!shouldRefresh) return path;

  const queryIndex = path.indexOf("?");
  const pathname = queryIndex >= 0 ? path.slice(0, queryIndex) : path;
  const query = queryIndex >= 0 ? path.slice(queryIndex + 1) : "";
  const params = new URLSearchParams(query);
  params.set(API_CACHE_REFRESH_QUERY_PARAM, String(Date.now()));
  return `${pathname}?${params.toString()}`;
}

async function executeRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const method =
    options.method ?? (options.body === undefined ? "GET" : "POST");
  const normalizedMethod = method.toUpperCase();
  const requestPath = withApiCacheRefresh(
    normalizedPath,
    normalizedMethod === "GET" && Boolean(options.forceRefresh),
  );
  const token = options.skipAuth ? null : readStoredAuthToken();
  const requestId = createNavigationRequestId();
  const progressBase = {
    requestId,
    method: normalizedMethod,
    path: normalizedPath,
  };
  let lastError: AutopartsApiError | null = null;

  emitNavigationRequestProgress({
    ...progressBase,
    phase: "start",
    loaded: 0,
    total: null,
    percent: 0,
  });

  try {
    for (const baseUrl of getBaseUrlCandidates()) {
      const headers = new Headers(options.headers);
      const isFormDataBody = isRequestFormDataBody(options.body);

      if (options.body !== undefined) {
        if (isFormDataBody) {
          headers.delete("Content-Type");
        } else {
          headers.set("Content-Type", "application/json");
        }
      }

      headers.set("Accept", "application/json");

      if (options.skipAuth) {
        headers.delete("Authorization");
        headers.delete("X-Auth-Token");
        headers.delete("X-Access-Token");
      } else if (token && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      const requestBody = createFetchRequestBody(options.body);

      try {
        const response = await fetch(`${baseUrl}${requestPath}`, {
          method,
          headers,
          credentials: "include",
          signal: options.signal,
          body: requestBody,
        });
        const responseTotal = readResponseContentLength(response);
        const cacheStatus = response.headers.get("X-Cache") || undefined;
        const retriableResponse = isRetriableStatus(response.status, baseUrl);

        emitNavigationRequestProgress({
          ...progressBase,
          phase: "headers",
          status: response.status,
          cacheStatus,
          loaded: 0,
          total: responseTotal,
          percent: responseTotal ? 0 : null,
        });

        const payload = await parseResponse(
          response,
          retriableResponse
            ? undefined
            : (progress) => {
                emitNavigationRequestProgress({
                  ...progressBase,
                  phase: "download",
                  status: response.status,
                  cacheStatus,
                  ...progress,
                });
              },
        );

        if (!retriableResponse) {
          preferredApiBaseUrl = baseUrl;
        }

        if (!response.ok) {
          const { message: resolvedMessage, field } = resolveErrorMessage(
            payload,
            `Request failed with status ${response.status}`,
          );
          const requestMessage = isLoginRequestPath(normalizedPath)
            ? getLoginErrorMessage(resolvedMessage, response.status)
            : resolvedMessage;
          const message = getServerAvailabilityErrorMessage(
            response.status,
            requestMessage,
          );
          const error = new AutopartsApiError(
            message,
            response.status,
            payload,
            field,
          );

          if (!options.skipAuth && response.status === 401) {
            clearStoredAuthSession();
          }

          if (retriableResponse) {
            lastError = error;
            continue;
          }

          throw error;
        }

        const responseToken = getAuthTokenFromResponseHeaders(response.headers);
        const payloadWithHeaderToken = attachAuthTokenToPayload(
          payload,
          responseToken,
        );

        if (normalizedMethod !== "GET") {
          if (isShopConversationMutationPath(normalizedPath)) {
            invalidateShopConversationApiReadCache();
          } else if (shouldInvalidateApiReadCache(normalizedPath)) {
            invalidateApiReadCache();
          }

          if (shouldInvalidateProductReadCache(normalizedPath)) {
            invalidateProductReadCache();
          }
          if (shouldInvalidateOfflineCatalogCache(normalizedPath)) {
            invalidateOfflineCatalogCache();
          }
          if (shouldInvalidateOrderAttachmentReadCache(normalizedPath)) {
            invalidateOrderAttachmentReadCache();
          }

          notifyNavigationPendingActionsChanged();
        }

        emitNavigationRequestProgress({
          ...progressBase,
          phase: "complete",
          status: response.status,
          cacheStatus,
          total: responseTotal,
          percent: 100,
        });

        if (options.preserveEnvelope) {
          return payloadWithHeaderToken as T;
        }

        const unwrappedPayload = unwrapEnvelope(payloadWithHeaderToken);
        return attachAuthTokenToPayload(unwrappedPayload, responseToken) as T;
      } catch (error) {
        if (error instanceof AutopartsApiError) {
          lastError = error;
          if (isRetriableStatus(error.status, baseUrl)) continue;
          throw error;
        }

        lastError = new AutopartsApiError(
          getNetworkRequestErrorMessage(error),
          0,
          error,
        );
      }
    }

    throw (
      lastError ??
      new AutopartsApiError(SERVER_MAINTENANCE_MESSAGE, 0)
    );
  } catch (error) {
    emitNavigationRequestProgress({
      ...progressBase,
      phase: "error",
      percent: 100,
      status: error instanceof AutopartsApiError ? error.status : 0,
    });
    throw error;
  }
}

function getJsonRequestBodySize(value: string | null): number | null {
  if (!value) return null;

  try {
    return new Blob([value]).size;
  } catch {
    return value.length;
  }
}

function resolveUploadProgress(
  event: ProgressEvent,
  fallbackTotal: number | null,
): ApiUploadProgress {
  const total = event.lengthComputable
    ? event.total
    : fallbackTotal && fallbackTotal > 0
      ? fallbackTotal
      : null;
  const loaded =
    total && total > 0 ? Math.min(event.loaded, total) : event.loaded;
  const isUploadBodySent = Boolean(total && total > 0 && loaded >= total);
  const rawPercent =
    total && total > 0 ? Math.max(0, Math.round((loaded / total) * 100)) : null;
  const percent = rawPercent === null ? null : Math.min(100, rawPercent);

  return {
    loaded,
    total,
    percent,
    lengthComputable: event.lengthComputable || Boolean(total),
    phase: isUploadBodySent ? "processing" : "uploading",
  };
}

async function requestWithUploadProgress<T = unknown>(
  path: string,
  options: UploadRequestOptions = {},
): Promise<T> {
  if (typeof XMLHttpRequest === "undefined") {
    return request<T>(path, options);
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const method =
    options.method ?? (options.body === undefined ? "GET" : "POST");
  const normalizedMethod = method.toUpperCase();
  const requestPath = withApiCacheRefresh(
    normalizedPath,
    normalizedMethod === "GET" && Boolean(options.forceRefresh),
  );
  const token = options.skipAuth ? null : readStoredAuthToken();
  let lastError: AutopartsApiError | null = null;

  for (const baseUrl of getBaseUrlCandidates()) {
    const headers = new Headers(options.headers);
    const isFormDataBody = isRequestFormDataBody(options.body);

    if (options.body !== undefined) {
      if (isFormDataBody) {
        headers.delete("Content-Type");
      } else {
        headers.set("Content-Type", "application/json");
      }
    }

    headers.set("Accept", "application/json");

    if (options.skipAuth) {
      headers.delete("Authorization");
      headers.delete("X-Auth-Token");
      headers.delete("X-Access-Token");
    } else if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const requestBody = createUploadRequestBody(options.body);
    const fallbackUploadTotal =
      Number.isFinite(options.uploadTotalBytes) &&
      Number(options.uploadTotalBytes) > 0
        ? Number(options.uploadTotalBytes)
        : typeof requestBody === "string"
          ? getJsonRequestBodySize(requestBody)
          : null;
    let lastUploadProgress: ApiUploadProgress | null = null;
    const emitUploadProgress = (progress: ApiUploadProgress) => {
      lastUploadProgress = progress;
      options.onUploadProgress?.(progress);
    };

    try {
      const { xhr, payload } = await new Promise<{
        xhr: XMLHttpRequest;
        payload: ApiResponseEnvelope | string | null;
      }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        const cleanup = () => {
          options.signal?.removeEventListener("abort", handleAbort);
        };

        const handleAbort = () => {
          xhr.abort();
          cleanup();
          reject(new Error("The request was aborted."));
        };

        if (options.signal?.aborted) {
          handleAbort();
          return;
        }

        xhr.open(method, `${baseUrl}${requestPath}`, true);
        xhr.withCredentials = true;

        headers.forEach((value, key) => {
          xhr.setRequestHeader(key, value);
        });

        if (options.onUploadProgress) {
          emitUploadProgress({
            loaded: 0,
            total: fallbackUploadTotal,
            percent: 0,
            lengthComputable: Boolean(fallbackUploadTotal),
            phase: "uploading",
          });

          xhr.upload.onprogress = (event) => {
            emitUploadProgress(
              resolveUploadProgress(event, fallbackUploadTotal),
            );
          };

          xhr.upload.onload = () => {
            const total = lastUploadProgress?.total ?? fallbackUploadTotal;
            emitUploadProgress({
              loaded: total ?? lastUploadProgress?.loaded ?? 0,
              total,
              percent: total ? 100 : (lastUploadProgress?.percent ?? null),
              lengthComputable: Boolean(total),
              phase: "processing",
            });
          };
        }

        xhr.onload = () => {
          cleanup();
          resolve({ xhr, payload: parseResponseText(xhr.responseText) });
        };

        xhr.onerror = () => {
          cleanup();
          reject(
            new Error(
              "The upload request could not reach the backend. Make sure the API server is running and allows file uploads.",
            ),
          );
        };

        xhr.onabort = () => {
          cleanup();
          reject(new Error("The request was aborted."));
        };

        options.signal?.addEventListener("abort", handleAbort, { once: true });
        xhr.send(requestBody);
      });

      if (!isRetriableStatus(xhr.status, baseUrl)) {
        preferredApiBaseUrl = baseUrl;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const { message: resolvedMessage, field } = resolveErrorMessage(
          payload,
          `Request failed with status ${xhr.status}`,
        );
        const requestMessage = isLoginRequestPath(normalizedPath)
          ? getLoginErrorMessage(resolvedMessage, xhr.status)
          : resolvedMessage;
        const message = getServerAvailabilityErrorMessage(
          xhr.status,
          requestMessage,
        );
        const error = new AutopartsApiError(
          message,
          xhr.status,
          payload,
          field,
        );

        if (!options.skipAuth && xhr.status === 401) {
          clearStoredAuthSession();
        }

        if (isRetriableStatus(xhr.status, baseUrl)) {
          lastError = error;
          continue;
        }

        throw error;
      }

      if (normalizedMethod !== "GET") {
        if (shouldInvalidateApiReadCache(normalizedPath)) {
          invalidateApiReadCache();
        }

        if (shouldInvalidateProductReadCache(normalizedPath)) {
          invalidateProductReadCache();
        }
        if (shouldInvalidateOfflineCatalogCache(normalizedPath)) {
          invalidateOfflineCatalogCache();
        }
        if (shouldInvalidateOrderAttachmentReadCache(normalizedPath)) {
          invalidateOrderAttachmentReadCache();
        }

        notifyNavigationPendingActionsChanged();
      }

      const responseToken = getAuthTokenFromXhrResponseHeaders(xhr);
      const payloadWithHeaderToken = attachAuthTokenToPayload(
        payload,
        responseToken,
      );

      if (options.onUploadProgress) {
        const total = lastUploadProgress?.total ?? fallbackUploadTotal;
        emitUploadProgress({
          loaded: total ?? lastUploadProgress?.loaded ?? 0,
          total,
          percent: 100,
          lengthComputable: Boolean(total),
          phase: "complete",
        });
      }

      if (options.preserveEnvelope) {
        return payloadWithHeaderToken as T;
      }

      const unwrappedPayload = unwrapEnvelope(payloadWithHeaderToken);
      return attachAuthTokenToPayload(unwrappedPayload, responseToken) as T;
    } catch (error) {
      if (options.signal?.aborted) {
        throw error;
      }

      if (error instanceof AutopartsApiError) {
        lastError = error;
        if (isRetriableStatus(error.status, baseUrl)) continue;
        throw error;
      }

      lastError = new AutopartsApiError(
        getNetworkRequestErrorMessage(error),
        0,
        error,
      );
    }
  }

  throw (
    lastError ??
    new AutopartsApiError(SERVER_MAINTENANCE_MESSAGE, 0)
  );
}

function inferUserViewRole(
  record: Record<string, unknown>,
): MarketplaceRole | undefined {
  const explicitRole = normalizeMarketplaceRole(
    getRecordProperty(record, [
      "role",
      "accountType",
      "account_type",
      "userRole",
      "user_role",
      "type",
      "kind",
      "userType",
      "user_type",
    ]),
  );

  if (explicitRole) return explicitRole;

  const email = getFirstString(record, [
    "email",
    "emailAddress",
    "email_address",
    "login",
    "username",
  ])?.toLowerCase();
  const label = [
    email,
    getFirstString(record, [
      "name",
      "fullName",
      "full_name",
      "displayName",
      "display_name",
    ]),
    getFirstString(record, ["status", "state"]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    email?.includes("admin") ||
    /\b(admin|administrator|superadmin)\b/.test(label)
  ) {
    return "admin";
  }

  if (
    getRecordProperty(record, [
      "vendorId",
      "vendor_id",
      "sellerId",
      "seller_id",
      "storeName",
      "store_name",
      "businessName",
      "business_name",
    ]) !== undefined ||
    /\b(vendor|seller|merchant|store|shop)\b/.test(label)
  ) {
    return "seller";
  }

  if (
    getRecordProperty(record, [
      "courierId",
      "courier_id",
      "driverId",
      "driver_id",
      "deliveryDriverId",
      "delivery_driver_id",
    ]) !== undefined ||
    /\b(courier|driver|delivery driver)\b/.test(label)
  ) {
    return "courier";
  }

  if (/\b(customer|buyer|client)\b/.test(label)) {
    return "buyer";
  }

  return undefined;
}

function normalizeUserView(value: unknown): UserView | null {
  const user = normalizeAuthenticatedUser(value);
  if (user) return { ...user };

  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const nestedUser = readNestedRecord(record, [
    "user",
    "account",
    "profile",
    "currentUser",
    "current_user",
    "customer",
    "vendor",
    "seller",
    "courier",
    "driver",
    "deliveryUser",
    "delivery_user",
    "admin",
    "node",
  ]);

  if (nestedUser && nestedUser !== record) {
    const nested = normalizeUserView(nestedUser);
    if (nested) return nested;
  }

  const email = getFirstString(record, [
    "email",
    "emailAddress",
    "email_address",
    "login",
    "username",
  ]);
  const id =
    getFirstString(record, [
      "id",
      "_id",
      "$id",
      "userId",
      "user_id",
      "accountId",
      "account_id",
      "customerId",
      "customer_id",
    ]) || email;
  const role = inferUserViewRole(record) ?? "buyer";

  if (!id || !email) return null;

  const displayName = getFirstString(record, ["displayName", "display_name"]);
  const name =
    getFirstString(record, [
      "name",
      "fullName",
      "full_name",
      "displayName",
      "display_name",
    ]) || email;

  return {
    ...record,
    id,
    name,
    email,
    role,
    isActive:
      getBoolean(
        getRecordProperty(record, [
          "isActive",
          "is_active",
          "active",
          "enabled",
        ]),
      ) ?? true,
    emailVerified: getBoolean(
      getRecordProperty(record, [
        "emailVerified",
        "email_verified",
        "isEmailVerified",
        "is_email_verified",
        "verified",
      ]),
    ),
    mustChangePassword: getBoolean(
      getRecordProperty(record, [
        "mustChangePassword",
        "must_change_password",
        "forcePasswordChange",
        "force_password_change",
      ]),
    ),
    displayName,
    avatarUrl:
      getFirstString(record, [
        "avatarUrl",
        "avatar_url",
        "avatar",
        "imageUrl",
        "image_url",
      ]) ?? null,
    phone: getFirstString(record, [
      "phone",
      "phoneNumber",
      "phone_number",
      "contactPhone",
      "contact_phone",
    ]),
    address: getFirstString(record, [
      "address",
      "addressLine1",
      "address_line_1",
      "streetAddress",
      "street_address",
    ]),
    nationality: getFirstString(record, [
      "nationality",
      "country",
      "countryName",
      "country_name",
    ]),
    preferredCurrency: getFirstString(record, [
      "preferredCurrency",
      "preferred_currency",
      "currencyPreference",
      "currency_preference",
    ])?.toUpperCase(),
    loyaltyBadges: getStringValues(
      getRecordProperty(record, ["loyaltyBadges", "loyalty_badges", "badges"]),
    ),
    courierIdentificationType: getFirstString(record, [
      "courierIdentificationType",
      "courier_identification_type",
    ]),
    courierIdentificationNumber: getFirstString(record, [
      "courierIdentificationNumber",
      "courier_identification_number",
    ]),
    courierIdentificationImageUrls: getImageStrings(
      getRecordProperty(record, [
        "courierIdentificationImageUrls",
        "courierIdentificationImageURLs",
        "courier_identification_image_urls",
      ]),
    ),
    courierExperience: getFirstString(record, [
      "courierExperience",
      "courier_experience",
    ]),
    courierOtherWorkExperience: getFirstString(record, [
      "courierOtherWorkExperience",
      "courier_other_work_experience",
    ]),
    courierVerificationStatus: normalizeCourierVerificationStatus(
      getRecordProperty(record, [
        "courierVerificationStatus",
        "courier_verification_status",
      ]),
    ),
    courierVerificationNotes: getFirstString(record, [
      "courierVerificationNotes",
      "courier_verification_notes",
    ]),
    courierCredentialsSubmittedAt: getFirstString(record, [
      "courierCredentialsSubmittedAt",
      "courier_credentials_submitted_at",
    ]),
    courierCredentialsVerifiedAt: getFirstString(record, [
      "courierCredentialsVerifiedAt",
      "courier_credentials_verified_at",
    ]),
    courierCredentialsVerifiedBy: getFirstString(record, [
      "courierCredentialsVerifiedBy",
      "courier_credentials_verified_by",
    ]),
    sellerIdentificationType: getFirstString(record, [
      "sellerIdentificationType",
      "seller_identification_type",
    ]),
    sellerIdentificationNumber: getFirstString(record, [
      "sellerIdentificationNumber",
      "seller_identification_number",
    ]),
    sellerIdentificationImageUrls: getImageStrings(
      getRecordProperty(record, [
        "sellerIdentificationImageUrls",
        "sellerIdentificationImageURLs",
        "seller_identification_image_urls",
      ]),
    ),
    sellerExperience: getFirstString(record, [
      "sellerExperience",
      "seller_experience",
    ]),
    sellerOtherWorkExperience: getFirstString(record, [
      "sellerOtherWorkExperience",
      "seller_other_work_experience",
    ]),
    sellerVerificationStatus: normalizeSellerVerificationStatus(
      getRecordProperty(record, [
        "sellerVerificationStatus",
        "seller_verification_status",
      ]),
    ),
    sellerVerificationNotes: getFirstString(record, [
      "sellerVerificationNotes",
      "seller_verification_notes",
    ]),
    sellerCredentialsSubmittedAt: getFirstString(record, [
      "sellerCredentialsSubmittedAt",
      "seller_credentials_submitted_at",
    ]),
    sellerCredentialsVerifiedAt: getFirstString(record, [
      "sellerCredentialsVerifiedAt",
      "seller_credentials_verified_at",
    ]),
    sellerCredentialsVerifiedBy: getFirstString(record, [
      "sellerCredentialsVerifiedBy",
      "seller_credentials_verified_by",
    ]),
    qualityAssessorProfessionalTitle: getFirstString(record, ["qualityAssessorProfessionalTitle", "quality_assessor_professional_title"]),
    qualityAssessorLicenseNumber: getFirstString(record, ["qualityAssessorLicenseNumber", "quality_assessor_license_number"]),
    qualityAssessorAccreditingBody: getFirstString(record, ["qualityAssessorAccreditingBody", "quality_assessor_accrediting_body"]),
    qualityAssessorCredentialImageUrls: getImageStrings(getRecordProperty(record, ["qualityAssessorCredentialImageUrls", "qualityAssessorCredentialImageURLs", "quality_assessor_credential_image_urls"])),
    qualityAssessorExperience: getFirstString(record, ["qualityAssessorExperience", "quality_assessor_experience"]),
    qualityAssessorESignatureUrl: getFirstString(record, ["qualityAssessorESignatureUrl", "quality_assessor_e_signature_url"]),
    qualityAssessorVerificationStatus: normalizeSellerVerificationStatus(getRecordProperty(record, ["qualityAssessorVerificationStatus", "quality_assessor_verification_status"])),
    qualityAssessorVerificationNotes: getFirstString(record, ["qualityAssessorVerificationNotes", "quality_assessor_verification_notes"]),
    qualityAssessorCredentialsSubmittedAt: getFirstString(record, ["qualityAssessorCredentialsSubmittedAt", "quality_assessor_credentials_submitted_at"]),
    qualityAssessorCredentialsVerifiedAt: getFirstString(record, ["qualityAssessorCredentialsVerifiedAt", "quality_assessor_credentials_verified_at"]),
    qualityAssessorCredentialsVerifiedBy: getFirstString(record, ["qualityAssessorCredentialsVerifiedBy", "quality_assessor_credentials_verified_by"]),
    createdAt: getFirstString(record, [
      "createdAt",
      "created_at",
      "created",
      "joinedAt",
      "joined_at",
    ]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at", "updated"]),
  };
}

export function normalizeProductView(value: unknown): ProductView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const nestedProduct = readNestedRecord(record, [
    "product",
    "item",
    "part",
    "listing",
    "node",
  ]);
  if (nestedProduct) {
    const nested = normalizeProductView(nestedProduct);
    if (nested) return nested;
  }

  const title =
    getFirstString(record, [
      "title",
      "name",
      "productName",
      "product_name",
      "displayName",
      "display_name",
      "partName",
      "part_name",
      "partNumber",
      "part_number",
      "sku",
    ]) || "Untitled Product";
  const id =
    getFirstString(record, [
      "id",
      "_id",
      "$id",
      "productId",
      "product_id",
      "partId",
      "part_id",
      "listingId",
      "listing_id",
    ]) ||
    getFirstString(record, [
      "sku",
      "SKU",
      "code",
      "partNumber",
      "part_number",
    ]) ||
    title;

  if (!id) return null;

  const images = normalizeProductImageUrls(record);
  const imageUrl = images[0];
  const vendorId = getFirstString(record, [
    "vendorId",
    "vendor_id",
    "sellerId",
    "seller_id",
    "userId",
    "user_id",
  ]);
  const price =
    getFirstNumber(record, ["price", "amount", "unitPrice", "unit_price"]) ?? 0;
  const salePrice = getFirstNumber(record, [
    "salePrice",
    "sale_price",
    "discountPrice",
    "discount_price",
  ]);
  const effectivePrice =
    getFirstNumber(record, ["effectivePrice", "effective_price"]) ??
    salePrice ??
    price;

  return {
    ...record,
    id,
    title,
    name:
      getFirstString(record, [
        "name",
        "title",
        "productName",
        "product_name",
        "displayName",
        "display_name",
      ]) || title,
    description: getFirstString(record, ["description", "details", "summary"]),
    sku: getFirstString(record, ["sku", "SKU", "code"]),
    productNumber: getFirstString(record, [
      "productNumber",
      "product_number",
      "sparelynkProductNumber",
      "sparelynk_product_number",
    ]),
    partNumber: getFirstString(record, [
      "partNumber",
      "part_number",
      "manufacturerPartNumber",
      "manufacturer_part_number",
      "mpn",
    ]),
    category: getFirstString(record, [
      "category",
      "categoryName",
      "category_name",
    ]),
    brand: getFirstString(record, ["brand", "manufacturer"]),
    vehicleType: getFirstString(record, ["vehicleType", "vehicle_type"]),
    vehicleModel: getFirstString(record, [
      "vehicleModel",
      "vehicle_model",
      "model",
    ]),
    modelYears: getNumberArray(
      getRecordProperty(record, ["modelYears", "model_years", "years"]),
    ),
    variant: getFirstString(record, ["variant", "trim"]),
    engine: getFirstString(record, ["engine", "engineType", "engine_type"]),
    market: getFirstString(record, ["market", "countryMarket", "country_market"]),
    location: getFirstString(record, ["location", "sellerLocation", "seller_location"]),
    condition: getFirstString(record, ["condition", "productCondition", "product_condition"]),
    conditionDescription: getFirstString(record, [
      "conditionDescription",
      "condition_description",
    ]),
    knownDefects: getFirstString(record, ["knownDefects", "known_defects"]),
    testingStatus: getFirstString(record, ["testingStatus", "testing_status"]),
    repairHistory: getFirstString(record, ["repairHistory", "repair_history"]),
    qualityAssuranceStatus:
      getFirstString(record, ["qualityAssuranceStatus", "quality_assurance_status"]) || "PENDING",
    qualityAssuranceProfessionalName: getFirstString(record, [
      "qualityAssuranceProfessionalName",
      "quality_assurance_professional_name",
    ]),
    qualityAssuranceProfessionalTitle: getFirstString(record, [
      "qualityAssuranceProfessionalTitle",
      "quality_assurance_professional_title",
    ]),
    qualityAssuranceCredentialNumber: getFirstString(record, [
      "qualityAssuranceCredentialNumber",
      "quality_assurance_credential_number",
    ]),
    qualityAssuranceSignatureUrl: getFirstString(record, [
      "qualityAssuranceSignatureUrl",
      "quality_assurance_signature_url",
    ]),
    qualityAssuranceEvidenceUrls: getImageStrings(
      getRecordProperty(record, [
        "qualityAssuranceEvidenceUrls",
        "quality_assurance_evidence_urls",
      ]),
    ),
    qualityAssuranceSellerNotes: getFirstString(record, [
      "qualityAssuranceSellerNotes",
      "quality_assurance_seller_notes",
    ]),
    qualityAssuranceAssessorUserId: getFirstString(record, ["qualityAssuranceAssessorUserId", "quality_assurance_assessor_user_id"]),
    qualityAssuranceAssessmentSource: getFirstString(record, ["qualityAssuranceAssessmentSource", "quality_assurance_assessment_source"]),
    qualityAssuranceWrittenAssessment: getFirstString(record, ["qualityAssuranceWrittenAssessment", "quality_assurance_written_assessment"]),
    qualityAssuranceAdminComment: getFirstString(record, [
      "qualityAssuranceAdminComment",
      "quality_assurance_admin_comment",
    ]),
    qualityAssuranceAdminEvidenceUrls: getImageStrings(
      getRecordProperty(record, [
        "qualityAssuranceAdminEvidenceUrls",
        "quality_assurance_admin_evidence_urls",
      ]),
    ),
    qualityAssuranceReviewedAt: getFirstString(record, [
      "qualityAssuranceReviewedAt",
      "quality_assurance_reviewed_at",
    ]),
    qualityAssurancePassedAt: getFirstString(record, [
      "qualityAssurancePassedAt",
      "quality_assurance_passed_at",
    ]),
    qualityAssuranceSeal: getBoolean(
      getRecordProperty(record, ["qualityAssuranceSeal", "quality_assurance_seal"]),
    ),
    price,
    salePrice,
    effectivePrice,
    currency: getFirstString(record, ["currency", "currencyCode", "currency_code"]),
    fulfillmentBySpareLynk: getBoolean(
      getRecordProperty(record, ["fulfillmentBySpareLynk", "fulfillment_by_sparelynk"]),
    ),
    fulfillmentFeePerUnit: getFirstNumber(record, [
      "fulfillmentFeePerUnit",
      "fulfillment_fee_per_unit",
    ]),
    advertisingActive: getBoolean(
      getRecordProperty(record, ["advertisingActive", "advertising_active"]),
    ),
    advertisingCostPerClick: getFirstNumber(record, [
      "advertisingCostPerClick",
      "advertising_cost_per_click",
    ]),
    advertisingBudgetRemaining: getFirstNumber(record, [
      "advertisingBudgetRemaining",
      "advertising_budget_remaining",
    ]),
    advertisingCampaignId: getFirstString(record, [
      "advertisingCampaignId",
      "advertising_campaign_id",
    ]),
    stock: getFirstNumber(record, [
      "stock",
      "stockQty",
      "stock_qty",
      "quantity",
      "availableQuantity",
      "available_quantity",
    ]),
    stockQty: getFirstNumber(record, [
      "stockQty",
      "stock_qty",
      "stock",
      "quantity",
    ]),
    quantity: getFirstNumber(record, [
      "quantity",
      "stock",
      "stockQty",
      "stock_qty",
    ]),
    status: getFirstString(record, ["status", "state", "listingState", "listing_state"]),
    listingState: getFirstString(record, ["listingState", "listing_state", "status", "state"]),
    vendorId,
    vendorName: getFirstString(record, [
      "vendorName",
      "vendor_name",
      "sellerName",
      "seller_name",
      "storeName",
      "store_name",
    ]),
    adminReviewComment: getFirstString(record, [
      "adminReviewComment",
      "admin_review_comment",
      "reviewComment",
      "review_comment",
      "adminComment",
      "admin_comment",
    ]),
    reviewComments: normalizeProductReviewComments(
      getRecordProperty(record, [
        "reviewComments",
        "review_comments",
        "comments",
        "commentThread",
        "comment_thread",
      ]),
    ),
    buyerReviews: normalizeBuyerReviews(
      getRecordProperty(record, ["buyerReviews", "buyer_reviews"]),
      {
        targetType: "product",
        targetId: id,
        productId: id,
        vendorId,
      },
    ),
    extraProtectionFee: getFirstNumber(record, [
      "extraProtectionFee",
      "extra_protection_fee",
    ]),
    baseShippingFee: getFirstNumber(record, [
      "baseShippingFee",
      "base_shipping_fee",
    ]),
    baseShippingFeeCurrency:
      getFirstString(record, [
        "baseShippingFeeCurrency",
        "base_shipping_fee_currency",
        "shippingFeeCurrency",
        "shipping_fee_currency",
      ]) ?? "USD",
    buyerShippingFeePercent: getFirstNumber(record, [
      "buyerShippingFeePercent",
      "buyer_shipping_fee_percent",
    ]),
    freeShippingQuantityThreshold: getFirstNumber(record, [
      "freeShippingQuantityThreshold",
      "free_shipping_quantity_threshold",
    ]),
    shopRating: getFirstNumber(record, ["shopRating", "shop_rating"]),
    shopReviews: getFirstNumber(record, ["shopReviews", "shop_reviews"]),
    imageUrl,
    primaryImageUrl: imageUrl,
    images,
    imageUrls: images,
    spinImageUrls: getImageStrings(
      getRecordProperty(record, ["spinImageUrls", "spin_image_urls", "spin360ImageUrls", "spin360_image_urls"]),
    ),
    rating: getFirstNumber(record, [
      "rating",
      "averageRating",
      "average_rating",
    ]),
    reviews: getFirstNumber(record, ["reviews", "reviewCount", "review_count"]),
    specs:
      readRecord(getRecordProperty(record, ["specs", "specifications"])) ??
      undefined,
    fitment: Array.isArray(record.fitment) ? record.fitment : undefined,
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  } as ProductView;
}

type BuyerReviewNormalizationContext = {
  targetType?: "product" | "shop";
  targetId?: string;
  productId?: string;
  vendorId?: string;
};

const BUYER_REVIEW_COLLECTION_KEYS = [
  "items",
  "reviews",
  "buyerReviews",
  "buyer_reviews",
  "productReviews",
  "product_reviews",
  "shopReviews",
  "shop_reviews",
  "results",
  "records",
  "entries",
  "content",
  "data",
  "result",
  "payload",
  "collection",
] as const;

const BUYER_REVIEW_MEDIA_COLLECTION_KEYS = [
  "media",
  "attachments",
  "files",
  "items",
  "reviewMedia",
  "review_media",
  "assets",
  "data",
  "result",
  "payload",
] as const;

function looksLikeBuyerReviewRecord(record: Record<string, unknown>): boolean {
  const hasReviewIdentity = Boolean(
    getFirstString(record, ["id", "_id", "$id", "reviewId", "review_id"]),
  );
  const hasReviewContent = Boolean(
    getFirstString(record, [
      "review",
      "message",
      "comment",
      "body",
      "text",
      "content",
    ]),
  );
  const hasReviewMedia =
    getRecordProperty(record, [
      "media",
      "attachments",
      "reviewMedia",
      "review_media",
    ]) !== undefined;
  const hasReviewTarget = Boolean(
    getFirstString(record, [
      "targetId",
      "target_id",
      "productId",
      "product_id",
      "vendorId",
      "vendor_id",
      "shopId",
      "shop_id",
      "sellerId",
      "seller_id",
    ]),
  );
  const hasBuyerIdentity = Boolean(
    getFirstString(record, [
      "buyerId",
      "buyer_id",
      "customerId",
      "customer_id",
      "reviewerId",
      "reviewer_id",
      "userId",
      "user_id",
    ]) || getRecordProperty(record, ["buyer", "customer", "reviewer"]),
  );
  const hasRating =
    getFirstNumber(record, ["rating", "stars", "score", "ratingValue"]) !==
    undefined;
  const hasNestedReviewCollection = BUYER_REVIEW_COLLECTION_KEYS.some((key) => {
    const nested = getRecordProperty(record, [key]);
    return Array.isArray(nested) || Boolean(readRecord(nested));
  });

  if (hasNestedReviewCollection && !hasReviewContent && !hasReviewMedia) {
    return false;
  }

  return (
    (hasReviewIdentity || hasReviewTarget || hasBuyerIdentity) &&
    (hasReviewContent || hasReviewMedia || hasRating)
  );
}

function collectBuyerReviewEntries(
  value: unknown,
  depth = 0,
  seen = new Set<unknown>(),
): unknown[] {
  if (value === null || value === undefined || depth > 7) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      collectBuyerReviewEntries(entry, depth + 1, seen),
    );
  }

  if (typeof value !== "object" || seen.has(value)) return [];

  const record = readRecord(value);
  if (!record) return [];

  seen.add(value);

  if (looksLikeBuyerReviewRecord(record)) {
    return [record];
  }

  const entries: unknown[] = [];

  for (const key of BUYER_REVIEW_COLLECTION_KEYS) {
    const nested = getRecordProperty(record, [key]);
    if (nested !== undefined && nested !== null) {
      entries.push(...collectBuyerReviewEntries(nested, depth + 1, seen));
    }
  }

  if (entries.length > 0) {
    return entries;
  }

  Object.values(record).forEach((nested) => {
    if (nested && typeof nested === "object") {
      entries.push(...collectBuyerReviewEntries(nested, depth + 1, seen));
    }
  });

  return entries;
}

function looksLikeBuyerReviewMediaRecord(
  record: Record<string, unknown>,
): boolean {
  return Boolean(
    getFirstString(record, [
      "url",
      "src",
      "publicUrl",
      "public_url",
      "fileUrl",
      "file_url",
      "downloadUrl",
      "download_url",
      "location",
      "key",
      "objectKey",
      "object_key",
    ]),
  );
}

function collectBuyerReviewMediaEntries(
  value: unknown,
  depth = 0,
  seen = new Set<unknown>(),
): unknown[] {
  if (value === null || value === undefined || depth > 6) return [];

  if (typeof value === "string") {
    return value.trim() ? [value.trim()] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      collectBuyerReviewMediaEntries(entry, depth + 1, seen),
    );
  }

  if (typeof value !== "object" || seen.has(value)) return [];

  const record = readRecord(value);
  if (!record) return [];

  seen.add(value);

  if (looksLikeBuyerReviewMediaRecord(record)) {
    return [record];
  }

  return BUYER_REVIEW_MEDIA_COLLECTION_KEYS.flatMap((key) => {
    const nested = getRecordProperty(record, [key]);
    return nested === undefined || nested === null
      ? []
      : collectBuyerReviewMediaEntries(nested, depth + 1, seen);
  });
}

function inferBuyerReviewMediaContentType(
  url: string,
  declaredContentType?: string,
): string {
  const declared = declaredContentType?.trim().toLowerCase();
  if (declared === "image") return "image/*";
  if (declared === "video") return "video/*";
  if (
    declared &&
    declared !== "application/octet-stream" &&
    declared.includes("/")
  ) {
    return declared;
  }

  const normalizedUrl = url.toLowerCase().split(/[?#]/)[0];
  const extension = normalizedUrl.split(".").pop() ?? "";
  const byExtension: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    heic: "image/heic",
    heif: "image/heif",
    svg: "image/svg+xml",
    mp4: "video/mp4",
    m4v: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    mkv: "video/x-matroska",
  };

  return byExtension[extension] ?? "application/octet-stream";
}

function normalizeBuyerReviewMedia(value: unknown): BuyerReviewMediaView[] {
  const normalized: BuyerReviewMediaView[] = [];
  const seen = new Set<string>();

  collectBuyerReviewMediaEntries(value).forEach((entry, index) => {
    const record = readRecord(entry);
    const nestedFile = record
      ? readRecord(
          getRecordProperty(record, ["file", "asset", "attachment", "media"]),
        )
      : null;
    const sources = normalizeReturnRefundPlaybackSources(
      record
        ? getRecordProperty(record, [
            "sources",
            "playbackSources",
            "playback_sources",
          ])
        : undefined,
    );
    const url =
      (typeof entry === "string" ? entry : undefined) ||
      (record
        ? getFirstString(record, [
            "url",
            "src",
            "publicUrl",
            "public_url",
            "fileUrl",
            "file_url",
            "downloadUrl",
            "download_url",
            "location",
          ])
        : undefined) ||
      (nestedFile
        ? getFirstString(nestedFile, [
            "url",
            "src",
            "publicUrl",
            "public_url",
            "fileUrl",
            "file_url",
            "downloadUrl",
            "download_url",
            "location",
          ])
        : undefined) ||
      sources[0]?.url;

    if (!url) return;

    const key =
      (record
        ? getFirstString(record, [
            "key",
            "objectKey",
            "object_key",
            "storageKey",
            "storage_key",
            "id",
            "_id",
          ])
        : undefined) ||
      (nestedFile
        ? getFirstString(nestedFile, [
            "key",
            "objectKey",
            "object_key",
            "storageKey",
            "storage_key",
            "id",
            "_id",
          ])
        : undefined) ||
      url;

    if (seen.has(key)) return;
    seen.add(key);

    const declaredContentType =
      (record
        ? getFirstString(record, [
            "contentType",
            "content_type",
            "mimeType",
            "mime_type",
            "type",
          ])
        : undefined) ||
      (nestedFile
        ? getFirstString(nestedFile, [
            "contentType",
            "content_type",
            "mimeType",
            "mime_type",
            "type",
          ])
        : undefined);
    const contentType = inferBuyerReviewMediaContentType(
      url,
      declaredContentType,
    );
    const declaredMediaType =
      (record
        ? getFirstString(record, ["mediaType", "media_type", "kind", "type"])
        : undefined) ||
      (nestedFile
        ? getFirstString(nestedFile, [
            "mediaType",
            "media_type",
            "kind",
            "type",
          ])
        : undefined);
    const normalizedMediaType = declaredMediaType?.trim().toLowerCase();
    const mediaType =
      normalizedMediaType === "image" ||
      normalizedMediaType === "photo" ||
      normalizedMediaType === "picture" ||
      contentType.startsWith("image/")
        ? "image"
        : "video";
    const fileName =
      (record
        ? getFirstString(record, [
            "fileName",
            "file_name",
            "name",
            "originalName",
            "original_name",
          ])
        : undefined) ||
      (nestedFile
        ? getFirstString(nestedFile, [
            "fileName",
            "file_name",
            "name",
            "originalName",
            "original_name",
          ])
        : undefined) ||
      getLastItem(key.split("/")) ||
      `review-media-${index + 1}`;

    normalized.push({
      url,
      key,
      mediaType,
      contentType,
      fileName,
      size:
        (record
          ? getFirstNumber(record, ["size", "fileSize", "file_size", "bytes"])
          : undefined) ||
        (nestedFile
          ? getFirstNumber(nestedFile, [
              "size",
              "fileSize",
              "file_size",
              "bytes",
            ])
          : undefined) ||
        0,
      sources,
    });
  });

  return normalized;
}

function normalizeBuyerReviews(
  value: unknown,
  context: BuyerReviewNormalizationContext = {},
): BuyerReviewView[] {
  const normalized: BuyerReviewView[] = [];
  const seen = new Map<string, number>();

  collectBuyerReviewEntries(value).forEach((entry, index) => {
    const record = readRecord(entry);
    if (!record) return;

    const productRecord = readRecord(
      getRecordProperty(record, ["product", "listing", "item"]),
    );
    const vendorRecord = readRecord(
      getRecordProperty(record, ["vendor", "seller", "shop", "store"]),
    );
    const buyerRecord = readRecord(
      getRecordProperty(record, ["buyer", "customer", "reviewer", "user"]),
    );
    const explicitTargetType = getFirstString(record, [
      "targetType",
      "target_type",
      "type",
      "reviewType",
      "review_type",
    ])
      ?.trim()
      .toLowerCase();
    const productId =
      getFirstString(record, [
        "productId",
        "product_id",
        "listingId",
        "listing_id",
        "itemId",
        "item_id",
      ]) ||
      (productRecord
        ? getFirstString(productRecord, [
            "id",
            "_id",
            "$id",
            "productId",
            "product_id",
          ])
        : undefined) ||
      context.productId;
    const vendorId =
      getFirstString(record, [
        "vendorId",
        "vendor_id",
        "sellerId",
        "seller_id",
        "shopId",
        "shop_id",
        "storeId",
        "store_id",
        "merchantId",
        "merchant_id",
      ]) ||
      (vendorRecord
        ? getFirstString(vendorRecord, [
            "id",
            "_id",
            "$id",
            "vendorId",
            "vendor_id",
            "sellerId",
            "seller_id",
            "shopId",
            "shop_id",
          ])
        : undefined) ||
      context.vendorId ||
      "";
    const targetType =
      context.targetType ||
      (explicitTargetType === "shop" ||
      explicitTargetType === "vendor" ||
      explicitTargetType === "seller" ||
      explicitTargetType === "store"
        ? "shop"
        : productId
          ? "product"
          : "shop");
    const targetId =
      getFirstString(record, ["targetId", "target_id"]) ||
      context.targetId ||
      (targetType === "product" ? productId : vendorId);

    if (!targetId) return;

    const createdAt =
      getFirstString(record, [
        "createdAt",
        "created_at",
        "submittedAt",
        "submitted_at",
        "timestamp",
        "date",
      ]) || "";
    const explicitId = getFirstString(record, [
      "id",
      "_id",
      "$id",
      "reviewId",
      "review_id",
    ]);
    const buyerId =
      getFirstString(record, [
        "buyerId",
        "buyer_id",
        "customerId",
        "customer_id",
        "reviewerId",
        "reviewer_id",
        "userId",
        "user_id",
      ]) ||
      (buyerRecord
        ? getFirstString(buyerRecord, [
            "id",
            "_id",
            "$id",
            "buyerId",
            "buyer_id",
            "customerId",
            "customer_id",
            "userId",
            "user_id",
          ])
        : undefined) ||
      "unknown-buyer";
    const id =
      explicitId ||
      `${targetType}-${targetId}-${buyerId}-${createdAt || index}`;
    const repliesValue = getRecordProperty(record, [
      "replies",
      "comments",
      "responses",
      "thread",
    ]);
    const replies = Array.isArray(repliesValue)
      ? repliesValue.flatMap((replyValue, replyIndex) => {
          const reply = readRecord(replyValue);
          if (!reply) return [];
          const message = getFirstString(reply, [
            "message",
            "comment",
            "review",
            "body",
            "text",
          ]);
          if (!message) return [];
          const authorRecord = readRecord(
            getRecordProperty(reply, ["author", "user", "sender"]),
          );
          const authorId =
            getFirstString(reply, [
              "authorId",
              "author_id",
              "userId",
              "user_id",
              "senderId",
              "sender_id",
            ]) ||
            (authorRecord
              ? getFirstString(authorRecord, ["id", "_id", "$id"])
              : undefined) ||
            "unknown-author";
          return [
            {
              id:
                getFirstString(reply, [
                  "id",
                  "_id",
                  "$id",
                  "replyId",
                  "reply_id",
                  "commentId",
                  "comment_id",
                ]) || `${id}-reply-${replyIndex}`,
              authorId,
              authorRole:
                getFirstString(reply, [
                  "authorRole",
                  "author_role",
                  "role",
                  "senderRole",
                  "sender_role",
                ]) ||
                (authorRecord
                  ? getFirstString(authorRecord, ["role", "userRole"])
                  : undefined) ||
                "buyer",
              authorName:
                getFirstString(reply, [
                  "authorName",
                  "author_name",
                  "senderName",
                  "sender_name",
                  "name",
                ]) ||
                (authorRecord
                  ? getFirstString(authorRecord, [
                      "name",
                      "displayName",
                      "display_name",
                      "fullName",
                      "full_name",
                    ])
                  : undefined) ||
                "Marketplace user",
              message,
              createdAt:
                getFirstString(reply, [
                  "createdAt",
                  "created_at",
                  "timestamp",
                  "date",
                ]) || "",
            },
          ];
        })
      : [];
    const media = normalizeBuyerReviewMedia(
      getRecordProperty(record, [
        "media",
        "attachments",
        "files",
        "reviewMedia",
        "review_media",
      ]),
    );
    const review: BuyerReviewView = {
      id,
      targetType,
      targetId,
      productId: targetType === "product" ? productId || targetId : productId,
      vendorId,
      orderId: getFirstString(record, [
        "orderId",
        "order_id",
        "purchaseId",
        "purchase_id",
      ]),
      buyerId,
      buyerName:
        getFirstString(record, [
          "buyerName",
          "buyer_name",
          "customerName",
          "customer_name",
          "reviewerName",
          "reviewer_name",
          "userName",
          "user_name",
        ]) ||
        (buyerRecord
          ? getFirstString(buyerRecord, [
              "name",
              "displayName",
              "display_name",
              "fullName",
              "full_name",
              "email",
            ])
          : undefined) ||
        "Verified buyer",
      rating:
        getFirstNumber(record, [
          "rating",
          "stars",
          "score",
          "ratingValue",
          "rating_value",
        ]) ?? 0,
      review:
        getFirstString(record, [
          "review",
          "message",
          "comment",
          "body",
          "text",
          "content",
        ]) || "",
      media,
      replies,
      createdAt,
      updatedAt: getFirstString(record, [
        "updatedAt",
        "updated_at",
        "editedAt",
        "edited_at",
      ]),
    };
    const duplicateIndex = seen.get(id);

    if (duplicateIndex === undefined) {
      seen.set(id, normalized.length);
      normalized.push(review);
      return;
    }

    const previous = normalized[duplicateIndex];
    const mediaByKey = new Map(
      [...(previous.media ?? []), ...(review.media ?? [])].map((item) => [
        item.key,
        item,
      ]),
    );
    const repliesById = new Map(
      [...(previous.replies ?? []), ...(review.replies ?? [])].map((reply) => [
        reply.id,
        reply,
      ]),
    );

    normalized[duplicateIndex] = {
      ...previous,
      ...review,
      review: review.review || previous.review,
      buyerName:
        review.buyerName !== "Verified buyer"
          ? review.buyerName
          : previous.buyerName,
      vendorId: review.vendorId || previous.vendorId,
      productId: review.productId || previous.productId,
      media: Array.from(mediaByKey.values()),
      replies: Array.from(repliesById.values()),
    };
  });

  return normalized;
}

export function normalizeWishlistItemView(
  value: unknown,
): WishlistItemView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const product = normalizeProductView(
    getRecordProperty(record, ["product", "item"]),
  );
  const productId =
    getFirstString(record, ["productId", "product_id", "itemId", "item_id"]) ||
    product?.id;

  if (!productId) return null;

  return {
    ...record,
    id:
      getFirstString(record, [
        "id",
        "_id",
        "$id",
        "wishlistId",
        "wishlist_id",
      ]) || productId,
    productId,
    userId: getFirstString(record, [
      "userId",
      "user_id",
      "customerId",
      "customer_id",
    ]),
    product,
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

export function normalizeCartItemView(value: unknown): CartItemView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const product = normalizeProductView(
    getRecordProperty(record, ["product", "item"]),
  );
  const productId =
    getFirstString(record, ["productId", "product_id", "itemId", "item_id"]) ||
    product?.id;

  if (!productId) return null;

  const quantity = Math.max(
    1,
    Math.floor(getFirstNumber(record, ["quantity", "qty", "count"]) ?? 1),
  );

  return {
    ...record,
    id:
      getFirstString(record, [
        "id",
        "_id",
        "$id",
        "cartItemId",
        "cart_item_id",
      ]) || productId,
    productId,
    userId: getFirstString(record, [
      "userId",
      "user_id",
      "customerId",
      "customer_id",
    ]),
    quantity,
    product,
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

function normalizeOrderTrackingPoint(
  value: unknown,
): OrderTrackingPointView | null {
  const record = readRecord(value);
  if (!record) return null;

  const latitude = getFirstNumber(record, ["latitude", "lat"]);
  const longitude = getFirstNumber(record, ["longitude", "lng", "lon"]);
  if (latitude === undefined || longitude === undefined) return null;

  return {
    ...record,
    latitude,
    longitude,
    label: getFirstString(record, ["label", "address", "name"]),
    status: getFirstString(record, ["status", "state"]),
    source: getFirstString(record, ["source"]),
    accuracyMeters: getFirstNumber(record, [
      "accuracyMeters",
      "accuracy_meters",
      "accuracy",
    ]),
    heading: getFirstNumber(record, ["heading"]),
    speedMps: getFirstNumber(record, ["speedMps", "speed_mps", "speed"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

function normalizeOrderTrackingLocation(
  value: unknown,
): OrderTrackingLocationView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const rawTrack = getRecordProperty(record, [
    "track",
    "history",
    "trackingHistory",
    "tracking_history",
  ]);
  const track = Array.isArray(rawTrack)
    ? rawTrack
        .map((point) => normalizeOrderTrackingPoint(point))
        .filter((point): point is OrderTrackingPointView => Boolean(point))
    : [];
  const latitude = getFirstNumber(record, ["latitude", "lat"]);
  const longitude = getFirstNumber(record, ["longitude", "lng", "lon"]);

  if (latitude === undefined || longitude === undefined) {
    return {
      ...record,
      latitude: 0,
      longitude: 0,
      track,
      isLive:
        getBoolean(getRecordProperty(record, ["isLive", "is_live", "live"])) ??
        false,
    };
  }

  return {
    ...record,
    orderId: getFirstString(record, ["orderId", "order_id"]),
    latitude,
    longitude,
    label: getFirstString(record, ["label", "address", "name"]),
    status: getFirstString(record, ["status", "state"]),
    source: getFirstString(record, ["source"]),
    accuracyMeters: getFirstNumber(record, [
      "accuracyMeters",
      "accuracy_meters",
      "accuracy",
    ]),
    heading: getFirstNumber(record, ["heading"]),
    speedMps: getFirstNumber(record, ["speedMps", "speed_mps", "speed"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
    expiresAt: getFirstString(record, ["expiresAt", "expires_at"]),
    isLive:
      getBoolean(getRecordProperty(record, ["isLive", "is_live", "live"])) ??
      false,
    track,
  };
}

function normalizeDeliveryStatus(
  value: unknown,
): OrderDeliveryStatus | undefined {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s-]+/g, "_");

  switch (normalized) {
    case "order_placed":
    case "placed":
    case "pending":
      return "order_placed";
    case "seller_processing":
    case "processing":
    case "packed":
    case "preparing":
      return "seller_processing";
    case "ready_for_pickup":
    case "pickup_ready":
    case "ready":
      return "ready_for_pickup";
    case "picked_up_by_courier":
    case "picked_up":
    case "courier_picked_up":
      return "picked_up_by_courier";
    case "in_transit":
    case "transit":
    case "shipped":
    case "redeliver":
    case "redelivery":
    case "retry_delivery":
    case "retry_redelivery":
      return "in_transit";
    case "out_for_delivery":
    case "outfordelivery":
      return "out_for_delivery";
    case "delivery_attempted":
    case "attempted":
    case "delivery_failed":
    case "failed_attempt":
    case "failed":
      return "delivery_attempted";
    case "delivered":
      return "delivered";
    case "received":
    case "completed":
      return "received";
    case "return_requested":
    case "return":
      return "return_requested";
    case "return_in_transit":
    case "return_to_seller":
    case "returning_to_seller":
      return "return_in_transit";
    case "returned":
      return "returned";
    case "refund_requested":
    case "refund":
      return "refund_requested";
    case "refunded":
      return "refunded";
    case "cancelled":
    case "canceled":
      return "cancelled";
    default:
      return undefined;
  }
}

function normalizeStringArray(value: unknown): string[] {
  if (typeof value === "string") {
    return value
      .split(/[\n,]+/g)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => getString(entry))
    .filter((entry): entry is string => Boolean(entry));
}

function normalizeOrderCourier(value: unknown): OrderCourierView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  return {
    ...record,
    courierId: getFirstString(record, [
      "courierId",
      "courier_id",
      "id",
      "userId",
      "user_id",
    ]),
    name: getFirstString(record, [
      "name",
      "courierName",
      "courier_name",
      "provider",
    ]),
    email: getFirstString(record, ["email", "courierEmail", "courier_email"]),
    trackingNumber: getFirstString(record, [
      "trackingNumber",
      "tracking_number",
      "waybill",
      "awb",
    ]),
    contactPhone: getFirstString(record, [
      "contactPhone",
      "contact_phone",
      "phone",
    ]),
    pickupLocation: getFirstString(record, [
      "pickupLocation",
      "pickup_location",
      "pickupAddress",
    ]),
    dropoffLocation: getFirstString(record, [
      "dropoffLocation",
      "dropoff_location",
      "dropoffAddress",
    ]),
    estimatedDeliveryAt: getFirstString(record, [
      "estimatedDeliveryAt",
      "estimated_delivery_at",
      "eta",
    ]),
    assignedAt: getFirstString(record, ["assignedAt", "assigned_at"]),
  };
}

function normalizeOrderCourierReassignment(
  value: unknown,
  index = 0,
): OrderCourierReassignmentView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const reason = getFirstString(record, ["reason", "reassignmentReason"]);
  const requestedAt = getFirstString(record, [
    "requestedAt",
    "requested_at",
    "createdAt",
    "created_at",
  ]);
  if (!reason || !requestedAt) return null;

  return {
    ...record,
    id:
      getFirstString(record, ["id", "_id", "$id", "reassignmentId"]) ??
      `courier-reassignment-${index}`,
    status: getFirstString(record, ["status", "reassignmentStatus"]) ?? "open",
    requestedByCourierId: getFirstString(record, [
      "requestedByCourierId",
      "requested_by_courier_id",
    ]),
    requestedByCourierName: getFirstString(record, [
      "requestedByCourierName",
      "requested_by_courier_name",
    ]),
    previousCourier: normalizeOrderCourier(
      getRecordProperty(record, ["previousCourier", "previous_courier"]),
    ),
    reason,
    handoffLocation: getFirstString(record, [
      "handoffLocation",
      "handoff_location",
    ]),
    notes: getFirstString(record, ["notes"]),
    requestedAt,
    acceptedByCourierId: getFirstString(record, [
      "acceptedByCourierId",
      "accepted_by_courier_id",
    ]),
    acceptedByCourierName: getFirstString(record, [
      "acceptedByCourierName",
      "accepted_by_courier_name",
    ]),
    acceptedAt: getFirstString(record, ["acceptedAt", "accepted_at"]),
    cancelledAt: getFirstString(record, ["cancelledAt", "cancelled_at"]),
  };
}

function normalizeOrderCourierReassignments(
  value: unknown,
): OrderCourierReassignmentView[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => normalizeOrderCourierReassignment(entry, index))
    .filter((entry): entry is OrderCourierReassignmentView => Boolean(entry));
}

function normalizeOrderDeliveryEvent(
  value: unknown,
  index = 0,
): OrderDeliveryEventView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const status =
    normalizeDeliveryStatus(
      getRecordProperty(record, [
        "status",
        "deliveryStatus",
        "delivery_status",
      ]),
    ) ?? "order_placed";
  const createdAt =
    getFirstString(record, [
      "createdAt",
      "created_at",
      "timestamp",
      "updatedAt",
      "updated_at",
    ]) ?? new Date().toISOString();

  return {
    ...record,
    id:
      getFirstString(record, ["id", "_id", "$id", "eventId", "event_id"]) ??
      `delivery-event-${index}`,
    status,
    label:
      getFirstString(record, ["label", "title", "message"]) ??
      titleCase(status),
    locationLabel: getFirstString(record, [
      "locationLabel",
      "location_label",
      "location",
      "address",
    ]),
    notes: getFirstString(record, ["notes", "note", "description"]),
    actorRole: getFirstString(record, [
      "actorRole",
      "actor_role",
      "role",
      "source",
    ]),
    courierName: getFirstString(record, [
      "courierName",
      "courier_name",
      "courier",
    ]),
    proofUrls: normalizeStringArray(
      getRecordProperty(record, [
        "proofUrls",
        "proof_urls",
        "proofs",
        "images",
      ]),
    ),
    paymentProofUrls: normalizeStringArray(
      getRecordProperty(record, [
        "paymentProofUrls",
        "payment_proof_urls",
        "paymentProofs",
      ]),
    ),
    createdAt,
  };
}

function normalizeOrderDeliveryEvents(
  value: unknown,
): OrderDeliveryEventView[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index) => normalizeOrderDeliveryEvent(entry, index))
    .filter((entry): entry is OrderDeliveryEventView => Boolean(entry));
}

function normalizeConversationProductAttachment(
  value: unknown,
  index = 0,
): ConversationProductAttachmentView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const id = getFirstString(record, [
    "id",
    "_id",
    "$id",
    "productId",
    "product_id",
  ]);
  if (!id) return null;

  return {
    id,
    name:
      getFirstString(record, [
        "name",
        "title",
        "productName",
        "product_name",
      ]) ?? `Product ${index + 1}`,
    imageUrl: getFirstString(record, [
      "imageUrl",
      "image_url",
      "image",
      "thumbnailUrl",
      "thumbnail_url",
    ]),
    price: getFirstNumber(record, ["price", "salePrice", "sale_price"]),
    vendorId: getFirstString(record, ["vendorId", "vendor_id"]),
  };
}

function normalizeConversationProductAttachments(
  value: unknown,
): ConversationProductAttachmentView[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index) => normalizeConversationProductAttachment(entry, index))
    .filter((entry): entry is ConversationProductAttachmentView =>
      Boolean(entry),
    );
}

function normalizeConversationMessageReply(
  value: unknown,
  fallbackRecord?: Record<string, unknown>,
): ConversationMessageReplyView | undefined {
  const replyRecord = readRecord(unwrapEnvelope(value));
  const messageId = replyRecord
    ? getFirstString(replyRecord, ["messageId", "message_id", "id", "_id"])
    : fallbackRecord
      ? getFirstString(fallbackRecord, [
          "replyToMessageId",
          "reply_to_message_id",
        ])
      : undefined;
  const message = replyRecord
    ? getFirstString(replyRecord, ["message", "body", "text"])
    : fallbackRecord
      ? getFirstString(fallbackRecord, ["replyToMessage", "reply_to_message"])
      : undefined;

  if (!messageId || !message) return undefined;

  return {
    messageId,
    senderName: replyRecord
      ? getFirstString(replyRecord, ["senderName", "sender_name"])
      : fallbackRecord
        ? getFirstString(fallbackRecord, [
            "replyToSenderName",
            "reply_to_sender_name",
          ])
        : undefined,
    message,
  };
}

const ORDER_MESSAGE_METADATA_PREFIX = "[[SPARELYNK_ORDER_MESSAGE_V1:";
const ORDER_MESSAGE_METADATA_SUFFIX = "]]\n";

type OrderMessageTransportMetadata = {
  attachment?: 0 | 1;
  contextType?: string;
  contextLabel?: string;
  replyTo?: ConversationMessageReplyView;
};

function normalizeOrderMessageTransportReply(
  value: unknown,
): ConversationMessageReplyView | undefined {
  const record = readRecord(value);
  if (!record) return undefined;

  const messageId = getFirstString(record, ["messageId", "id"]);
  const message = getFirstString(record, ["message", "text"]);
  if (!messageId || !message) return undefined;

  return {
    messageId,
    senderName: getFirstString(record, ["senderName", "name"]),
    message,
  };
}

function decodeOrderMessageTransportContent(value: string): {
  message: string;
  metadata?: OrderMessageTransportMetadata;
} {
  if (!value.startsWith(ORDER_MESSAGE_METADATA_PREFIX)) {
    return { message: value };
  }

  const suffixIndex = value.indexOf(
    ORDER_MESSAGE_METADATA_SUFFIX,
    ORDER_MESSAGE_METADATA_PREFIX.length,
  );
  if (suffixIndex < 0) return { message: value };

  const encodedMetadata = value.slice(
    ORDER_MESSAGE_METADATA_PREFIX.length,
    suffixIndex,
  );

  try {
    const parsed = JSON.parse(decodeURIComponent(encodedMetadata)) as Record<
      string,
      unknown
    >;
    const attachmentValue = parsed.a;
    const contextType =
      typeof parsed.t === "string" ? parsed.t.trim() : undefined;
    const contextLabel =
      typeof parsed.l === "string" ? parsed.l.trim() : undefined;
    const replyTo = normalizeOrderMessageTransportReply(parsed.r);

    return {
      message: value.slice(suffixIndex + ORDER_MESSAGE_METADATA_SUFFIX.length),
      metadata: {
        ...(attachmentValue === 0 || attachmentValue === 1
          ? { attachment: attachmentValue }
          : {}),
        ...(contextType ? { contextType } : {}),
        ...(contextLabel ? { contextLabel } : {}),
        ...(replyTo ? { replyTo } : {}),
      },
    };
  } catch {
    return { message: value };
  }
}

function normalizeConversationMessageContext(
  value: unknown,
  fallbackRecord?: Record<string, unknown>,
): ConversationMessageContextView | undefined {
  const contextRecord = readRecord(unwrapEnvelope(value)) ?? fallbackRecord;
  if (!contextRecord) return undefined;

  const type = getFirstString(contextRecord, [
    "type",
    "contextType",
    "context_type",
    "attachmentType",
    "attachment_type",
  ]);
  const productId = getFirstString(contextRecord, ["productId", "product_id"]);
  const orderId = getFirstString(contextRecord, ["orderId", "order_id"]);
  const orderIds = Array.from(
    new Set([
      ...normalizeStringArray(
        getRecordProperty(contextRecord, [
          "orderIds",
          "order_ids",
          "attachedOrderIds",
          "attached_order_ids",
        ]),
      ),
      ...(orderId ? [orderId] : []),
    ]),
  );
  const id =
    getFirstString(contextRecord, [
      "id",
      "contextId",
      "context_id",
      "referenceId",
      "reference_id",
    ]) ??
    (type === "order" || type === "orders" ? orderId : productId) ??
    orderId ??
    productId;
  const products = normalizeConversationProductAttachments(
    getRecordProperty(contextRecord, [
      "products",
      "attachedProducts",
      "attached_products",
      "productAttachments",
      "product_attachments",
    ]),
  );
  const images = Array.from(
    new Set([
      ...normalizeStringArray(
        getRecordProperty(contextRecord, [
          "images",
          "contextImages",
          "context_images",
          "imageUrls",
          "image_urls",
          "productImages",
          "product_images",
          "image",
          "imageUrl",
          "image_url",
        ]),
      ),
      ...products
        .map((product) => product.imageUrl?.trim() ?? "")
        .filter(Boolean),
    ]),
  );
  const label =
    getFirstString(contextRecord, [
      "label",
      "contextLabel",
      "context_label",
      "title",
      "productName",
      "product_name",
      "shopName",
      "shop_name",
      "sellerName",
      "seller_name",
      "orderNumber",
      "order_number",
    ]) ??
    ((type === "order" || type === "orders") && id
      ? `${type === "orders" ? "Orders" : "Order"} ${id}`
      : type === "shop"
        ? "Shop inquiry"
        : "Product inquiry");

  if (!type && !id && images.length === 0 && products.length === 0) {
    return undefined;
  }

  const resolvedProductId =
    productId ?? (products.length === 1 ? products[0]?.id : undefined);
  const resolvedType =
    type ?? (orderId ? "order" : products.length > 1 ? "products" : "product");

  if (
    (resolvedType === "product" || resolvedType === "products") &&
    products.length === 0 &&
    images.length === 0
  ) {
    return undefined;
  }

  return {
    type: resolvedType,
    id: id ?? orderId ?? resolvedProductId ?? products[0]?.id ?? "context",
    label:
      products.length > 1 && label === "Product inquiry"
        ? `${products.length} products attached`
        : label,
    images,
    productId: resolvedProductId,
    orderId: orderIds[0],
    ...(orderIds.length > 0 ? { orderIds } : {}),
    ...(products.length > 0 ? { products } : {}),
  };
}

function normalizeConversationMediaAttachment(
  value: unknown,
  index = 0,
): ConversationMediaAttachmentView | null {
  const record = readRecord(value);
  if (!record) return null;

  const url = getFirstString(record, ["url", "fileUrl", "file_url", "src"]);
  if (!url) return null;

  const contentType =
    getFirstString(record, ["contentType", "content_type", "mimeType", "mime_type", "type"]) ??
    "application/octet-stream";
  const explicitMediaType = getFirstString(record, ["mediaType", "media_type", "kind"]);
  const mediaType =
    explicitMediaType ??
    (contentType.startsWith("image/")
      ? "image"
      : contentType.startsWith("video/")
        ? "video"
        : contentType.startsWith("audio/")
          ? "audio"
          : "document");

  return {
    id: getFirstString(record, ["id", "_id", "$id"]) ?? `attachment-${index}`,
    url,
    key: getFirstString(record, ["key"]) || undefined,
    fileName:
      getFirstString(record, ["fileName", "file_name", "name", "filename"]) ??
      `attachment-${index + 1}`,
    contentType,
    mediaType,
    size: Math.max(
      0,
      Math.floor(
        getNumber(getRecordProperty(record, ["size", "fileSize", "file_size"])) ?? 0,
      ),
    ),
  };
}

function normalizeConversationMediaAttachments(value: unknown): ConversationMediaAttachmentView[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => normalizeConversationMediaAttachment(item, index))
    .filter((item): item is ConversationMediaAttachmentView => Boolean(item));
}

function normalizeOrderMessage(
  value: unknown,
  index = 0,
): OrderMessageView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const rawMessage = getFirstString(record, ["message", "body", "text"]) ?? "";
  const attachments = normalizeConversationMediaAttachments(
    getRecordProperty(record, ["attachments", "media", "files"]),
  );
  if (!rawMessage && attachments.length === 0) return null;

  const transportContent = decodeOrderMessageTransportContent(rawMessage);
  const persistedContext = normalizeConversationMessageContext(
    getRecordProperty(record, [
      "context",
      "messageContext",
      "message_context",
      "attachment",
    ]),
    record,
  );
  const context =
    transportContent.metadata?.attachment === 0
      ? undefined
      : transportContent.metadata?.contextType ||
          transportContent.metadata?.contextLabel
        ? {
            ...(persistedContext ?? {
              type: transportContent.metadata.contextType || "order",
              id:
                getFirstString(record, ["orderId", "order_id"]) ||
                "order-context",
              label: transportContent.metadata.contextLabel || "Attached order",
              images: [],
            }),
            ...(transportContent.metadata.contextType
              ? { type: transportContent.metadata.contextType }
              : {}),
            ...(transportContent.metadata.contextLabel
              ? { label: transportContent.metadata.contextLabel }
              : {}),
          }
        : persistedContext;

  return {
    id:
      getFirstString(record, ["id", "_id", "$id", "messageId", "message_id"]) ??
      `order-message-${index}`,
    vendorId: getFirstString(record, ["vendorId", "vendor_id"]),
    senderId: getFirstString(record, [
      "senderId",
      "sender_id",
      "userId",
      "user_id",
    ]),
    senderRole:
      getFirstString(record, ["senderRole", "sender_role", "role"]) ?? "seller",
    senderName:
      getFirstString(record, ["senderName", "sender_name", "name"]) ?? "User",
    senderAvatarUrl: getFirstString(record, [
      "senderAvatarUrl",
      "sender_avatar_url",
      "avatarUrl",
      "avatar_url",
      "avatar",
      "imageUrl",
      "image_url",
    ]),
    message: transportContent.message,
    channel: getFirstString(record, ["channel"]) === "courier_buyer" ? "courier_buyer" : "courier_seller",
    createdAt:
      getFirstString(record, ["createdAt", "created_at", "timestamp"]) ??
      new Date().toISOString(),
    context,
    replyTo:
      transportContent.metadata?.replyTo ??
      normalizeConversationMessageReply(
        getRecordProperty(record, [
          "replyTo",
          "reply_to",
          "repliedMessage",
          "replied_message",
        ]),
        record,
      ),
    ...(attachments.length > 0 ? { attachments } : {}),
  };
}

function normalizeOrderMessages(value: unknown): OrderMessageView[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => normalizeOrderMessage(entry, index))
    .filter((entry): entry is OrderMessageView => Boolean(entry));
}

function normalizeShopConversation(
  value: unknown,
): ShopConversationView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;
  const buyerId = getFirstString(record, ["buyerId", "buyer_id"]);
  const vendorId = getFirstString(record, [
    "vendorId",
    "vendor_id",
    "sellerId",
    "seller_id",
  ]);
  if (!buyerId || !vendorId) return null;
  return {
    id: getFirstString(record, ["id", "_id", "$id"]),
    buyerId,
    vendorId,
    buyerName: getFirstString(record, ["buyerName", "buyer_name"]) || "Buyer",
    sellerName:
      getFirstString(record, [
        "sellerName",
        "seller_name",
        "shopName",
        "shop_name",
      ]) || "Seller shop",
    buyerAvatarUrl: getFirstString(record, [
      "buyerAvatarUrl",
      "buyer_avatar_url",
      "buyerAvatar",
      "buyer_avatar",
    ]),
    sellerAvatarUrl: getFirstString(record, [
      "sellerAvatarUrl",
      "seller_avatar_url",
      "sellerAvatar",
      "seller_avatar",
      "shopAvatarUrl",
      "shop_avatar_url",
    ]),
    messages: normalizeOrderMessages(getRecordProperty(record, ["messages"])),
    unseenCount: Math.max(
      0,
      Math.floor(
        getNumber(getRecordProperty(record, ["unseenCount", "unseen_count"])) ??
          0,
      ),
    ),
    buyerLastReadAt: getFirstString(record, [
      "buyerLastReadAt",
      "buyer_last_read_at",
    ]),
    sellerLastReadAt: getFirstString(record, [
      "sellerLastReadAt",
      "seller_last_read_at",
    ]),
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

type ShopConversationCacheRole = "buyer" | "seller";

const shopConversationListCache = new Map<string, ShopConversationView[]>();

function readCachedShopConversationList(
  role: ShopConversationCacheRole,
  participantId: string,
): ShopConversationView[] {
  const normalizedParticipantId = participantId.trim();
  if (!normalizedParticipantId) return [];

  return [
    ...(shopConversationListCache.get(
      getShopConversationCacheKey(role, normalizedParticipantId),
    ) ?? []),
  ];
}

function getShopConversationCacheKey(
  role: ShopConversationCacheRole,
  participantId: string,
): string {
  return `${role}:${participantId.trim()}`;
}

function getShopConversationIdentity(
  conversation: Pick<ShopConversationView, "buyerId" | "vendorId">,
): string {
  return `${conversation.buyerId.trim()}:${conversation.vendorId.trim()}`;
}

function getShopConversationTimestamp(
  conversation: ShopConversationView,
): number {
  const value =
    conversation.updatedAt ||
    getLastItem(conversation.messages)?.createdAt ||
    conversation.createdAt;
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function getShopConversationMessageIdentity(message: OrderMessageView): string {
  const id = String(message.id ?? "").trim();
  if (id && !/^order-message-\d+$/.test(id)) return `id:${id}`;

  return [
    message.senderId ?? "",
    message.senderRole,
    message.createdAt,
    message.message,
  ].join(":");
}

function mergeCachedShopConversation(
  previous: ShopConversationView,
  incoming: ShopConversationView,
): ShopConversationView {
  const incomingIsNewer =
    getShopConversationTimestamp(incoming) >=
    getShopConversationTimestamp(previous);
  const newer = incomingIsNewer ? incoming : previous;
  const older = incomingIsNewer ? previous : incoming;
  const messages = new Map<string, OrderMessageView>();

  for (const message of [...older.messages, ...newer.messages]) {
    const key = getShopConversationMessageIdentity(message);
    const existingMessage = messages.get(key);
    messages.set(
      key,
      existingMessage ? { ...existingMessage, ...message } : message,
    );
  }

  return {
    ...older,
    ...newer,
    id: newer.id || older.id,
    buyerName: newer.buyerName || older.buyerName || "Buyer",
    sellerName: newer.sellerName || older.sellerName || "Seller shop",
    buyerAvatarUrl: newer.buyerAvatarUrl || older.buyerAvatarUrl,
    sellerAvatarUrl: newer.sellerAvatarUrl || older.sellerAvatarUrl,
    messages: Array.from(messages.values()).sort((left, right) => {
      const leftTime = Date.parse(left.createdAt);
      const rightTime = Date.parse(right.createdAt);
      if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) return 0;
      return leftTime - rightTime;
    }),
    unseenCount: Math.max(0, newer.unseenCount ?? older.unseenCount ?? 0),
    buyerLastReadAt: newer.buyerLastReadAt || older.buyerLastReadAt,
    sellerLastReadAt: newer.sellerLastReadAt || older.sellerLastReadAt,
    createdAt: older.createdAt || newer.createdAt,
    updatedAt: newer.updatedAt || older.updatedAt,
  };
}

function cacheShopConversationList(
  role: ShopConversationCacheRole,
  participantId: string,
  incoming: ShopConversationView[],
): ShopConversationView[] {
  const normalizedParticipantId = participantId.trim();
  if (!normalizedParticipantId) return incoming;

  const cacheKey = getShopConversationCacheKey(role, normalizedParticipantId);
  const conversations = new Map<string, ShopConversationView>();

  for (const conversation of shopConversationListCache.get(cacheKey) ?? []) {
    conversations.set(getShopConversationIdentity(conversation), conversation);
  }

  for (const conversation of incoming) {
    const belongsToParticipant =
      role === "buyer"
        ? conversation.buyerId === normalizedParticipantId
        : conversation.vendorId === normalizedParticipantId;
    if (!belongsToParticipant) continue;

    const key = getShopConversationIdentity(conversation);
    const previous = conversations.get(key);
    conversations.set(
      key,
      previous
        ? mergeCachedShopConversation(previous, conversation)
        : conversation,
    );
  }

  const merged = Array.from(conversations.values()).sort(
    (left, right) =>
      getShopConversationTimestamp(right) - getShopConversationTimestamp(left),
  );
  shopConversationListCache.set(cacheKey, merged);
  return merged;
}

function cacheShopConversationMessageResult(
  payload: SendShopConversationMessagePayload,
  result: ShopConversationMessageResult,
): void {
  const role: ShopConversationCacheRole =
    payload.senderRole === "seller" ? "seller" : "buyer";
  const participantId = role === "buyer" ? result.buyerId : result.vendorId;

  cacheShopConversationList(role, participantId, [
    {
      id: result.id,
      buyerId: result.buyerId,
      vendorId: result.vendorId,
      buyerName: result.buyerName,
      sellerName: result.sellerName,
      buyerAvatarUrl: result.buyerAvatarUrl,
      sellerAvatarUrl: result.sellerAvatarUrl,
      messages: [result.message],
      unseenCount: result.unseenCount,
      createdAt: result.createdAt,
      updatedAt: result.updatedAt || result.message.createdAt,
    },
  ]);
}

function markCachedShopConversationRead(
  payload: MarkShopConversationReadPayload,
): void {
  const cacheKey = getShopConversationCacheKey(
    payload.readerRole,
    payload.readerId,
  );
  const cached = shopConversationListCache.get(cacheKey);
  if (!cached) return;

  const readAt = new Date().toISOString();
  shopConversationListCache.set(
    cacheKey,
    cached.map((conversation) =>
      conversation.buyerId === payload.buyerId &&
      conversation.vendorId === payload.vendorId
        ? {
            ...conversation,
            unseenCount: 0,
            ...(payload.readerRole === "buyer"
              ? { buyerLastReadAt: readAt }
              : { sellerLastReadAt: readAt }),
          }
        : conversation,
    ),
  );
}

function normalizeShopConversationMessageResult(
  value: unknown,
): ShopConversationMessageResult | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const directMessage = normalizeOrderMessage(
    getRecordProperty(record, ["message", "latestMessage", "latest_message"]),
  );
  const buyerId = getFirstString(record, ["buyerId", "buyer_id"]);
  const vendorId = getFirstString(record, [
    "vendorId",
    "vendor_id",
    "sellerId",
    "seller_id",
  ]);

  if (directMessage && buyerId && vendorId) {
    return {
      id: getFirstString(record, ["id", "_id", "$id"]),
      buyerId,
      vendorId,
      buyerName: getFirstString(record, ["buyerName", "buyer_name"]) || "Buyer",
      sellerName:
        getFirstString(record, [
          "sellerName",
          "seller_name",
          "shopName",
          "shop_name",
        ]) || "Seller shop",
      buyerAvatarUrl: getFirstString(record, [
        "buyerAvatarUrl",
        "buyer_avatar_url",
        "buyerAvatar",
        "buyer_avatar",
      ]),
      sellerAvatarUrl: getFirstString(record, [
        "sellerAvatarUrl",
        "seller_avatar_url",
        "sellerAvatar",
        "seller_avatar",
        "shopAvatarUrl",
        "shop_avatar_url",
      ]),
      message: directMessage,
      unseenCount: Math.max(
        0,
        Math.floor(
          getNumber(
            getRecordProperty(record, ["unseenCount", "unseen_count"]),
          ) ?? 0,
        ),
      ),
      createdAt: getFirstString(record, ["createdAt", "created_at"]),
      updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
    };
  }

  const conversation = normalizeShopConversation(value);
  const latestMessage = getLastItem(conversation?.messages);
  if (!conversation || !latestMessage) return null;

  return {
    id: conversation.id,
    buyerId: conversation.buyerId,
    vendorId: conversation.vendorId,
    buyerName: conversation.buyerName,
    sellerName: conversation.sellerName,
    buyerAvatarUrl: conversation.buyerAvatarUrl,
    sellerAvatarUrl: conversation.sellerAvatarUrl,
    message: latestMessage,
    unseenCount: conversation.unseenCount,
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt,
  };
}

function normalizeTrackingConfigView(value: unknown): TrackingConfigView {
  const record = readRecord(unwrapEnvelope(value)) ?? {};

  return {
    ...record,
    googleMapsPlatformApiKey:
      getFirstString(record, [
        "googleMapsPlatformApiKey",
        "google_maps_platform_api_key",
        "apiKey",
        "key",
      ]) ?? "",
    googleMapsMapId:
      getFirstString(record, [
        "googleMapsMapId",
        "google_maps_map_id",
        "mapId",
        "map_id",
      ]) ?? "",
    trackingLocationTtlSeconds:
      getFirstNumber(record, [
        "trackingLocationTtlSeconds",
        "tracking_location_ttl_seconds",
        "locationTTLSeconds",
      ]) ?? 900,
    trackingBroadcastIntervalMs:
      getFirstNumber(record, [
        "trackingBroadcastIntervalMs",
        "tracking_broadcast_interval_ms",
        "broadcastIntervalMS",
      ]) ?? 5000,
  };
}

function normalizeReverseGeocodeLocationView(
  value: unknown,
): ReverseGeocodeLocationView {
  const record = readRecord(unwrapEnvelope(value)) ?? {};

  return {
    ...record,
    latitude: getFirstNumber(record, ["latitude", "lat"]) ?? Number.NaN,
    longitude:
      getFirstNumber(record, ["longitude", "lng", "lon"]) ?? Number.NaN,
    label:
      getFirstString(record, [
        "label",
        "locationLabel",
        "location_label",
        "formattedAddress",
        "formatted_address",
      ]) ?? "Marked location",
    addressLine1: getFirstString(record, [
      "addressLine1",
      "address_line_1",
      "address",
      "street",
    ]),
    city: getFirstString(record, ["city", "locality"]),
    state: getFirstString(record, ["state", "province"]),
    postalCode: getFirstString(record, [
      "postalCode",
      "postal_code",
      "zipCode",
      "zip_code",
    ]),
    country: getFirstString(record, ["country"]),
  };
}

function normalizeReturnRefundPlaybackSources(
  value: unknown,
): ReturnRefundPlaybackSourceView[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const record = readRecord(entry);
    if (!record) return [];
    const url = getFirstString(record, ["url", "src"]);
    if (!url) return [];
    return [
      {
        url,
        type: getFirstString(record, ["type", "contentType", "content_type"]),
        label: getFirstString(record, ["label", "name"]),
        width: getFirstNumber(record, ["width"]),
        bitrate: getFirstNumber(record, ["bitrate", "bandwidth"]),
      },
    ];
  });
}

function normalizeReturnRefundRequest(
  value: unknown,
): ReturnRefundRequestView | null {
  const record = readRecord(value);
  if (!record) return null;
  const id = getFirstString(record, ["id", "_id"]);
  const action = getFirstString(record, ["action"]);
  const status = getFirstString(record, ["status"]);
  if (!id || !action || !status) return null;
  const rawEvidence = getRecordProperty(record, ["evidence", "media"]);
  const evidence = Array.isArray(rawEvidence)
    ? rawEvidence.flatMap((entry) => {
        const item = readRecord(entry);
        if (!item) return [];
        const url = getFirstString(item, ["url", "publicUrl", "public_url"]);
        const key = getFirstString(item, ["key", "objectKey", "object_key"]);
        if (!url || !key) return [];
        const contentType =
          getFirstString(item, ["contentType", "content_type"]) ||
          "application/octet-stream";
        return [
          {
            url,
            key,
            mediaType: (getFirstString(item, ["mediaType", "media_type"]) ===
            "image"
              ? "image"
              : "video") as "image" | "video",
            contentType,
            fileName:
              getFirstString(item, ["fileName", "file_name"]) ||
              getLastItem(key.split("/")) ||
              "evidence",
            size: getFirstNumber(item, ["size", "fileSize", "file_size"]) ?? 0,
            sources: normalizeReturnRefundPlaybackSources(
              getRecordProperty(item, [
                "sources",
                "playbackSources",
                "playback_sources",
              ]),
            ),
          },
        ];
      })
    : [];
  return {
    id,
    action: (action === "refund" ? "refund" : "return") as "return" | "refund",
    status,
    buyerNotes: getFirstString(record, ["buyerNotes", "buyer_notes", "notes"]),
    evidence,
    refundAmount:
      getFirstNumber(record, ["refundAmount", "refund_amount"]) ?? 0,
    submittedAt: getFirstString(record, ["submittedAt", "submitted_at"]) || "",
    reviewedAt: getFirstString(record, ["reviewedAt", "reviewed_at"]),
    reviewedBy: getFirstString(record, ["reviewedBy", "reviewed_by"]),
    adminNotes: getFirstString(record, ["adminNotes", "admin_notes"]),
  };
}

function normalizeOrderItemView(value: unknown): OrderItemView | null {
  const record = readRecord(value);
  if (!record) return null;

  const productId = getFirstString(record, [
    "productId",
    "product_id",
    "id",
  ]);
  if (!productId) return null;

  const quantity = Math.max(
    1,
    Math.floor(getFirstNumber(record, ["quantity", "qty"]) ?? 1),
  );
  const unitPrice = Math.max(
    0,
    getFirstNumber(record, ["unitPrice", "unit_price", "price"]) ?? 0,
  );
  const extraProtectionFee = Math.max(
    0,
    getFirstNumber(record, [
      "extraProtectionFee",
      "extra_protection_fee",
    ]) ?? 0,
  );
  const extraProtectionSelected =
    getBoolean(
      getRecordProperty(record, [
        "extraProtectionSelected",
        "extra_protection_selected",
      ]),
    ) ?? false;

  return {
    ...record,
    productId,
    productName:
      getFirstString(record, [
        "productName",
        "product_name",
        "title",
        "name",
      ]) ?? "Marketplace product",
    productImage: selectPrimaryProductImage(record),
    vendorId: getFirstString(record, [
      "vendorId",
      "vendor_id",
      "sellerId",
      "seller_id",
    ]),
    productNumber: getFirstString(record, [
      "productNumber",
      "product_number",
      "sparelynkProductNumber",
    ]),
    partNumber: getFirstString(record, [
      "partNumber",
      "part_number",
      "manufacturerPartNumber",
      "mpn",
    ]),
    sku: getFirstString(record, ["sku", "SKU"]),
    category: getFirstString(record, ["category", "categoryName", "category_name"]),
    referralFeePercent: getFirstNumber(record, [
      "referralFeePercent",
      "referral_fee_percent",
    ]),
    referralFeeAmount: getFirstNumber(record, [
      "referralFeeAmount",
      "referral_fee_amount",
    ]),
    fulfillmentBySpareLynk: getBoolean(
      getRecordProperty(record, ["fulfillmentBySpareLynk", "fulfillment_by_sparelynk"]),
    ),
    fulfillmentFeePerUnit: getFirstNumber(record, [
      "fulfillmentFeePerUnit",
      "fulfillment_fee_per_unit",
    ]),
    fulfillmentFeeAmount: getFirstNumber(record, [
      "fulfillmentFeeAmount",
      "fulfillment_fee_amount",
    ]),
    quantity,
    unitPrice,
    extraProtectionSelected,
    extraProtectionFee,
    total:
      getFirstNumber(record, ["total", "lineTotal", "line_total"]) ??
      quantity * (unitPrice + (extraProtectionSelected ? extraProtectionFee : 0)),
  };
}

export function normalizeOrderView(value: unknown): OrderView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const id = getFirstString(record, [
    "id",
    "_id",
    "$id",
    "orderId",
    "order_id",
  ]);
  if (!id) return null;

  const rawItems = Array.isArray(record.items)
    ? record.items
    : Array.isArray(record.orderItems)
      ? record.orderItems
      : Array.isArray(record.order_items)
        ? record.order_items
        : undefined;
  const items = (rawItems ?? [])
    .map((item) => normalizeOrderItemView(item))
    .filter((item): item is OrderItemView => Boolean(item));
  const fulfillmentItems = items.filter((item) => item.fulfillmentBySpareLynk);
  const derivedFulfillmentUnits = fulfillmentItems.reduce(
    (sum, item) => sum + Math.max(1, Number(item.quantity) || 1),
    0,
  );
  const firstItem = Array.isArray(items) ? readRecord(items[0]) : null;
  const shippingAddress = readRecord(
    getRecordProperty(record, ["shippingAddress", "shipping_address"]),
  ) as OrderAddressPayload | null;
  const recordImage = selectPrimaryProductImage(record);
  const firstItemImage = firstItem
    ? selectPrimaryProductImage(firstItem)
    : undefined;
  const courierReassignments = normalizeOrderCourierReassignments(
    getRecordProperty(record, [
      "courierReassignments",
      "courier_reassignments",
    ]),
  );
  const courierReassignment =
    normalizeOrderCourierReassignment(
      getRecordProperty(record, [
        "courierReassignment",
        "courier_reassignment",
      ]),
    ) ??
    [...courierReassignments]
      .reverse()
      .find((entry) => entry.status.toLowerCase() === "open") ??
    null;

  return {
    ...record,
    id,
    orderNumber: getFirstString(record, [
      "orderNumber",
      "order_number",
      "displayName",
      "display_name",
    ]),
    displayName: getFirstString(record, [
      "displayName",
      "display_name",
      "orderNumber",
      "order_number",
    ]),
    customerId: getFirstString(record, [
      "customerId",
      "customer_id",
      "buyerId",
      "buyer_id",
      "userId",
      "user_id",
    ]),
    customerDisplayName: getFirstString(record, [
      "customerDisplayName",
      "customer_display_name",
      "buyerName",
      "buyer_name",
    ]),
    customerEmail: getFirstString(record, ["customerEmail", "customer_email"]),
    vendorId: getFirstString(record, [
      "vendorId",
      "vendor_id",
      "sellerId",
      "seller_id",
    ]),
    productId:
      getFirstString(record, ["productId", "product_id"]) ||
      (firstItem
        ? getFirstString(firstItem, ["productId", "product_id", "id"])
        : undefined),
    productName:
      getFirstString(record, [
        "productName",
        "product_name",
        "title",
        "itemName",
        "item_name",
      ]) ||
      (firstItem
        ? getFirstString(firstItem, [
            "productName",
            "product_name",
            "title",
            "name",
          ])
        : undefined),
    productImage: recordImage || firstItemImage,
    quantity:
      getFirstNumber(record, ["quantity"]) ||
      (firstItem ? getFirstNumber(firstItem, ["quantity", "qty"]) : undefined),
    unitPrice:
      getFirstNumber(record, ["unitPrice", "unit_price", "price"]) ||
      (firstItem
        ? getFirstNumber(firstItem, ["unitPrice", "unit_price", "price"])
        : undefined),
    status: getFirstString(record, ["status", "orderStatus", "order_status"]),
    paymentStatus: getFirstString(record, ["paymentStatus", "payment_status"]),
    fulfillmentStatus: getFirstString(record, [
      "fulfillmentStatus",
      "fulfillment_status",
    ]),
    subtotal: getFirstNumber(record, ["subtotal", "subTotal", "sub_total"]),
    tax: getFirstNumber(record, ["tax", "taxTotal", "tax_total"]),
    shipping: getFirstNumber(record, [
      "shipping",
      "shippingTotal",
      "shipping_total",
      "shippingFee",
      "shipping_fee",
    ]),
    shippingFee: getFirstNumber(record, [
      "shippingFee",
      "shipping_fee",
      "shipping",
      "shippingTotal",
      "shipping_total",
    ]),
    buyerShippingFee: getFirstNumber(record, [
      "buyerShippingFee",
      "buyer_shipping_fee",
    ]),
    sellerShippingContribution: getFirstNumber(record, [
      "sellerShippingContribution",
      "seller_shipping_contribution",
    ]),
    shippingPayer: getFirstString(record, ["shippingPayer", "shipping_payer"]),
    freeShippingApplied:
      getBoolean(
        getRecordProperty(record, [
          "freeShippingApplied",
          "free_shipping_applied",
        ]),
      ) ?? false,
    freeShippingReason: getFirstString(record, [
      "freeShippingReason",
      "free_shipping_reason",
    ]),
    freeShippingQuantityThreshold: getFirstNumber(record, [
      "freeShippingQuantityThreshold",
      "free_shipping_quantity_threshold",
    ]),
    discount: getFirstNumber(record, [
      "discount",
      "discountTotal",
      "discount_total",
    ]),
    total:
      getFirstNumber(record, [
        "total",
        "grandTotal",
        "grand_total",
        "amount",
      ]) ?? 0,
    extraProtectionFee: getFirstNumber(record, [
      "extraProtectionFee",
      "extra_protection_fee",
    ]),
    extraProtectionSelected:
      getBoolean(
        getRecordProperty(record, [
          "extraProtectionSelected",
          "extra_protection_selected",
        ]),
      ) ?? false,
    currency: getFirstString(record, ["currency"]),
    fulfillmentBySpareLynk:
      getBoolean(
        getRecordProperty(record, [
          "fulfillmentBySpareLynk",
          "fulfillment_by_sparelynk",
        ]),
      ) ?? (fulfillmentItems.length > 0),
    fulfillmentUnits:
      getFirstNumber(record, ["fulfillmentUnits", "fulfillment_units"]) ??
      derivedFulfillmentUnits,
    courierFeePercent: getFirstNumber(record, [
      "courierFeePercent",
      "courier_fee_percent",
    ]),
    courierFeeAmount: getFirstNumber(record, [
      "courierFeeAmount",
      "courier_fee_amount",
    ]),
    courierPayoutAmount: getFirstNumber(record, [
      "courierPayoutAmount",
      "courier_payout_amount",
    ]),
    courierWalletCreditedAt: getFirstString(record, [
      "courierWalletCreditedAt",
      "courier_wallet_credited_at",
    ]),
    sellerWalletCreditedAt: getFirstString(record, [
      "sellerWalletCreditedAt",
      "seller_wallet_credited_at",
    ]),
    platformWalletCreditedAt: getFirstString(record, [
      "platformWalletCreditedAt",
      "platform_wallet_credited_at",
    ]),
    platformFeePercent: getFirstNumber(record, [
      "platformFeePercent",
      "platform_fee_percent",
    ]),
    referralFeeAmount: getFirstNumber(record, [
      "referralFeeAmount",
      "referral_fee_amount",
    ]),
    perItemSellingFeeAmount: getFirstNumber(record, [
      "perItemSellingFeeAmount",
      "per_item_selling_fee_amount",
    ]),
    fulfillmentFeeAmount: getFirstNumber(record, [
      "fulfillmentFeeAmount",
      "fulfillment_fee_amount",
    ]),
    platformFeeAmount: getFirstNumber(record, [
      "platformFeeAmount",
      "platform_fee_amount",
    ]),
    sellerNetAmount: getFirstNumber(record, [
      "sellerNetAmount",
      "seller_net_amount",
    ]),
    rendezvousLocation: getFirstString(record, [
      "rendezvousLocation",
      "rendezvous_location",
    ]),
    rendezvousAdditionalLocationInfo: getFirstString(record, [
      "rendezvousAdditionalLocationInfo",
      "rendezvous_additional_location_info",
      "rendezvousSpecificLocation",
      "rendezvous_specific_location",
    ]),
    rendezvousLatitude: getFirstNumber(record, [
      "rendezvousLatitude",
      "rendezvous_latitude",
      "handoffLatitude",
      "handoff_latitude",
    ]),
    rendezvousLongitude: getFirstNumber(record, [
      "rendezvousLongitude",
      "rendezvous_longitude",
      "handoffLongitude",
      "handoff_longitude",
    ]),
    rendezvousLocationSource: getFirstString(record, [
      "rendezvousLocationSource",
      "rendezvous_location_source",
      "handoffLocationSource",
      "handoff_location_source",
    ]),
    settlementStatus: getFirstString(record, [
      "settlementStatus",
      "settlement_status",
    ]),
    codRemittanceStatus: getFirstString(record, [
      "codRemittanceStatus",
      "cod_remittance_status",
    ]),
    courierPaymentStatus: getFirstString(record, [
      "courierPaymentStatus",
      "courier_payment_status",
    ]),
    paymentMethod: getFirstString(record, ["paymentMethod", "payment_method"]),
    deliveryMethod: getFirstString(record, [
      "deliveryMethod",
      "delivery_method",
      "fulfillmentMethod",
      "fulfillment_method",
    ]),
    deliveryMode: getFirstString(record, ["deliveryMode", "delivery_mode"]) as
      OrderDeliveryMode | undefined,
    courierOfferPublished:
      getBoolean(
        getRecordProperty(record, [
          "courierOfferPublished",
          "courier_offer_published",
        ]),
      ) ?? false,
    courierOfferPublishedAt: getFirstString(record, [
      "courierOfferPublishedAt",
      "courier_offer_published_at",
    ]),
    deliveryStatus: normalizeDeliveryStatus(
      getRecordProperty(record, [
        "deliveryStatus",
        "delivery_status",
        "parcelStatus",
        "parcel_status",
      ]),
    ),
    courier: normalizeOrderCourier(
      getRecordProperty(record, [
        "courier",
        "deliveryCourier",
        "delivery_courier",
      ]),
    ),
    courierRating: (() => {
      const ratingRecord = readRecord(
        getRecordProperty(record, ["courierRating", "courier_rating"]),
      );
      if (!ratingRecord) return null;
      const rating = getFirstNumber(ratingRecord, ["rating"]);
      const buyerId = getFirstString(ratingRecord, ["buyerId", "buyer_id"]);
      if (!rating || !buyerId) return null;
      return {
        rating,
        review: getFirstString(ratingRecord, ["review"]),
        buyerId,
        buyerName: getFirstString(ratingRecord, ["buyerName", "buyer_name"]) ?? "Buyer",
        createdAt: getFirstString(ratingRecord, ["createdAt", "created_at"]) ?? "",
        updatedAt: getFirstString(ratingRecord, ["updatedAt", "updated_at"]) ?? "",
      } satisfies CourierRatingView;
    })(),
    courierReassignment,
    courierReassignments,
    courierId: getFirstString(record, ["courierId", "courier_id"]),
    courierName: getFirstString(record, ["courierName", "courier_name"]),
    courierEmail: getFirstString(record, ["courierEmail", "courier_email"]),
    trackingNumber: getFirstString(record, [
      "trackingNumber",
      "tracking_number",
      "waybill",
      "awb",
    ]),
    pickupLocation: getFirstString(record, [
      "pickupLocation",
      "pickup_location",
    ]),
    dropoffLocation: getFirstString(record, [
      "dropoffLocation",
      "dropoff_location",
    ]),
    estimatedDeliveryAt: getFirstString(record, [
      "estimatedDeliveryAt",
      "estimated_delivery_at",
      "eta",
    ]),
    deliveryEvents: normalizeOrderDeliveryEvents(
      getRecordProperty(record, [
        "deliveryEvents",
        "delivery_events",
        "trackingEvents",
        "tracking_events",
      ]),
    ),
    messages: normalizeOrderMessages(
      getRecordProperty(record, [
        "messages",
        "orderMessages",
        "order_messages",
      ]),
    ),
    sellerMessagesReadAtByVendor: getRecordProperty(record, ["sellerMessagesReadAtByVendor"]) as Record<string, string> | undefined,
    courierMessagesReadAtByVendor: getRecordProperty(record, ["courierMessagesReadAtByVendor"]) as Record<string, string> | undefined,
    sellerMessagesReadAt: getFirstString(record, [
      "sellerMessagesReadAt",
      "seller_messages_read_at",
    ]),
    courierMessagesReadAt: getFirstString(record, [
      "courierMessagesReadAt",
      "courier_messages_read_at",
    ]),
    courierBuyerMessagesReadAt: getFirstString(record, ["courierBuyerMessagesReadAt"]),
    cancellationReason: getFirstString(record, [
      "cancellationReason",
      "cancellation_reason",
      "cancelReason",
      "cancel_reason",
    ]),
    proofUrls: normalizeStringArray(
      getRecordProperty(record, ["proofUrls", "proof_urls", "proofs"]),
    ),
    paymentProofUrls: normalizeStringArray(
      getRecordProperty(record, [
        "paymentProofUrls",
        "payment_proof_urls",
        "paymentProofs",
      ]),
    ),
    returnRefundRequest: normalizeReturnRefundRequest(
      getRecordProperty(record, [
        "returnRefundRequest",
        "return_refund_request",
      ]),
    ),
    trackingLocation: normalizeOrderTrackingLocation(
      getRecordProperty(record, ["trackingLocation", "tracking_location"]),
    ),
    itemCount:
      getFirstNumber(record, ["itemCount", "item_count"]) ?? items?.length,
    items,
    shippingAddress,
    billingAddress: readRecord(
      getRecordProperty(record, ["billingAddress", "billing_address"]),
    ) as OrderAddressPayload | null,
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

function normalizeVendorProfileView(value: unknown): VendorProfileView | null {
  const record = readRecord(unwrapEnvelope(value));
  if (!record) return null;

  const nested = readNestedRecord(record, [
    "vendor",
    "profile",
    "vendorProfile",
    "vendor_profile",
  ]);
  if (nested) {
    const vendor = normalizeVendorProfileView(nested);
    if (vendor) return vendor;
  }

  const id = getFirstString(record, [
    "id",
    "_id",
    "$id",
    "vendorId",
    "vendor_id",
    "profileId",
    "profile_id",
  ]);
  if (!id) return null;

  return {
    ...record,
    id,
    userId: getFirstString(record, [
      "userId",
      "user_id",
      "ownerId",
      "owner_id",
    ]),
    name: getFirstString(record, [
      "name",
      "storeName",
      "store_name",
      "businessName",
      "business_name",
    ]),
    storeName: getFirstString(record, ["storeName", "store_name", "name"]),
    businessName: getFirstString(record, ["businessName", "business_name"]),
    email: getFirstString(record, ["email"]),
    phone: getFirstString(record, ["phone", "phoneNumber", "phone_number"]),
    address: getFirstString(record, [
      "address",
      "businessAddress",
      "business_address",
    ]),
    description: getFirstString(record, ["description", "bio"]),
    status: getFirstString(record, ["status", "state"]),
    rating: getFirstNumber(record, [
      "rating",
      "averageRating",
      "average_rating",
    ]),
    createdAt: getFirstString(record, ["createdAt", "created_at"]),
    updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
  };
}

const PAGE_ITEM_KEYS = [
  "items",
  "data",
  "results",
  "rows",
  "docs",
  "documents",
  "records",
  "list",
  "values",
  "content",
  "entries",
  "nodes",
  "edges",
  "products",
  "orders",
  "users",
  "vendors",
  "wishlist",
];

const PAGE_ENVELOPE_KEYS = [
  "data",
  "result",
  "payload",
  "page",
  "collection",
] as const;

function findDirectPageItemArray(value: unknown, depth = 0): unknown[] | null {
  if (Array.isArray(value)) return value;
  if (depth > 3) return null;

  const record = readRecord(value);
  if (!record) return null;

  for (const key of PAGE_ITEM_KEYS) {
    const direct = getRecordProperty(record, [key]);
    if (Array.isArray(direct)) {
      return direct;
    }
  }

  for (const key of PAGE_ENVELOPE_KEYS) {
    const nested = getRecordProperty(record, [key]);
    const direct = findDirectPageItemArray(nested, depth + 1);
    if (direct !== null) {
      return direct;
    }
  }

  return null;
}

function collectPageItemArrays(
  value: unknown,
  depth = 0,
  seen = new Set<unknown>(),
): unknown[][] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return [value];
  if (typeof value !== "object") return [];
  if (seen.has(value)) return [];
  if (depth > 6) return [];

  const record = readRecord(value);
  if (!record) return [];

  seen.add(value);

  const arrays: unknown[][] = [];

  for (const key of PAGE_ITEM_KEYS) {
    const direct = getRecordProperty(record, [key]);
    if (Array.isArray(direct)) {
      arrays.push(direct);
    }
  }

  for (const key of [
    "data",
    "result",
    "payload",
    "page",
    "collection",
    "meta",
    "metadata",
  ]) {
    const nested = getRecordProperty(record, [key]);
    arrays.push(...collectPageItemArrays(nested, depth + 1, seen));
  }

  Object.values(record).forEach((entry) => {
    if (entry && typeof entry === "object") {
      arrays.push(...collectPageItemArrays(entry, depth + 1, seen));
    }
  });

  return arrays;
}

function findPageMetadataRecord(
  value: unknown,
  depth = 0,
  seen = new Set<unknown>(),
): Record<string, unknown> | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "object") return null;
  if (seen.has(value)) return null;
  if (depth > 6) return null;

  const record = readRecord(value);
  if (!record) return null;

  seen.add(value);

  if (
    getFirstNumber(record, ["total", "totalItems", "total_items", "count"]) !==
      undefined ||
    getFirstNumber(record, ["page", "currentPage", "current_page"]) !==
      undefined ||
    getFirstNumber(record, [
      "pageSize",
      "page_size",
      "limit",
      "perPage",
      "per_page",
    ]) !== undefined ||
    getFirstNumber(record, ["totalPages", "total_pages", "pages"]) !== undefined
  ) {
    return record;
  }

  for (const key of [
    "data",
    "result",
    "payload",
    "page",
    "collection",
    "meta",
    "metadata",
  ]) {
    const nested = getRecordProperty(record, [key]);
    const nestedRecord = findPageMetadataRecord(nested, depth + 1, seen);
    if (nestedRecord) return nestedRecord;
  }

  return record;
}

function unwrapCollectionEntry(value: unknown): unknown {
  const record = readRecord(value);
  if (!record) return value;

  const nested = getRecordProperty(record, [
    "node",
    "product",
    "user",
    "order",
    "vendor",
    "item",
    "record",
    "document",
    "value",
  ]);

  return nested ?? value;
}

function normalizePageItems<T>(
  candidates: unknown[],
  normalizer: (value: unknown) => T | null,
): T[] {
  const arrays = candidates.flatMap((candidate) =>
    collectPageItemArrays(candidate),
  );
  let bestItems: T[] = [];

  for (const arrayItems of arrays) {
    const normalizedItems = arrayItems
      .map((entry) => normalizer(unwrapCollectionEntry(entry)))
      .filter((entry): entry is T => entry !== null);

    if (normalizedItems.length > bestItems.length) {
      bestItems = normalizedItems;
    }
  }

  if (bestItems.length > 0) {
    return bestItems;
  }

  return candidates
    .map((entry) => normalizer(unwrapCollectionEntry(entry)))
    .filter((entry): entry is T => entry !== null);
}

export function normalizePageResult<T>(
  value: unknown,
  normalizer: (value: unknown) => T | null,
  fallbackPage = 1,
  fallbackPageSize = 10,
): PageResult<T> {
  const candidates = Array.from(new Set([value, unwrapEnvelope(value)]));
  const metadataSource =
    candidates
      .map((candidate) => findPageMetadataRecord(candidate))
      .find((record): record is Record<string, unknown> => Boolean(record)) ??
    null;
  let foundDirectCollection = false;
  let items: T[] = [];

  for (const candidate of candidates) {
    const directItems = findDirectPageItemArray(candidate);
    if (directItems === null) {
      continue;
    }

    foundDirectCollection = true;
    items = directItems
      .map((entry) => normalizer(unwrapCollectionEntry(entry)))
      .filter((entry): entry is T => entry !== null);
    break;
  }

  if (!foundDirectCollection) {
    items = normalizePageItems(candidates, normalizer);
  }

  const page = metadataSource
    ? getFirstNumber(metadataSource, ["page", "currentPage", "current_page"])
    : undefined;
  const pageSize = metadataSource
    ? getFirstNumber(metadataSource, [
        "pageSize",
        "page_size",
        "limit",
        "perPage",
        "per_page",
      ])
    : undefined;
  const total = metadataSource
    ? getFirstNumber(metadataSource, [
        "total",
        "totalItems",
        "total_items",
        "count",
      ])
    : undefined;
  const totalPages = metadataSource
    ? getFirstNumber(metadataSource, ["totalPages", "total_pages", "pages"])
    : undefined;
  const resolvedTotal = Math.max(total ?? 0, items.length);

  return {
    items,
    total: resolvedTotal,
    page: page ?? fallbackPage,
    pageSize: pageSize ?? fallbackPageSize,
    totalPages:
      totalPages ??
      Math.ceil(resolvedTotal / Math.max(pageSize ?? fallbackPageSize, 1)),
  };
}

function normalizeSingle<T>(
  value: unknown,
  normalizer: (value: unknown) => T | null,
): T {
  const normalized = normalizer(value);
  if (!normalized)
    throw new AutopartsApiError(
      "The backend returned an invalid response.",
      0,
      value,
    );
  return normalized;
}

function buildListQuery(
  query: PageQuery | undefined,
  fallbackPageSize: number,
): PageQuery {
  const page = query?.page ?? 1;
  const pageSize = query?.pageSize ?? fallbackPageSize;

  return {
    ...query,
    page,
    pageSize,
    page_size: pageSize,
    limit: pageSize,
    perPage: pageSize,
    per_page: pageSize,
  };
}

const LIST_QUERY_TRANSPORT_KEYS = new Set([
  "page",
  "pageSize",
  "page_size",
  "limit",
  "perPage",
  "per_page",
  "sortBy",
  "sort_by",
  "sortDirection",
  "sort_direction",
]);

function canRetryListRequestWithoutQuery(
  query: PageQuery | undefined,
): boolean {
  if (!query) return false;

  return Object.entries(query).every(([key, value]) => {
    if (value === undefined || value === null || value === "") return true;
    return LIST_QUERY_TRANSPORT_KEYS.has(key);
  });
}

function getQueryIdentity(
  query: PageQuery | undefined,
  keys: string[],
): string | undefined {
  if (!query) return undefined;

  for (const key of keys) {
    const normalized = normalizeIdentity(query[key]);
    if (normalized) return normalized;
  }

  return undefined;
}

function rebuildFilteredPageResult<T>(
  result: PageResult<T>,
  items: T[],
): PageResult<T> {
  const pageSize = Math.max(result.pageSize || items.length || 1, 1);
  const total = items.length;

  return {
    ...result,
    items,
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };
}

function filterProductPageResultByQuery(
  result: PageResult<ProductView>,
  query: PageQuery | undefined,
): PageResult<ProductView> {
  const vendorId = getQueryIdentity(query, [
    "vendorId",
    "vendor_id",
    "sellerId",
    "seller_id",
    "shopId",
    "shop_id",
  ]);
  if (!vendorId) return result;

  return rebuildFilteredPageResult(
    result,
    result.items.filter(
      (product) => normalizeIdentity(product.vendorId) === vendorId,
    ),
  );
}

function readOrderItemVendorId(value: unknown): string {
  const record = readRecord(value);
  if (!record) return "";

  return normalizeIdentity(
    getRecordProperty(record, [
      "vendorId",
      "vendor_id",
      "sellerId",
      "seller_id",
      "shopId",
      "shop_id",
    ]),
  );
}

function orderMatchesVendorQuery(order: OrderView, vendorId: string): boolean {
  if (normalizeIdentity(order.vendorId) === vendorId) return true;

  return (
    Array.isArray(order.items) &&
    order.items.some((item) => readOrderItemVendorId(item) === vendorId)
  );
}

function filterOrderPageResultByQuery(
  result: PageResult<OrderView>,
  query: PageQuery | undefined,
): PageResult<OrderView> {
  const vendorId = getQueryIdentity(query, [
    "vendorId",
    "vendor_id",
    "sellerId",
    "seller_id",
    "shopId",
    "shop_id",
  ]);
  const customerId = getQueryIdentity(query, [
    "customerId",
    "customer_id",
    "buyerId",
    "buyer_id",
    "userId",
    "user_id",
  ]);

  if (!vendorId && !customerId) return result;

  return rebuildFilteredPageResult(
    result,
    result.items.filter((order) => {
      if (vendorId && !orderMatchesVendorQuery(order, vendorId)) return false;
      if (customerId && normalizeIdentity(order.customerId) !== customerId)
        return false;
      return true;
    }),
  );
}

const PRODUCT_READ_CACHE_TTL_MS = 120_000;
const ORDER_ATTACHMENT_READ_CACHE_TTL_MS = 120_000;

type ProductListCacheEntry = {
  expiresAt: number;
  value: PageResult<ProductView>;
};

type ProductDetailCacheEntry = {
  expiresAt: number;
  value: ProductView;
};

const productListCache = new Map<string, ProductListCacheEntry>();
const productListRequests = new Map<string, Promise<PageResult<ProductView>>>();
const productDetailCache = new Map<string, ProductDetailCacheEntry>();
const productDetailRequests = new Map<string, Promise<ProductView>>();

type OrderAttachmentListCacheEntry = {
  expiresAt: number;
  value: PageResult<OrderView>;
};

const orderAttachmentListCache = new Map<
  string,
  OrderAttachmentListCacheEntry
>();
const orderAttachmentListRequests = new Map<
  string,
  Promise<PageResult<OrderView>>
>();

function getProductReadCacheScope(): string {
  const session = readStoredAuthSession();
  return `${session?.user?.id ?? "anonymous"}:${session?.user?.role ?? "guest"}`;
}

function buildProductListCacheKey(query: PageQuery | undefined): string {
  const normalizedQuery = buildListQuery(query, 12);
  const sortedEntries = Object.entries(normalizedQuery)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .sort(([left], [right]) => left.localeCompare(right));

  return `${getProductReadCacheScope()}:${JSON.stringify(sortedEntries)}`;
}

function buildProductDetailCacheKey(id: string): string {
  return `${getProductReadCacheScope()}:${id.trim()}`;
}

function buildOrderAttachmentListCacheKey(
  query: PageQuery | undefined,
): string {
  const normalizedQuery = buildListQuery(query, 10);
  const sortedEntries = Object.entries(normalizedQuery)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .sort(([left], [right]) => left.localeCompare(right));

  return `${getProductReadCacheScope()}:${JSON.stringify(sortedEntries)}`;
}

function isAttachmentCatalogQuery(query: PageQuery | undefined): boolean {
  const value = query?.attachmentCatalog;
  return value === true || value === 1 || value === "true";
}

function shouldInvalidateProductReadCache(path: string): boolean {
  const normalized = path.toLowerCase();
  return (
    normalized.startsWith("/products") ||
    normalized.startsWith("/buyer-reviews") ||
    normalized.includes("/buyer-reviews") ||
    normalized.includes("/reviews")
  );
}

function shouldInvalidateOrderAttachmentReadCache(path: string): boolean {
  return path.toLowerCase().startsWith("/orders");
}

function invalidateOrderAttachmentReadCache(): void {
  orderAttachmentListCache.clear();
  orderAttachmentListRequests.clear();
}

function invalidateProductReadCache(productId?: string): void {
  productListCache.clear();
  productListRequests.clear();

  if (!productId) {
    productDetailCache.clear();
    productDetailRequests.clear();
    return;
  }

  const normalizedId = productId.trim();
  for (const key of productDetailCache.keys()) {
    if (key.endsWith(`:${normalizedId}`)) {
      productDetailCache.delete(key);
    }
  }
  for (const key of productDetailRequests.keys()) {
    if (key.endsWith(`:${normalizedId}`)) {
      productDetailRequests.delete(key);
    }
  }
}

async function requestPageResultFromPaths<T>(
  paths: string[],
  query: PageQuery | undefined,
  normalizer: (value: unknown) => T | null,
  fallbackPageSize: number,
): Promise<PageResult<T>> {
  const listQuery = buildListQuery(query, fallbackPageSize);
  let firstEmptyResult: PageResult<T> | null = null;
  let lastError: unknown;

  for (const path of paths) {
    const queryAttempts = canRetryListRequestWithoutQuery(listQuery)
      ? [listQuery, undefined]
      : [listQuery];

    for (const queryAttempt of queryAttempts) {
      try {
        const response = await request(
          `${path}${buildQueryString(queryAttempt)}`,
          { preserveEnvelope: true },
        );
        const result = normalizePageResult(
          response,
          normalizer,
          listQuery.page ?? 1,
          listQuery.pageSize ?? fallbackPageSize,
        );

        if (result.items.length > 0) {
          return result;
        }

        firstEmptyResult ??= result;

        if (!canRetryListRequestWithoutQuery(listQuery)) {
          return result;
        }
      } catch (error) {
        lastError = error;

        if (
          error instanceof AutopartsApiError &&
          [401, 403, 404, 405].includes(error.status)
        ) {
          break;
        }

        throw error;
      }
    }
  }

  if (firstEmptyResult) {
    return firstEmptyResult;
  }

  if (lastError instanceof Error) {
    throw lastError;
  }

  throw new AutopartsApiError(
    "Unable to load live marketplace data.",
    0,
    lastError,
  );
}

export type UserRole = MarketplaceRole;
export type UserStatus = "active" | "suspended";

export type User = Omit<UserView, "role" | "status"> & {
  id: string;
  backendId?: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  joinDate: string;
  avatar?: string;
  avatarUrl?: string | null;
  phone?: string;
  address?: string;
  loyaltyBadges?: string[];
  courierIdentificationType?: string;
  courierIdentificationNumber?: string;
  courierIdentificationImageUrls?: string[];
  courierSelfieImageUrls?: string[];
  courierDeliveryMethod?: string;
  courierDriverLicenseNumber?: string;
  courierDriverLicenseExpiryDate?: string;
  courierDriverLicenseImageUrls?: string[];
  courierVehiclePlateNumber?: string;
  courierVehicleRegistrationExpiryDate?: string;
  courierVehicleRegistrationImageUrls?: string[];
  courierInsuranceProvider?: string;
  courierInsurancePolicyNumber?: string;
  courierInsuranceExpiryDate?: string;
  courierInsuranceImageUrls?: string[];
  courierEmergencyContactName?: string;
  courierEmergencyContactPhone?: string;
  courierExperience?: string;
  courierOtherWorkExperience?: string;
  courierVerificationStatus?: CourierVerificationStatus;
  courierVerificationNotes?: string;
  courierCredentialsSubmittedAt?: string;
  courierCredentialsVerifiedAt?: string;
  courierCredentialsVerifiedBy?: string;
  sellerIdentificationType?: string;
  sellerIdentificationNumber?: string;
  sellerIdentificationImageUrls?: string[];
  sellerSelfieImageUrls?: string[];
  sellerClassification?: string;
  sellerBusinessName?: string;
  sellerBusinessRegistrationNumber?: string;
  sellerBusinessRegistrationImageUrls?: string[];
  sellerTaxIdentificationNumber?: string;
  sellerBirCertificateImageUrls?: string[];
  sellerProofOfAddressImageUrls?: string[];
  sellerExperience?: string;
  sellerOtherWorkExperience?: string;
  sellerVerificationStatus?: SellerVerificationStatus;
  sellerVerificationNotes?: string;
  sellerCredentialsSubmittedAt?: string;
  sellerCredentialsVerifiedAt?: string;
  sellerCredentialsVerifiedBy?: string;
  qualityAssessorProfessionalTitle?: string;
  qualityAssessorLicenseNumber?: string;
  qualityAssessorAccreditingBody?: string;
  qualityAssessorCredentialImageUrls?: string[];
  qualityAssessorExperience?: string;
  qualityAssessorESignatureUrl?: string;
  qualityAssessorVerificationStatus?: SellerVerificationStatus;
  qualityAssessorVerificationNotes?: string;
  qualityAssessorCredentialsSubmittedAt?: string;
  qualityAssessorCredentialsVerifiedAt?: string;
  qualityAssessorCredentialsVerifiedBy?: string;
  totalOrders?: number;
  totalSpent?: number;
  totalSales?: number;
};

export type CreateUserAccountPayload = {
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  password?: string;
  avatar?: string;
  courierIdentificationType?: string;
  courierIdentificationNumber?: string;
  courierIdentificationImageUrls?: string[];
  courierSelfieImageUrls?: string[];
  courierDeliveryMethod?: string;
  courierDriverLicenseNumber?: string;
  courierDriverLicenseExpiryDate?: string;
  courierDriverLicenseImageUrls?: string[];
  courierVehiclePlateNumber?: string;
  courierVehicleRegistrationExpiryDate?: string;
  courierVehicleRegistrationImageUrls?: string[];
  courierInsuranceProvider?: string;
  courierInsurancePolicyNumber?: string;
  courierInsuranceExpiryDate?: string;
  courierInsuranceImageUrls?: string[];
  courierEmergencyContactName?: string;
  courierEmergencyContactPhone?: string;
  courierExperience?: string;
  courierOtherWorkExperience?: string;
  courierVerificationStatus?: CourierVerificationStatus;
  courierVerificationNotes?: string;
  courierCredentialsVerifiedBy?: string;
  sellerIdentificationType?: string;
  sellerIdentificationNumber?: string;
  sellerIdentificationImageUrls?: string[];
  sellerSelfieImageUrls?: string[];
  sellerClassification?: string;
  sellerBusinessName?: string;
  sellerBusinessRegistrationNumber?: string;
  sellerBusinessRegistrationImageUrls?: string[];
  sellerTaxIdentificationNumber?: string;
  sellerBirCertificateImageUrls?: string[];
  sellerProofOfAddressImageUrls?: string[];
  sellerExperience?: string;
  sellerOtherWorkExperience?: string;
  sellerVerificationStatus?: SellerVerificationStatus;
  sellerVerificationNotes?: string;
  sellerCredentialsVerifiedBy?: string;
  qualityAssessorProfessionalTitle?: string;
  qualityAssessorLicenseNumber?: string;
  qualityAssessorAccreditingBody?: string;
  qualityAssessorCredentialImageUrls?: string[];
  qualityAssessorExperience?: string;
  qualityAssessorESignatureUrl?: string;
  qualityAssessorVerificationStatus?: SellerVerificationStatus;
  qualityAssessorVerificationNotes?: string;
  qualityAssessorCredentialsVerifiedBy?: string;
};

export type UpdateUserAccountPayload = Partial<CreateUserAccountPayload>;

export type CredentialUploadView = {
  url: string;
  key?: string;
  fileName?: string;
  contentType?: string;
  size?: number;
};

export type SendUserMessagePayload = {
  subject: string;
  message: string;
};

export type ProductListingState =
  "draft" | "submitted" | "approved" | "live" | "archived";

export type ProductReviewAuthorRole = "admin" | "seller";

export type ProductReviewComment = {
  id: string;
  authorRole: ProductReviewAuthorRole;
  authorName?: string;
  message: string;
  createdAt: string;
  updatedAt?: string;
};

export type BuyerReviewReplyView = {
  id: string;
  authorId: string;
  authorRole: "buyer" | "seller" | "admin" | string;
  authorName: string;
  message: string;
  createdAt: string;
};

export type BuyerReviewMediaView = {
  url: string;
  key: string;
  mediaType: "image" | "video";
  contentType: string;
  fileName: string;
  size: number;
  sources?: ReturnRefundPlaybackSourceView[];
};

export type BuyerReviewView = {
  id: string;
  targetType: "product" | "shop";
  targetId: string;
  productId?: string;
  vendorId: string;
  orderId?: string;
  buyerId: string;
  buyerName: string;
  rating: number;
  review: string;
  media?: BuyerReviewMediaView[];
  replies?: BuyerReviewReplyView[];
  createdAt: string;
  updatedAt?: string;
};

export type CreateBuyerReviewPayload = {
  vendorId: string;
  productId?: string;
  orderId?: string;
  buyerId: string;
  buyerName: string;
  rating: number;
  review: string;
  media?: BuyerReviewMediaView[];
};

const PRODUCT_DELETED_REVIEW_COMMENT = "__SPARELYNK_PRODUCT_DELETED__";

export type Product = Omit<
  ProductView,
  | "brand"
  | "category"
  | "status"
  | "description"
  | "imageUrl"
  | "images"
  | "rating"
  | "reviews"
> & {
  id: string;
  backendId: string;
  name: string;
  brand: string;
  productNumber?: string;
  partNumber?: string;
  vehicleType?: string;
  vehicleModel?: string;
  modelYears?: number[];
  variant?: string;
  engine?: string;
  market?: string;
  location?: string;
  condition?: string;
  conditionDescription?: string;
  knownDefects?: string;
  testingStatus?: string;
  repairHistory?: string;
  qualityAssuranceStatus: "PENDING" | "PASSED" | "REJECTED";
  qualityAssuranceProfessionalName: string;
  qualityAssuranceProfessionalTitle: string;
  qualityAssuranceCredentialNumber: string;
  qualityAssuranceSignatureUrl: string;
  qualityAssuranceEvidenceUrls: string[];
  qualityAssuranceSellerNotes: string;
  qualityAssuranceAssessorUserId?: string;
  qualityAssuranceAssessmentSource: string;
  qualityAssuranceWrittenAssessment: string;
  qualityAssuranceAdminComment: string;
  qualityAssuranceAdminEvidenceUrls: string[];
  qualityAssuranceReviewedAt?: string;
  qualityAssurancePassedAt?: string;
  qualityAssuranceSeal: boolean;
  sellerName: string;
  vendorId?: string;
  category: string;
  description: string;
  price: number;
  originalPrice?: number;
  listingState: ProductListingState;
  inStock: boolean;
  stockQty: number;
  adminReviewComment: string;
  reviewComments: ProductReviewComment[];
  image: string;
  imageUrl?: string;
  images: string[];
  featured: boolean;
  rating: number;
  reviews: number;
  buyerReviews: BuyerReviewView[];
  commissionPercent: number;
  commissionAmount: number;
  sellerNetPrice: number;
  extraProtectionFee: number;
  baseShippingFee: number;
  baseShippingFeeCurrency: string;
  buyerShippingFeePercent: number;
  freeShippingQuantityThreshold: number;
  shopRating: number;
  shopReviews: number;
  compatibility: string[];
  specifications: Record<string, string>;
};

export type OrderStatus =
  | "pending"
  | "processing"
  | "shipped"
  | "delivered"
  | "completed"
  | "cancelled"
  | "refunded";

export type Order = {
  id: string;
  orderNumber?: string;
  displayName?: string;
  customerId?: string;
  customerDisplayName?: string;
  customerEmail?: string;
  vendorId?: string;
  vendorName?: string;
  productId?: string;
  productName: string;
  productImage: string;
  quantity: number;
  price: number;
  unitPrice?: number;
  subtotal?: number;
  tax?: number;
  shipping?: number;
  shippingFee?: number;
  buyerShippingFee?: number;
  sellerShippingContribution?: number;
  shippingPayer?: string;
  freeShippingApplied?: boolean;
  freeShippingReason?: string;
  freeShippingQuantityThreshold?: number;
  discount?: number;
  total: number;
  extraProtectionFee?: number;
  extraProtectionSelected?: boolean;
  currency?: string;
  status: OrderStatus;
  buyerName: string;
  orderDate: string;
  shippingAddress: string;
  shippingAddressDetails?: OrderAddressPayload | null;
  paymentMethod?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  deliveryMethod?: string;
  deliveryMode?: OrderDeliveryMode;
  courierOfferPublished?: boolean;
  courierOfferPublishedAt?: string;
  deliveryStatus?: OrderDeliveryStatus | string;
  courier?: OrderCourierView | null;
  courierRating?: CourierRatingView | null;
  courierReassignment?: OrderCourierReassignmentView | null;
  courierReassignments?: OrderCourierReassignmentView[];
  courierId?: string;
  courierName?: string;
  courierEmail?: string;
  trackingNumber?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  estimatedDeliveryAt?: string;
  deliveryEvents?: OrderDeliveryEventView[];
  messages?: OrderMessageView[];
  cancellationReason?: string;
  proofUrls?: string[];
  paymentProofUrls?: string[];
  returnRefundRequest?: ReturnRefundRequestView | null;
  trackingLocation?: OrderTrackingLocationView | null;
  courierFeePercent?: number;
  courierFeeAmount?: number;
  courierPayoutAmount?: number;
  courierWalletCreditedAt?: string;
  sellerWalletCreditedAt?: string;
  platformWalletCreditedAt?: string;
  platformFeePercent?: number;
  platformFeeAmount?: number;
  sellerNetAmount?: number;
  rendezvousLocation?: string;
  rendezvousAdditionalLocationInfo?: string;
  rendezvousLatitude?: number;
  rendezvousLongitude?: number;
  rendezvousLocationSource?: string;
  settlementStatus?: string;
  codRemittanceStatus?: string;
  courierPaymentStatus?: string;
  itemCount?: number;
  items?: OrderItemView[];
  fulfillmentBySpareLynk?: boolean;
  fulfillmentUnits?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type SalesDataPoint = {
  month: string;
  revenue: number;
  orders: number;
};

export type PlatformStats = {
  totalUsers: number;
  totalSellers: number;
  totalProducts: number;
  totalOrders: number;
  totalRevenue: number;
  revenueByCurrency?: Record<string, number>;
  monthlyGrowth: number;
  activeOrders: number;
  pendingApprovals: number;
};

export type MarketplaceData = {
  users: User[];
  products: Product[];
  orders: Order[];
  salesData: SalesDataPoint[];
  platformStats: PlatformStats;
};

export const categories = [
  { id: "brakes", name: "Brakes", icon: "Disc" },
  { id: "engine", name: "Engine", icon: "Cog" },
  { id: "lighting", name: "Lighting", icon: "Lightbulb" },
  { id: "suspension", name: "Suspension", icon: "Circle" },
  { id: "exhaust", name: "Exhaust", icon: "Wind" },
  { id: "electrical", name: "Electrical", icon: "Zap" },
  { id: "exterior", name: "Exterior", icon: "Wrench" },
  { id: "interior", name: "Interior", icon: "GitBranch" },
  { id: "uncategorized", name: "Uncategorized", icon: "Wrench" },
] as const;

function normalizeAdminStatus(value: unknown, isActive?: boolean): UserStatus {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (
    ["suspended", "inactive", "disabled", "blocked"].includes(normalized ?? "")
  ) {
    return "suspended";
  }

  if (isActive === false) {
    return "suspended";
  }

  return "active";
}

function normalizeListingState(value: unknown): ProductListingState {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s_-]+/g, "");

  switch (normalized) {
    case "approved":
      return "approved";
    case "live":
    case "active":
    case "published":
      return "live";
    case "archived":
    case "rejected":
    case "removed":
      return "archived";
    case "submitted":
    case "pending":
    case "pendingreview":
    case "underreview":
      return "submitted";
    default:
      return "draft";
  }
}

function toBackendListingState(state: ProductListingState): string {
  switch (state) {
    case "approved":
      return "APPROVED";
    case "live":
      return "LIVE";
    case "archived":
      return "ARCHIVED";
    case "submitted":
      return "SUBMITTED";
    case "draft":
    default:
      return "DRAFT";
  }
}

function normalizeProductReviewAuthorRole(
  value: unknown,
): ProductReviewAuthorRole {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (normalized === "seller" || normalized === "vendor") {
    return "seller";
  }

  return "admin";
}

function normalizeProductReviewComments(
  value: unknown,
): ProductReviewComment[] {
  const values = Array.isArray(value) ? value : [];

  return values
    .map((entry, index): ProductReviewComment | null => {
      const record = readRecord(entry);
      if (!record) {
        return null;
      }

      const message = getFirstString(record, [
        "message",
        "comment",
        "body",
        "text",
      ]);
      if (!message) {
        return null;
      }

      return {
        id:
          getFirstString(record, [
            "id",
            "_id",
            "$id",
            "commentId",
            "comment_id",
          ]) || `comment-${index}`,
        authorRole: normalizeProductReviewAuthorRole(
          getRecordProperty(record, [
            "authorRole",
            "author_role",
            "role",
            "senderRole",
            "sender_role",
          ]),
        ),
        authorName: getFirstString(record, [
          "authorName",
          "author_name",
          "senderName",
          "sender_name",
          "name",
        ]),
        message,
        createdAt:
          getFirstString(record, ["createdAt", "created_at", "timestamp"]) ||
          new Date().toISOString(),
        updatedAt: getFirstString(record, ["updatedAt", "updated_at"]),
      };
    })
    .filter((comment): comment is ProductReviewComment => Boolean(comment));
}

function normalizeOrderStatus(value: unknown): OrderStatus {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s_-]+/g, "");

  switch (normalized) {
    case "delivered":
      return "delivered";
    case "completed":
    case "complete":
      return "completed";
    case "cancelled":
    case "canceled":
    case "void":
      return "cancelled";
    case "refunded":
      return "refunded";
    case "shipped":
    case "intransit":
      return "shipped";
    case "processing":
    case "packed":
    case "confirmed":
    case "paid":
    case "paymentpaid":
      return "processing";
    case "pending":
    default:
      return "pending";
  }
}

function normalizeIdentity(value: unknown): string {
  return getString(value)?.toLowerCase() ?? "";
}

function getUserRequestIdentifier(user: User): string {
  return user.backendId || user.id || user.email;
}

function buildUserMutationPayload(
  payload: CreateUserAccountPayload | UpdateUserAccountPayload,
  options: { isCreate?: boolean } = {},
) {
  const isActive = payload.status ? payload.status === "active" : undefined;
  const isAdmin = payload.role === "admin";
  const isCourier = payload.role === "courier";
  const isCredentialMutation =
    Boolean(payload.password) || Boolean(options.isCreate);
  const requiresPasswordChange = Boolean(payload.password && isAdmin);
  const emailVerified = isCredentialMutation
    ? isCourier
      ? false
      : true
    : undefined;

  return {
    name: payload.name,
    fullName: payload.name,
    displayName: payload.name,
    email: payload.email,
    role: payload.role,
    accountType: payload.role,
    userRole: payload.role,
    status: payload.status,
    isActive,
    is_active: isActive,
    password: payload.password,
    mustChangePassword: payload.password ? requiresPasswordChange : undefined,
    must_change_password: payload.password ? requiresPasswordChange : undefined,
    emailVerified,
    email_verified: emailVerified,
    avatarUrl: payload.avatar,
    avatar_url: payload.avatar,
    avatar: payload.avatar,
    courierIdentificationType: payload.courierIdentificationType,
    courierIdentificationNumber: payload.courierIdentificationNumber,
    courierIdentificationImageUrls: payload.courierIdentificationImageUrls,
    courierSelfieImageUrls: payload.courierSelfieImageUrls,
    courierDeliveryMethod: payload.courierDeliveryMethod,
    courierDriverLicenseNumber: payload.courierDriverLicenseNumber,
    courierDriverLicenseExpiryDate: payload.courierDriverLicenseExpiryDate,
    courierDriverLicenseImageUrls: payload.courierDriverLicenseImageUrls,
    courierVehiclePlateNumber: payload.courierVehiclePlateNumber,
    courierVehicleRegistrationExpiryDate:
      payload.courierVehicleRegistrationExpiryDate,
    courierVehicleRegistrationImageUrls:
      payload.courierVehicleRegistrationImageUrls,
    courierInsuranceProvider: payload.courierInsuranceProvider,
    courierInsurancePolicyNumber: payload.courierInsurancePolicyNumber,
    courierInsuranceExpiryDate: payload.courierInsuranceExpiryDate,
    courierInsuranceImageUrls: payload.courierInsuranceImageUrls,
    courierEmergencyContactName: payload.courierEmergencyContactName,
    courierEmergencyContactPhone: payload.courierEmergencyContactPhone,
    courierExperience: payload.courierExperience,
    courierOtherWorkExperience: payload.courierOtherWorkExperience,
    courierVerificationStatus: payload.courierVerificationStatus,
    courierVerificationNotes: payload.courierVerificationNotes,
    courierCredentialsVerifiedBy: payload.courierCredentialsVerifiedBy,
    sellerIdentificationType: payload.sellerIdentificationType,
    sellerIdentificationNumber: payload.sellerIdentificationNumber,
    sellerIdentificationImageUrls: payload.sellerIdentificationImageUrls,
    sellerSelfieImageUrls: payload.sellerSelfieImageUrls,
    sellerClassification: payload.sellerClassification,
    sellerBusinessName: payload.sellerBusinessName,
    sellerBusinessRegistrationNumber:
      payload.sellerBusinessRegistrationNumber,
    sellerBusinessRegistrationImageUrls:
      payload.sellerBusinessRegistrationImageUrls,
    sellerTaxIdentificationNumber: payload.sellerTaxIdentificationNumber,
    sellerBirCertificateImageUrls: payload.sellerBirCertificateImageUrls,
    sellerProofOfAddressImageUrls: payload.sellerProofOfAddressImageUrls,
    sellerExperience: payload.sellerExperience,
    sellerOtherWorkExperience: payload.sellerOtherWorkExperience,
    sellerVerificationStatus: payload.sellerVerificationStatus,
    sellerVerificationNotes: payload.sellerVerificationNotes,
    sellerCredentialsVerifiedBy: payload.sellerCredentialsVerifiedBy,
    qualityAssessorProfessionalTitle: payload.qualityAssessorProfessionalTitle,
    qualityAssessorLicenseNumber: payload.qualityAssessorLicenseNumber,
    qualityAssessorAccreditingBody: payload.qualityAssessorAccreditingBody,
    qualityAssessorCredentialImageUrls: payload.qualityAssessorCredentialImageUrls,
    qualityAssessorExperience: payload.qualityAssessorExperience,
    qualityAssessorESignatureUrl: payload.qualityAssessorESignatureUrl,
    qualityAssessorVerificationStatus: payload.qualityAssessorVerificationStatus,
    qualityAssessorVerificationNotes: payload.qualityAssessorVerificationNotes,
    qualityAssessorCredentialsVerifiedBy: payload.qualityAssessorCredentialsVerifiedBy,
  };
}

function inferProductCategory(product: ProductView): string {
  const explicitCategory = getString(product.category)?.trim();

  if (explicitCategory) {
    return explicitCategory;
  }

  const haystack = [
    product.title,
    product.name,
    product.description,
    product.brand,
    product.sku,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/brake|rotor|caliper|pad/.test(haystack)) return "brakes";
  if (/headlight|tail.?light|lamp|bulb|led|fog/.test(haystack))
    return "lighting";
  if (/engine|filter|spark|oil|belt|radiator|pump/.test(haystack))
    return "engine";
  if (/strut|shock|suspension|control arm|ball joint/.test(haystack))
    return "suspension";
  if (/exhaust|catalytic|muffler|converter/.test(haystack)) return "exhaust";
  if (/battery|sensor|alternator|starter|relay|fuse/.test(haystack))
    return "electrical";
  if (/mirror|bumper|fender|grille|door|hood/.test(haystack)) return "exterior";
  if (/seat|mat|dashboard|trim|console/.test(haystack)) return "interior";

  return "uncategorized";
}

function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function stringifySpecValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") return value.trim() || undefined;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    const values = value
      .map((entry) => stringifySpecValue(entry))
      .filter((entry): entry is string => Boolean(entry));
    return values.join(", ") || undefined;
  }
  if (isRecord(value)) {
    const label = getFirstString(value, ["name", "label", "value", "title"]);
    if (label) return label;
  }

  return undefined;
}

function normalizeProductSpecifications(
  product: ProductView,
): Record<string, string> {
  const specs =
    readRecord(
      getRecordProperty(product, ["specs", "specifications", "attributes"]),
    ) ?? {};
  const normalized: Record<string, string> = {};

  Object.entries(specs).forEach(([key, value]) => {
    const displayValue = stringifySpecValue(value);
    if (displayValue) {
      normalized[titleCase(key)] = displayValue;
    }
  });

  if (product.sku && !normalized.SKU) normalized.SKU = product.sku;
  if (product.productNumber && !normalized["SpareLynk Product Number"])
    normalized["SpareLynk Product Number"] = product.productNumber;
  if (product.partNumber && !normalized["Part Number"])
    normalized["Part Number"] = product.partNumber;
  if (product.brand && !normalized.Brand) normalized.Brand = product.brand;
  if (product.vehicleType && !normalized["Vehicle Type"])
    normalized["Vehicle Type"] = titleCase(product.vehicleType);
  if (product.vehicleModel && !normalized["Vehicle Model"])
    normalized["Vehicle Model"] = product.vehicleModel;
  if (product.modelYears?.length && !normalized["Model Years"])
    normalized["Model Years"] = product.modelYears.join(", ");
  if (product.variant && !normalized.Variant)
    normalized.Variant = product.variant;
  if (product.engine && !normalized.Engine) normalized.Engine = product.engine;
  if (product.market && !normalized.Market) normalized.Market = product.market;
  if (product.location && !normalized.Location)
    normalized.Location = product.location;
  if (product.condition && !normalized.Condition)
    normalized.Condition = titleCase(product.condition);
  if (product.conditionDescription && !normalized["Condition Description"])
    normalized["Condition Description"] = product.conditionDescription;
  if (product.knownDefects && !normalized["Known Defects"])
    normalized["Known Defects"] = product.knownDefects;
  if (product.testingStatus && !normalized["Testing Status"])
    normalized["Testing Status"] = titleCase(product.testingStatus);
  if (product.repairHistory && !normalized["Repair History"])
    normalized["Repair History"] = product.repairHistory;
  if (product.category && !normalized.Category)
    normalized.Category = titleCase(product.category);
  if (product.vendorName && !normalized.Seller)
    normalized.Seller = product.vendorName;

  return normalized;
}

function stringifyCompatibilityEntry(value: unknown): string | undefined {
  const direct = getString(value);
  if (direct) return direct;

  if (!isRecord(value)) return undefined;

  const year = getFirstString(value, ["year", "modelYear", "model_year"]);
  const make = getFirstString(value, [
    "make",
    "brand",
    "vehicleMake",
    "vehicle_make",
  ]);
  const model = getFirstString(value, [
    "model",
    "vehicleModel",
    "vehicle_model",
  ]);
  const trim = getFirstString(value, ["trim", "variant"]);
  const engine = getFirstString(value, ["engine", "engineType", "engine_type"]);
  const label = getFirstString(value, [
    "label",
    "name",
    "vehicle",
    "displayName",
    "display_name",
  ]);

  const composed = [year, make, model, trim, engine]
    .filter(Boolean)
    .join(" ")
    .trim();
  return composed || label;
}

function normalizeProductCompatibility(product: ProductView): string[] {
  const compatibilitySource = getRecordProperty(product, [
    "compatibility",
    "compatibleVehicles",
    "compatible_vehicles",
    "fitment",
    "fitments",
    "vehicles",
  ]);
  const values = Array.isArray(compatibilitySource)
    ? [...compatibilitySource]
    : [];
  if (product.brand || product.vehicleModel || product.modelYears?.length) {
    const years = product.modelYears?.length ? product.modelYears : [undefined];
    years.forEach((year) => {
      values.push({
        year,
        make: product.brand,
        model: product.vehicleModel,
        trim: product.variant,
        engine: product.engine,
      });
    });
  }

  return Array.from(
    new Set(
      values
        .map((entry) => stringifyCompatibilityEntry(entry))
        .filter((entry): entry is string => Boolean(entry)),
    ),
  );
}

function formatAddress(
  address: OrderAddressPayload | null | undefined,
): string {
  if (!address) return "Shipping address unavailable";

  const locationLabel = getFirstString(address, [
    "locationLabel",
    "location_label",
    "formattedAddress",
    "formatted_address",
  ]);
  const line1 = getFirstString(address, [
    "addressLine1",
    "address_line_1",
    "address",
    "street",
  ]);
  const additionalLocationInfo = getFirstString(address, [
    "additionalLocationInfo",
    "additional_location_info",
  ]);
  const line2 = getFirstString(address, ["addressLine2", "address_line_2"]);
  const city = getFirstString(address, ["city"]);
  const state = getFirstString(address, ["state", "province"]);
  const postalCode = getFirstString(address, [
    "postalCode",
    "postal_code",
    "zipCode",
    "zip_code",
  ]);
  const country = getFirstString(address, ["country"]);

  if (locationLabel) {
    return [locationLabel, additionalLocationInfo || line2]
      .filter(Boolean)
      .join(" • ");
  }

  return (
    [
      locationLabel,
      line1 !== locationLabel ? line1 : "",
      additionalLocationInfo,
      line2 !== additionalLocationInfo ? line2 : "",
      [city, state, postalCode].filter(Boolean).join(", "),
      country,
    ]
      .filter(Boolean)
      .join(", ") || "Shipping address unavailable"
  );
}

export function normalizeAdminUser(
  value: UserView,
  activity?: Partial<Pick<User, "totalOrders" | "totalSpent" | "totalSales">>,
): User {
  const status = normalizeAdminStatus(value.status, value.isActive);

  return {
    ...value,
    id: value.id,
    backendId: value.id,
    name: value.name || value.displayName || value.email,
    email: value.email,
    role: value.role,
    status,
    joinDate: value.createdAt || value.updatedAt || new Date().toISOString(),
    avatar: resolveUserAvatarUrl(value),
    totalOrders:
      activity?.totalOrders ??
      getFirstNumber(value, ["totalOrders", "total_orders"]) ??
      0,
    totalSpent:
      activity?.totalSpent ??
      getFirstNumber(value, ["totalSpent", "total_spent"]) ??
      0,
    totalSales:
      activity?.totalSales ??
      getFirstNumber(value, ["totalSales", "total_sales"]) ??
      0,
  };
}

export function normalizeAdminProduct(value: ProductView): Product {
  const stockQty =
    getFirstNumber(value, [
      "stockQty",
      "stock_qty",
      "stock",
      "quantity",
      "availableQuantity",
      "available_quantity",
    ]) ?? 0;
  const listingState = normalizeListingState(
    getRecordProperty(value, [
      "listingState",
      "listing_state",
      "reviewState",
      "review_state",
      "status",
      "state",
    ]),
  );
  const basePrice = value.price ?? 0;
  const salePrice = value.salePrice;
  const price =
    typeof salePrice === "number" && salePrice > 0 ? salePrice : basePrice;
  const explicitOriginalPrice = getFirstNumber(value, [
    "originalPrice",
    "original_price",
    "compareAtPrice",
    "compare_at_price",
  ]);
  const commissionPercent = getProductCommissionPercent(value);
  const commissionAmount = getProductCommissionAmount(
    value,
    price,
    commissionPercent,
  );
  const sellerNetPrice = getProductSellerNetPrice(
    value,
    price,
    commissionAmount,
  );
  const images = normalizeProductImageUrls(
    value.primaryImageUrl,
    value.imageUrl,
    value.images,
    value.imageUrls,
    value,
  );
  const image = images[0] || "";
  const explicitFeatured = getBoolean(
    getRecordProperty(value, [
      "featured",
      "isFeatured",
      "is_featured",
      "highlighted",
    ]),
  );

  return {
    ...value,
    id: value.id,
    backendId: value.id,
    name: value.name || value.title || "Untitled Product",
    brand: value.brand || "Generic",
    sellerName:
      value.vendorName ||
      getFirstString(value, [
        "sellerName",
        "seller_name",
        "storeName",
        "store_name",
      ]) ||
      "Unassigned seller",
    category: inferProductCategory(value),
    description:
      value.description || "No product description has been provided yet.",
    price,
    originalPrice:
      explicitOriginalPrice && explicitOriginalPrice > price
        ? explicitOriginalPrice
        : salePrice && basePrice > price
          ? basePrice
          : undefined,
    listingState,
    inStock: stockQty > 0,
    stockQty,
    adminReviewComment:
      value.adminReviewComment ||
      getFirstString(value, [
        "adminReviewComment",
        "admin_review_comment",
        "reviewComment",
        "review_comment",
        "adminComment",
        "admin_comment",
      ]) ||
      "",
    qualityAssuranceStatus:
      String(value.qualityAssuranceStatus ?? "PENDING").trim().toUpperCase() === "PASSED"
        ? "PASSED"
        : String(value.qualityAssuranceStatus ?? "PENDING").trim().toUpperCase() === "REJECTED"
          ? "REJECTED"
          : "PENDING",
    qualityAssuranceProfessionalName: value.qualityAssuranceProfessionalName || "",
    qualityAssuranceProfessionalTitle: value.qualityAssuranceProfessionalTitle || "",
    qualityAssuranceCredentialNumber: value.qualityAssuranceCredentialNumber || "",
    qualityAssuranceSignatureUrl: value.qualityAssuranceSignatureUrl || "",
    qualityAssuranceEvidenceUrls: value.qualityAssuranceEvidenceUrls ?? [],
    qualityAssuranceSellerNotes: value.qualityAssuranceSellerNotes || "",
    qualityAssuranceAssessorUserId: value.qualityAssuranceAssessorUserId,
    qualityAssuranceAssessmentSource: value.qualityAssuranceAssessmentSource || "",
    qualityAssuranceWrittenAssessment: value.qualityAssuranceWrittenAssessment || "",
    qualityAssuranceAdminComment: value.qualityAssuranceAdminComment || "",
    qualityAssuranceAdminEvidenceUrls: value.qualityAssuranceAdminEvidenceUrls ?? [],
    qualityAssuranceReviewedAt: value.qualityAssuranceReviewedAt,
    qualityAssurancePassedAt: value.qualityAssurancePassedAt,
    qualityAssuranceSeal:
      value.qualityAssuranceSeal === true ||
      (listingState === "live" &&
        String(value.qualityAssuranceStatus ?? "").trim().toUpperCase() === "PASSED"),
    reviewComments: normalizeProductReviewComments(
      getRecordProperty(value, [
        "reviewComments",
        "review_comments",
        "comments",
        "commentThread",
        "comment_thread",
      ]),
    ),
    image,
    imageUrl: image || undefined,
    primaryImageUrl: image || undefined,
    images,
    imageUrls: images,
    spinImageUrls: getImageStrings(
      getRecordProperty(value, ["spinImageUrls", "spin_image_urls", "spin360ImageUrls", "spin360_image_urls"]),
    ),
    featured:
      explicitFeatured ??
      (listingState === "live" || listingState === "approved"),
    rating:
      value.rating ??
      getFirstNumber(value, ["averageRating", "average_rating"]) ??
      0,
    reviews:
      value.reviews ??
      getFirstNumber(value, ["reviewCount", "review_count"]) ??
      0,
    buyerReviews: normalizeBuyerReviews(
      getRecordProperty(value, ["buyerReviews", "buyer_reviews"]),
    ),
    commissionPercent,
    commissionAmount,
    sellerNetPrice,
    extraProtectionFee:
      getFirstNumber(value, ["extraProtectionFee", "extra_protection_fee"]) ??
      0,
    baseShippingFee:
      getFirstNumber(value, ["baseShippingFee", "base_shipping_fee"]) ?? 15,
    baseShippingFeeCurrency:
      getFirstString(value, [
        "baseShippingFeeCurrency",
        "base_shipping_fee_currency",
        "shippingFeeCurrency",
        "shipping_fee_currency",
      ]) ?? "USD",
    buyerShippingFeePercent:
      getFirstNumber(value, [
        "buyerShippingFeePercent",
        "buyer_shipping_fee_percent",
      ]) ?? 100,
    freeShippingQuantityThreshold:
      getFirstNumber(value, [
        "freeShippingQuantityThreshold",
        "free_shipping_quantity_threshold",
      ]) ?? 0,
    shopRating: getFirstNumber(value, ["shopRating", "shop_rating"]) ?? 0,
    shopReviews: getFirstNumber(value, ["shopReviews", "shop_reviews"]) ?? 0,
    compatibility: normalizeProductCompatibility(value),
    specifications: normalizeProductSpecifications(value),
  };
}

function getOrderItemName(order: OrderView): string {
  const itemNames = (Array.isArray(order.items) ? order.items : [])
    .map((item) => {
      const itemRecord = readRecord(item);
      if (!itemRecord) return "";
      const nestedProduct = readRecord(
        getRecordProperty(itemRecord, ["product", "item"]),
      );
      return (
        getFirstString(itemRecord, [
          "title",
          "name",
          "productName",
          "product_name",
        ]) ||
        (nestedProduct
          ? getFirstString(nestedProduct, [
              "title",
              "name",
              "productName",
              "product_name",
            ])
          : undefined) ||
        ""
      ).trim();
    })
    .filter(Boolean);
  const uniqueItemNames = Array.from(
    new Map(itemNames.map((name) => [name.toLowerCase(), name])).values(),
  );
  if (uniqueItemNames.length > 0) return uniqueItemNames.join(", ");

  return (
    getFirstString(order, [
      "productName",
      "product_name",
      "title",
      "itemName",
      "item_name",
    ]) || "Marketplace order"
  );
}

function getOrderItemQuantity(order: OrderView): number | undefined {
  const items = Array.isArray(order.items) ? order.items : [];
  if (items.length === 0) return undefined;

  const total = items.reduce((sum, item) => {
    const itemRecord = readRecord(item);
    const quantity = itemRecord
      ? getFirstNumber(itemRecord, [
          "quantity",
          "qty",
          "itemCount",
          "item_count",
        ])
      : undefined;
    return sum + Math.max(0, quantity ?? 1);
  }, 0);

  return total > 0 ? total : items.length;
}

export function normalizeAdminOrder(
  value: OrderView,
  userLookup?: Map<string, User>,
): Order {
  const customerId = normalizeIdentity(value.customerId);
  const matchedUser = customerId ? userLookup?.get(customerId) : undefined;
  const shippingName = value.shippingAddress
    ? [
        getFirstString(value.shippingAddress, ["firstName", "first_name"]),
        getFirstString(value.shippingAddress, ["lastName", "last_name"]),
      ]
        .filter(Boolean)
        .join(" ")
    : undefined;
  const firstItem = Array.isArray(value.items)
    ? readRecord(value.items[0])
    : null;
  const productId =
    value.productId ||
    (firstItem
      ? getFirstString(firstItem, ["productId", "product_id", "id"])
      : undefined);
  const productImage = selectPrimaryProductImage(
    value.productImage,
    firstItem,
    value,
  );
  const quantity =
    getOrderItemQuantity(value) ??
    value.quantity ??
    value.itemCount ??
    (firstItem ? getFirstNumber(firstItem, ["quantity", "qty"]) : undefined) ??
    1;
  const price =
    value.unitPrice ||
    (firstItem
      ? getFirstNumber(firstItem, ["unitPrice", "unit_price", "price"])
      : undefined) ||
    value.total ||
    0;
  const orderItems = value.items ?? [];
  const derivedFulfillmentUnits = orderItems.reduce(
    (sum, item) =>
      item.fulfillmentBySpareLynk
        ? sum + Math.max(1, Number(item.quantity) || 1)
        : sum,
    0,
  );

  return {
    ...value,
    id: value.id,
    orderNumber: value.orderNumber,
    status: normalizeOrderStatus(
      value.status || value.fulfillmentStatus || value.paymentStatus,
    ),
    buyerName:
      value.customerDisplayName ||
      getFirstString(value, [
        "buyerName",
        "buyer_name",
        "customerName",
        "customer_name",
      ]) ||
      matchedUser?.name ||
      shippingName ||
      "Customer",
    productId,
    productName: getOrderItemName(value),
    productImage,
    quantity,
    price,
    total: value.total ?? 0,
    extraProtectionFee: value.extraProtectionFee ?? 0,
    extraProtectionSelected: Boolean(value.extraProtectionSelected),
    orderDate: value.createdAt || value.updatedAt || new Date().toISOString(),
    shippingAddress: formatAddress(value.shippingAddress),
    shippingAddressDetails: value.shippingAddress ?? null,
    paymentMethod: value.paymentMethod,
    deliveryMethod: value.deliveryMethod,
    deliveryStatus: value.deliveryStatus,
    courier: value.courier ?? null,
    courierReassignment:
      value.courierReassignment ??
      [...(value.courierReassignments ?? [])]
        .reverse()
        .find(
          (entry) => String(entry.status).trim().toLowerCase() === "open",
        ) ??
      null,
    courierReassignments: value.courierReassignments ?? [],
    courierId: value.courierId ?? value.courier?.courierId,
    courierName: value.courierName ?? value.courier?.name,
    courierEmail: value.courierEmail ?? value.courier?.email,
    trackingNumber: value.trackingNumber ?? value.courier?.trackingNumber,
    pickupLocation: value.pickupLocation ?? value.courier?.pickupLocation,
    dropoffLocation: value.dropoffLocation ?? value.courier?.dropoffLocation,
    estimatedDeliveryAt:
      value.estimatedDeliveryAt ?? value.courier?.estimatedDeliveryAt,
    deliveryEvents: value.deliveryEvents ?? [],
    messages: value.messages ?? [],
    cancellationReason: value.cancellationReason,
    proofUrls: value.proofUrls ?? [],
    paymentProofUrls: value.paymentProofUrls ?? [],
    returnRefundRequest: value.returnRefundRequest ?? null,
    trackingLocation: value.trackingLocation ?? null,
    itemCount: value.itemCount ?? orderItems.length,
    items: orderItems,
    fulfillmentBySpareLynk:
      value.fulfillmentBySpareLynk ?? (derivedFulfillmentUnits > 0),
    fulfillmentUnits: value.fulfillmentUnits ?? derivedFulfillmentUnits,
  };
}

function buildUserLookup(usersList: User[]): Map<string, User> {
  const lookup = new Map<string, User>();

  usersList.forEach((user) => {
    [user.id, user.backendId, user.email].forEach((value) => {
      const key = normalizeIdentity(value);
      if (key) lookup.set(key, user);
    });
  });

  return lookup;
}

function applyOrderActivityToUsers(
  usersList: User[],
  ordersList: Order[],
): User[] {
  const activity = new Map<
    string,
    { totalOrders: number; totalSpent: number; totalSales: number }
  >();

  usersList.forEach((user) => {
    activity.set(user.id, {
      totalOrders: user.totalOrders ?? 0,
      totalSpent: user.totalSpent ?? 0,
      totalSales: user.totalSales ?? 0,
    });
  });

  ordersList.forEach((order) => {
    const customerId = normalizeIdentity(order.customerId);
    const user = usersList.find(
      (entry) =>
        normalizeIdentity(entry.id) === customerId ||
        normalizeIdentity(entry.backendId) === customerId,
    );

    if (user) {
      const current = activity.get(user.id) ?? {
        totalOrders: 0,
        totalSpent: 0,
        totalSales: 0,
      };
      current.totalOrders += 1;
      current.totalSpent += order.total;
      activity.set(user.id, current);
    }

    const vendorId = normalizeIdentity(order.vendorId);
    const seller = usersList.find(
      (entry) =>
        entry.role === "seller" &&
        (normalizeIdentity(entry.id) === vendorId ||
          normalizeIdentity(entry.backendId) === vendorId),
    );

    if (seller) {
      const current = activity.get(seller.id) ?? {
        totalOrders: 0,
        totalSpent: 0,
        totalSales: 0,
      };
      current.totalSales += order.total;
      activity.set(seller.id, current);
    }
  });

  return usersList.map((user) => ({
    ...user,
    ...(activity.get(user.id) ?? {}),
  }));
}

function buildSalesData(ordersList: Order[]): SalesDataPoint[] {
  const now = new Date();
  const buckets = Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      key: `${date.getFullYear()}-${date.getMonth()}`,
      month: date.toLocaleString("en-US", { month: "short" }),
      revenue: 0,
      orders: 0,
    };
  });
  const bucketMap = new Map(buckets.map((bucket) => [bucket.key, bucket]));

  ordersList.forEach((order) => {
    const date = new Date(order.orderDate);
    if (Number.isNaN(date.getTime())) return;

    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const bucket = bucketMap.get(key);
    if (!bucket) return;

    bucket.revenue += order.total;
    bucket.orders += 1;
  });

  return buckets.map(({ month, revenue, orders }) => ({
    month,
    revenue,
    orders,
  }));
}

function calculateMonthlyGrowth(usersList: User[]): number {
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  let current = 0;
  let previous = 0;
  for (const user of usersList) {
    const joinedAt = new Date(user.joinDate);
    if (Number.isNaN(joinedAt.getTime())) continue;

    if (joinedAt >= currentMonthStart) {
      current += 1;
    } else if (joinedAt >= previousMonthStart) {
      previous += 1;
    }
  }

  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }

  return Math.round(((current - previous) / previous) * 100);
}

function emptyPageResult<T>(pageSize: number): PageResult<T> {
  return {
    items: [],
    total: 0,
    page: 1,
    pageSize,
    totalPages: 0,
  };
}

export function isProductPendingReview(product: Product): boolean {
  return (
    product.listingState === "draft" || product.listingState === "submitted"
  );
}

export function isProductApproved(product: Product): boolean {
  return product.listingState === "approved" || product.listingState === "live";
}

export function getProductReviewLabel(product: Product): string {
  switch (product.listingState) {
    case "live":
      return "Live";
    case "approved":
      return "Approved";
    case "archived":
      return "Archived";
    case "submitted":
      return "Pending Review";
    case "draft":
    default:
      return "Draft";
  }
}

export async function fetchProducts(query?: PageQuery): Promise<Product[]> {
  const result = await products.list<PageResult<ProductView>>({
    page: 1,
    pageSize: 500,
    summary: true,
    ...query,
  });

  return result.items
    .map(normalizeAdminProduct)
    .filter(
      (product) =>
        product.adminReviewComment !== PRODUCT_DELETED_REVIEW_COMMENT,
    );
}

export async function fetchProductById(
  productId: string,
): Promise<Product | null> {
  const normalizedId = productId.trim();
  if (!normalizedId) {
    return null;
  }

  const product = normalizeAdminProduct(
    await products.getById<ProductView>(normalizedId),
  );

  return product.adminReviewComment === PRODUCT_DELETED_REVIEW_COMMENT
    ? null
    : product;
}

export async function fetchFeaturedProducts(limit = 4): Promise<Product[]> {
  const productList = await fetchProducts({
    page: 1,
    pageSize: Math.max(limit * 3, 12),
    sortBy: "createdAt",
    sortDirection: "desc",
  });
  const featuredProducts = productList.filter(
    (product) => product.featured && product.inStock,
  );

  return (featuredProducts.length > 0 ? featuredProducts : productList).slice(
    0,
    limit,
  );
}

export type CategoryFilterOption = {
  value: string;
  label: string;
};

export function getAvailableBrands(productList: Product[] = []): string[] {
  return Array.from(
    new Set(productList.map((product) => product.brand.trim()).filter(Boolean)),
  ).sort((left, right) => left.localeCompare(right));
}

export function getAvailableCategories(
  productList: Product[] = [],
): CategoryFilterOption[] {
  return Array.from(
    new Set(
      productList.map((product) => product.category.trim()).filter(Boolean),
    ),
  )
    .sort((left, right) => titleCase(left).localeCompare(titleCase(right)))
    .map((category) => ({
      value: category,
      label: titleCase(category),
    }));
}

export async function fetchProductCompatibility(
  productId: string,
): Promise<string[]> {
  const product = await products.getById<ProductView>(productId);
  return normalizeAdminProduct(product).compatibility;
}

export async function fetchUsers(query?: PageQuery): Promise<User[]> {
  const result = await users.list<PageResult<UserView>>({
    page: 1,
    pageSize: 500,
    ...query,
  });

  return result.items.map((user) => normalizeAdminUser(user));
}

export async function fetchOrdersByBuyer(
  buyerId: string,
  query?: PageQuery,
): Promise<Order[]> {
  if (!buyerId) return [];

  const result = await orders.list<PageResult<OrderView>>({
    page: 1,
    pageSize: 60,
    sortBy: "createdAt",
    sortDirection: "desc",
    ...query,
    customerId: buyerId,
    summary: true,
  });

  return result.items.map((order) => normalizeAdminOrder(order));
}

export async function fetchPlatformStats(): Promise<PlatformStats> {
  try {
    return await request<PlatformStats>("/platform/stats", {
      skipAuth: true,
    });
  } catch (error) {
    if (!(error instanceof AutopartsApiError) || error.status !== 404) {
      throw error;
    }
  }

  const [vendorResult, productResult, orderResult] = await Promise.all([
    vendors.list<PageResult<VendorProfileView>>({ page: 1, pageSize: 1 }),
    products.list<PageResult<ProductView>>({
      page: 1,
      pageSize: 1,
      listingState: "LIVE",
      summary: true,
    }),
    orders.list<PageResult<OrderView>>({
      page: 1,
      pageSize: 100,
      sortBy: "createdAt",
      sortDirection: "desc",
      summary: true,
    }),
  ]);
  const normalizedOrders = orderResult.items.map((order) =>
    normalizeAdminOrder(order),
  );
  const activeOrderStatuses: OrderStatus[] = [
    "pending",
    "processing",
    "shipped",
  ];

  return {
    totalUsers: 0,
    totalSellers: vendorResult.total,
    totalProducts: productResult.total,
    totalOrders: orderResult.total,
    totalRevenue: normalizedOrders.reduce(
      (total, order) =>
        order.status === "delivered" || order.status === "completed"
          ? total + (order.platformFeeAmount ?? 0)
          : total,
      0,
    ),
    monthlyGrowth: 0,
    activeOrders: normalizedOrders.filter((order) =>
      activeOrderStatuses.includes(order.status),
    ).length,
    pendingApprovals: 0,
  };
}

export async function approveProductListing(
  productId: string,
  adminReviewComment = "",
  qualityAssuranceAdminEvidenceUrls: string[] = [],
  qualityAssuranceReviewedProofUrls: string[] = [],
): Promise<Product> {
  const response = await request(
    `/products/${encodeURIComponent(productId)}/listing-state`,
    {
      method: "PATCH",
      body: {
        listingState: toBackendListingState("approved"),
        adminReviewComment: adminReviewComment.trim(),
        qualityAssuranceAdminEvidenceUrls,
        qualityAssuranceReviewedProofUrls,
      },
    },
  );

  return normalizeAdminProduct(normalizeSingle(response, normalizeProductView));
}

export async function rejectProductListing(
  productId: string,
  adminReviewComment = "",
  qualityAssuranceAdminEvidenceUrls: string[] = [],
): Promise<Product> {
  const response = await request(
    `/products/${encodeURIComponent(productId)}/listing-state`,
    {
      method: "PATCH",
      body: {
        listingState: toBackendListingState("archived"),
        adminReviewComment: adminReviewComment.trim(),
        qualityAssuranceAdminEvidenceUrls,
      },
    },
  );

  return normalizeAdminProduct(normalizeSingle(response, normalizeProductView));
}

export async function submitProductQualityAssessment(
  productId: string,
  payload: {
    approved: boolean;
    writtenAssessment: string;
    evidenceUrls: string[];
    professionalName?: string;
    professionalTitle?: string;
    credentialNumber?: string;
    signatureUrl?: string;
  },
): Promise<Product> {
  const response = await request(
    `/products/${encodeURIComponent(productId)}/quality-assessment`,
    { method: "POST", body: payload },
  );
  return normalizeAdminProduct(normalizeSingle(response, normalizeProductView));
}

export async function addProductReviewComment(
  productId: string,
  payload: {
    authorRole: ProductReviewAuthorRole;
    message: string;
    authorName?: string;
  },
): Promise<Product> {
  const normalizedProductId = getString(productId);
  const message = payload.message.trim();

  if (!normalizedProductId) {
    throw new AutopartsApiError(
      "Product ID is required",
      400,
      { field: "id" },
      "id",
    );
  }

  if (!message) {
    throw new AutopartsApiError(
      "Comment is required",
      400,
      { field: "message" },
      "message",
    );
  }

  const response = await request(
    `/products/${encodeURIComponent(normalizedProductId)}/review-comments`,
    {
      method: "POST",
      body: {
        authorRole: payload.authorRole,
        authorName: payload.authorName?.trim() || undefined,
        message,
      },
    },
  );

  return normalizeAdminProduct(normalizeSingle(response, normalizeProductView));
}

export async function deleteProductReviewComment(
  productId: string,
  commentId: string,
): Promise<Product> {
  const normalizedProductId = getString(productId);
  const normalizedCommentId = getString(commentId);

  if (!normalizedProductId) {
    throw new AutopartsApiError(
      "Product ID is required",
      400,
      { field: "id" },
      "id",
    );
  }

  if (!normalizedCommentId) {
    throw new AutopartsApiError(
      "Comment ID is required",
      400,
      { field: "commentId" },
      "commentId",
    );
  }

  const response = await request(
    `/products/${encodeURIComponent(normalizedProductId)}/review-comments/${encodeURIComponent(normalizedCommentId)}`,
    { method: "DELETE" },
  );

  return normalizeAdminProduct(normalizeSingle(response, normalizeProductView));
}

export async function clearProductAdminReviewComment(
  productId: string,
  listingState: ProductListingState,
): Promise<Product> {
  const normalizedProductId = getString(productId);

  if (!normalizedProductId) {
    throw new AutopartsApiError(
      "Product ID is required",
      400,
      { field: "id" },
      "id",
    );
  }

  const response = await request(
    `/products/${encodeURIComponent(normalizedProductId)}/listing-state`,
    {
      method: "PATCH",
      body: {
        listingState: toBackendListingState(listingState),
        adminReviewComment: "",
      },
    },
  );

  return normalizeAdminProduct(normalizeSingle(response, normalizeProductView));
}

export async function deleteProductListing(productId: string): Promise<void> {
  const normalizedProductId = getString(productId);

  if (!normalizedProductId) {
    throw new AutopartsApiError(
      "Product ID is required",
      400,
      { field: "id" },
      "id",
    );
  }

  try {
    await products.delete(normalizedProductId);
    return;
  } catch (error) {
    const canFallbackToArchive =
      error instanceof AutopartsApiError &&
      (error.status === 404 || error.status === 405 || error.status >= 500);

    if (!canFallbackToArchive) {
      throw error;
    }
  }

  await request(
    `/products/${encodeURIComponent(normalizedProductId)}/listing-state`,
    {
      method: "PATCH",
      body: {
        listingState: toBackendListingState("archived"),
        adminReviewComment: PRODUCT_DELETED_REVIEW_COMMENT,
      },
    },
  );
}

export async function createUserAccount(
  payload: CreateUserAccountPayload,
): Promise<User> {
  const response = await request("/admin/users", {
    method: "POST",
    body: buildUserMutationPayload(payload, { isCreate: true }),
  });

  const createdUser = normalizeAdminUser(
    normalizeSingle(response, normalizeUserView),
  );
  await ensureSellerVendorProfile(createdUser);
  return createdUser;
}

export async function updateUserAccount(
  user: User,
  payload: UpdateUserAccountPayload,
): Promise<User> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  const response = await request(`/admin/users/${identifier}`, {
    method: "PATCH",
    body: buildUserMutationPayload(payload),
  });

  return normalizeAdminUser(normalizeSingle(response, normalizeUserView));
}

export async function reviewCourierCredentials(
  user: User,
  decision: Extract<
    CourierVerificationStatus,
    "PARTIALLY_VERIFIED" | "FULLY_VERIFIED" | "REJECTED"
  >,
  notes: string,
): Promise<User> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  const response = await request(`/admin/users/${identifier}`, {
    method: "PATCH",
    body: {
      courierVerificationStatus: decision,
      courierVerificationNotes: notes.trim(),
    },
  });

  return normalizeAdminUser(normalizeSingle(response, normalizeUserView), user);
}

export async function reviewSellerCredentials(
  user: User,
  decision: Extract<
    SellerVerificationStatus,
    "PARTIALLY_VERIFIED" | "FULLY_VERIFIED" | "REJECTED"
  >,
  notes: string,
): Promise<User> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  const response = await request(`/admin/users/${identifier}`, {
    method: "PATCH",
    body: {
      sellerVerificationStatus: decision,
      sellerVerificationNotes: notes.trim(),
    },
  });

  return normalizeAdminUser(normalizeSingle(response, normalizeUserView), user);
}

export async function reviewQualityAssessorCredentials(
  user: User,
  decision: Extract<SellerVerificationStatus, "PARTIALLY_VERIFIED" | "FULLY_VERIFIED" | "REJECTED">,
  notes: string,
): Promise<User> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  const response = await request(`/admin/users/${identifier}`, {
    method: "PATCH",
    body: {
      qualityAssessorVerificationStatus: decision,
      qualityAssessorVerificationNotes: notes.trim(),
    },
  });
  return normalizeAdminUser(normalizeSingle(response, normalizeUserView), user);
}

export async function suspendUserAccount(user: User): Promise<User> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  const response = await request(`/admin/users/${identifier}/suspend`, {
    method: "PATCH",
    body: { id: user.backendId || user.id, email: user.email },
  });

  return normalizeAdminUser(normalizeSingle(response, normalizeUserView));
}

export async function activateUserAccount(user: User): Promise<User> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  const response = await request(`/admin/users/${identifier}/activate`, {
    method: "PATCH",
    body: { id: user.backendId || user.id, email: user.email },
  });

  return normalizeAdminUser(normalizeSingle(response, normalizeUserView));
}

export async function deleteUserAccount(user: User): Promise<void> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));

  await request(`/admin/users/${identifier}`, {
    method: "DELETE",
    body: { id: user.backendId || user.id, email: user.email },
  });
}

export async function resendUserCredentials(user: User): Promise<void> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));

  await request(`/admin/users/${identifier}/resend-credentials`, {
    method: "POST",
    body: {
      id: user.backendId || user.id,
      userId: user.backendId || user.id,
      user_id: user.backendId || user.id,
      email: user.email,
      identifier: user.email,
      login: user.email,
    },
  });
}

export async function sendUserMessage(
  user: User,
  payload: SendUserMessagePayload,
): Promise<void> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));

  await request(`/admin/users/${identifier}/messages`, {
    method: "POST",
    body: {
      subject: payload.subject,
      message: payload.message,
      text: payload.message,
      body: payload.message,
    },
  });
}

export async function refreshMarketplaceData(): Promise<MarketplaceData> {
  const [
    platformStats,
    userResult,
    draftProductResult,
    submittedProductResult,
    orderResult,
  ] = await Promise.all([
    fetchPlatformStats(),
    users.list<PageResult<UserView>>({
      page: 1,
      pageSize: 500,
      sortBy: "createdAt",
      sortDirection: "desc",
    }),
    products.list<PageResult<ProductView>>({
      page: 1,
      pageSize: 6,
      listingState: "DRAFT",
      sortBy: "createdAt",
      sortDirection: "desc",
    }),
    products.list<PageResult<ProductView>>({
      page: 1,
      pageSize: 6,
      listingState: "SUBMITTED",
      sortBy: "createdAt",
      sortDirection: "desc",
    }),
    orders
      .list<PageResult<OrderView>>({
        page: 1,
        pageSize: 500,
        sortBy: "createdAt",
        sortDirection: "desc",
        summary: true,
      })
      .catch(() => emptyPageResult<OrderView>(500)),
  ]);

  const initialUsers = userResult.items.map((user) => normalizeAdminUser(user));
  const userLookup = buildUserLookup(initialUsers);
  const normalizedProducts = [
    ...draftProductResult.items,
    ...submittedProductResult.items,
  ].map(normalizeAdminProduct);
  const normalizedOrders = orderResult.items.map((order) =>
    normalizeAdminOrder(order, userLookup),
  );
  const normalizedUsers = applyOrderActivityToUsers(
    initialUsers,
    normalizedOrders,
  );

  return {
    users: normalizedUsers,
    products: normalizedProducts,
    orders: normalizedOrders,
    salesData: buildSalesData(normalizedOrders),
    platformStats,
  };
}

function resolveAuthSessionFromResponse(
  response: unknown,
  remember = false,
  fallbackToken?: string,
): AuthSession | null {
  const session = normalizeAuthSession(response);
  const token =
    getSessionAuthToken(session) ||
    getAuthTokenFromValue(response) ||
    getAuthTokenFromValue(unwrapEnvelope(response)) ||
    fallbackToken;
  const records = uniqueRecords([
    ...collectRecords(response),
    ...collectRecords(unwrapEnvelope(response)),
  ]);
  const resolvedUser =
    session?.user ?? findFirstAuthenticatedUser(records, token);
  const storedToken = rememberAuthTokenOnly(token, remember);

  if (session && resolvedUser && storedToken) {
    const resolvedSession = mergeAuthSessionToken(
      {
        ...session,
        user: resolvedUser,
      },
      storedToken,
    );

    if (shouldPersistSession(resolvedSession)) {
      rememberAuthSession(resolvedSession, remember);
      return resolvedSession;
    }
  }

  if (storedToken && resolvedUser) {
    const tokenSession: AuthSession = {
      user: resolvedUser,
      token: storedToken,
      accessToken: storedToken,
      tokenType: "Bearer",
      expiresAt: getJwtExpiresAt(readJwtClaims(storedToken)),
      expiresIn: getJwtExpiresIn(readJwtClaims(storedToken)),
    };

    rememberAuthSession(tokenSession, remember);
    return tokenSession;
  }

  if (session && resolvedUser) {
    const hasPendingEmailVerification =
      Boolean(session.requiresEmailVerification) ||
      Boolean(session.emailVerificationSent) ||
      resolvedUser.emailVerified === false;
    const hasPendingPasswordChange = Boolean(resolvedUser.mustChangePassword);
    const hasPendingTwoFactor = Boolean(
      session.requiresTwoFactor && session.twoFactorChallengeToken,
    );
    const tokenlessSession: AuthSession = {
      ...session,
      user: resolvedUser,
      token: undefined,
      accessToken: undefined,
      tokenType: undefined,
    };

    if (hasUsableAuthSession(tokenlessSession)) {
      rememberAuthSession(tokenlessSession, remember);
      return tokenlessSession;
    }

    if (
      hasPendingEmailVerification ||
      hasPendingPasswordChange ||
      hasPendingTwoFactor
    ) {
      return tokenlessSession;
    }
  }

  return null;
}

function isSuccessfulAuthResponse(response: unknown): boolean {
  const records = uniqueRecords([
    ...collectRecords(response),
    ...collectRecords(unwrapEnvelope(response)),
  ]);

  return records.some(
    (record) =>
      getBoolean(
        getRecordProperty(record, ["success", "ok", "authenticated"]),
      ) === true,
  );
}

async function ensureSellerVendorProfile(
  user: AuthenticatedUser | UserView | null | undefined,
): Promise<VendorProfileView | null> {
  if (!user || user.role !== "seller") return null;

  const userId = getString(user.id);
  if (!userId) return null;

  try {
    const response = await request(
      `/users/${encodeURIComponent(userId)}/vendor-profile`,
      { preserveEnvelope: true },
    );
    return normalizeSingle(response, normalizeVendorProfileView);
  } catch (error) {
    if (
      !(error instanceof AutopartsApiError) ||
      ![404, 405].includes(error.status)
    ) {
      return null;
    }
  }

  try {
    const response = await request("/vendors", {
      method: "POST",
      preserveEnvelope: true,
      body: {
        userId,
        user_id: userId,
        businessName: user.name || user.displayName || user.email || "Seller",
        business_name: user.name || user.displayName || user.email || "Seller",
        status: "PENDING",
      },
    });

    return normalizeSingle(response, normalizeVendorProfileView);
  } catch {
    return null;
  }
}

async function resolveAuthSessionOrThrow(
  response: unknown,
  remember = false,
): Promise<AuthSession> {
  const session = resolveAuthSessionFromResponse(response, remember);

  if (session) {
    return session;
  }

  clearStoredAuthSession();

  if (isSuccessfulAuthResponse(response)) {
    throw new AutopartsApiError(
      "Login succeeded, but the backend did not return a usable bearer token. Please apply the backend auth response fix and restart the backend server.",
      0,
      response,
    );
  }

  throw new AutopartsApiError(
    "Unable to sign in. Please check your email and password.",
    0,
    response,
  );
}

function normalizeTwoFactorSettings(value: unknown): TwoFactorSettings {
  const record = readRecord(unwrapEnvelope(value)) ?? {};
  return {
    emailEnabled:
      getFirstBoolean(record, ["emailEnabled", "email_enabled"]) ?? false,
    authenticatorEnabled:
      getFirstBoolean(record, [
        "authenticatorEnabled",
        "authenticator_enabled",
      ]) ?? false,
    primaryEmail: getFirstString(record, ["primaryEmail", "primary_email"]),
    backupEmail: getFirstString(record, ["backupEmail", "backup_email"]),
    backupEmails: (() => { const value = getRecordProperty(record, ["backupEmails", "backup_emails"]); return Array.isArray(value) ? value.map(String).filter(Boolean) : undefined; })(),
    maskedEmail: getFirstString(record, ["maskedEmail", "masked_email"]),
    maskedBackupEmail: getFirstString(record, [
      "maskedBackupEmail",
      "masked_backup_email",
    ]),
    maskedBackupEmails: (() => { const value = getRecordProperty(record, ["maskedBackupEmails", "masked_backup_emails"]); return Array.isArray(value) ? value.map(String).filter(Boolean) : undefined; })(),
    backupCodesRemaining: getFirstNumber(record, ["backupCodesRemaining", "backup_codes_remaining"]),
    backupCodesViewable: getFirstBoolean(record, ["backupCodesViewable", "backup_codes_viewable"]),
    twoFactorAttemptsRemaining: getFirstNumber(record, ["twoFactorAttemptsRemaining", "two_factor_attempts_remaining"]),
    twoFactorLocked: getFirstBoolean(record, ["twoFactorLocked", "two_factor_locked"]),
  };
}

function normalizeAuthenticatorSetup(value: unknown): AuthenticatorSetup {
  const record = readRecord(unwrapEnvelope(value)) ?? {};
  const secret = getFirstString(record, ["secret"]);
  const otpauthUrl = getFirstString(record, [
    "otpauthUrl",
    "otpAuthUrl",
    "otpauth_url",
  ]);
  const setupToken = getFirstString(record, ["setupToken", "setup_token"]);

  if (!secret || !otpauthUrl || !setupToken) {
    throw new AutopartsApiError(
      "The authenticator setup response is incomplete.",
      0,
      value,
    );
  }

  return { secret, otpauthUrl, setupToken };
}

const auth = {
  async login(payload: {
    email: string;
    password: string;
    remember?: boolean;
  }): Promise<AuthSession> {
    const remember = Boolean(payload.remember);
    let response: unknown;
    let lastLoginError: AutopartsApiError | null = null;

    for (const loginPath of LOGIN_PATHS) {
      try {
        response = await request(loginPath, {
          method: "POST",
          forceRefresh: true,
          skipAuth: true,
          preserveEnvelope: true,
          body: {
            email: payload.email,
            identifier: payload.email,
            username: payload.email,
            login: payload.email,
            password: payload.password,
            remember,
            rememberMe: remember,
          },
        });
        lastLoginError = null;
        break;
      } catch (error) {
        if (
          error instanceof AutopartsApiError &&
          [404, 405].includes(error.status)
        ) {
          lastLoginError = error;
          continue;
        }

        throw error;
      }
    }

    if (response === undefined) {
      throw (
        lastLoginError ??
        new AutopartsApiError(
          "Unable to sign in. Please check your email and password.",
          0,
        )
      );
    }

    const session = resolveAuthSessionFromResponse(response, remember);
    if (session) {
      await ensureSellerVendorProfile(session.user);
      return session;
    }

    const tokenlessAdminSession = buildTokenlessAdminSessionFromSuccessfulLogin(
      response,
      payload.email,
      remember,
    );
    if (tokenlessAdminSession) return tokenlessAdminSession;

    return await resolveAuthSessionOrThrow(response, remember);
  },

  async googleLogin(payload: { credential: string; remember?: boolean }): Promise<AuthSession> {
    const remember = Boolean(payload.remember);
    const response = await request("/auth/google", {
      method: "POST",
      forceRefresh: true,
      skipAuth: true,
      preserveEnvelope: true,
      body: {
        credential: payload.credential,
        idToken: payload.credential,
        remember,
        rememberMe: remember,
      },
    });
    const session = resolveAuthSessionFromResponse(response, remember);
    if (session) {
      await ensureSellerVendorProfile(session.user);
      return session;
    }
    return await resolveAuthSessionOrThrow(response, remember);
  },

  async googleSignup(payload: {
    credential: string;
    name?: string;
    phone?: string;
    primaryDeliveryAddress?: string;
    role: Exclude<MarketplaceRole, "admin"> | MarketplaceRole;
    agree?: boolean;
    remember?: boolean;
  }): Promise<AuthSession> {
    const remember = Boolean(payload.remember);
    const primaryDeliveryAddress = payload.primaryDeliveryAddress?.trim();
    const response = await request("/auth/google/signup", {
      method: "POST",
      forceRefresh: true,
      skipAuth: true,
      preserveEnvelope: true,
      body: {
        credential: payload.credential,
        idToken: payload.credential,
        name: payload.name?.trim(),
        fullName: payload.name?.trim(),
        phone: payload.phone?.trim(),
        phoneNumber: payload.phone?.trim(),
        primaryDeliveryAddress,
        address: primaryDeliveryAddress,
        addressLine1: primaryDeliveryAddress,
        role: payload.role,
        accountType: payload.role,
        userRole: payload.role,
        agreeToTerms: Boolean(payload.agree),
        termsAccepted: Boolean(payload.agree),
        acceptedTerms: Boolean(payload.agree),
        remember,
        rememberMe: remember,
      },
    });
    const session = resolveAuthSessionFromResponse(response, remember);
    if (session) {
      await ensureSellerVendorProfile(session.user);
      return session;
    }
    return await resolveAuthSessionOrThrow(response, remember);
  },

  async register(payload: {
    name: string;
    email: string;
    password: string;
    phone: string;
    primaryDeliveryAddress?: string;
    role: Exclude<MarketplaceRole, "admin"> | MarketplaceRole;
    agree?: boolean;
  }) {
    const primaryDeliveryAddress = payload.primaryDeliveryAddress?.trim();
    const buyerAddress =
      payload.role === "buyer" && primaryDeliveryAddress
        ? {
            primaryDeliveryAddress,
            address: primaryDeliveryAddress,
            addressLine1: primaryDeliveryAddress,
          }
        : {};

    return request<AuthSession | UserView>("/auth/signup", {
      method: "POST",
      body: {
        name: payload.name,
        fullName: payload.name,
        email: payload.email,
        password: payload.password,
        phone: payload.phone,
        phoneNumber: payload.phone,
        ...buyerAddress,
        role: payload.role,
        accountType: payload.role,
        userRole: payload.role,
        agreeToTerms: Boolean(payload.agree),
        termsAccepted: Boolean(payload.agree),
        acceptedTerms: Boolean(payload.agree),
      },
    });
  },

  async verifyEmail(payload: {
    token: string;
  }): Promise<AuthSession | UserView> {
    const response = await request<AuthSession | UserView>(
      "/auth/verify-email",
      {
        method: "POST",
        body: payload,
      },
    );
    const session = normalizeAuthSession(response);

    if (hasUsableAuthSession(session)) {
      rememberAuthSession(session, false);
      return session;
    }

    return response;
  },

  async resendEmailVerification(payload: { email: string }) {
    const email = payload.email.trim().toLowerCase();
    if (!email) {
      throw new AutopartsApiError("Email is required", 400, { field: "email" }, "email");
    }

    return request("/auth/verify-email/resend", {
      method: "POST",
      skipAuth: true,
      body: {
        email,
        identifier: email,
      },
    });
  },

  async getCurrentUser(
    _payload?: unknown,
    options?: { forceRefresh?: boolean },
  ) {
    // An anonymous visitor has no stored access token. Do not issue /auth/me
    // during login-page loading: a 401 is expected, but would flood the console
    // when both route loaders and UI components independently probe the session.
    // Logged-in sessions still refresh against the backend and handle 401 normally.
    if (!readStoredAuthToken()) {
      return readStoredAuthSession();
    }

    const response = await request("/auth/me", {
      method: "GET",
      forceRefresh: options?.forceRefresh,
    });
    const session = normalizeAuthSession(response);

    if (hasUsableAuthSession(session)) {
      return rememberAuthSession(session, true) ?? session;
    }

    return response;
  },

  async forgotPassword(payload: { email: string }) {
    return request("/auth/forgot-password", {
      method: "POST",
      body: {
        email: payload.email,
        identifier: payload.email,
      },
    });
  },

  async resetPassword(payload: {
    token: string;
    password: string;
    confirmPassword?: string;
  }) {
    const response = await request("/auth/reset-password", {
      method: "POST",
      body: {
        token: payload.token,
        password: payload.password,
        newPassword: payload.password,
        confirmPassword: payload.confirmPassword ?? payload.password,
      },
    });
    const session = normalizeAuthSession(response);

    if (hasUsableAuthSession(session)) {
      rememberAuthSession(session, false);
      return session;
    }

    return response;
  },

  async changePassword(payload: {
    token?: string;
    userId?: string;
    email?: string;
    currentPassword?: string;
    password: string;
    confirmPassword?: string;
  }): Promise<AuthSession | unknown> {
    const response = await request("/auth/change-password", {
      method: "POST",
      body: {
        token: payload.token ?? readStoredAuthToken() ?? undefined,
        accessToken: payload.token ?? readStoredAuthToken() ?? undefined,
        userId: payload.userId,
        user_id: payload.userId,
        email: payload.email,
        identifier: payload.email,
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
        oldPassword: payload.currentPassword,
        password: payload.password,
        newPassword: payload.password,
        new_password: payload.password,
        confirmPassword: payload.confirmPassword ?? payload.password,
        confirm_password: payload.confirmPassword ?? payload.password,
      },
    });
    const session = normalizeAuthSession(response);

    if (hasUsableAuthSession(session)) {
      rememberAuthSession(session, false);
      return session;
    }

    return response;
  },

  async getTwoFactorSettings(): Promise<TwoFactorSettings> {
    const response = await request("/auth/2fa/settings", {
      method: "GET",
      forceRefresh: true,
      preserveEnvelope: true,
    });
    return normalizeTwoFactorSettings(response);
  },

  async updateBackupEmail(payload: {
    currentPassword: string;
    backupEmail?: string;
    backupEmails?: string[];
  }): Promise<TwoFactorSettings> {
    const response = await request("/auth/2fa/backup-email", {
      method: "PUT",
      preserveEnvelope: true,
      body: {
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
        backupEmail: payload.backupEmail,
        backup_email: payload.backupEmail,
        backupEmails: payload.backupEmails,
        backup_emails: payload.backupEmails,
      },
    });
    return normalizeTwoFactorSettings(response);
  },

  async generateTwoFactorBackupCodes(payload: { currentPassword: string }): Promise<BackupCodes> {
    const response = await request<BackupCodes>("/auth/2fa/backup-codes", {
      method: "POST",
      preserveEnvelope: false,
      body: { currentPassword: payload.currentPassword, current_password: payload.currentPassword },
    });
    return response;
  },

  async viewTwoFactorBackupCodes(payload: { currentPassword: string }): Promise<BackupCodes> {
    const response = await request<BackupCodes>("/auth/2fa/backup-codes/view", {
      method: "POST",
      preserveEnvelope: false,
      body: { currentPassword: payload.currentPassword, current_password: payload.currentPassword },
    });
    return response;
  },

  async enableEmailTwoFactor(payload: {
    currentPassword: string;
  }): Promise<TwoFactorSettings> {
    const response = await request("/auth/2fa/email/enable", {
      method: "POST",
      preserveEnvelope: true,
      body: {
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
      },
    });
    return normalizeTwoFactorSettings(response);
  },

  async disableEmailTwoFactor(payload: {
    currentPassword: string;
  }): Promise<TwoFactorSettings> {
    const response = await request("/auth/2fa/email/disable", {
      method: "POST",
      preserveEnvelope: true,
      body: {
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
      },
    });
    return normalizeTwoFactorSettings(response);
  },

  async beginAuthenticatorSetup(payload: {
    currentPassword: string;
  }): Promise<AuthenticatorSetup> {
    const response = await request("/auth/2fa/authenticator/setup", {
      method: "POST",
      preserveEnvelope: true,
      body: {
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
      },
    });
    return normalizeAuthenticatorSetup(response);
  },

  async confirmAuthenticatorSetup(payload: {
    currentPassword: string;
    setupToken: string;
    code: string;
  }): Promise<TwoFactorSettings> {
    const response = await request("/auth/2fa/authenticator/confirm", {
      method: "POST",
      preserveEnvelope: true,
      body: {
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
        setupToken: payload.setupToken,
        setup_token: payload.setupToken,
        code: payload.code,
        otp: payload.code,
      },
    });
    return normalizeTwoFactorSettings(response);
  },

  async disableAuthenticator(payload: {
    currentPassword: string;
    code: string;
  }): Promise<TwoFactorSettings> {
    const response = await request("/auth/2fa/authenticator/disable", {
      method: "POST",
      preserveEnvelope: true,
      body: {
        currentPassword: payload.currentPassword,
        current_password: payload.currentPassword,
        code: payload.code,
        otp: payload.code,
      },
    });
    return normalizeTwoFactorSettings(response);
  },

  async verifyTwoFactor(payload: {
    challengeToken: string;
    method: "email" | "authenticator" | "backup_code" | "backup_email";
    code: string;
    remember?: boolean;
  }): Promise<AuthSession> {
    const response = await request("/auth/2fa/verify", {
      method: "POST",
      forceRefresh: true,
      skipAuth: true,
      preserveEnvelope: true,
      body: {
        challengeToken: payload.challengeToken,
        challenge_token: payload.challengeToken,
        method: payload.method,
        code: payload.code,
        otp: payload.code,
      },
    });
    const session = normalizeAuthSession(response);
    if (session?.requiresTwoFactor || hasUsableAuthSession(session)) {
      if (!session.requiresTwoFactor) rememberAuthSession(session, Boolean(payload.remember));
      return session;
    }
    return resolveAuthSessionOrThrow(response, Boolean(payload.remember));
  },

  async resendTwoFactorEmail(payload: {
    challengeToken: string;
  }): Promise<AuthSession> {
    const response = await request("/auth/2fa/resend-email", {
      method: "POST",
      forceRefresh: true,
      skipAuth: true,
      preserveEnvelope: true,
      body: {
        challengeToken: payload.challengeToken,
        challenge_token: payload.challengeToken,
      },
    });
    const session = normalizeAuthSession(response);
    if (session?.requiresTwoFactor && session.twoFactorChallengeToken) {
      return session;
    }
    throw new AutopartsApiError(
      "Unable to send a new two-factor authentication code.",
      0,
      response,
    );
  },

  async sendTwoFactorBackupEmail(payload: { challengeToken: string; backupEmail: string }): Promise<AuthSession> {
    const response = await request("/auth/2fa/send-backup-email", {
      method: "POST",
      forceRefresh: true,
      skipAuth: true,
      preserveEnvelope: true,
      body: { challengeToken: payload.challengeToken, challenge_token: payload.challengeToken, backupEmail: payload.backupEmail, backup_email: payload.backupEmail },
    });
    const session = normalizeAuthSession(response);
    if (session?.requiresTwoFactor && session.twoFactorChallengeToken) return session;
    throw new AutopartsApiError("Unable to send a backup email verification code.", 0, response);
  },

  async logout() {
    try {
      return await request("/auth/logout", { method: "POST" });
    } finally {
      clearStoredAuthSession();
    }
  },
};

type CredentialUploadFolder =
  | "courier-credentials"
  | "seller-credentials"
  | "quality-assessor-credentials";

type CredentialUploadSessionView = {
  uploadId: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedBytes: number;
  remainingBytes: number;
  percent: number;
  remainingPercent: number;
  uploadedChunks: number[];
};

const CREDENTIAL_UPLOAD_CHUNK_BYTES = 512 * 1024;

function credentialUploadEndpoint(
  folder: CredentialUploadFolder,
  action: "initiate" | "status" | "chunk" | "complete" | "abort",
) {
  return `/users/${folder}/uploads/${action}`;
}

function normalizeCredentialUploadSession(
  value: unknown,
): CredentialUploadSessionView {
  const record = readRecord(unwrapEnvelope(value)) ?? {};
  const uploadId = getFirstString(record, ["uploadId", "upload_id"]);
  const fileSize = getFirstNumber(record, ["fileSize", "file_size"]) ?? 0;
  const chunkSize = getFirstNumber(record, ["chunkSize", "chunk_size"]) ?? 0;
  const totalChunks = getFirstNumber(record, ["totalChunks", "total_chunks"]) ?? 0;
  const uploadedBytes =
    getFirstNumber(record, ["uploadedBytes", "uploaded_bytes"]) ?? 0;
  const remainingBytes =
    getFirstNumber(record, ["remainingBytes", "remaining_bytes"]) ??
    Math.max(0, fileSize - uploadedBytes);
  const percent = getFirstNumber(record, ["percent"]) ?? 0;
  const remainingPercent =
    getFirstNumber(record, ["remainingPercent", "remaining_percent"]) ??
    Math.max(0, 100 - percent);
  const uploadedChunksValue =
    record.uploadedChunks ?? record.uploaded_chunks;
  const uploadedChunks = Array.isArray(uploadedChunksValue)
    ? uploadedChunksValue
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item >= 0)
    : [];

  if (!uploadId || fileSize <= 0 || chunkSize <= 0 || totalChunks <= 0) {
    throw new AutopartsApiError(
      "The credential upload session response is incomplete.",
      0,
      value,
    );
  }

  return {
    uploadId,
    fileSize,
    chunkSize,
    totalChunks,
    uploadedBytes,
    remainingBytes,
    percent: Math.max(0, Math.min(100, Math.round(percent))),
    remainingPercent: Math.max(
      0,
      Math.min(100, Math.round(remainingPercent)),
    ),
    uploadedChunks,
  };
}

function normalizeCredentialUploadView(value: unknown): CredentialUploadView {
  const record = readRecord(unwrapEnvelope(value));
  const url = getFirstString(record ?? {}, [
    "url",
    "imageUrl",
    "image_url",
    "publicUrl",
    "public_url",
  ]);

  if (!url) {
    throw new AutopartsApiError(
      "The credential image upload did not return a URL.",
      0,
      value,
    );
  }

  return {
    url,
    key: getFirstString(record ?? {}, ["key", "objectKey", "object_key"]),
    fileName: getFirstString(record ?? {}, ["fileName", "file_name"]),
    contentType: getFirstString(record ?? {}, [
      "contentType",
      "content_type",
    ]),
    size: getFirstNumber(record ?? {}, ["size", "sizeBytes", "size_bytes"]),
  };
}

function createCredentialUploadAbortError() {
  const error = new Error("The credential upload was cancelled.");
  error.name = "AbortError";
  return error;
}

function isResumableCredentialUploadError(error: unknown) {
  return (
    !(error instanceof AutopartsApiError) ||
    error.status === 0 ||
    error.status >= 500
  );
}

function createCredentialImageUploadTask(
  userId: string,
  file: File,
  folder: CredentialUploadFolder,
  options: CredentialUploadTaskOptions = {},
): CredentialUploadTask {
  let uploadId = "";
  let paused = false;
  let cancelled = false;
  let currentState: CredentialUploadTaskState = "preparing";
  let activeController: AbortController | null = null;
  let resumeUpload: (() => void) | null = null;

  const emitState = (state: CredentialUploadTaskState, error?: unknown) => {
    currentState = state;
    options.onStateChange?.(state, error);
  };

  const emitProgress = (
    loaded: number,
    total: number,
    phase: ApiUploadProgressPhase,
    uploadedChunks: number,
    totalChunks: number,
  ) => {
    const boundedLoaded = Math.max(0, Math.min(total, loaded));
    const percent = total > 0 ? Math.round((boundedLoaded / total) * 100) : 0;
    options.onUploadProgress?.({
      loaded: boundedLoaded,
      total,
      percent,
      remainingPercent: Math.max(0, 100 - percent),
      lengthComputable: total > 0,
      phase,
      uploadedChunks,
      totalChunks,
    });
  };

  const waitUntilResumed = async () => {
    while (paused && !cancelled) {
      await new Promise<void>((resolve) => {
        resumeUpload = resolve;
      });
      resumeUpload = null;
    }
    if (cancelled) throw createCredentialUploadAbortError();
  };

  const readStatus = async () =>
    normalizeCredentialUploadSession(
      await request(
        `${credentialUploadEndpoint(folder, "status")}?${new URLSearchParams({ uploadId })}`,
        { forceRefresh: true },
      ),
    );

  const promise = (async () => {
    if (typeof FormData === "undefined") {
      throw new AutopartsApiError(
        "File uploads are not supported in this browser session.",
        0,
      );
    }

    emitState("preparing");
    emitProgress(
      0,
      file.size,
      "preparing",
      0,
      Math.ceil(file.size / CREDENTIAL_UPLOAD_CHUNK_BYTES),
    );

    let session = normalizeCredentialUploadSession(
      await request(credentialUploadEndpoint(folder, "initiate"), {
        method: "POST",
        body: {
          userId,
          ownerId: userId,
          fileName: file.name || "credential-image",
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
          chunkSize: CREDENTIAL_UPLOAD_CHUNK_BYTES,
        },
      }),
    );
    uploadId = session.uploadId;
    emitState(paused ? "paused" : "uploading");
    emitProgress(
      session.uploadedBytes,
      session.fileSize,
      "uploading",
      session.uploadedChunks.length,
      session.totalChunks,
    );

    for (let chunkIndex = 0; chunkIndex < session.totalChunks; chunkIndex += 1) {
      while (!session.uploadedChunks.includes(chunkIndex)) {
        await waitUntilResumed();
        const chunkStart = chunkIndex * session.chunkSize;
        const chunkEnd = Math.min(file.size, chunkStart + session.chunkSize);
        const chunk = file.slice(chunkStart, chunkEnd);
        const formData = new FormData();
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", String(chunkIndex));
        formData.append("chunk", chunk, `${file.name || "credential-image"}.part-${chunkIndex}`);
        const controller = new AbortController();
        activeController = controller;

        try {
          const response = await requestWithUploadProgress(
            credentialUploadEndpoint(folder, "chunk"),
            {
              method: "POST",
              body: formData,
              signal: controller.signal,
              uploadTotalBytes: chunk.size,
              onUploadProgress: (chunkProgress) => {
                const chunkLoaded = Math.min(
                  chunk.size,
                  Math.max(0, chunkProgress.loaded),
                );
                emitProgress(
                  session.uploadedBytes + chunkLoaded,
                  session.fileSize,
                  "uploading",
                  session.uploadedChunks.length,
                  session.totalChunks,
                );
              },
            },
          );
          session = normalizeCredentialUploadSession(response);
          emitProgress(
            session.uploadedBytes,
            session.fileSize,
            "uploading",
            session.uploadedChunks.length,
            session.totalChunks,
          );
        } catch (error) {
          if (cancelled) throw createCredentialUploadAbortError();
          if (
            !paused &&
            (!isResumableCredentialUploadError(error) ||
              options.pauseOnRecoverableError === false)
          ) {
            throw error;
          }
          paused = true;
          emitState("paused", error);

          while (true) {
            await waitUntilResumed();
            try {
              session = await readStatus();
              break;
            } catch (statusError) {
              if (!isResumableCredentialUploadError(statusError)) {
                throw statusError;
              }
              paused = true;
              emitState("paused", statusError);
            }
          }

          emitState("uploading");
          emitProgress(
            session.uploadedBytes,
            session.fileSize,
            "uploading",
            session.uploadedChunks.length,
            session.totalChunks,
          );
        } finally {
          if (activeController === controller) activeController = null;
        }
      }
    }

    await waitUntilResumed();
    emitState("processing");
    emitProgress(
      session.fileSize,
      session.fileSize,
      "processing",
      session.totalChunks,
      session.totalChunks,
    );
    const completed = await request(
      credentialUploadEndpoint(folder, "complete"),
      {
        method: "POST",
        body: { uploadId },
      },
    );
    const upload = normalizeCredentialUploadView(completed);
    emitProgress(
      session.fileSize,
      session.fileSize,
      "complete",
      session.totalChunks,
      session.totalChunks,
    );
    emitState("complete");
    return upload;
  })().catch(async (error) => {
    if (cancelled && uploadId) {
      try {
        await request(credentialUploadEndpoint(folder, "abort"), {
          method: "POST",
          body: { uploadId },
        });
      } catch {
        // The local task is already cancelled even if backend cleanup fails.
      }
      emitState("cancelled", error);
    }
    throw error;
  });

  return {
    promise,
    pause: () => {
      if (
        cancelled ||
        paused ||
        (currentState !== "preparing" && currentState !== "uploading")
      ) {
        return;
      }
      paused = true;
      activeController?.abort();
      emitState("paused");
    },
    resume: () => {
      if (cancelled || !paused) return;
      paused = false;
      resumeUpload?.();
      emitState("uploading");
    },
    cancel: () => {
      if (cancelled) return;
      cancelled = true;
      paused = false;
      activeController?.abort();
      resumeUpload?.();
    },
    isPaused: () => paused,
  };
}

const users = {
  async list<T = PageResult<UserView>>(query?: PageQuery): Promise<T> {
    return requestPageResultFromPaths(
      ["/users", "/admin/users"],
      query,
      normalizeUserView,
      10,
    ) as Promise<T>;
  },

  async getById<T = UserView>(id: string): Promise<T> {
    const response = await request(`/users/${encodeURIComponent(id)}`);
    return normalizeSingle(response, normalizeUserView) as T;
  },

  async create<T = UserView>(payload: Record<string, unknown>): Promise<T> {
    const response = await request("/users", { method: "POST", body: payload });
    return normalizeSingle(response, normalizeUserView) as T;
  },

  async update<T = UserView>(
    id: string,
    payload: Record<string, unknown>,
  ): Promise<T> {
    const response = await request(`/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: payload,
    });
    return normalizeSingle(response, normalizeUserView) as T;
  },

  async delete(id: string) {
    return request(`/users/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async resendCredentials<T = unknown>(id: string): Promise<T> {
    return request(`/users/${encodeURIComponent(id)}/resend-credentials`, {
      method: "POST",
    });
  },

  async uploadAvatar(
    file: File,
    ownerId?: string | null,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
  ): Promise<CredentialUploadView> {
    const formData = new FormData();
    formData.append("file", file, file.name || "avatar.webp");
    formData.append("fileName", file.name || "avatar.webp");
    formData.append("contentType", file.type || "image/webp");
    if (ownerId?.trim()) {
      formData.append("ownerId", ownerId.trim());
    }

    const response = await requestWithUploadProgress("/users/avatar/upload", {
      method: "POST",
      body: formData,
      uploadTotalBytes: file.size,
      onUploadProgress,
    });
    return normalizeCredentialUploadView(response);
  },

  startCourierCredentialImageUpload(
    userId: string,
    file: File,
    options?: CredentialUploadTaskOptions,
  ): CredentialUploadTask {
    return createCredentialImageUploadTask(
      userId,
      file,
      "courier-credentials",
      options,
    );
  },

  async uploadCourierCredentialImage(
    userId: string,
    file: File,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
  ): Promise<CredentialUploadView> {
    return createCredentialImageUploadTask(
      userId,
      file,
      "courier-credentials",
      { onUploadProgress, pauseOnRecoverableError: false },
    ).promise;
  },

  startSellerCredentialImageUpload(
    userId: string,
    file: File,
    options?: CredentialUploadTaskOptions,
  ): CredentialUploadTask {
    return createCredentialImageUploadTask(
      userId,
      file,
      "seller-credentials",
      options,
    );
  },

  async uploadSellerCredentialImage(
    userId: string,
    file: File,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
  ): Promise<CredentialUploadView> {
    return createCredentialImageUploadTask(
      userId,
      file,
      "seller-credentials",
      { onUploadProgress, pauseOnRecoverableError: false },
    ).promise;
  },

  startQualityAssessorCredentialImageUpload(
    userId: string,
    file: File,
    options?: CredentialUploadTaskOptions,
  ): CredentialUploadTask {
    return createCredentialImageUploadTask(userId, file, "quality-assessor-credentials", options);
  },

  async uploadQualityAssessorCredentialImage(
    userId: string,
    file: File,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
  ): Promise<CredentialUploadView> {
    return createCredentialImageUploadTask(
      userId,
      file,
      "quality-assessor-credentials",
      { onUploadProgress, pauseOnRecoverableError: false },
    ).promise;
  },
};

const products = {
  async list<T = PageResult<ProductView>>(query?: PageQuery): Promise<T> {
    const cacheKey = buildProductListCacheKey(query);
    const cached = productListCache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
      return cached.value as T;
    }

    const pending = productListRequests.get(cacheKey);
    if (pending) {
      return (await pending) as T;
    }

    const requestPromise = (async () => {
      const result = await requestPageResultFromPaths(
        ["/products"],
        query,
        normalizeProductView,
        12,
      );
      const filtered = filterProductPageResultByQuery(result, query);
      productListCache.set(cacheKey, {
        expiresAt: Date.now() + PRODUCT_READ_CACHE_TTL_MS,
        value: filtered,
      });
      return filtered;
    })();

    productListRequests.set(cacheKey, requestPromise);

    try {
      return (await requestPromise) as T;
    } finally {
      productListRequests.delete(cacheKey);
    }
  },

  async getById<T = ProductView>(id: string): Promise<T> {
    const normalizedId = id.trim();
    const cacheKey = buildProductDetailCacheKey(normalizedId);
    const cached = productDetailCache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
      return cached.value as T;
    }

    const pending = productDetailRequests.get(cacheKey);
    if (pending) {
      return (await pending) as T;
    }

    const requestPromise = (async () => {
      const response = await request(
        `/products/${encodeURIComponent(normalizedId)}`,
      );
      const normalized = normalizeSingle(response, normalizeProductView);
      productDetailCache.set(cacheKey, {
        expiresAt: Date.now() + PRODUCT_READ_CACHE_TTL_MS,
        value: normalized,
      });
      return normalized;
    })();

    productDetailRequests.set(cacheKey, requestPromise);

    try {
      return (await requestPromise) as T;
    } finally {
      productDetailRequests.delete(cacheKey);
    }
  },

  async listFitments(id: string): Promise<FitmentView[]> {
    const response = await request(
      `/products/${encodeURIComponent(id.trim())}/fitments?page=1&pageSize=100`,
      { forceRefresh: true },
    );
    const unwrapped = unwrapEnvelope(response);
    const record = readRecord(unwrapped);
    const items = Array.isArray(unwrapped)
      ? unwrapped
      : Array.isArray(record?.items)
        ? record.items
        : [];

    return items
      .map((value) => {
        const item = readRecord(value);
        if (!item) return null;
        const productId = getFirstString(item, ["productId", "product_id"]);
        const make = getFirstString(item, ["make", "vehicleMake", "vehicle_make"]);
        const model = getFirstString(item, ["model", "vehicleModel", "vehicle_model"]);
        const year = getFirstNumber(item, ["year", "modelYear", "model_year"]);
        if (!productId || !make || !model || !year) return null;
        return {
          ...item,
          id: getFirstString(item, ["id", "_id"]) ?? `${productId}-${make}-${model}-${year}`,
          productId,
          productDisplayName: getFirstString(item, ["productDisplayName", "product_display_name"]),
          make,
          model,
          year,
          trim: getFirstString(item, ["trim", "variant"]),
          engine: getFirstString(item, ["engine", "engineType", "engine_type"]),
          confidence: getFirstString(item, ["confidence"]),
          compatibilityLabel: getFirstString(item, ["compatibilityLabel", "compatibility_label"]),
          notes: getFirstString(item, ["notes"]),
          createdAt: getFirstString(item, ["createdAt", "created_at"]),
          updatedAt: getFirstString(item, ["updatedAt", "updated_at"]),
        } satisfies FitmentView;
      })
      .filter((item): item is FitmentView => Boolean(item));
  },

  async create<T = ProductView>(
    payload: Record<string, unknown>,
    options: Omit<RequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    const response = await request("/products", {
      ...options,
      method: "POST",
      body: payload,
    });
    invalidateProductReadCache();
    return normalizeSingle(response, normalizeProductView) as T;
  },

  async update<T = ProductView>(
    id: string,
    payload: Record<string, unknown>,
    options: Omit<RequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    const normalizedId = id.trim();
    const response = await request(
      `/products/${encodeURIComponent(normalizedId)}`,
      {
        ...options,
        method: "PATCH",
        body: payload,
      },
    );
    invalidateProductReadCache(normalizedId);
    return normalizeSingle(response, normalizeProductView) as T;
  },

  async adjustStock<T = ProductView>(
    id: string,
    delta: number,
    options: Omit<RequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    if (!Number.isInteger(delta) || delta === 0) {
      throw new AutopartsApiError(
        "Stock adjustment must be a non-zero whole number.",
        400,
        { delta },
      );
    }

    const normalizedId = id.trim();
    const response = await request(
      `/products/${encodeURIComponent(normalizedId)}/stock`,
      {
        ...options,
        method: "PATCH",
        body: { delta },
      },
    );
    invalidateProductReadCache(normalizedId);
    return normalizeSingle(response, normalizeProductView) as T;
  },

  async uploadImage<T = unknown>(
    payload: Record<string, unknown>,
    options: Omit<UploadRequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    return requestWithUploadProgress<T>("/products/upload-image", {
      ...options,
      method: "POST",
      body: payload,
    });
  },

  async delete(id: string) {
    const normalizedId = id.trim();
    const response = await request(
      `/products/${encodeURIComponent(normalizedId)}`,
      { method: "DELETE" },
    );
    invalidateProductReadCache(normalizedId);
    return response;
  },
};

function normalizeStripePaymentConfig(value: unknown): StripePaymentConfigView {
  const record = readRecord(unwrapEnvelope(value)) ?? {};

  return {
    ...record,
    enabled:
      getBoolean(
        getRecordProperty(record, [
          "enabled",
          "isEnabled",
          "is_enabled",
          "configured",
        ]),
      ) ?? false,
    publishableKey: getFirstString(record, [
      "publishableKey",
      "publishable_key",
      "stripePublishableKey",
    ]),
    currency: getFirstString(record, ["currency"]) ?? "usd",
  };
}

function isCheckoutJsonCompatibilityError(error: unknown): boolean {
  if (!(error instanceof AutopartsApiError)) {
    return false;
  }

  const message = String(error.message ?? "").toLowerCase();
  const field = String(error.field ?? "").toLowerCase();

  return (
    error.status === 400 &&
    (message.includes("valid json") ||
      message.includes("unknown field") ||
      message.includes("cannot unmarshal") ||
      field === "body")
  );
}

function normalizeCheckoutAddressCoordinate(
  value: unknown,
  minimum: number,
  maximum: number,
): number | undefined {
  const coordinate = getNumber(value);

  if (
    coordinate === undefined ||
    coordinate < minimum ||
    coordinate > maximum
  ) {
    return undefined;
  }

  return coordinate;
}

function setCheckoutPayloadValue(
  target: Record<string, unknown>,
  key: string,
  value: unknown,
) {
  if (value !== undefined && value !== null) {
    target[key] = value;
  }
}

function normalizeCheckoutOrderAddress(
  address: OrderAddressPayload | undefined,
): OrderAddressPayload | undefined {
  if (!address) return undefined;

  const addressRecord = address as Record<string, unknown>;
  const nestedLocation = readRecord(
    getRecordProperty(addressRecord, [
      "location",
      "mapLocation",
      "map_location",
      "coordinates",
      "position",
    ]),
  );
  const latitude =
    normalizeCheckoutAddressCoordinate(
      getRecordProperty(addressRecord, ["latitude", "lat"]),
      -90,
      90,
    ) ??
    normalizeCheckoutAddressCoordinate(
      nestedLocation
        ? getRecordProperty(nestedLocation, ["latitude", "lat"])
        : undefined,
      -90,
      90,
    );
  const longitude =
    normalizeCheckoutAddressCoordinate(
      getRecordProperty(addressRecord, ["longitude", "lng", "lon"]),
      -180,
      180,
    ) ??
    normalizeCheckoutAddressCoordinate(
      nestedLocation
        ? getRecordProperty(nestedLocation, ["longitude", "lng", "lon"])
        : undefined,
      -180,
      180,
    );
  const locationLabel =
    getFirstString(addressRecord, [
      "locationLabel",
      "location_label",
      "mapLocationLabel",
      "map_location_label",
    ]) ||
    (nestedLocation
      ? getFirstString(nestedLocation, ["label", "locationLabel"])
      : undefined);
  const normalizedAddress: Record<string, unknown> = {};

  setCheckoutPayloadValue(
    normalizedAddress,
    "firstName",
    getFirstString(addressRecord, ["firstName", "first_name"]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "lastName",
    getFirstString(addressRecord, ["lastName", "last_name"]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "email",
    getFirstString(addressRecord, ["email"]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "phone",
    getFirstString(addressRecord, ["phone", "phoneNumber", "phone_number"]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "addressLine1",
    getFirstString(addressRecord, [
      "addressLine1",
      "address_line_1",
      "address",
      "streetAddress",
      "street_address",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "addressLine2",
    getFirstString(addressRecord, [
      "addressLine2",
      "address_line_2",
      "unit",
      "apartment",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "city",
    getFirstString(addressRecord, ["city", "locality"]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "state",
    getFirstString(addressRecord, [
      "state",
      "province",
      "region",
      "administrativeArea",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "postalCode",
    getFirstString(addressRecord, [
      "postalCode",
      "postal_code",
      "zipCode",
      "zip_code",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "country",
    getFirstString(addressRecord, ["country", "countryName"]),
  );
  setCheckoutPayloadValue(
    normalizedAddress,
    "additionalLocationInfo",
    getFirstString(addressRecord, [
      "additionalLocationInfo",
      "additional_location_info",
    ]),
  );

  if (latitude !== undefined) {
    normalizedAddress.latitude = latitude;
    normalizedAddress.lat = latitude;
  }
  if (longitude !== undefined) {
    normalizedAddress.longitude = longitude;
    normalizedAddress.lng = longitude;
  }
  if (locationLabel) {
    normalizedAddress.locationLabel = locationLabel;
  }
  if (latitude !== undefined && longitude !== undefined) {
    normalizedAddress.location = {
      latitude,
      longitude,
      lat: latitude,
      lng: longitude,
      ...(locationLabel ? { label: locationLabel } : {}),
    };
  }

  return normalizedAddress as OrderAddressPayload;
}

function normalizeCheckoutOrderItem(
  item: CreateCheckoutOrderPayload["items"][number],
): CreateCheckoutOrderPayload["items"][number] {
  const record = item as Record<string, unknown>;
  const normalizedItem: Record<string, unknown> = {};

  setCheckoutPayloadValue(
    normalizedItem,
    "productId",
    getFirstString(record, ["productId", "product_id", "id"]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "productName",
    getFirstString(record, ["productName", "product_name", "title", "name"]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "productImage",
    getFirstString(record, [
      "productImage",
      "product_image",
      "imageUrl",
      "image_url",
      "image",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "vendorId",
    getFirstString(record, ["vendorId", "vendor_id", "sellerId", "seller_id"]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "productNumber",
    getFirstString(record, [
      "productNumber",
      "product_number",
      "sparelynkProductNumber",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "partNumber",
    getFirstString(record, [
      "partNumber",
      "part_number",
      "manufacturerPartNumber",
      "mpn",
    ]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "sku",
    getFirstString(record, ["sku", "SKU"]),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "quantity",
    getNumber(getRecordProperty(record, ["quantity", "qty"])),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "unitPrice",
    getNumber(getRecordProperty(record, ["unitPrice", "unit_price", "price"])),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "extraProtectionSelected",
    getBoolean(
      getRecordProperty(record, [
        "extraProtectionSelected",
        "extra_protection_selected",
      ]),
    ),
  );
  setCheckoutPayloadValue(
    normalizedItem,
    "extraProtectionFee",
    getNumber(
      getRecordProperty(record, ["extraProtectionFee", "extra_protection_fee"]),
    ),
  );

  return normalizedItem as CreateCheckoutOrderPayload["items"][number];
}

function normalizeCheckoutOrderPayload<T extends CreateCheckoutOrderPayload>(
  payload: T,
): T {
  const source = payload as Record<string, unknown>;
  const normalizedPayload: Record<string, unknown> = {
    items: Array.isArray(payload.items)
      ? payload.items.map(normalizeCheckoutOrderItem)
      : [],
  };
  const stringFields = [
    "customerId",
    "vendorId",
    "orderNumber",
    "status",
    "shippingPayer",
    "freeShippingReason",
    "currency",
    "paymentMethod",
    "paymentStatus",
    "deliveryMethod",
    "deliveryMode",
    "courierId",
    "courierName",
    "courierEmail",
    "trackingNumber",
    "pickupLocation",
    "dropoffLocation",
    "estimatedDeliveryAt",
    "rendezvousLocation",
    "notes",
    "successUrl",
    "cancelUrl",
  ];
  const numberFields = [
    "subtotal",
    "shippingFee",
    "buyerShippingFee",
    "freeShippingQuantityThreshold",
    "tax",
    "discount",
    "total",
    "courierFeePercent",
    "platformFeePercent",
  ];

  for (const key of stringFields) {
    setCheckoutPayloadValue(normalizedPayload, key, getString(source[key]));
  }
  for (const key of numberFields) {
    setCheckoutPayloadValue(normalizedPayload, key, getNumber(source[key]));
  }
  setCheckoutPayloadValue(
    normalizedPayload,
    "freeShippingApplied",
    getBoolean(source.freeShippingApplied),
  );

  const shippingAddress = normalizeCheckoutOrderAddress(
    payload.shippingAddress,
  );
  const billingAddress = normalizeCheckoutOrderAddress(payload.billingAddress);
  if (shippingAddress) normalizedPayload.shippingAddress = shippingAddress;
  if (billingAddress) normalizedPayload.billingAddress = billingAddress;

  return normalizedPayload as T;
}

function normalizeCheckoutPaymentMethod(value: unknown): string | undefined {
  const normalized = getString(value)
    ?.toLowerCase()
    .replace(/[\s-]+/g, "_");

  switch (normalized) {
    case "online_payment":
    case "card":
    case "stripe_checkout":
      return "stripe";
    case "stripe":
      return "stripe";
    case "cash_on_delivery":
    case "cod":
      return "cash_on_delivery";
    case "pickup":
    case "pick_up":
      return "pickup";
    case "wallet":
    case "buyer_wallet":
    case "wallet_balance":
      return "wallet";
    default:
      return getString(value);
  }
}

function toLegacyCheckoutPayload<T extends CreateCheckoutOrderPayload>(
  payload: T,
): T {
  const rest: CreateCheckoutOrderPayload = { ...payload };
  delete rest.deliveryMethod;
  const normalizedPaymentMethod = normalizeCheckoutPaymentMethod(
    payload.paymentMethod,
  );

  return {
    ...rest,
    ...(normalizedPaymentMethod
      ? { paymentMethod: normalizedPaymentMethod }
      : {}),
  } as T;
}

function toStripeCheckoutPayload(
  payload: CreateStripeCheckoutPayload,
): CreateStripeCheckoutPayload {
  const normalizedPayload = normalizeCheckoutOrderPayload(payload);

  return {
    ...normalizedPayload,
    paymentMethod: "stripe",
    paymentStatus: payload.paymentStatus ?? "PENDING",
  };
}

function resolveReviewMediaChunkProgress(
  progress: ApiUploadProgress,
  chunkBytes: number,
): number {
  if (progress.total && progress.total > 0) {
    const transferRatio = Math.min(
      1,
      Math.max(0, progress.loaded / progress.total),
    );
    return Math.round(chunkBytes * transferRatio);
  }

  return Math.min(chunkBytes, Math.max(0, progress.loaded));
}

function sanitizeBuyerReviewMediaPayload(
  media: BuyerReviewMediaView,
): BuyerReviewMediaView | null {
  const url = String(media.url ?? "").trim();
  const key = String(media.key ?? "").trim();
  const contentType = String(media.contentType ?? "").trim();
  const fileName = String(media.fileName ?? "").trim();
  const size = Number(media.size);

  if (!url || !key || !contentType || !fileName || !Number.isFinite(size)) {
    return null;
  }

  const sources: ReturnRefundPlaybackSourceView[] = (media.sources ?? [])
    .map((source): ReturnRefundPlaybackSourceView | null => {
      const sourceUrl = String(source.url ?? "").trim();
      if (!sourceUrl) {
        return null;
      }

      const width = Number(source.width);
      const bitrate = Number(source.bitrate);

      return {
        url: sourceUrl,
        ...(source.type ? { type: String(source.type).trim() } : {}),
        ...(source.label ? { label: String(source.label).trim() } : {}),
        ...(Number.isFinite(width) && width > 0 ? { width } : {}),
        ...(Number.isFinite(bitrate) && bitrate > 0 ? { bitrate } : {}),
      };
    })
    .filter((source): source is ReturnRefundPlaybackSourceView =>
      Boolean(source),
    );

  return {
    url,
    key,
    mediaType: media.mediaType === "image" ? "image" : "video",
    contentType,
    fileName,
    size,
    ...(sources.length > 0 ? { sources } : {}),
  };
}

function sanitizeBuyerReviewMediaPayloads(
  media: CreateBuyerReviewPayload["media"],
): BuyerReviewMediaView[] | undefined {
  if (!Array.isArray(media) || media.length === 0) {
    return undefined;
  }

  const sanitized = media
    .map((item) => sanitizeBuyerReviewMediaPayload(item))
    .filter((item): item is BuyerReviewMediaView => Boolean(item));

  return sanitized.length > 0 ? sanitized : undefined;
}

function buildBuyerReviewJSONPayload(
  targetType: "product" | "shop",
  targetId: string,
  payload: CreateBuyerReviewPayload,
): Record<string, unknown> {
  const media = sanitizeBuyerReviewMediaPayloads(payload.media);
  const reviewPayload: Record<string, unknown> = {
    ...payload,
    targetType,
    targetId,
    vendorId: targetType === "shop" ? targetId : payload.vendorId || targetId,
    ...(media ? { media } : {}),
  };

  if (!media) {
    delete reviewPayload.media;
  }

  if (targetType === "product") {
    reviewPayload.productId = payload.productId || targetId;
  }

  return reviewPayload;
}

function buildBuyerReviewJSONBody(
  targetType: "product" | "shop",
  targetId: string,
  payload: CreateBuyerReviewPayload,
): string {
  return JSON.stringify(
    buildBuyerReviewJSONPayload(targetType, targetId, payload),
  );
}

const buyerReviewJSONHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
} as const;

function buildBuyerReviewFormDataPayload(
  payload: Record<string, unknown>,
): FormData {
  const formData = new FormData();

  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (typeof value === "object") {
      formData.set(key, JSON.stringify(value));
      continue;
    }

    formData.set(key, String(value));
  }

  return formData;
}

function isBuyerReviewJSONDecodeApiError(error: unknown): boolean {
  if (!(error instanceof AutopartsApiError)) {
    return false;
  }

  const message = String(error.message ?? "").toLowerCase();
  const field = String(error.field ?? "").toLowerCase();

  return (
    message.includes("valid json") ||
    message.includes("parse the request body as json") ||
    (field === "body" &&
      (message.includes("parse") || message.includes("json")))
  );
}

function getBuyerReviewActionHint(action: string): string {
  const normalizedAction = action.toLowerCase();

  if (normalizedAction.includes("initiation")) {
    return "Expected upload-initiation JSON fields: targetType, targetId, buyerId, fileName, contentType, and fileSize.";
  }

  if (normalizedAction.includes("completion")) {
    return "Expected upload-completion JSON fields: targetType, targetId, buyerId, uploadId, key, fileName, contentType, mediaType, and fileSize.";
  }

  if (normalizedAction.includes("chunk")) {
    return "Expected chunk request fields: targetType, targetId, buyerId, uploadId, key, partNumber, and a chunk file.";
  }

  if (normalizedAction.includes("product review")) {
    return "Expected product-review JSON fields: vendorId, productId, buyerId, buyerName, rating, review, and optional media.";
  }

  if (normalizedAction.includes("shop review")) {
    return "Expected shop-review JSON fields: vendorId, buyerId, buyerName, rating, review, and optional media.";
  }

  return "Check the API response field and the browser Network tab for the exact rejected value.";
}

async function requestBuyerReviewJSONWithFormFallback<T>(
  path: string,
  payload: Record<string, unknown>,
  action: string,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await request<T>(path, {
      method: "POST",
      headers: buyerReviewJSONHeaders,
      body: payload,
      signal,
    });
  } catch (error) {
    if (!isBuyerReviewJSONDecodeApiError(error)) {
      throw createBuyerReviewContextError(error, action);
    }

    try {
      return await request<T>(path, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: buildBuyerReviewFormDataPayload(payload),
        signal,
      });
    } catch (fallbackError) {
      throw createBuyerReviewContextError(fallbackError, action);
    }
  }
}

type BuyerReviewMediaPartUploadInput = {
  targetType: "product" | "shop";
  targetId: string;
  buyerId: string;
  uploadId: string;
  key: string;
  partNumber: number;
  chunk: Blob;
  fileName: string;
  onUploadProgress?: (progress: ApiUploadProgress) => void;
  signal?: AbortSignal;
};

function createBuyerReviewMediaPartFormData({
  targetType,
  targetId,
  buyerId,
  uploadId,
  key,
  partNumber,
  chunk,
  fileName,
}: BuyerReviewMediaPartUploadInput): FormData {
  const formData = new FormData();
  formData.set("targetType", targetType);
  formData.set("targetId", targetId);
  formData.set("buyerId", buyerId);
  formData.set("uploadId", uploadId);
  formData.set("key", key);
  formData.set("partNumber", String(partNumber));
  formData.set(
    "chunk",
    chunk,
    `${fileName || "review-media"}.part-${partNumber}`,
  );
  return formData;
}

function createBuyerReviewContextError(
  error: unknown,
  action: string,
): AutopartsApiError {
  if (error instanceof AutopartsApiError) {
    const originalMessage = String(error.message ?? "").trim();
    const normalizedMessage = originalMessage.toLowerCase();
    const field = String(error.field ?? "").trim();
    const statusLabel = error.status > 0 ? `HTTP ${error.status}` : "network";
    const fieldLabel = field ? ` field "${field}"` : "";
    const hint = getBuyerReviewActionHint(action);
    let message = `${action} failed (${statusLabel}${fieldLabel}): ${
      originalMessage || "The API rejected the request."
    }`;

    if (
      normalizedMessage === "must be valid json" ||
      normalizedMessage.includes("must be valid json") ||
      normalizedMessage.includes("could not parse the request body as json")
    ) {
      message = `${action} failed (${statusLabel}${fieldLabel}): the API could not parse the request body as JSON. ${hint}`;
    } else if (field) {
      message = `${action} failed (${statusLabel}) at ${field}: ${
        originalMessage || "The API rejected the request."
      } ${hint}`;
    } else {
      message = `${message}. ${hint}`;
    }

    return new AutopartsApiError(
      message.trim(),
      error.status,
      error.data,
      error.field,
    );
  }

  return new AutopartsApiError(
    error instanceof Error
      ? error.message
      : `${action} failed. Please try again.`,
    0,
    error,
  );
}

async function uploadBuyerReviewMediaPart(
  input: BuyerReviewMediaPartUploadInput,
): Promise<void> {
  try {
    await requestWithUploadProgress("/buyer-reviews/media/uploads/part", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: createBuyerReviewMediaPartFormData(input),
      signal: input.signal,
      onUploadProgress: input.onUploadProgress,
    });
  } catch (error) {
    throw createBuyerReviewContextError(
      error,
      `Review media upload chunk ${input.partNumber}`,
    );
  }
}

const reviews = {
  async listProduct(
    productId: string,
    vendorId?: string,
  ): Promise<BuyerReviewView[]> {
    const response = await request(
      `/products/${encodeURIComponent(productId)}/buyer-reviews`,
    );
    return normalizeBuyerReviews(response, {
      targetType: "product",
      targetId: productId,
      productId,
      vendorId,
    }).filter((review) => review.targetType === "product");
  },
  async createProduct(
    productId: string,
    payload: CreateBuyerReviewPayload,
  ): Promise<BuyerReviewView> {
    try {
      const response = await request(
        `/products/${encodeURIComponent(productId)}/buyer-reviews`,
        {
          method: "POST",
          headers: buyerReviewJSONHeaders,
          body: buildBuyerReviewJSONBody("product", productId, payload),
        },
      );
      const reviews = normalizeBuyerReviews(response, {
        targetType: "product",
        targetId: productId,
        productId,
        vendorId: payload.vendorId,
      }).filter((review) => review.targetType === "product");
      if (!reviews[0])
        throw new AutopartsApiError(
          "The backend returned an invalid product review.",
          0,
          response,
        );
      return reviews[0];
    } catch (error) {
      throw createBuyerReviewContextError(error, "Product review submission");
    }
  },
  async listShop(vendorId: string): Promise<BuyerReviewView[]> {
    const response = await request(
      `/shops/${encodeURIComponent(vendorId)}/reviews`,
    );
    return normalizeBuyerReviews(response, {
      targetType: "shop",
      targetId: vendorId,
      vendorId,
    }).filter((review) => review.targetType === "shop");
  },
  async listSeller(vendorId: string): Promise<BuyerReviewView[]> {
    const response = await request(
      `/vendors/${encodeURIComponent(vendorId)}/buyer-reviews`,
    );
    return normalizeBuyerReviews(response, { vendorId }).filter(
      (review) =>
        review.vendorId === vendorId ||
        (review.targetType === "shop" && review.targetId === vendorId),
    );
  },
  async createShop(
    vendorId: string,
    payload: CreateBuyerReviewPayload,
  ): Promise<BuyerReviewView> {
    try {
      const response = await request(
        `/shops/${encodeURIComponent(vendorId)}/reviews`,
        {
          method: "POST",
          headers: buyerReviewJSONHeaders,
          body: buildBuyerReviewJSONBody("shop", vendorId, payload),
        },
      );
      const reviews = normalizeBuyerReviews(response, {
        targetType: "shop",
        targetId: vendorId,
        vendorId,
      }).filter((review) => review.targetType === "shop");
      if (!reviews[0])
        throw new AutopartsApiError(
          "The backend returned an invalid shop review.",
          0,
          response,
        );
      return reviews[0];
    } catch (error) {
      throw createBuyerReviewContextError(error, "Shop review submission");
    }
  },
  async uploadMediaResumable(
    targetType: "product" | "shop",
    targetId: string,
    buyerId: string,
    file: File,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
    signal?: AbortSignal,
  ): Promise<BuyerReviewMediaView> {
    const contentType = resolveReturnEvidenceContentType(file);
    const fingerprint = `${targetType}:${targetId}:${buyerId}:${file.name}:${file.size}:${file.lastModified}`;
    const storageKey = `sparelynk-review-upload:${fingerprint}`;
    let session: ResumableUploadSessionView | null = null;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) session = JSON.parse(stored) as ResumableUploadSessionView;
    } catch {
      session = null;
    }
    const identity = { targetType, targetId, buyerId };
    if (!session?.uploadId || !session.key) {
      const initiated = await requestBuyerReviewJSONWithFormFallback(
        "/buyer-reviews/media/uploads/initiate",
        {
          ...identity,
          fileName: file.name,
          contentType,
          fileSize: file.size,
        },
        "Review media upload initiation",
        signal,
      );
      const record = readRecord(unwrapEnvelope(initiated)) ?? {};
      const uploadId = getFirstString(record, ["uploadId", "upload_id"]);
      const key = getFirstString(record, ["key"]);
      if (!uploadId || !key)
        throw new AutopartsApiError(
          "The backend did not create a resumable review-media upload.",
          0,
          initiated,
        );
      session = {
        uploadId,
        key,
        partSize:
          getFirstNumber(record, ["partSize", "part_size"]) ?? 8 * 1024 * 1024,
        mediaType:
          getFirstString(record, ["mediaType", "media_type"]) === "image"
            ? "image"
            : "video",
        contentType:
          getFirstString(record, ["contentType", "content_type"]) ||
          contentType,
        fileName:
          getFirstString(record, ["fileName", "file_name"]) || file.name,
        fileSize: file.size,
        uploadedParts: [],
      };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(session));
      } catch {
        /* optional */
      }
    }
    const statusQuery = new URLSearchParams({
      uploadId: session.uploadId,
      key: session.key,
      ...identity,
    });
    const statusResponse = await request(
      `/buyer-reviews/media/uploads/status?${statusQuery.toString()}`,
      { signal },
    ).catch((error) => {
      throw createBuyerReviewContextError(
        error,
        "Review media upload status check",
      );
    });
    const statusRecord = readRecord(unwrapEnvelope(statusResponse)) ?? {};
    const rawParts = getRecordProperty(statusRecord, [
      "uploadedParts",
      "uploaded_parts",
    ]);
    const uploadedParts = new Set(
      Array.isArray(rawParts)
        ? rawParts
            .map((part) =>
              getFirstNumber(readRecord(part) ?? {}, [
                "partNumber",
                "part_number",
              ]),
            )
            .filter((part): part is number => Boolean(part))
        : [],
    );
    const partSize = Math.max(
      REVIEW_MEDIA_UPLOAD_PART_BYTES,
      session.partSize || REVIEW_MEDIA_UPLOAD_PART_BYTES,
    );
    const totalParts = Math.ceil(file.size / partSize);
    let completedBytes = 0;
    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(file.size, start + partSize);
      if (uploadedParts.has(partNumber)) {
        completedBytes += end - start;
        onUploadProgress?.({
          loaded: completedBytes,
          total: file.size,
          percent: Math.round((completedBytes / file.size) * 100),
          lengthComputable: true,
        });
        continue;
      }
      const chunk = file.slice(start, end, contentType);
      const chunkBytes = end - start;

      await uploadBuyerReviewMediaPart({
        targetType,
        targetId,
        buyerId,
        uploadId: session.uploadId,
        key: session.key,
        partNumber,
        chunk,
        fileName: file.name,
        signal,
        onUploadProgress: (progress) => {
          const chunkLoaded = resolveReviewMediaChunkProgress(
            progress,
            chunkBytes,
          );
          const loaded = Math.min(file.size, completedBytes + chunkLoaded);
          onUploadProgress?.({
            loaded,
            total: file.size,
            percent: Math.round((loaded / file.size) * 100),
            lengthComputable: true,
          });
        },
      });
      completedBytes += end - start;
    }
    const completed = await requestBuyerReviewJSONWithFormFallback(
      "/buyer-reviews/media/uploads/complete",
      {
        ...identity,
        uploadId: session.uploadId,
        key: session.key,
        fileName: file.name,
        contentType: contentType || session.contentType,
        mediaType: session.mediaType,
        fileSize: file.size,
      },
      "Review media upload completion",
      signal,
    );
    const record = readRecord(unwrapEnvelope(completed)) ?? {};
    const media = normalizeBuyerReviewMedia([record])[0];
    if (!media)
      throw new AutopartsApiError(
        "The backend returned an invalid uploaded review-media file.",
        0,
        completed,
      );
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* optional */
    }
    onUploadProgress?.({
      loaded: file.size,
      total: file.size,
      percent: 100,
      lengthComputable: true,
    });
    return media;
  },
  async reply(
    reviewId: string,
    payload: {
      authorId: string;
      authorRole: "buyer" | "seller" | "admin";
      authorName: string;
      message: string;
    },
  ): Promise<BuyerReviewView> {
    const response = await request(
      `/buyer-reviews/${encodeURIComponent(reviewId)}/replies`,
      {
        method: "POST",
        headers: buyerReviewJSONHeaders,
        body: JSON.stringify(payload),
      },
    );
    const reviews = normalizeBuyerReviews(response);
    if (!reviews[0])
      throw new AutopartsApiError(
        "The backend returned an invalid review reply.",
        0,
        response,
      );
    return reviews[0];
  },
};

function resolveReturnEvidenceContentType(file: File): string {
  const declared = file.type.trim().toLowerCase().split(";")[0];
  if (declared && declared !== "application/octet-stream") return declared;

  const extension = file.name.toLowerCase().split(".").pop() ?? "";
  const byExtension: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    heic: "image/heic",
    heif: "image/heif",
    mp4: "video/mp4",
    m4v: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    mkv: "video/x-matroska",
    av1: "video/av1",
  };
  return byExtension[extension] ?? declared ?? "application/octet-stream";
}

const orders = {
  async list<T = PageResult<OrderView>>(query?: PageQuery): Promise<T> {
    if (!isAttachmentCatalogQuery(query)) {
      const result = await requestPageResultFromPaths(
        ["/orders"],
        query,
        normalizeOrderView,
        10,
      );
      return filterOrderPageResultByQuery(result, query) as T;
    }

    const cacheKey = buildOrderAttachmentListCacheKey(query);
    const cached = orderAttachmentListCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value as T;
    }

    const pending = orderAttachmentListRequests.get(cacheKey);
    if (pending) {
      return (await pending) as T;
    }

    const requestPromise = (async () => {
      const result = await requestPageResultFromPaths(
        ["/orders"],
        query,
        normalizeOrderView,
        10,
      );
      const filtered = filterOrderPageResultByQuery(result, query);
      orderAttachmentListCache.set(cacheKey, {
        expiresAt: Date.now() + ORDER_ATTACHMENT_READ_CACHE_TTL_MS,
        value: filtered,
      });
      return filtered;
    })();

    orderAttachmentListRequests.set(cacheKey, requestPromise);
    try {
      return (await requestPromise) as T;
    } finally {
      orderAttachmentListRequests.delete(cacheKey);
    }
  },

  async getById<T = OrderView>(id: string): Promise<T> {
    const response = await request(`/orders/${encodeURIComponent(id)}`);
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async getTrackingConfig<T = TrackingConfigView>(): Promise<T> {
    const response = await request("/tracking/config", { skipAuth: true });
    return normalizeTrackingConfigView(response) as T;
  },

  async reverseGeocodeLocation<T = ReverseGeocodeLocationView>(
    latitude: number,
    longitude: number,
    language?: string,
    context?: ReverseGeocodeLocationContext,
  ): Promise<T> {
    const query = new URLSearchParams({
      latitude: String(latitude),
      longitude: String(longitude),
    });
    const normalizedLanguage = language?.trim();
    if (normalizedLanguage) query.set("language", normalizedLanguage);

    const contextParameters = {
      label: context?.label,
      addressLine1: context?.addressLine1,
      city: context?.city,
      state: context?.state,
      postalCode: context?.postalCode,
      country: context?.country,
    };
    Object.entries(contextParameters).forEach(([key, value]) => {
      const normalizedValue = value?.trim();
      if (normalizedValue) query.set(key, normalizedValue);
    });

    const response = await request(
      `/tracking/reverse-geocode?${query.toString()}`,
      { skipAuth: true },
    );
    return normalizeReverseGeocodeLocationView(response) as T;
  },

  async calculateRouteDistance<T = RouteDistanceView>(
    origin: { latitude: number; longitude: number },
    destination: { latitude: number; longitude: number },
  ): Promise<T> {
    const response = await request("/tracking/route-distance", {
      method: "POST",
      body: { origin, destination },
    });
    return normalizeSingle(response, (value) => {
      const record = readRecord(unwrapEnvelope(value)) ?? {};
      return {
        ...record,
        distanceMeters:
          getFirstNumber(record, ["distanceMeters", "distance_meters"]) ?? 0,
        durationSeconds:
          getFirstNumber(record, ["durationSeconds", "duration_seconds"]) ?? 0,
        distanceText:
          getFirstString(record, ["distanceText", "distance_text"]) ?? "",
        durationText:
          getFirstString(record, ["durationText", "duration_text"]) ?? "",
      };
    }) as T;
  },

  async getTrackingLocation<T = OrderTrackingLocationView>(
    id: string,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/tracking`,
    );
    return normalizeOrderTrackingLocation(response) as T;
  },

  async updateTrackingLocation<T = OrderView>(
    id: string,
    payload: UpdateOrderTrackingPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/tracking`,
      {
        method: "PATCH",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async updateShippingAddress<T = OrderView>(
    id: string,
    shippingAddress: UpdateOrderShippingAddressPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/shipping-address`,
      {
        method: "PATCH",
        body: { shippingAddress },
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async updatePaymentStatus<T = OrderView>(
    id: string,
    paymentStatus: string,
    actor?: Omit<UpdateOrderPaymentStatusPayload, "paymentStatus">,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/payment-status`,
      {
        method: "PATCH",
        body: { paymentStatus, ...actor },
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async confirmStripePayment<T = OrderView>(
    id: string,
    sessionId: string,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/stripe-confirm`,
      {
        method: "POST",
        body: { sessionId } satisfies ConfirmStripeOrderPaymentPayload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async cancel<T = OrderView>(
    id: string,
    payload: CancelOrderPayload,
  ): Promise<T> {
    const response = await request(`/orders/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: payload,
    });
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async updateDelivery<T = OrderView>(
    id: string,
    payload: UpdateOrderDeliveryPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/delivery`,
      {
        method: "PATCH",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async updateDeliveryEvent<T = OrderView>(
    id: string,
    eventId: string,
    payload: UpdateOrderDeliveryEventPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/delivery-events/${encodeURIComponent(eventId)}`,
      {
        method: "PATCH",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async claimDelivery<T = OrderView>(
    id: string,
    payload: ClaimOrderDeliveryPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/courier-claim`,
      {
        method: "POST",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async confirmCourierPickup<T = OrderView>(
    id: string,
    payload: ConfirmCourierPickupPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/courier-pickup`,
      {
        method: "POST",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async requestCourierReassignment<T = OrderView>(
    id: string,
    payload: RequestCourierReassignmentPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/courier-reassignment`,
      {
        method: "POST",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async cancelCourierReassignment<T = OrderView>(
    id: string,
    payload: CancelCourierReassignmentPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/courier-reassignment`,
      {
        method: "DELETE",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async uploadDeliveryProof(
    id: string,
    file: File,
    proofKind: "delivery" | "payment" = "delivery",
    onUploadProgress?: (progress: ApiUploadProgress) => void,
    signal?: AbortSignal,
  ): Promise<DeliveryProofUploadView> {
    const contentType = resolveReturnEvidenceContentType(file);

    const describeUploadError = (error: unknown, stage: string) => {
      const status = error instanceof AutopartsApiError ? error.status : 0;
      const field =
        error instanceof AutopartsApiError ? error.field : undefined;
      const originalMessage =
        error instanceof Error
          ? error.message.trim()
          : typeof error === "string"
            ? error.trim()
            : "Unknown upload error.";
      const normalizedMessage = originalMessage.toLowerCase();
      const statusLabel = status > 0 ? `HTTP ${status}` : "network error";
      let action =
        "Check that the backend upload routes are deployed and that the API server can reach the configured S3 bucket.";

      if (
        normalizedMessage.includes("permanentredirect") ||
        normalizedMessage.includes("authorizationheadermalformed") ||
        normalizedMessage.includes("incorrectendpoint") ||
        normalizedMessage.includes("specified endpoint")
      ) {
        action =
          "The S3 bucket region does not match the configured endpoint. Deploy the latest backend so it can retry with the region returned by S3, set AWS_REGION to the bucket's actual region, and keep AWS_S3_BUCKET as the bucket name only.";
      } else if (status === 401 || status === 403) {
        action =
          "Sign in again and verify that the backend IAM credentials allow s3:PutObject and the multipart upload permissions.";
      } else if (status === 404 || status === 405) {
        action =
          "Deploy the latest backend because the delivery-proof upload endpoint is missing or does not accept this method.";
      } else if (status === 413) {
        action = "Use an image that is 12 MB or smaller.";
      } else if (
        status === 415 ||
        field === "file" ||
        field === "contentType"
      ) {
        action = "Use a JPEG, PNG, WebP, GIF, HEIC, or HEIF image.";
      } else if (status >= 500) {
        action =
          "Verify AWS_REGION, AWS_S3_BUCKET or AWS_S3_DELIVERY_PROOF_BUCKET, AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY, and the required S3 upload permissions on the backend.";
      } else if (status === 0) {
        action =
          "Check the device connection, API URL, reverse proxy, CORS rules, and whether the backend is running.";
      }

      return `${stage} failed with ${statusLabel}${field ? ` on field “${field}”` : ""}. ${originalMessage} ${action}`;
    };

    const uploadDirect = async (): Promise<DeliveryProofUploadView> => {
      const formData = new FormData();
      formData.append("proofKind", proofKind);
      formData.append("file", file, file.name);

      try {
        const response = await requestWithUploadProgress(
          `/orders/${encodeURIComponent(id)}/delivery-proof`,
          {
            method: "POST",
            body: formData,
            signal,
            uploadTotalBytes: file.size,
            onUploadProgress,
          },
        );
        const record = readRecord(unwrapEnvelope(response)) ?? {};
        const url = getFirstString(record, ["url", "publicUrl", "public_url"]);
        const key = getFirstString(record, ["key", "objectKey", "object_key"]);
        if (!url || !key) {
          throw new AutopartsApiError(
            "The backend accepted the image but did not return its storage URL and object key.",
            0,
            response,
          );
        }

        return {
          url,
          key,
          contentType:
            getFirstString(record, ["contentType", "content_type"]) ||
            contentType,
          size: getFirstNumber(record, ["size"]) ?? file.size,
        };
      } catch (error) {
        if (signal?.aborted) throw error;
        const status = error instanceof AutopartsApiError ? error.status : 0;
        throw new AutopartsApiError(
          describeUploadError(error, "Direct delivery-proof upload"),
          status,
          error instanceof AutopartsApiError ? error.data : error,
          error instanceof AutopartsApiError ? error.field : undefined,
        );
      }
    };

    // Files smaller than the S3 multipart minimum gain no resumability from a
    // one-part multipart upload, so use the simpler and more widely supported
    // PutObject route. Larger files still use chunked, resumable multipart upload.
    if (file.size < DELIVERY_PROOF_MULTIPART_MIN_BYTES) {
      return uploadDirect();
    }

    const fingerprint = [
      id,
      proofKind,
      file.name,
      file.size,
      file.lastModified,
      contentType,
    ].join(":");
    const storageKey = `sparelynk-delivery-proof-upload:${fingerprint}`;

    const readUploadPart = (
      value: unknown,
      fallbackSize = 0,
    ): ResumableUploadPartView | null => {
      const record = readRecord(unwrapEnvelope(value));
      if (!record) return null;
      const partNumber = getFirstNumber(record, ["partNumber", "part_number"]);
      const etag = getFirstString(record, ["etag", "eTag", "ETag"]);
      if (!partNumber || partNumber < 1 || !etag) return null;
      return {
        partNumber,
        etag,
        size:
          getFirstNumber(record, ["size", "partSize", "part_size"]) ??
          fallbackSize,
      };
    };

    const readStoredSession =
      (): DeliveryProofResumableUploadSessionView | null => {
        try {
          const stored = window.localStorage.getItem(storageKey);
          if (!stored) return null;
          const parsed = JSON.parse(
            stored,
          ) as DeliveryProofResumableUploadSessionView;
          if (
            !parsed?.uploadId ||
            !parsed.key ||
            parsed.fileSize !== file.size ||
            parsed.proofKind !== proofKind ||
            parsed.contentType !== contentType
          ) {
            window.localStorage.removeItem(storageKey);
            return null;
          }
          return {
            ...parsed,
            uploadedParts: Array.isArray(parsed.uploadedParts)
              ? parsed.uploadedParts
              : [],
          };
        } catch {
          return null;
        }
      };

    const persistSession = (
      session: DeliveryProofResumableUploadSessionView,
    ) => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(session));
      } catch {
        /* resumable storage is optional */
      }
    };

    const clearStoredSession = () => {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        /* resumable storage is optional */
      }
    };

    const initiateSession = async () => {
      const initiated = await request(
        `/orders/${encodeURIComponent(id)}/delivery-proof/uploads/initiate`,
        {
          method: "POST",
          body: {
            fileName: file.name,
            contentType,
            fileSize: file.size,
            proofKind,
          },
          signal,
        },
      );
      const record = readRecord(unwrapEnvelope(initiated)) ?? {};
      const uploadId = getFirstString(record, ["uploadId", "upload_id"]);
      const key = getFirstString(record, ["key"]);
      if (!uploadId || !key) {
        throw new AutopartsApiError(
          "The backend did not create a resumable delivery proof upload.",
          0,
          initiated,
        );
      }
      const session: DeliveryProofResumableUploadSessionView = {
        uploadId,
        key,
        partSize:
          getFirstNumber(record, ["partSize", "part_size"]) ?? 8 * 1024 * 1024,
        proofKind,
        contentType:
          getFirstString(record, ["contentType", "content_type"]) ||
          contentType,
        fileName:
          getFirstString(record, ["fileName", "file_name"]) || file.name,
        fileSize: file.size,
        uploadedParts: [],
      };
      persistSession(session);
      return session;
    };

    const canUseStoredUploadState = (error: unknown) => {
      if (signal?.aborted) return false;
      if (!(error instanceof AutopartsApiError)) return true;
      return (
        error.status === 0 ||
        error.status === 403 ||
        error.status >= 500 ||
        [405, 408, 409, 425, 429].includes(error.status)
      );
    };

    let session = readStoredSession();
    if (!session) {
      try {
        session = await initiateSession();
      } catch (multipartError) {
        if (signal?.aborted) throw multipartError;
        clearStoredSession();
        try {
          return await uploadDirect();
        } catch (directError) {
          const multipartDetail = describeUploadError(
            multipartError,
            "Resumable upload initialization",
          );
          const directDetail =
            directError instanceof Error
              ? directError.message
              : describeUploadError(directError, "Direct upload fallback");
          const status =
            directError instanceof AutopartsApiError
              ? directError.status
              : multipartError instanceof AutopartsApiError
                ? multipartError.status
                : 0;
          throw new AutopartsApiError(
            `${multipartDetail} The automatic direct-upload fallback also failed. ${directDetail}`,
            status,
            { multipartError, directError },
            directError instanceof AutopartsApiError
              ? directError.field
              : multipartError instanceof AutopartsApiError
                ? multipartError.field
                : undefined,
          );
        }
      }
    }

    const getStatus = async () => {
      const query = new URLSearchParams({
        uploadId: session.uploadId,
        key: session.key,
      });
      return request(
        `/orders/${encodeURIComponent(id)}/delivery-proof/uploads/status?${query.toString()}`,
        { signal },
      );
    };

    let statusResponse: unknown | null = null;
    try {
      statusResponse = await getStatus();
    } catch (error) {
      const message = error instanceof Error ? error.message.toLowerCase() : "";
      const staleSession =
        error instanceof AutopartsApiError &&
        (error.status === 404 ||
          message.includes("nosuchupload") ||
          message.includes("no such upload") ||
          message.includes("upload does not exist"));
      if (staleSession && !signal?.aborted) {
        clearStoredSession();
        session = await initiateSession();
        try {
          statusResponse = await getStatus();
        } catch (statusError) {
          if (!canUseStoredUploadState(statusError)) throw statusError;
          statusResponse = null;
        }
      } else if (!canUseStoredUploadState(error)) {
        throw error;
      }
    }

    const statusRecord = readRecord(unwrapEnvelope(statusResponse)) ?? {};
    const alreadyCompleted = Boolean(
      getRecordProperty(statusRecord, [
        "completed",
        "isCompleted",
        "is_completed",
      ]),
    );
    if (alreadyCompleted) {
      const completedUrl = getFirstString(statusRecord, [
        "url",
        "publicUrl",
        "public_url",
      ]);
      const completedKey = getFirstString(statusRecord, [
        "key",
        "objectKey",
        "object_key",
      ]);
      if (completedUrl && completedKey) {
        clearStoredSession();
        onUploadProgress?.({
          loaded: file.size,
          total: file.size,
          percent: 100,
          lengthComputable: true,
          phase: "complete",
        });
        return {
          url: completedUrl,
          key: completedKey,
          contentType:
            getFirstString(statusRecord, ["contentType", "content_type"]) ||
            contentType,
          size: getFirstNumber(statusRecord, ["size"]) ?? file.size,
        };
      }
    }

    const statusUnavailable = Boolean(
      getRecordProperty(statusRecord, [
        "statusUnavailable",
        "status_unavailable",
      ]),
    );
    const hasAuthoritativeStatus =
      statusResponse !== null && !statusUnavailable;
    const uploadedPartMap = new Map<number, ResumableUploadPartView>();

    if (!hasAuthoritativeStatus) {
      for (const storedPart of session.uploadedParts ?? []) {
        const normalizedPart = readUploadPart(storedPart, storedPart.size);
        if (normalizedPart) {
          uploadedPartMap.set(normalizedPart.partNumber, normalizedPart);
        }
      }
    }

    const rawParts = getRecordProperty(statusRecord, [
      "uploadedParts",
      "uploaded_parts",
    ]);
    if (Array.isArray(rawParts)) {
      for (const rawPart of rawParts) {
        const normalizedPart = readUploadPart(rawPart);
        if (normalizedPart) {
          uploadedPartMap.set(normalizedPart.partNumber, normalizedPart);
        }
      }
    }

    const partSize = Math.max(
      5 * 1024 * 1024,
      getFirstNumber(statusRecord, ["partSize", "part_size"]) ||
        session.partSize ||
        8 * 1024 * 1024,
    );
    session.partSize = partSize;
    session.uploadedParts = Array.from(uploadedPartMap.values()).sort(
      (left, right) => left.partNumber - right.partNumber,
    );
    persistSession(session);

    const uploadedParts = new Set(uploadedPartMap.keys());
    const totalParts = Math.ceil(file.size / partSize);
    let completedBytes = 0;

    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(file.size, start + partSize);
      if (uploadedParts.has(partNumber)) completedBytes += end - start;
    }

    onUploadProgress?.({
      loaded: completedBytes,
      total: file.size,
      percent:
        file.size > 0
          ? Math.min(100, Math.round((completedBytes / file.size) * 100))
          : 0,
      lengthComputable: true,
      phase: completedBytes >= file.size ? "processing" : "uploading",
    });

    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(file.size, start + partSize);
      const chunkSize = end - start;
      if (uploadedParts.has(partNumber)) continue;

      const formData = new FormData();
      formData.append("uploadId", session.uploadId);
      formData.append("key", session.key);
      formData.append("partNumber", String(partNumber));
      formData.append(
        "chunk",
        file.slice(start, end, contentType),
        `${file.name}.part-${partNumber}`,
      );

      let uploadedPartResponse: unknown;
      try {
        uploadedPartResponse = await requestWithUploadProgress(
          `/orders/${encodeURIComponent(id)}/delivery-proof/uploads/part`,
          {
            method: "POST",
            body: formData,
            signal,
            uploadTotalBytes: chunkSize,
            onUploadProgress: (progress) => {
              const ratio =
                progress.total && progress.total > 0
                  ? Math.min(1, progress.loaded / progress.total)
                  : Math.min(1, progress.loaded / chunkSize);
              const chunkLoaded = Math.min(
                chunkSize,
                Math.round(chunkSize * ratio),
              );
              const loaded = Math.min(file.size, completedBytes + chunkLoaded);
              onUploadProgress?.({
                loaded,
                total: file.size,
                percent: Math.min(100, Math.round((loaded / file.size) * 100)),
                lengthComputable: true,
                phase:
                  progress.phase === "processing" ? "processing" : "uploading",
              });
            },
          },
        );
      } catch (error) {
        if (signal?.aborted) throw error;
        const status = error instanceof AutopartsApiError ? error.status : 0;
        throw new AutopartsApiError(
          describeUploadError(
            error,
            `Delivery-proof chunk ${partNumber} of ${totalParts}`,
          ),
          status,
          error instanceof AutopartsApiError ? error.data : error,
          error instanceof AutopartsApiError ? error.field : undefined,
        );
      }

      const uploadedPart = readUploadPart(uploadedPartResponse, chunkSize);
      if (!uploadedPart || uploadedPart.partNumber !== partNumber) {
        throw new AutopartsApiError(
          "The backend did not confirm the uploaded delivery proof chunk.",
          0,
          uploadedPartResponse,
        );
      }

      completedBytes += chunkSize;
      uploadedParts.add(partNumber);
      uploadedPartMap.set(partNumber, {
        ...uploadedPart,
        size: chunkSize,
      });
      session.uploadedParts = Array.from(uploadedPartMap.values()).sort(
        (left, right) => left.partNumber - right.partNumber,
      );
      persistSession(session);
      onUploadProgress?.({
        loaded: completedBytes,
        total: file.size,
        percent: Math.min(100, Math.round((completedBytes / file.size) * 100)),
        lengthComputable: true,
        phase: completedBytes >= file.size ? "processing" : "uploading",
      });
    }

    onUploadProgress?.({
      loaded: file.size,
      total: file.size,
      percent: 100,
      lengthComputable: true,
      phase: "processing",
    });

    let completed: unknown;
    try {
      completed = await request(
        `/orders/${encodeURIComponent(id)}/delivery-proof/uploads/complete`,
        {
          method: "POST",
          body: {
            uploadId: session.uploadId,
            key: session.key,
            fileName: file.name,
            contentType,
            proofKind,
            fileSize: file.size,
            parts: session.uploadedParts,
          },
          signal,
        },
      );
    } catch (error) {
      if (signal?.aborted) throw error;
      const status = error instanceof AutopartsApiError ? error.status : 0;
      throw new AutopartsApiError(
        describeUploadError(error, "Finalizing the resumable upload"),
        status,
        error instanceof AutopartsApiError ? error.data : error,
        error instanceof AutopartsApiError ? error.field : undefined,
      );
    }
    const record = readRecord(unwrapEnvelope(completed)) ?? {};
    const url = getFirstString(record, ["url", "publicUrl", "public_url"]);
    const key = getFirstString(record, ["key", "objectKey", "object_key"]);
    if (!url || !key) {
      throw new AutopartsApiError(
        "The backend returned an invalid delivery proof upload.",
        0,
        completed,
      );
    }

    clearStoredSession();
    onUploadProgress?.({
      loaded: file.size,
      total: file.size,
      percent: 100,
      lengthComputable: true,
      phase: "complete",
    });
    return {
      url,
      key,
      contentType:
        getFirstString(record, ["contentType", "content_type"]) || contentType,
      size: getFirstNumber(record, ["size"]) ?? file.size,
    };
  },

  async uploadReturnEvidenceResumable(
    id: string,
    file: File,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
    signal?: AbortSignal,
  ): Promise<ReturnRefundEvidenceView> {
    const contentType = resolveReturnEvidenceContentType(file);
    const fingerprint = `${id}:${file.name}:${file.size}:${file.lastModified}`;
    const storageKey = `sparelynk-return-upload:${fingerprint}`;
    let session: ResumableUploadSessionView | null = null;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) session = JSON.parse(stored) as ResumableUploadSessionView;
    } catch {
      session = null;
    }

    if (!session?.uploadId || !session.key) {
      const initiated = await request(
        `/orders/${encodeURIComponent(id)}/return-media/uploads/initiate`,
        {
          method: "POST",
          body: { fileName: file.name, contentType, fileSize: file.size },
          signal,
        },
      );
      const record = readRecord(unwrapEnvelope(initiated)) ?? {};
      const uploadId = getFirstString(record, ["uploadId", "upload_id"]);
      const key = getFirstString(record, ["key"]);
      if (!uploadId || !key)
        throw new AutopartsApiError(
          "The backend did not create a resumable upload.",
          0,
          initiated,
        );
      session = {
        uploadId,
        key,
        partSize:
          getFirstNumber(record, ["partSize", "part_size"]) ?? 8 * 1024 * 1024,
        mediaType:
          getFirstString(record, ["mediaType", "media_type"]) === "image"
            ? "image"
            : "video",
        contentType:
          getFirstString(record, ["contentType", "content_type"]) ||
          contentType,
        fileName:
          getFirstString(record, ["fileName", "file_name"]) || file.name,
        fileSize: file.size,
        uploadedParts: [],
      };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(session));
      } catch {
        /* storage is optional */
      }
    }

    const query = new URLSearchParams({
      uploadId: session.uploadId,
      key: session.key,
    });
    const statusResponse = await request(
      `/orders/${encodeURIComponent(id)}/return-media/uploads/status?${query.toString()}`,
      { signal },
    );
    const statusRecord = readRecord(unwrapEnvelope(statusResponse)) ?? {};
    const rawParts = getRecordProperty(statusRecord, [
      "uploadedParts",
      "uploaded_parts",
    ]);
    const uploadedParts = new Set(
      Array.isArray(rawParts)
        ? rawParts
            .map((part) =>
              getFirstNumber(readRecord(part) ?? {}, [
                "partNumber",
                "part_number",
              ]),
            )
            .filter((part): part is number => Boolean(part))
        : [],
    );
    const partSize = Math.max(
      5 * 1024 * 1024,
      session.partSize || 8 * 1024 * 1024,
    );
    const totalParts = Math.ceil(file.size / partSize);
    let completedBytes = 0;
    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(file.size, start + partSize);
      if (uploadedParts.has(partNumber)) {
        completedBytes += end - start;
        onUploadProgress?.({
          loaded: completedBytes,
          total: file.size,
          percent: Math.round((completedBytes / file.size) * 100),
          lengthComputable: true,
        });
        continue;
      }
      const formData = new FormData();
      formData.append("uploadId", session.uploadId);
      formData.append("key", session.key);
      formData.append("partNumber", String(partNumber));
      formData.append(
        "chunk",
        file.slice(start, end),
        `${file.name}.part-${partNumber}`,
      );
      await requestWithUploadProgress(
        `/orders/${encodeURIComponent(id)}/return-media/uploads/part`,
        {
          method: "POST",
          body: formData,
          signal,
          onUploadProgress: (progress) => {
            const loaded = Math.min(
              file.size,
              completedBytes + progress.loaded,
            );
            onUploadProgress?.({
              loaded,
              total: file.size,
              percent: Math.round((loaded / file.size) * 100),
              lengthComputable: true,
            });
          },
        },
      );
      completedBytes += end - start;
    }

    const completed = await request(
      `/orders/${encodeURIComponent(id)}/return-media/uploads/complete`,
      {
        method: "POST",
        body: {
          uploadId: session.uploadId,
          key: session.key,
          fileName: file.name,
          contentType: contentType || session.contentType,
          mediaType: session.mediaType,
          fileSize: file.size,
        },
        signal,
      },
    );
    const completedRecord = readRecord(unwrapEnvelope(completed)) ?? {};
    const url = getFirstString(completedRecord, [
      "url",
      "publicUrl",
      "public_url",
    ]);
    const key = getFirstString(completedRecord, ["key"]);
    if (!url || !key)
      throw new AutopartsApiError(
        "The backend returned an invalid uploaded evidence file.",
        0,
        completed,
      );
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* storage is optional */
    }
    onUploadProgress?.({
      loaded: file.size,
      total: file.size,
      percent: 100,
      lengthComputable: true,
    });
    return {
      url,
      key,
      mediaType:
        getFirstString(completedRecord, ["mediaType", "media_type"]) === "image"
          ? "image"
          : "video",
      contentType:
        getFirstString(completedRecord, ["contentType", "content_type"]) ||
        contentType,
      fileName:
        getFirstString(completedRecord, ["fileName", "file_name"]) || file.name,
      size: getFirstNumber(completedRecord, ["size"]) ?? file.size,
      sources: normalizeReturnRefundPlaybackSources(
        getRecordProperty(completedRecord, [
          "sources",
          "playbackSources",
          "playback_sources",
        ]),
      ),
    };
  },

  async reviewReturnRefund<T = OrderView>(
    id: string,
    payload: {
      decision: "approve" | "reject";
      reviewedBy?: string;
      adminNotes?: string;
    },
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/return-refund/review`,
      { method: "PATCH", body: payload },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async getMessages(id: string, channel?: "courier_seller" | "courier_buyer", vendorId?: string): Promise<OrderMessageView[]> {
    const params = new URLSearchParams();
    if (channel) params.set("channel", channel);
    if (vendorId && channel !== "courier_buyer") params.set("vendorId", vendorId);
    const response = await request(
      `/orders/${encodeURIComponent(id)}/messages${params.size ? `?${params}` : ""}`,
    );
    const unwrapped = unwrapEnvelope(response);
    const items = Array.isArray(unwrapped)
      ? unwrapped
      : Array.isArray(readRecord(unwrapped)?.items)
        ? (readRecord(unwrapped)?.items as unknown[])
        : [];
    return normalizeOrderMessages(items);
  },

  async uploadMessageAttachment(
    orderId: string,
    file: File,
    channel: "courier_seller" | "courier_buyer",
    onProgress?: (progress: ApiUploadProgress) => void,
    vendorId?: string,
  ): Promise<ConversationMediaAttachmentView> {
    const body = new FormData();
    body.append("file", file, file.name);
    body.append("channel", channel);
    if (vendorId && channel === "courier_seller") body.append("vendorId", vendorId);
    const response = await requestWithUploadProgress(
      `/orders/${encodeURIComponent(orderId)}/messages/attachments`,
      { method: "POST", body, uploadTotalBytes: file.size, onUploadProgress: onProgress },
    );
    const attachment = normalizeConversationMediaAttachment(unwrapEnvelope(response));
    if (!attachment) throw new AutopartsApiError("Invalid uploaded attachment", 0, response);
    return attachment;
  },

  async sendMessage(
    id: string,
    payload: CreateOrderMessagePayload,
  ): Promise<OrderMessageView> {
    const path = `/orders/${encodeURIComponent(id)}/messages`;
    let response: unknown;

    try {
      response = await request(path, {
        method: "POST",
        body: serializeOrderMessagePayload(payload),
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message.trim().toLowerCase() : "";
      const isBodyCompatibilityError =
        error instanceof AutopartsApiError &&
        [400, 409, 415, 422].includes(error.status) &&
        (String(error.field ?? "")
          .trim()
          .toLowerCase() === "body" ||
          message.includes("valid json") ||
          message.includes("invalid json") ||
          message.includes("unknown field") ||
          message.includes("decode"));

      if (!payload.context || !isBodyCompatibilityError) throw error;

      const fallbackBody = serializeOrderMessagePayload({
        ...payload,
        context: undefined,
      });
      fallbackBody.message = encodeOrderMessageTransportContent(
        payload,
        normalizeOutgoingConversationContext(payload.context),
      );

      response = await request(path, {
        method: "POST",
        body: fallbackBody,
      });
    }

    const message = normalizeOrderMessage(response);
    if (!message) {
      throw new AutopartsApiError(
        "The backend returned an invalid message.",
        0,
        response,
      );
    }
    return {
      ...message,
      ...(payload.context
        ? { context: payload.context }
        : { context: undefined }),
      ...(payload.replyTo ? { replyTo: payload.replyTo } : {}),
    };
  },

  async markMessagesRead(
    id: string,
    payload: MarkOrderMessagesReadPayload,
  ): Promise<OrderMessagesReadResult> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/messages/read`,
      { method: "POST", body: payload },
    );
    const record = readRecord(unwrapEnvelope(response));
    if (!record) {
      throw new AutopartsApiError(
        "The backend returned an invalid message read state.",
        0,
        response,
      );
    }

    const orderId = getFirstString(record, ["orderId", "order_id"]) || id;
    const readerRole = getFirstString(record, ["readerRole", "reader_role"]);
    const readAt = getFirstString(record, ["readAt", "read_at"]);

    if ((readerRole !== "seller" && readerRole !== "courier" && readerRole !== "buyer") || !readAt) {
      throw new AutopartsApiError(
        "The backend returned an invalid message read state.",
        0,
        response,
      );
    }

    return { orderId, readerRole, readAt };
  },

  async markReceived<T = OrderView>(
    id: string,
    payload: Omit<BuyerOrderDeliveryActionPayload, "action"> = {},
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/received`,
      {
        method: "POST",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async rateCourier<T = OrderView>(
    id: string,
    payload: RateCourierPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/courier-rating`,
      { method: "POST", body: payload },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async requestReturnRefund<T = OrderView>(
    id: string,
    payload: BuyerOrderDeliveryActionPayload,
  ): Promise<T> {
    const response = await request(
      `/orders/${encodeURIComponent(id)}/return-refund`,
      {
        method: "POST",
        body: payload,
      },
    );
    return normalizeSingle(response, normalizeOrderView) as T;
  },

  async createCheckout<T = OrderView>(
    payload: CreateCheckoutOrderPayload,
  ): Promise<T> {
    const normalizedPayload = normalizeCheckoutOrderPayload(payload);

    try {
      const response = await request("/orders", {
        method: "POST",
        body: serializeJsonRequestBody(normalizedPayload),
      });
      return normalizeSingle(response, normalizeOrderView) as T;
    } catch (error) {
      if (!isCheckoutJsonCompatibilityError(error)) {
        throw error;
      }

      const response = await request("/orders", {
        method: "POST",
        body: serializeJsonRequestBody(
          toLegacyCheckoutPayload(normalizedPayload),
        ),
      });
      return normalizeSingle(response, normalizeOrderView) as T;
    }
  },

  async getStripeConfig<T = StripePaymentConfigView>(): Promise<T> {
    const response = await request("/payments/stripe/config");
    return normalizeStripePaymentConfig(response) as T;
  },

  async createStripeCheckout<T = StripeCheckoutOrderView>(
    payload: CreateStripeCheckoutPayload,
  ): Promise<T> {
    const stripePayload = toStripeCheckoutPayload(payload);
    const requestOptions: RequestOptions = {
      method: "POST",
      body: serializeJsonRequestBody(stripePayload),
    };

    try {
      return (await request("/orders/stripe-checkout", requestOptions)) as T;
    } catch (error) {
      if (
        error instanceof AutopartsApiError &&
        [404, 405].includes(error.status)
      ) {
        return request(
          "/payments/stripe/checkout",
          requestOptions,
        ) as Promise<T>;
      }

      if (isCheckoutJsonCompatibilityError(error)) {
        return request("/orders/stripe-checkout", {
          method: "POST",
          body: serializeJsonRequestBody(
            toLegacyCheckoutPayload(stripePayload),
          ),
        }) as Promise<T>;
      }

      throw error;
    }
  },

  async createStripeCheckoutForOrder<T = StripeCheckoutOrderView>(
    id: string,
    payload: RetryStripeCheckoutPayload,
  ): Promise<T> {
    return request(`/orders/${encodeURIComponent(id)}/stripe-checkout`, {
      method: "POST",
      body: payload,
    }) as Promise<T>;
  },

  async update<T = OrderView>(
    id: string,
    payload: Record<string, unknown>,
  ): Promise<T> {
    const response = await request(`/orders/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: payload,
    });
    return normalizeSingle(response, normalizeOrderView) as T;
  },
};

function normalizeWalletRecipientSearchText(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function walletRecipientMatchesSearch(
  values: Array<string | undefined>,
  search: string,
) {
  const query = normalizeWalletRecipientSearchText(search);
  if (!query) return false;

  return values.some((value) =>
    normalizeWalletRecipientSearchText(value).includes(query),
  );
}

function walletRecipientDisplayName(
  user?: UserView | null,
  vendor?: VendorProfileView | null,
) {
  return (
    vendor?.businessName ||
    vendor?.storeName ||
    vendor?.name ||
    user?.displayName ||
    user?.name ||
    user?.email ||
    vendor?.email ||
    vendor?.id ||
    user?.id ||
    "Wallet owner"
  );
}

async function searchWalletRecipients(
  role: MarketplaceRole,
  search: string,
): Promise<WalletRecipientSearchResultView[]> {
  const recipientRole = normalizeWalletRecipientSearchText(
    role,
  ) as MarketplaceRole;
  const query = String(search ?? "").trim();

  if (recipientRole === "admin") {
    return [
      {
        id: "platform",
        walletOwnerId: "platform",
        role: "admin",
        name: "Platform admin wallet",
        email: "admin wallet",
      },
    ];
  }

  if (!query) return [];

  const usersResult = await users
    .list<PageResult<UserView>>({
      page: 1,
      pageSize: 500,
      sortBy: "name",
      sortDirection: "asc",
    })
    .catch((): PageResult<UserView> => ({
      items: [],
      total: 0,
      page: 1,
      pageSize: 500,
      totalPages: 0,
    }));

  const usersForRole = usersResult.items.filter(
    (user) => normalizeWalletRecipientSearchText(user.role) === recipientRole,
  );
  const usersById = new Map(usersForRole.map((user) => [user.id, user]));
  const userRecipients = usersForRole
    .filter((user) =>
      walletRecipientMatchesSearch(
        [user.id, user.name, user.displayName, user.email],
        query,
      ),
    )
    .map<WalletRecipientSearchResultView>((user) => ({
      id: user.id,
      walletOwnerId: user.id,
      role: recipientRole,
      name: walletRecipientDisplayName(user),
      email: user.email,
    }));

  if (recipientRole !== "seller") {
    return userRecipients.slice(0, 8);
  }

  const vendorResult = await vendors
    .list<PageResult<VendorProfileView>>({
      page: 1,
      pageSize: 500,
      sortBy: "name",
      sortDirection: "asc",
    })
    .catch((): PageResult<VendorProfileView> => ({
      items: [],
      total: 0,
      page: 1,
      pageSize: 500,
      totalPages: 0,
    }));

  const vendorRecipients = vendorResult.items
    .filter((vendor) => {
      const linkedUser = vendor.userId ? usersById.get(vendor.userId) : null;
      return walletRecipientMatchesSearch(
        [
          vendor.id,
          vendor.userId,
          vendor.name,
          vendor.storeName,
          vendor.businessName,
          vendor.email,
          linkedUser?.name,
          linkedUser?.displayName,
          linkedUser?.email,
        ],
        query,
      );
    })
    .map<WalletRecipientSearchResultView>((vendor) => {
      const linkedUser = vendor.userId ? usersById.get(vendor.userId) : null;
      return {
        id: vendor.id,
        walletOwnerId: vendor.id,
        role: "seller",
        name: walletRecipientDisplayName(linkedUser, vendor),
        email: vendor.email || linkedUser?.email || "Email unavailable",
        profileName: vendor.businessName || vendor.storeName || vendor.name,
      };
    });

  const unique = new Map<string, WalletRecipientSearchResultView>();
  for (const item of [...vendorRecipients, ...userRecipients]) {
    if (!unique.has(item.walletOwnerId)) {
      unique.set(item.walletOwnerId, item);
    }
  }

  return Array.from(unique.values()).slice(0, 8);
}

const wallet = {
  async getSummary(
    role: MarketplaceRole,
    userId?: string,
  ): Promise<WalletSummaryView> {
    const params = new URLSearchParams({ role });
    if (userId) params.set("userId", userId);
    return request(
      `/wallet/summary?${params.toString()}`,
    ) as Promise<WalletSummaryView>;
  },

  async getStripeBalance(): Promise<StripeBalanceView> {
    return request(
      "/payments/stripe/balance",
    ) as Promise<StripeBalanceView>;
  },

  async searchRecipients(
    role: MarketplaceRole,
    search: string,
  ): Promise<WalletRecipientSearchResultView[]> {
    return searchWalletRecipients(role, search);
  },

  async getSettings(): Promise<MarketplaceFeeSettingsView> {
    return request("/wallet/settings") as Promise<MarketplaceFeeSettingsView>;
  },

  async updateSettings(
    settings: MarketplaceFeeSettingsUpdate,
  ): Promise<MarketplaceFeeSettingsView> {
    return request("/wallet/settings", {
      method: "PATCH",
      body: settings,
    }) as Promise<MarketplaceFeeSettingsView>;
  },

  async createStripeCashInCheckout(
    payload: WalletStripeCashInPayload,
  ): Promise<WalletStripeCashInCheckoutView> {
    const requestOptions: RequestOptions = {
      method: "POST",
      body: payload,
    };
    const paths = [
      "/wallet/cashins/stripe-checkout",
      "/payments/stripe/wallet-cashins/checkout",
      "/payments/stripe/cashins/checkout",
    ];

    let lastError: unknown = null;

    for (const path of paths) {
      try {
        return (await request(
          path,
          requestOptions,
        )) as WalletStripeCashInCheckoutView;
      } catch (error) {
        if (
          error instanceof AutopartsApiError &&
          [404, 405].includes(error.status)
        ) {
          lastError = error;
          continue;
        }

        throw error;
      }
    }

    throw (
      lastError ?? new AutopartsApiError("Unable to start Stripe cash in", 404)
    );
  },

  async confirmStripeCashIn(
    payload: WalletStripeCashInConfirmationPayload,
  ): Promise<WalletStripeCashInConfirmationView> {
    const requestOptions: RequestOptions = {
      method: "POST",
      body: payload,
    };
    const paths = [
      "/wallet/cashins/stripe-confirm",
      "/payments/stripe/wallet-cashins/confirm",
      "/payments/stripe/cashins/confirm",
    ];

    let lastError: unknown = null;

    for (const path of paths) {
      try {
        return (await request(
          path,
          requestOptions,
        )) as WalletStripeCashInConfirmationView;
      } catch (error) {
        if (
          error instanceof AutopartsApiError &&
          [404, 405].includes(error.status)
        ) {
          lastError = error;
          continue;
        }

        throw error;
      }
    }

    throw (
      lastError ??
      new AutopartsApiError("Unable to confirm Stripe cash in", 404)
    );
  },

  async cashIn(payload: WalletCashInPayload): Promise<WalletSummaryView> {
    return request("/wallet/cashins", {
      method: "POST",
      body: payload,
    }) as Promise<WalletSummaryView>;
  },

  async transfer(
    payload: WalletTransferPayload,
  ): Promise<WalletTransferReceiptView> {
    return request("/wallet/transfers", {
      method: "POST",
      body: payload,
    }) as Promise<WalletTransferReceiptView>;
  },

  async requestCashoutOTP(
    payload: WalletCashoutOTPPayload,
  ): Promise<WalletCashoutOTPChallengeView> {
    return request("/wallet/cashouts/otp", {
      method: "POST",
      body: payload,
    }) as Promise<WalletCashoutOTPChallengeView>;
  },

  async cashout(payload: WalletCashoutPayload): Promise<WalletCashoutView> {
    return request("/wallet/cashouts", {
      method: "POST",
      body: payload,
    }) as Promise<WalletCashoutView>;
  },
};

const vendors = {
  async list<T = PageResult<VendorProfileView>>(query?: PageQuery): Promise<T> {
    return requestPageResultFromPaths(
      ["/vendors", "/vendor-profiles"],
      query,
      normalizeVendorProfileView,
      10,
    ) as Promise<T>;
  },

  async getById<T = VendorProfileView>(id: string): Promise<T> {
    const response = await request(`/vendors/${encodeURIComponent(id)}`);
    return normalizeSingle(response, normalizeVendorProfileView) as T;
  },

  async getByUserId<T = VendorProfileView>(userId: string): Promise<T> {
    const normalizedUserId = getString(userId);

    if (!normalizedUserId) {
      throw new AutopartsApiError("Vendor profile not found", 404);
    }

    const response = await request(
      `/users/${encodeURIComponent(normalizedUserId)}/vendor-profile`,
      { preserveEnvelope: true },
    );
    return normalizeSingle(response, normalizeVendorProfileView) as T;
  },

  async create<T = VendorProfileView>(
    payload: Record<string, unknown>,
  ): Promise<T> {
    const response = await request("/vendors", {
      method: "POST",
      body: payload,
      preserveEnvelope: true,
    });
    return normalizeSingle(response, normalizeVendorProfileView) as T;
  },
};

const cart = {
  async listForUser<T = PageResult<CartItemView>>(
    userId: string,
    query?: PageQuery,
  ): Promise<T> {
    const response = await request(
      `/users/${encodeURIComponent(userId)}/cart${buildQueryString(query)}`,
    ).catch(() => request(`/cart${buildQueryString({ ...query, userId })}`));

    return normalizePageResult(
      response,
      normalizeCartItemView,
      query?.page ?? 1,
      query?.pageSize ?? 24,
    ) as T;
  },

  async setItem<T = CartItemView>(
    userId: string,
    productId: string,
    quantity: number,
  ): Promise<T> {
    const normalizedQuantity = Math.max(1, Math.floor(Number(quantity) || 1));
    const body = {
      userId,
      user_id: userId,
      productId,
      product_id: productId,
      quantity: normalizedQuantity,
    };

    const response = await request(
      `/users/${encodeURIComponent(userId)}/cart`,
      {
        method: "POST",
        body,
      },
    ).catch(() =>
      request(
        `/cart/${encodeURIComponent(productId)}${buildQueryString({ userId })}`,
        {
          method: "PUT",
          body,
        },
      ),
    );

    return normalizeSingle(response, normalizeCartItemView) as T;
  },

  async remove(userId: string, productId: string) {
    return request(
      `/users/${encodeURIComponent(userId)}/cart/${encodeURIComponent(productId)}`,
      {
        method: "DELETE",
      },
    ).catch(() =>
      request(
        `/cart/${encodeURIComponent(productId)}${buildQueryString({ userId })}`,
        {
          method: "DELETE",
        },
      ),
    );
  },

  async clear(userId: string) {
    return request(`/users/${encodeURIComponent(userId)}/cart`, {
      method: "DELETE",
    }).catch(() =>
      request(`/cart${buildQueryString({ userId })}`, {
        method: "DELETE",
      }),
    );
  },
};

const wishlist = {
  async listForUser<T = PageResult<WishlistItemView>>(
    userId: string,
    query?: PageQuery,
  ): Promise<T> {
    const response = await request(
      `/users/${encodeURIComponent(userId)}/wishlist${buildQueryString(query)}`,
    ).catch(() =>
      request(`/wishlist${buildQueryString({ ...query, userId })}`),
    );

    return normalizePageResult(
      response,
      normalizeWishlistItemView,
      query?.page ?? 1,
      query?.pageSize ?? 24,
    ) as T;
  },

  async add<T = WishlistItemView>(
    userId: string,
    productId: string,
  ): Promise<T> {
    const body = { userId, user_id: userId, productId, product_id: productId };
    const response = await request(
      `/users/${encodeURIComponent(userId)}/wishlist`,
      {
        method: "POST",
        body,
      },
    ).catch(() => request("/wishlist", { method: "POST", body }));

    return normalizeSingle(response, normalizeWishlistItemView) as T;
  },

  async remove(userId: string, productId: string) {
    return request(
      `/users/${encodeURIComponent(userId)}/wishlist/${encodeURIComponent(productId)}`,
      {
        method: "DELETE",
      },
    ).catch(() =>
      request(
        `/wishlist/${encodeURIComponent(productId)}${buildQueryString({ userId })}`,
        {
          method: "DELETE",
        },
      ),
    );
  },
};

const customerData = {
  async track<T = unknown>(event: TrackCustomerEventPayload): Promise<T> {
    return request<T>("/analytics/events", { method: "POST", body: event });
  },

  async trackBatch<T = unknown>(events: TrackCustomerEventPayload[]): Promise<T> {
    return request<T>("/analytics/events/batch", {
      method: "POST",
      body: { events },
    });
  },

  async recommendations(
    options: { anonymousId?: string; limit?: number } = {},
  ): Promise<RecommendationResultView> {
    const response = await request(
      `/personalization/recommendations${buildQueryString(options)}`,
    );
    const record = readRecord(unwrapEnvelope(response)) ?? {};
    const items = Array.isArray(record.items) ? record.items : [];
    return {
      ...record,
      personalized: getBoolean(record.personalized) ?? false,
      items: items
        .map((item) => {
          const itemRecord = readRecord(item);
          if (!itemRecord) return null;
          const productValue = normalizeProductView(itemRecord.product);
          if (!productValue?.id) return null;
          return {
            product: productValue,
            score: getNumber(itemRecord.score) ?? 0,
            reasons: Array.isArray(itemRecord.reasons)
              ? itemRecord.reasons.map((reason) => String(reason)).filter(Boolean)
              : [],
          } satisfies ProductRecommendationView;
        })
        .filter((item): item is ProductRecommendationView => Boolean(item)),
    } as RecommendationResultView;
  },

  async profile(): Promise<CustomerProfileView> {
    return request<CustomerProfileView>("/personalization/profile");
  },

  async deleteProfile(anonymousId?: string): Promise<void> {
    await request(
      `/personalization/profile${buildQueryString({ anonymousId })}`,
      { method: "DELETE" },
    );
  },

  async experiment(
    experimentKey: string,
    variants: string[] = ["control", "personalized"],
    anonymousId?: string,
  ): Promise<ExperimentAssignmentView> {
    return request<ExperimentAssignmentView>(
      `/experiments/${encodeURIComponent(experimentKey)}${buildQueryString({
        variants: variants.join(","),
        anonymousId,
      })}`,
    );
  },

  async summary(days = 30): Promise<CustomerDataSummaryView> {
    return request<CustomerDataSummaryView>(
      `/admin/analytics/customer-data${buildQueryString({ days })}`,
    );
  },
};

const MAX_CONVERSATION_CONTEXT_IMAGES = 12;
const MAX_CONVERSATION_CONTEXT_PRODUCTS = 12;
const MAX_CONVERSATION_CONTEXT_IMAGE_URL_LENGTH = 4096;

function normalizeOutgoingConversationImage(value: unknown): string | null {
  const image = String(value ?? "").trim();
  if (!image || image.length > MAX_CONVERSATION_CONTEXT_IMAGE_URL_LENGTH) {
    return null;
  }

  // Never place large in-memory image payloads in a JSON message request.
  if (/^(?:data|blob):/i.test(image)) {
    return null;
  }

  return image;
}

function normalizeOutgoingConversationContext(
  context?: ConversationMessageContextView,
): ConversationMessageContextView | undefined {
  if (!context) return undefined;

  const type = String(context.type ?? "")
    .trim()
    .toLowerCase();
  const productId = String(context.productId ?? "").trim();
  const orderId = String(context.orderId ?? "").trim();
  const orderIds = Array.from(
    new Set(
      [
        ...(Array.isArray(context.orderIds) ? context.orderIds : []),
        orderId,
      ]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean),
    ),
  ).slice(0, MAX_CONVERSATION_CONTEXT_PRODUCTS);
  const id =
    String(context.id ?? "").trim() ||
    (type === "order" || type === "orders" ? orderIds[0] : productId) ||
    orderId ||
    productId;
  const label = String(context.label ?? "").trim();
  const products = (Array.isArray(context.products) ? context.products : [])
    .map((product) => ({
      id: String(product.id ?? "").trim(),
      name: String(product.name ?? "").trim(),
      imageUrl:
        normalizeOutgoingConversationImage(product.imageUrl) ?? undefined,
      price:
        typeof product.price === "number" && Number.isFinite(product.price)
          ? product.price
          : undefined,
      vendorId: String(product.vendorId ?? "").trim() || undefined,
    }))
    .filter((product) => product.id && product.name)
    .slice(0, MAX_CONVERSATION_CONTEXT_PRODUCTS);
  const images = Array.from(
    new Set([
      ...(Array.isArray(context.images) ? context.images : [])
        .map(normalizeOutgoingConversationImage)
        .filter((image): image is string => Boolean(image)),
      ...products
        .map((product) => product.imageUrl)
        .filter((image): image is string => Boolean(image)),
    ]),
  ).slice(0, MAX_CONVERSATION_CONTEXT_IMAGES);

  if (!type || !id) return undefined;

  if (
    (type === "product" || type === "products") &&
    products.length === 0 &&
    images.length === 0
  ) {
    return undefined;
  }

  return {
    type,
    id,
    label:
      label ||
      (type === "order" || type === "orders"
        ? `${type === "orders" ? "Orders" : "Order"} ${id}`
        : type === "shop"
          ? "Shop inquiry"
          : "Product inquiry"),
    images,
    ...(productId ? { productId } : {}),
    ...(orderIds[0] ? { orderId: orderIds[0] } : {}),
    ...(orderIds.length > 0 ? { orderIds } : {}),
    ...(products.length > 0 ? { products } : {}),
  };
}

function encodeOrderMessageTransportContent(
  payload: CreateOrderMessagePayload,
  context?: ConversationMessageContextView,
): string {
  const replyTo = payload.replyTo
    ? {
        messageId: String(payload.replyTo.messageId ?? "").trim(),
        senderName:
          String(payload.replyTo.senderName ?? "")
            .trim()
            .slice(0, 80) || undefined,
        message: String(payload.replyTo.message ?? "")
          .trim()
          .slice(0, 160),
      }
    : undefined;
  const normalizedReplyTo =
    replyTo?.messageId && replyTo.message ? replyTo : undefined;
  const isMultiOrderContext = Boolean(
    context &&
    (context.type.trim().toLowerCase() === "orders" ||
      /^\d+\s+orders?\s+attached:/i.test(context.label)),
  );
  const metadata: Record<string, unknown> = {
    ...(!context ? { a: 0 } : {}),
    ...(isMultiOrderContext
      ? {
          a: 1,
          t: "orders",
          l: context?.label.slice(0, 160),
        }
      : {}),
    ...(normalizedReplyTo ? { r: normalizedReplyTo } : {}),
  };
  const message = String(payload.message ?? "").trim();

  if (Object.keys(metadata).length === 0) return message;

  const encodedMetadata = encodeURIComponent(JSON.stringify(metadata));
  return `${ORDER_MESSAGE_METADATA_PREFIX}${encodedMetadata}${ORDER_MESSAGE_METADATA_SUFFIX}${message}`;
}

function serializeOrderMessagePayload(
  payload: CreateOrderMessagePayload,
): Record<string, unknown> {
  const context = normalizeOutgoingConversationContext(payload.context);
  const backendContext = context
    ? {
        type: context.type,
        id: context.id,
        label: context.label,
        images: context.images,
        ...(context.productId ? { productId: context.productId } : {}),
        ...(context.orderId ? { orderId: context.orderId } : {}),
        ...(context.orderIds?.length ? { orderIds: context.orderIds } : {}),
        ...(context.products?.length ? { products: context.products } : {}),
      }
    : undefined;
  const requestPayload: Record<string, unknown> = {
    senderId: String(payload.senderId ?? "").trim() || undefined,
    senderRole: String(payload.senderRole ?? "")
      .trim()
      .toLowerCase(),
    senderName: String(payload.senderName ?? "").trim(),
    message: encodeOrderMessageTransportContent(payload, context),
    channel: payload.channel || "courier_seller",
    ...(payload.vendorId ? { vendorId: payload.vendorId } : {}),
    attachments: payload.attachments || [],
    ...(context
      ? {
          context: backendContext,
          contextType: context.type,
          contextId: context.id,
          contextLabel: context.label,
          contextImages: context.images,
          ...(context.productId ? { productId: context.productId } : {}),
          ...(context.orderId ? { orderId: context.orderId } : {}),
          ...(context.orderIds?.length ? { orderIds: context.orderIds } : {}),
        }
      : {}),
  };

  return requestPayload;
}

function serializeShopConversationMessagePayload(
  payload: SendShopConversationMessagePayload,
): string {
  const context = normalizeOutgoingConversationContext(payload.context);
  const replyTo = payload.replyTo
    ? {
        messageId: String(payload.replyTo.messageId ?? "").trim(),
        senderName:
          String(payload.replyTo.senderName ?? "").trim() || undefined,
        message: String(payload.replyTo.message ?? "").trim(),
      }
    : undefined;
  const normalizedReplyTo =
    replyTo?.messageId && replyTo.message ? replyTo : undefined;
  const requestPayload: Record<string, unknown> = {
    buyerId: String(payload.buyerId ?? "").trim(),
    vendorId: String(payload.vendorId ?? "").trim(),
    buyerName: String(payload.buyerName ?? "").trim(),
    sellerName: String(payload.sellerName ?? "").trim(),
    senderId: String(payload.senderId ?? "").trim(),
    senderRole: String(payload.senderRole ?? "")
      .trim()
      .toLowerCase(),
    senderName: String(payload.senderName ?? "").trim(),
    senderAvatarUrl: String(payload.senderAvatarUrl ?? "").trim() || undefined,
    message: String(payload.message ?? "").trim(),
    ...(payload.attachments?.length
      ? {
          attachments: payload.attachments.map((attachment) => ({
            id: String(attachment.id ?? "").trim(),
            url: String(attachment.url ?? "").trim(),
            fileName: String(attachment.fileName ?? "").trim(),
            contentType: String(attachment.contentType ?? "").trim(),
            mediaType: String(attachment.mediaType ?? "").trim(),
            size: Number(attachment.size ?? 0),
          })),
        }
      : {}),
    ...(normalizedReplyTo
      ? {
          replyTo: normalizedReplyTo,
          replyToMessageId: normalizedReplyTo.messageId,
          replyToSenderName: normalizedReplyTo.senderName,
          replyToMessage: normalizedReplyTo.message,
        }
      : {}),
    ...(context
      ? {
          context,
          contextType: context.type,
          contextId: context.id,
          contextLabel: context.label,
          contextImages: context.images,
          ...(context.products?.length
            ? {
                products: context.products,
                productIds: context.products.map((product) => product.id),
              }
            : {}),
          ...(context.productId ? { productId: context.productId } : {}),
          ...(context.orderId ? { orderId: context.orderId } : {}),
          ...(context.orderIds?.length ? { orderIds: context.orderIds } : {}),
        }
      : {}),
  };

  return JSON.stringify(requestPayload);
}

function shouldRetryShopMessageWithoutContext(
  error: unknown,
  context?: ConversationMessageContextView,
): boolean {
  if (!context || !(error instanceof AutopartsApiError)) return false;
  if (![400, 404, 409, 422].includes(error.status)) return false;

  const field = String(error.field ?? "")
    .trim()
    .toLowerCase();
  const message = String(error.message ?? "")
    .trim()
    .toLowerCase();

  const bodyDecodeFailure =
    field === "body" &&
    (message.includes("json") ||
      message.includes("request body") ||
      message.includes("decode"));

  return (
    bodyDecodeFailure ||
    field === "context" ||
    field.startsWith("context.") ||
    message.includes("context") ||
    message.includes("product") ||
    message.includes("order") ||
    message.includes("not found")
  );
}

const SHOP_CONVERSATION_ENDPOINTS = [
  "/messages/conversations",
  "/shop-conversations",
] as const;
const SHOP_CONVERSATION_READ_TIMEOUT_MS = 25_000;
const SHOP_CONVERSATION_WRITE_TIMEOUT_MS = 15_000;

function shouldTryNextShopConversationEndpoint(error: unknown): boolean {
  return (
    error instanceof AutopartsApiError &&
    (error.status === 0 || error.status === 404 || error.status === 405)
  );
}

function normalizeShopConversationRealtimeEvent(
  value: unknown,
): ShopConversationRealtimeEvent | null {
  const record = readRecord(value);
  if (!record) return null;

  const type = getString(record.type);
  const buyerId = getString(record.buyerId ?? record.buyer_id);
  const vendorId = getString(
    record.vendorId ?? record.vendor_id ?? record.sellerId ?? record.seller_id,
  );

  if (!type || !buyerId || !vendorId) return null;

  return {
    type,
    buyerId,
    vendorId,
    data: record.data,
    publishedAt: getString(record.publishedAt ?? record.published_at),
  };
}

async function consumeShopConversationEventStream(
  response: Response,
  onEvent: (event: ShopConversationRealtimeEvent) => void,
): Promise<void> {
  if (!response.body || typeof response.body.getReader !== "function") {
    throw new AutopartsApiError(
      "The browser does not support real-time message streaming.",
      0,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;

      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      let boundary = buffer.indexOf("\n\n");

      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf("\n\n");

        const data = block
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n")
          .trim();
        if (!data) continue;

        try {
          const event = normalizeShopConversationRealtimeEvent(
            JSON.parse(data),
          );
          if (event) onEvent(event);
        } catch {
          // Ignore malformed events and keep the Redis stream connected.
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function subscribeToShopConversationEvents({
  participantId,
  role,
  vendorId,
  onEvent,
  onError,
}: SubscribeShopConversationEventsInput): () => void {
  const normalizedParticipantId = String(participantId ?? "").trim();
  if (!normalizedParticipantId || typeof window === "undefined") {
    return () => undefined;
  }

  let closed = false;
  let activeController: AbortController | null = null;

  const connect = async () => {
    while (!closed) {
      let lastError: unknown = null;

      for (const baseUrl of getBaseUrlCandidates()) {
        for (const endpoint of SHOP_CONVERSATION_ENDPOINTS) {
          if (closed) return;

          const controller = new AbortController();
          activeController = controller;
          const query = buildQueryString({
            participantId: normalizedParticipantId,
            role,
            vendorId: String(vendorId ?? "").trim() || undefined,
          });
          const headers = new Headers({ Accept: "text/event-stream" });
          const token = readStoredAuthToken();
          if (token) headers.set("Authorization", `Bearer ${token}`);

          try {
            const response = await fetch(
              `${baseUrl}${endpoint}/events${query}`,
              {
                method: "GET",
                headers,
                credentials: "include",
                cache: "no-store",
                signal: controller.signal,
              },
            );

            if (!response.ok) {
              throw new AutopartsApiError(
                `Real-time messaging failed with status ${response.status}.`,
                response.status,
              );
            }

            preferredApiBaseUrl = baseUrl;
            await consumeShopConversationEventStream(response, onEvent);
            lastError = new AutopartsApiError(
              "The real-time messaging connection closed.",
              0,
            );
          } catch (error) {
            if (closed || controller.signal.aborted) return;
            lastError = error;
          } finally {
            if (activeController === controller) activeController = null;
          }

          if (
            lastError instanceof AutopartsApiError &&
            ![0, 404, 405, 503].includes(lastError.status)
          ) {
            break;
          }
        }
      }

      if (closed) return;
      onError?.(lastError);
      await new Promise<void>((resolve) => window.setTimeout(resolve, 3000));
    }
  };

  void connect();

  return () => {
    closed = true;
    activeController?.abort();
    activeController = null;
  };
}

async function requestShopConversationEndpoint<T = unknown>(
  suffix: "" | "/messages" | "/read",
  query: Record<string, unknown> | undefined,
  options: RequestOptions = {},
): Promise<T> {
  let lastError: unknown;
  let didTimeout = false;
  const timeoutMs =
    suffix === "/messages"
      ? SHOP_CONVERSATION_WRITE_TIMEOUT_MS
      : SHOP_CONVERSATION_READ_TIMEOUT_MS;
  const timeoutController = options.signal ? null : new AbortController();
  const timeoutId = timeoutController
    ? window.setTimeout(() => {
        didTimeout = true;
        timeoutController.abort();
      }, timeoutMs)
    : null;
  const requestOptions: RequestOptions = timeoutController
    ? {
        ...options,
        signal: timeoutController.signal,
        allowReadCacheWithSignal: suffix === "",
      }
    : options;

  try {
    for (const endpoint of SHOP_CONVERSATION_ENDPOINTS) {
      try {
        return await request<T>(
          `${endpoint}${suffix}${buildQueryString(query)}`,
          requestOptions,
        );
      } catch (error) {
        lastError = error;
        if (didTimeout) {
          throw new AutopartsApiError(
            suffix === "/messages"
              ? "Message sending timed out. Please try again."
              : "The conversation request timed out. Please retry.",
            408,
            error,
          );
        }
        if (!shouldTryNextShopConversationEndpoint(error)) {
          throw error;
        }
      }
    }
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new AutopartsApiError("Unable to reach the conversation service.", 0);
}

const SHOP_CONVERSATION_ATTACHMENT_ENDPOINTS = [
  "/messages/conversations/attachments",
  "/shop-conversations/attachments",
] as const;

async function uploadShopConversationAttachment(
  file: File,
  participant: Pick<
    SendShopConversationMessagePayload,
    "buyerId" | "vendorId" | "senderId" | "senderRole"
  >,
  onUploadProgress?: (progress: ApiUploadProgress) => void,
): Promise<ConversationMediaAttachmentView> {
  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("buyerId", String(participant.buyerId ?? "").trim());
  formData.append("vendorId", String(participant.vendorId ?? "").trim());
  formData.append("senderId", String(participant.senderId ?? "").trim());
  formData.append("senderRole", String(participant.senderRole ?? "").trim());

  let lastError: unknown;
  for (const endpoint of SHOP_CONVERSATION_ATTACHMENT_ENDPOINTS) {
    try {
      const response = await requestWithUploadProgress(endpoint, {
        method: "POST",
        body: formData,
        uploadTotalBytes: file.size,
        onUploadProgress,
      });
      const attachment = normalizeConversationMediaAttachment(unwrapEnvelope(response));
      if (!attachment) {
        throw new AutopartsApiError(
          "The backend returned an invalid attachment.",
          0,
          response,
        );
      }
      return attachment;
    } catch (error) {
      lastError = error;
      if (!shouldTryNextShopConversationEndpoint(error)) {
        throw error;
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new AutopartsApiError("Unable to upload the attachment.", 0);
}

const shopConversations = {
  async get(
    buyerId: string,
    vendorId: string,
    signal?: AbortSignal,
  ): Promise<ShopConversationView> {
    const response = await requestShopConversationEndpoint(
      "",
      { buyerId, vendorId, role: "buyer" },
      signal ? { signal } : undefined,
    );
    const conversation = normalizeShopConversation(response);
    if (!conversation) {
      throw new AutopartsApiError(
        "The backend returned an invalid shop conversation.",
        0,
        response,
      );
    }
    return (
      cacheShopConversationList("buyer", buyerId, [conversation]).find(
        (entry) => entry.vendorId === conversation.vendorId,
      ) ?? conversation
    );
  },

  async getForQualityAssessor(
    assessorId: string,
    vendorId: string,
    signal?: AbortSignal,
  ): Promise<ShopConversationView> {
    const response = await requestShopConversationEndpoint(
      "",
      { buyerId: assessorId, vendorId, role: "quality_assessor" },
      signal ? { signal } : undefined,
    );
    const conversation = normalizeShopConversation(response);
    if (!conversation) {
      throw new AutopartsApiError(
        "The backend returned an invalid assessor conversation.",
        0,
        response,
      );
    }
    return conversation;
  },

  async listForQualityAssessor(
    assessorId: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<ShopConversationView[]> {
    const response = await requestShopConversationEndpoint(
      "",
      { buyerId: assessorId, role: "quality_assessor" },
      { forceRefresh: Boolean(options.forceRefresh) },
    );
    const unwrapped = unwrapEnvelope(response);
    const items = Array.isArray(unwrapped)
      ? unwrapped
      : Array.isArray(readRecord(unwrapped)?.items)
        ? (readRecord(unwrapped)?.items as unknown[])
        : [];
    return items
      .map((item) => normalizeShopConversation(item))
      .filter((item): item is ShopConversationView => Boolean(item));
  },

  async listForSeller(
    vendorId: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<ShopConversationView[]> {
    try {
      const response = await requestShopConversationEndpoint(
        "",
        { vendorId, role: "seller" },
        { forceRefresh: Boolean(options.forceRefresh) },
      );
      const unwrapped = unwrapEnvelope(response);
      const items = Array.isArray(unwrapped)
        ? unwrapped
        : Array.isArray(readRecord(unwrapped)?.items)
          ? (readRecord(unwrapped)?.items as unknown[])
          : [];
      const conversations = items
        .map((item) => normalizeShopConversation(item))
        .filter((item): item is ShopConversationView => Boolean(item));
      return cacheShopConversationList("seller", vendorId, conversations);
    } catch (error) {
      const cached = readCachedShopConversationList("seller", vendorId);
      if (cached.length > 0) return cached;
      throw error;
    }
  },

  async listForBuyer(
    buyerId: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<ShopConversationView[]> {
    try {
      const response = await requestShopConversationEndpoint(
        "",
        { buyerId, role: "buyer" },
        { forceRefresh: Boolean(options.forceRefresh) },
      );
      const unwrapped = unwrapEnvelope(response);
      const items = Array.isArray(unwrapped)
        ? unwrapped
        : Array.isArray(readRecord(unwrapped)?.items)
          ? (readRecord(unwrapped)?.items as unknown[])
          : [];
      const conversations = items
        .map((item) => normalizeShopConversation(item))
        .filter((item): item is ShopConversationView => Boolean(item));
      return cacheShopConversationList("buyer", buyerId, conversations);
    } catch (error) {
      const cached = readCachedShopConversationList("buyer", buyerId);
      if (cached.length > 0) return cached;
      throw error;
    }
  },

  async uploadAttachment(
    file: File,
    participant: Pick<
      SendShopConversationMessagePayload,
      "buyerId" | "vendorId" | "senderId" | "senderRole"
    >,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
  ): Promise<ConversationMediaAttachmentView> {
    return uploadShopConversationAttachment(file, participant, onUploadProgress);
  },

  async sendMessage(
    payload: SendShopConversationMessagePayload,
  ): Promise<ShopConversationMessageResult> {
    const requestOptions = {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: serializeShopConversationMessagePayload(payload),
    } as const;

    let response: unknown;
    try {
      response = await requestShopConversationEndpoint(
        "/messages",
        undefined,
        requestOptions,
      );
    } catch (error) {
      if (!shouldRetryShopMessageWithoutContext(error, payload.context)) {
        throw error;
      }

      response = await requestShopConversationEndpoint("/messages", undefined, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: serializeShopConversationMessagePayload({
          ...payload,
          context: undefined,
        }),
      });
    }

    const result = normalizeShopConversationMessageResult(response);
    if (!result) {
      throw new AutopartsApiError(
        "The backend returned an invalid shop message.",
        0,
        response,
      );
    }
    cacheShopConversationMessageResult(payload, result);
    return result;
  },

  subscribe(input: SubscribeShopConversationEventsInput): () => void {
    return subscribeToShopConversationEvents(input);
  },

  async markRead(payload: MarkShopConversationReadPayload): Promise<void> {
    await requestShopConversationEndpoint("/read", undefined, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: {
        buyerId: String(payload.buyerId ?? "").trim(),
        vendorId: String(payload.vendorId ?? "").trim(),
        readerId: String(payload.readerId ?? "").trim(),
        readerRole: String(payload.readerRole ?? "")
          .trim()
          .toLowerCase(),
      },
    });
    markCachedShopConversationRead(payload);
  },
};

export async function switchMarketplaceRole(
  userId: string,
  role: Extract<MarketplaceRole, "buyer" | "seller">,
): Promise<AuthenticatedUser> {
  const updatedUser = await users.update<UserView>(userId, {
    role,
    accountType: role,
    userRole: role,
  });

  if (role === "seller") {
    await ensureSellerVendorProfile(updatedUser);
  }

  updateStoredAuthSessionUser(updatedUser);
  return updatedUser;
}

const assistant = {
  async capabilities(signal?: AbortSignal): Promise<AssistantCapabilitiesResponse> {
    return request<AssistantCapabilitiesResponse>("/assistant/capabilities", {
      method: "GET",
      signal,
    });
  },
  async chat(
    payload: AssistantChatPayload,
    signal?: AbortSignal,
  ): Promise<AssistantChatResponse> {
    return request<AssistantChatResponse>("/assistant/chat", {
      method: "POST",
      body: payload,
      signal,
    });
  },
  async accountRecovery(message: string, locked = false): Promise<{ reply: string; options?: Array<{ id: string; label: string }> }> {
    return request("/assistant/account-recovery", { method: "POST", skipAuth: true, body: { message, locked } });
  },
  async feedback(
    payload: AssistantFeedbackPayload,
    signal?: AbortSignal,
  ): Promise<AssistantFeedbackResponse> {
    return request<AssistantFeedbackResponse>("/assistant/feedback", {
      method: "POST",
      body: payload,
      signal,
    });
  },
};


export async function fetchAssessmentReport(
  range?: { from?: string; to?: string },
): Promise<AssessmentReportView> {
  const params = new URLSearchParams();
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  const query = params.toString();
  return request<AssessmentReportView>(`/assessment-reports${query ? `?${query}` : ""}`);
}

export async function fetchRoleReportInsights(
  range?: { from?: string; to?: string },
): Promise<RoleReportView> {
  const params = new URLSearchParams();
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  const query = params.toString();
  return request<RoleReportView>(`/reports/insights${query ? `?${query}` : ""}`);
}

export async function fetchReportEmailPreferences(): Promise<ReportEmailPreferences> {
  return request<ReportEmailPreferences>("/reports/email-preferences");
}

export async function updateReportEmailPreferences(
  payload: Partial<ReportEmailPreferences>,
): Promise<ReportEmailPreferences> {
  return request<ReportEmailPreferences>("/reports/email-preferences", {
    method: "PATCH",
    body: payload,
  });
}

export async function sendCurrentUserReport(
  payload: SendReportEmailPayload,
): Promise<void> {
  await request("/reports/email", { method: "POST", body: payload });
}

export async function fetchPushNotificationConfig(): Promise<PushNotificationConfig> {
  return request<PushNotificationConfig>("/push/config", { forceRefresh: true });
}

export async function registerPushSubscription(
  payload: PushSubscriptionPayload,
): Promise<void> {
  await request("/push/subscriptions", { method: "POST", body: payload });
}

export async function removePushSubscription(endpoint: string): Promise<void> {
  await request("/push/subscriptions", {
    method: "DELETE",
    body: { endpoint },
  });
}

export async function registerReportPushSubscription(
  payload: ReportPushSubscriptionPayload,
): Promise<void> {
  await registerPushSubscription(payload);
}

export async function removeReportPushSubscription(endpoint: string): Promise<void> {
  await removePushSubscription(endpoint);
}

export async function sendUserReport(
  user: User,
  payload: SendReportEmailPayload,
): Promise<void> {
  const identifier = encodeURIComponent(getUserRequestIdentifier(user));
  await request(`/admin/users/${identifier}/reports/email`, {
    method: "POST",
    body: payload,
  });
}

async function fetchAdminReports(
  from?: string,
  to?: string,
): Promise<AdminReportResponse> {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const query = params.toString();
  return request<AdminReportResponse>(
    `/admin/reports${query ? `?${query}` : ""}`,
  );
}

async function rewardAdminReportParticipant(
  payload: AdminReportRewardPayload,
): Promise<AdminReportRewardResponse> {
  return request<AdminReportRewardResponse>("/admin/reports/rewards", {
    method: "POST",
    body: payload,
  });
}

const warehouse = {
  seller: {
    async get(): Promise<SellerWarehouseResponse> {
      return request<SellerWarehouseResponse>("/seller/warehouse", { forceRefresh: true });
    },
    async createTransfer(payload: { productId: string; quantity: number; notes?: string }): Promise<WarehouseTransferView> {
      return request<WarehouseTransferView>("/seller/warehouse/transfers", { method: "POST", body: payload });
    },
    async cancelTransfer(transferId: string): Promise<WarehouseTransferView> {
      return request<WarehouseTransferView>(`/seller/warehouse/transfers/${encodeURIComponent(transferId)}/cancel`, { method: "POST", body: {} });
    },
  },
  admin: {
    async get(): Promise<WarehouseOverviewResponse> {
      return request<WarehouseOverviewResponse>("/admin/warehouse", { forceRefresh: true });
    },
    async createBin(payload: { code: string; zone: string; aisle?: string; rack?: string; shelf?: string; capacity?: number; active?: boolean }): Promise<WarehouseBinView> {
      return request<WarehouseBinView>("/admin/warehouse/bins", { method: "POST", body: payload });
    },
    async updateBin(binId: string, payload: { code?: string; zone?: string; aisle?: string; rack?: string; shelf?: string; capacity?: number; active?: boolean }): Promise<WarehouseBinView> {
      return request<WarehouseBinView>(`/admin/warehouse/bins/${encodeURIComponent(binId)}`, { method: "PATCH", body: payload });
    },
    async receiveTransfer(transferId: string, payload: { quantity: number; binId?: string; binCode?: string; scanCode: string }): Promise<WarehouseTransferView> {
      return request<WarehouseTransferView>(`/admin/warehouse/transfers/${encodeURIComponent(transferId)}/receive`, { method: "POST", body: payload });
    },
    async moveInventory(payload: { productId: string; vendorId: string; fromBinId: string; toBinId: string; quantity: number; scanCode: string }): Promise<{ moved: boolean }> {
      return request<{ moved: boolean }>("/admin/warehouse/inventory/move", { method: "POST", body: payload });
    },
    async scanTask(taskId: string, payload: { stage: "PICK" | "PACK"; scanCode: string; productId?: string; quantity?: number }): Promise<WarehouseTaskView> {
      return request<WarehouseTaskView>(`/admin/warehouse/tasks/${encodeURIComponent(taskId)}/scan`, { method: "POST", body: payload });
    },
    async dispatchTask(taskId: string, scanCode: string): Promise<WarehouseTaskView> {
      return request<WarehouseTaskView>(`/admin/warehouse/tasks/${encodeURIComponent(taskId)}/dispatch`, { method: "POST", body: { scanCode } });
    },
  },
};

const sellerServices = {
  async get(): Promise<SellerServicesResponse> {
    return request<SellerServicesResponse>("/seller/services", { forceRefresh: true });
  },

  async createSubscriptionCheckout(): Promise<SellerSubscriptionCheckoutView> {
    return request<SellerSubscriptionCheckoutView>("/seller/services/subscription/checkout", {
      method: "POST",
      body: {},
    });
  },

  async confirmSubscriptionCheckout(sessionId: string): Promise<SellerSubscriptionView> {
    return request<SellerSubscriptionView>("/seller/services/subscription/confirm", {
      method: "POST",
      body: { sessionId },
    });
  },

  async cancelSubscription(): Promise<SellerSubscriptionView> {
    return request<SellerSubscriptionView>("/seller/services/subscription/cancel", {
      method: "PATCH",
      body: {},
    });
  },

  async setFulfillment(productId: string, enabled: boolean): Promise<SellerServiceProductView> {
    return request<SellerServiceProductView>(
      `/seller/services/fulfillment/${encodeURIComponent(productId)}`,
      { method: "PATCH", body: { enabled } },
    );
  },

  async createAdvertisingCampaign(payload: SellerAdvertisingCreatePayload): Promise<AdvertisingCampaignView> {
    return request<AdvertisingCampaignView>("/seller/services/advertising", {
      method: "POST",
      body: payload,
    });
  },

  async cancelAdvertisingCampaign(campaignId: string): Promise<AdvertisingCampaignView> {
    return request<AdvertisingCampaignView>(
      `/seller/services/advertising/${encodeURIComponent(campaignId)}/cancel`,
      { method: "PATCH", body: {} },
    );
  },
};

async function recordSponsoredProductClick(productId: string): Promise<void> {
  await request(`/products/${encodeURIComponent(productId)}/ad-click`, {
    method: "POST",
    body: {},
  });
}

const accountRecovery = {
  async start(payload: { challengeToken: string; requestType: "unlock" | "reset_2fa"; message: string }): Promise<AccountRecoveryCase> {
    return request<AccountRecoveryCase>("/auth/account-recovery/cases", { method: "POST", skipAuth: true, body: payload });
  },
  async get(caseId: string, accessToken: string): Promise<AccountRecoveryCase> {
    return request<AccountRecoveryCase>(`/auth/account-recovery/cases/${encodeURIComponent(caseId)}`, { method: "GET", skipAuth: true, forceRefresh: true, headers: { "X-Recovery-Token": accessToken } });
  },
  async message(caseId: string, accessToken: string, message: string): Promise<AccountRecoveryCase> {
    return request<AccountRecoveryCase>(`/auth/account-recovery/cases/${encodeURIComponent(caseId)}/messages`, {
      method: "POST",
      skipAuth: true,
      headers: { "X-Recovery-Token": accessToken },
      body: { message },
    });
  },
  async verifyOTP(caseId: string, accessToken: string, code: string, remember = false): Promise<AuthSession> {
    const response = await request(`/auth/account-recovery/cases/${encodeURIComponent(caseId)}/verify-otp`, {
      method: "POST",
      skipAuth: true,
      preserveEnvelope: true,
      headers: { "X-Recovery-Token": accessToken },
      body: { code, remember },
    });
    return resolveAuthSessionOrThrow(response, remember);
  },
  async listAdmin(status = ""): Promise<AccountRecoveryCase[]> {
    return request<AccountRecoveryCase[]>(`/admin/account-recovery/cases${status ? `?status=${encodeURIComponent(status)}` : ""}`, { forceRefresh: true });
  },
  async adminMessage(caseId: string, message: string): Promise<AccountRecoveryCase> {
    return request<AccountRecoveryCase>(`/admin/account-recovery/cases/${encodeURIComponent(caseId)}/messages`, { method: "POST", body: { message } });
  },
  async setRequirements(caseId: string, comment: string, requiredKinds: string[]): Promise<AccountRecoveryCase> {
    return request<AccountRecoveryCase>(`/admin/account-recovery/cases/${encodeURIComponent(caseId)}/requirements`, { method: "POST", body: { comment, requiredKinds } });
  },
  async decide(caseId: string, decision: "approve" | "reject", comment: string, resetTwoFactor: boolean): Promise<AccountRecoveryCase> {
    return request<AccountRecoveryCase>(`/admin/account-recovery/cases/${encodeURIComponent(caseId)}/decision`, { method: "POST", body: { decision, comment, resetTwoFactor } });
  },
  async uploadEvidence(caseId: string, accessToken: string, kind: string, file: File, onProgress?: (percent: number) => void): Promise<AccountRecoveryCase> {
    const resumeKey = `sparelynk:recovery-upload:${caseId}:${kind}:${file.name}:${file.size}:${file.lastModified}`;
    let initiated: AccountRecoveryUploadStatus | null = null;
    const recoveryStorage = getStorage("session");
    let savedUploadId: string | null = null;
    try {
      savedUploadId = recoveryStorage?.getItem(resumeKey) ?? null;
    } catch {
      savedUploadId = null;
    }
    if (savedUploadId) {
      try {
        initiated = await request<AccountRecoveryUploadStatus>(`/auth/account-recovery/uploads/status?uploadId=${encodeURIComponent(savedUploadId)}`, {
          method: "GET",
          skipAuth: true,
          forceRefresh: true,
          headers: { "X-Recovery-Token": accessToken },
        });
      } catch {
        try {
          recoveryStorage?.removeItem(resumeKey);
        } catch {
          // Restricted storage should not block a fresh upload.
        }
      }
    }
    if (!initiated) {
      initiated = await request<AccountRecoveryUploadStatus>("/auth/account-recovery/uploads/initiate", {
        method: "POST",
        skipAuth: true,
        headers: { "X-Recovery-Token": accessToken },
        body: { caseId, kind, fileName: file.name, contentType: file.type || "application/octet-stream", fileSize: file.size },
      });
      try {
        recoveryStorage?.setItem(resumeKey, initiated.uploadId);
      } catch {
        // Uploads still work when browser storage is unavailable.
      }
    }
    const uploaded = new Set(initiated.uploadedChunks ?? []);
    onProgress?.(initiated.percent ?? 0);
    for (let index = 0; index < initiated.totalChunks; index += 1) {
      if (uploaded.has(index)) continue;
      const start = index * initiated.chunkSize;
      const chunk = file.slice(start, Math.min(file.size, start + initiated.chunkSize));
      let lastError: unknown;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const form = new FormData();
          form.append("uploadId", initiated.uploadId);
          form.append("chunkIndex", String(index));
          form.append("chunk", chunk, file.name);
          const status = await request<AccountRecoveryUploadStatus>("/auth/account-recovery/uploads/chunk", {
            method: "POST",
            skipAuth: true,
            headers: { "X-Recovery-Token": accessToken },
            body: form,
          });
          onProgress?.(status.percent ?? Math.round(((index + 1) / initiated.totalChunks) * 100));
          lastError = undefined;
          break;
        } catch (error) {
          lastError = error;
          await new Promise((resolve) => window.setTimeout(resolve, 350 * (attempt + 1)));
        }
      }
      if (lastError) throw lastError;
    }
    const completed = await request<AccountRecoveryCase>("/auth/account-recovery/uploads/complete", {
      method: "POST",
      skipAuth: true,
      headers: { "X-Recovery-Token": accessToken },
      body: { uploadId: initiated.uploadId },
    });
    try {
      recoveryStorage?.removeItem(resumeKey);
    } catch {
      // Ignore storage cleanup failures after a successful upload.
    }
    return completed;
  },
};

const partRequests = {
  async list(
    query: PageQuery & { status?: PartRequestStatus } = {},
  ): Promise<PageResult<PartRequestView>> {
    return request<PageResult<PartRequestView>>(
      `/part-requests${buildQueryString(query)}`,
      { forceRefresh: true },
    );
  },

  async options(): Promise<PartRequestOptions> {
    return request<PartRequestOptions>("/part-requests/options", {
      forceRefresh: true,
    });
  },

  async pendingCount(): Promise<PartRequestPendingCountView> {
    return request<PartRequestPendingCountView>("/part-requests/pending-count", {
      forceRefresh: true,
    });
  },

  async notifications(limit = 20): Promise<PartRequestNotificationsView> {
    const normalizedLimit = Math.min(50, Math.max(1, Math.trunc(limit) || 20));
    return request<PartRequestNotificationsView>(
      `/part-requests/notifications?limit=${normalizedLimit}`,
      { forceRefresh: true },
    );
  },

  async markNotificationRead(id: string): Promise<void> {
    await request(`/part-requests/notifications/${encodeURIComponent(id)}/read`, {
      method: "PATCH",
      body: {},
    });
  },

  async markAllNotificationsRead(): Promise<void> {
    await request("/part-requests/notifications/read-all", {
      method: "PATCH",
      body: {},
    });
  },

  async checkDuplicate(
    payload: CreatePartRequestPayload,
  ): Promise<PartRequestCheckResult> {
    return request<PartRequestCheckResult>(
      "/part-requests/check",
      { method: "POST", body: payload },
    );
  },

  async create(
    payload: CreatePartRequestPayload,
  ): Promise<PartRequestView | PartRequestExistingProductView> {
    return request<PartRequestView | PartRequestExistingProductView>(
      "/part-requests",
      {
        method: "POST",
        body: payload,
      },
    );
  },

  async update(
    id: string,
    payload: CreatePartRequestPayload,
  ): Promise<PartRequestView | PartRequestExistingProductView> {
    return request<PartRequestView | PartRequestExistingProductView>(
      `/part-requests/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: payload,
      },
    );
  },

  async uploadImage(
    file: File,
    onUploadProgress?: (progress: ApiUploadProgress) => void,
  ): Promise<PartRequestImageUploadView> {
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("folder", "part-requests");
    return requestWithUploadProgress<PartRequestImageUploadView>(
      "/part-requests/upload-image",
      {
        method: "POST",
        body: form,
        uploadTotalBytes: file.size,
        onUploadProgress,
      },
    );
  },

  async review(
    id: string,
    decision: "approve" | "reject",
    note = "",
  ): Promise<PartRequestView> {
    return request<PartRequestView>(
      `/part-requests/${encodeURIComponent(id)}/review`,
      { method: "PATCH", body: { decision, note } },
    );
  },

  async respond(id: string, note = ""): Promise<PartRequestView> {
    return request<PartRequestView>(
      `/part-requests/${encodeURIComponent(id)}/seller-responses`,
      { method: "POST", body: { note } },
    );
  },
};

const navigation = {
  async pendingActions(): Promise<NavigationPendingActionsResponse> {
    return request<NavigationPendingActionsResponse>(
      "/navigation/pending-actions",
      { forceRefresh: true },
    );
  },
};

const health = {
  getBaseUrl() {
    return getConfiguredBaseUrl();
  },

  async check() {
    return request("/health");
  },
};

export const autopartsApi = {
  auth,
  assistant,
  accountRecovery,
  navigation,
  partRequests,
  users,
  products,
  reviews,
  orders,
  shopConversations,
  vendors,
  cart,
  wishlist,
  customerData,
  wallet,
  sellerServices,
  warehouse,
  recordSponsoredProductClick,
  health,
  reports: {
    fetchEmailPreferences: fetchReportEmailPreferences,
    updateEmailPreferences: updateReportEmailPreferences,
    sendToCurrentUser: sendCurrentUserReport,
    fetchInsights: fetchRoleReportInsights,
    fetchAssessments: fetchAssessmentReport,
  },
  buyer: {
    fetchProducts,
    fetchFeaturedProducts,
    fetchPlatformStats,
    fetchProductCompatibility,
    fetchOrdersByBuyer,
    fetchUsers,
    getAvailableBrands,
  },
  admin: {
    refreshMarketplaceData,
    fetchProducts,
    approveProductListing,
    rejectProductListing,
    addProductReviewComment,
    deleteProductReviewComment,
    clearProductAdminReviewComment,
    deleteProductListing,
    createUserAccount,
    updateUserAccount,
    reviewCourierCredentials,
    reviewSellerCredentials,
    suspendUserAccount,
    activateUserAccount,
    deleteUserAccount,
    resendUserCredentials,
    sendUserMessage,
    sendUserReport,
    fetchReports: fetchAdminReports,
    rewardReportParticipant: rewardAdminReportParticipant,
    accountRecovery,
  },
  request,
  requestWithUploadProgress,
};

export default autopartsApi;
